// pages/StaffDashboardPage.jsx — extracted from App.js (behaviour-preserving split).
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import {
  AlertTriangle,
  ArrowLeft,
  ArrowLeftRight,
  ArrowRight,
  Ban,
  Banknote,
  Bell,
  BellOff,
  Calendar,
  Check,
  CheckCircle2,
  ChevronDown,
  ClipboardCheck,
  ClipboardList,
  Coins,
  CreditCard,
  HandCoins,
  Landmark,
  LogOut,
  Mail,
  Minus,
  Phone,
  Plus,
  Printer,
  QrCode,
  Receipt,
  RefreshCw,
  Search,
  Shuffle,
  SplitSquareHorizontal,
  Ticket,
  Undo2,
  Users,
  Wallet,
  Wine,
  X,
  X as XIcon,
  History,
} from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { FastInput } from "@/components/FastInput";
import { PendingApprovalsCard } from "@/components/PendingApprovalsCard";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "@/components/ui/sonner";
import { useRealtimeConnection, useRealtimeEvent } from "@/lib/realtime";
import { applyRealtimeEvent, hydrateFromSnapshot, loadDashboardBundle, deleteOne as localDbDeleteOne } from "@/lib/localDb";
import { dedupeLocalOrders, isLocalOrderId, mergeTableSessions, replaceWithServerSessions, serverHasLocalOrder, upsertServerOrder, withRecountedOrders } from "@/lib/mergeState";
import { applyTendersToSession, ledgerTotals, localWrite, serviceFeeOn, sessionServiceFeePct, withRecomputedBill } from "@/lib/eventEngine";
import { EventType } from "@/lib/events";
import { EVENT_SYNC_FLUSHED, hasUnsyncedEventsForSession, queuedRowIds } from "@/lib/eventSync";
import { performStockAction } from "@/lib/stockSync";
import { FallbackBlockDialog, ServiceAvailabilityBadge } from "@/components/prep/FallbackBlockDialog";
import { printSlip } from "@/lib/printer";
import { PrinterSettingsDialog } from "@/components/PrinterSettingsDialog";
import { ManagerPinDialog } from "@/components/ManagerPinDialog";
import { PendingOrdersPanel } from "@/components/PendingOrdersPanel";
import { LONG_WAIT_MIN, LongWaitAlerts, longWaitOrders, longWaitSessionIds } from "@/components/LongWaitAlerts";
import { isSessionPaid, sessionPaymentState, verifyManagerPin } from "@/lib/tableClose";
import { MeshStatusPill } from "@/components/MeshStatusPill";
import { crumb as crashCrumb } from "@/lib/crashReporter";
import {
  diffSettings,
  hasReceiptDrift,
  loadCachedBusiness,
  mergeBusinessSettings,
  reportSettingsDrift,
  saveCachedBusiness,
  STALE_AFTER_MS as SETTINGS_STALE_AFTER_MS,
} from "@/lib/settingsSync";
import { useConnectivity } from "@/hooks/useConnectivity";
import {
  API,
  BACKEND_URL,
  clearSession,
  confirmSession,
  formatPrice,
  getOrderTimeLabel,
  getSession,
  http,
  isOfflineToken,
  isTransientCloudError,
  postWithRetry,
  useOfflineSessionUpgrade,
} from "@/app/core";
import { ChimeVolumeControl, playLongWaitChime, playOrderReadyChime, playStaffChime, playStaffConfirmationChime, useLoginChimeOnMount } from "@/app/chimes";
import { beginLogoutSuppression, exitFullscreenIfActive, FullscreenToggleButton, ThemeToggleButton, useCoalescedUpdater } from "@/app/fullscreen";
import { DashboardLoadingState, EmptyState, computeSidesPricingForLine, groupTipRowsBySplitTable, SplitGuestBreakdown, StatusBadge, useAutoLogoutOnHidden } from "@/app/fragments";
import { KitchenTimeAgo } from "@/pages/stations/stationShared";
import { SideSelectionSheet } from "@/app/menuFragments";



function PollingIndicator() {
  return (
    <div className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-3 py-1 text-xs font-medium text-muted-foreground">
      <span className="polling-dot" />
      Live updates
    </div>
  );
}

/**
 * StaffDashboardPage
 *
 * Session 30.10 — EMBEDDED MODE. The Owner/Manager Overview mounts this
 * same component with `embedded` + `embeddedSession` (the owner token) so
 * managers get the exact Staff table workflow — receipt, add items,
 * split/merge/move/void, move to account, payments, print/email, close
 * (PIN-gated when unpaid) — without a second implementation. In embedded
 * mode we: use the owner session, never redirect to /staff, hide the
 * Staff header + notifications feed, replace the table chips with a
 * compact "Active Tables ▾" selector, mute chimes, and hide EVERY
 * "Open table" entry point (opening tables stays a staff workflow).
 */
// Session 69 — one memoised card per menu item in the staff "Add items"
// grid. Before this the whole 80–300 card grid was re-created on EVERY tap
// (each tap re-renders the 5 000-line dashboard); now only the tapped card
// re-renders. Handlers are stable (useCallback + refs in the parent) so
// React.memo's shallow compare holds.
const StaffAddItemCard = React.memo(function StaffAddItemCard({ item, quantity, stagedCount, needsConfig, inSearch, onIncrement, onDecrement }) {
  const inCart = quantity > 0 || stagedCount > 0;
  // Session 72 — an item the server says may NOT be sold (out of stock and
  // not flagged "sell anyway") is rendered disabled on EVERY staff device:
  // no Add button, greyed card, explicit badge. Tapping it only explains why.
  const soldOut = item.sellable === false;
  const handleIncrement = (e) => { e.preventDefault(); e.stopPropagation(); onIncrement(item, quantity, needsConfig); };
  const handleDecrement = (e) => { e.preventDefault(); e.stopPropagation(); onDecrement(item, quantity); };
  return (
    <div
      role="button"
      tabIndex={0}
      aria-disabled={soldOut || undefined}
      onClick={handleIncrement}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") handleIncrement(e); }}
      className={`group relative flex min-h-[7.5rem] select-none flex-col justify-between rounded-2xl border p-3 text-left transition-[transform,background-color,border-color] duration-150 ${
        soldOut
          ? "cursor-not-allowed border-dashed border-border bg-muted/40 opacity-60"
          : inCart
          ? "cursor-pointer border-[hsl(var(--accent))] bg-[hsl(var(--accent))]/10 shadow-sm active:scale-[0.98]"
          : "cursor-pointer border-border bg-card hover:border-[hsl(var(--accent))]/50 hover:bg-secondary/40 active:scale-[0.98]"
      }`}
      style={{ touchAction: "manipulation" }}
      data-testid={`staff-add-items-row-${item.id}`}
      data-sold-out={soldOut ? "true" : undefined}
    >
      <div className="min-w-0">
        <p className="line-clamp-2 text-[15px] font-semibold leading-snug">{item.name}</p>
        <p className="mt-1 flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-sm font-semibold tabular-nums text-foreground/80">
          {formatPrice(item.price)}
          {inSearch && item._categoryName ? <span className="rounded-full bg-secondary px-1.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">{item._categoryName}</span> : null}
          {needsConfig && !soldOut ? <span className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground/70">customisable</span> : null}
        </p>
        {/* Session 41 — service-location availability (policy × stock); hidden items never reach here. */}
        {soldOut && !item.service_availability ? (
          <span className="mt-1 inline-flex items-center rounded-full bg-rose-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-rose-700 dark:bg-rose-950/50 dark:text-rose-300" data-testid={`staff-add-items-oos-${item.id}`}>Out of stock</span>
        ) : (
          <ServiceAvailabilityBadge availability={item.service_availability} className="mt-1" />
        )}
      </div>
      {/* Session 72 — the footer WRAPS: on a 2-column phone grid the
          "1 configured" pill and the Configure button no longer collide;
          the pill takes its own line and the button stays right-aligned. */}
      <div className="mt-2 flex flex-wrap items-end justify-end gap-x-2 gap-y-1.5">
        {stagedCount > 0 ? (
          <span className="mr-auto inline-flex h-6 max-w-full items-center truncate whitespace-nowrap rounded-full bg-[hsl(var(--primary))]/15 px-2 text-[11px] font-semibold text-[hsl(var(--primary))]" data-testid={`staff-add-items-configured-${item.id}`}>{stagedCount} configured</span>
        ) : null}
        {soldOut ? (
          <span className="inline-flex h-10 shrink-0 items-center rounded-xl border border-border px-3.5 text-sm font-semibold text-muted-foreground" data-testid={`staff-add-items-unavailable-${item.id}`}>
            <Ban className="mr-1 h-4 w-4" />
            Unavailable
          </span>
        ) : !needsConfig && quantity ? (
          <div className="flex shrink-0 items-center gap-0.5 rounded-full border border-border bg-background p-0.5" onClick={(e) => e.stopPropagation()} style={{ touchAction: "manipulation" }}>
            <button type="button" aria-label={`Remove one ${item.name}`} className="flex h-10 w-10 items-center justify-center rounded-full text-foreground transition-transform hover:bg-secondary active:scale-90" onClick={handleDecrement} data-testid={`staff-add-items-decrement-${item.id}`}><Minus className="h-4 w-4" /></button>
            <span className="min-w-7 text-center text-base font-bold tabular-nums" data-testid={`staff-add-items-qty-${item.id}`}>{quantity}</span>
            <button type="button" aria-label={`Add another ${item.name}`} className="flex h-10 w-10 items-center justify-center rounded-full bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))] transition-transform hover:bg-[hsl(var(--accent))]/90 active:scale-90" onClick={handleIncrement} data-testid={`staff-add-items-increment-${item.id}`}><Plus className="h-4 w-4" /></button>
          </div>
        ) : (
          <button
            type="button"
            aria-label={`Add ${item.name}`}
            className="inline-flex h-10 shrink-0 items-center gap-1 rounded-xl bg-[hsl(var(--accent))] px-3.5 text-sm font-semibold text-[hsl(var(--accent-foreground))] transition-transform hover:bg-[hsl(var(--accent))]/90 active:scale-95"
            onClick={handleIncrement}
            style={{ touchAction: "manipulation" }}
            data-testid={`staff-add-items-add-${item.id}`}
          >
            <Plus className="h-4 w-4" />
            {needsConfig ? "Configure" : "Add"}
          </button>
        )}
      </div>
    </div>
  );
});

export function StaffDashboardPage({ embedded = false, embeddedSession = null } = {}) {
  const navigate = useNavigate();
  useLoginChimeOnMount();
  const connectivity = useConnectivity(); // Session 30.8 — LAN/internet tri-state
  const [session, setSessionState] = useState(() => (embedded ? embeddedSession : getSession("staff")));
  useEffect(() => { if (embedded) setSessionState(embeddedSession || null); }, [embedded, embeddedSession]);
  // Session 26 — promote a pending session to confirmed as soon as
  // this dashboard renders successfully. If the render throws, the
  // session remains pending and RoleLoginPage will clear it on the
  // next visit so the user can pick a different waiter instead of
  // being trapped in a crash loop.
  useEffect(() => { if (embedded) return; try { confirmSession("staff"); } catch (_) { /* noop */ } }, [embedded]);
  // Session 46 — hand an offline sign-in over to a real server session the
  // moment the internet is back (prevents the 401 → kick-out loop).
  useOfflineSessionUpgrade("staff", embedded ? null : session, setSessionState);
  // Session 58 — never start from "no settings". Seed the business context
  // from the last settings this device received (localStorage), else from
  // the login response, so receipts don't silently fall back to VAT 15% /
  // default fee while /auth/me is still loading or failing.
  const [context, setContextRaw] = useState(() => {
    try {
      const sessBiz = session?.business || null;
      const cached = loadCachedBusiness(sessBiz?.slug || sessBiz?.id || session?.token);
      const biz = cached || sessBiz;
      if (!biz) return null;
      // When were these settings last confirmed by the server? Cache rows
      // carry `_cached_at`; a login payload is as old as the login itself.
      const seededAt = Date.parse(cached?._cached_at || session?._login_at || "") || Date.now();
      return { role: session?.role || "staff", business: biz, user: session?.user || null, _seeded: cached ? "cache" : "login", _seeded_at: seededAt };
    } catch (_) { return null; }
  });
  const contextRef = useRef(context);
  // { lastOkAt: ms of last authoritative settings, failures: consecutive /auth/me failures }
  const settingsSyncRef = useRef({ lastOkAt: context?._seeded_at || 0, failures: 0, defaultsSince: Date.now() });
  const setContext = useCallback((next) => {
    setContextRaw((prev) => {
      const value = typeof next === "function" ? next(prev) : next;
      contextRef.current = value;
      try {
        const b = value?.business;
        if (b) saveCachedBusiness(b.slug || b.id || session?.token, b);
      } catch (_) { /* best effort */ }
      return value;
    });
  }, [session?.token]);
  /**
   * Apply authoritative business settings from the server (full /auth/me
   * summary, the compact `business_settings` blob on table-sessions, or a
   * `business_settings_updated` realtime event). Auto-corrects the local
   * copy and files a "Settings sync" error-log entry when this device had
   * been working from stale or default values.
   */
  const applyServerBusiness = useCallback((serverBiz, source, { full = false } = {}) => {
    if (!serverBiz || typeof serverBiz !== "object") return;
    const prevCtx = contextRef.current;
    const prevBiz = prevCtx?.business || null;
    const sync = settingsSyncRef.current;
    const now = Date.now();
    try {
      const diff = diffSettings(prevBiz, serverBiz);
      if (diff.length && hasReceiptDrift(prevBiz, serverBiz)) {
        const staleFor = sync.lastOkAt ? now - sync.lastOkAt : now - (sync.defaultsSince || now);
        // A fresh owner change (we had current settings < 60 s ago) is
        // normal propagation — just apply it. Anything older means this
        // screen was charging guests with wrong numbers: log it.
        if (!prevBiz || prevBiz.vat_rate == null) {
          reportSettingsDrift({ driftKind: "settings_defaults", role: "staff", diff, staleForMs: staleFor, source, business: serverBiz, extra: { seeded_from: prevCtx?._seeded || null } });
        } else if (staleFor > SETTINGS_STALE_AFTER_MS) {
          reportSettingsDrift({ driftKind: "settings_drift", role: "staff", diff, staleForMs: staleFor, source, business: serverBiz, extra: { seeded_from: prevCtx?._seeded || null } });
        }
      }
    } catch (_) { /* detector must never break the dashboard */ }
    sync.lastOkAt = now;
    sync.failures = 0;
    if (full) return; // caller sets the whole context from /auth/me
    setContext((prev) => {
      const merged = mergeBusinessSettings(prev?.business || null, serverBiz);
      if (prev && prev.business && diffSettings(prev.business, serverBiz).length === 0 && prev.business.settings_updated_at === merged.settings_updated_at) return prev;
      return { ...(prev || { role: "staff" }), business: merged };
    });
  }, [setContext]);
  const [notificationsMuted, setNotificationsMuted] = useState(embedded);
  const notificationsMutedRef = useRef(embedded);
  useEffect(() => { notificationsMutedRef.current = notificationsMuted; }, [notificationsMuted]);
  const [menuCatalog, setMenuCatalog] = useState({ categories: [] });
  const [tableSessions, setTableSessionsRaw] = useState([]);
  // Session 72 — ONE SOURCE OF TRUTH. Every optimistic (this-device) change
  // to the table list is stamped here; WS-applied deltas are stamped in
  // `lastWsMutationAtRef`. `fetchDashboard` reads both: when the device has
  // been quiet for QUIET_BEFORE_AUTHORITATIVE_MS before the fetch started,
  // everything it knows about is already in the server snapshot, so the
  // snapshot REPLACES local state (no merge heuristics can keep a ghost
  // alive). While an action is in flight the monotonic merge still applies
  // so the UI never flickers backwards.
  const lastLocalMutationAtRef = useRef(0);
  const lastWsMutationAtRef = useRef(0);
  // Session 72 — after ANY local or WS change to a session's orders, the
  // counters (withRecountedOrders) AND the bill figures (withRecomputedBill:
  // total_value / fee / amount_owed / outstanding / fully_paid) are
  // recomputed with the one server rule, so chips, receipt and close-check
  // read consistent money until the next server snapshot replaces them.
  const recountSessionRef = useRef((sess) => withRecountedOrders(sess));
  recountSessionRef.current = (sess) => {
    const counted = withRecountedOrders(sess);
    if (!counted || !Array.isArray(counted.orders)) return counted;
    try { return withRecomputedBill(counted, contextRef.current?.business || null); } catch (_) { return counted; }
  };
  const QUIET_BEFORE_AUTHORITATIVE_MS = 2500;
  const setTableSessions = useCallback((updater) => {
    lastLocalMutationAtRef.current = Date.now();
    setTableSessionsRaw((prev) => {
      const next = typeof updater === "function" ? updater(prev) : updater;
      if (!Array.isArray(next) || next === prev) return next;
      return next.map((s, i) => (prev && s === prev[i] ? s : recountSessionRef.current(s)));
    });
  }, []);
  const [statusFilter, setStatusFilter] = useState("active-tables");
  // Session 27.99 — Table-session preservation ref.
  //
  // Under load (10+ tables active), operators reported tables that
  // appeared correctly on Device B then randomly disappeared, then
  // sometimes reappeared. Root cause: `fetchDashboard` (line ~10738)
  // OVERWRITES the whole tableSessions state with the server
  // response. If a transient partial-result / timeout / replica-lag
  // window causes the server to briefly omit a session that we've
  // seen a moment ago, the receiver's state loses that session
  // entirely — even though it's still open on the origin device.
  //
  // Fix mirrors the kitchen-notif merge (Session 27.83/27.89): stamp
  // every session we see (from any source — poll OR WS event) into
  // this ref with a timestamp, and when applying a new server
  // response, PRESERVE any session id we've seen in the last 45 s
  // that the server response didn't include. If the session was
  // truly closed the WS `table_closed` event explicitly removes it,
  // OR the 45 s grace expires and the next poll authoritatively
  // drops it.
  const recentlySeenSessionsRef = useRef(new Map());
  // Session 72 — sessions a WS `table_session_closed` removed in the last
  // few seconds; a snapshot that predates the close must not bring them back.
  const recentlyClosedSessionsRef = useRef(new Map());
  const RECENTLY_CLOSED_TTL_MS = 8000;
  // "Open Table" dialog state — used when a waiter wants to start a session
  // on behalf of a customer who doesn't want to scan the menu themselves.
  const [openTableDialog, setOpenTableDialog] = useState({ open: false, tableNumber: "" });
  const submitOpenTable = async () => {
    const tableNumber = String(openTableDialog.tableNumber || "").trim();
    if (!tableNumber) {
      toast.error("Enter a table number or name.");
      return;
    }
    // Session 27.3 — MIGRATED TO LOCAL EVENT ENGINE.
    //
    // "Open Table" was the single biggest offline gap for waiters:
    // when wifi flakes, the previous `http.post("/staff/table-sessions")`
    // failed silently and the waiter couldn't start taking an order.
    //
    // New flow (mirrors the ORDER_CREATED migration in Session 26):
    //   1. Generate a client-side session id + change_id.
    //   2. `localWrite({ type: TABLE_OPENED, payload: { table_session } })`
    //      returns immediately after the reducer stores the session in
    //      IndexedDB — the UI can navigate straight to the "add items"
    //      picker.
    //   3. The event sync loop (eventSync.js) drains the queue when
    //      connectivity returns. The server-side reducer
    //      `_apply_table_opened_event` performs the real Mongo insert
    //      (idempotent by change_id, so replays are safe).
    //
    // We keep an ONLINE fast-path that also hits `/staff/table-sessions`
    // when we're connected — this way the operator instantly sees the
    // authoritative server id / server-side timestamps without waiting
    // for the sync loop's next drain (~3s). If the online call fails we
    // silently fall through to the local-only path; the sync loop will
    // catch up.
    try {
      const localId = (() => {
        try {
          if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
        } catch (_) { /* noop */ }
        return `ts-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
      })();
      const nowIso = new Date().toISOString();
      const actor = session?.user || null;
      const optimisticSession = {
        id: localId,
        business_id: session?.business?.id || null,
        table_number: tableNumber,
        status: "open",
        opened_at: nowIso,
        closed_at: null,
        created_at: nowIso,
        updated_at: nowIso,
        assigned_user_id: actor?.id || null,
        assigned_user_name: actor?.full_name || actor?.name || null,
        assigned_user_role: actor?.role || null,
        assigned_at: actor ? nowIso : null,
        orders: [],
        total_amount: 0,
        outstanding_amount: 0,
      };

      // Write locally FIRST — this is what makes offline "open table" work.
      await localWrite({
        type: EventType.TABLE_OPENED,
        payload: { table_session: optimisticSession },
        session,
      });

      // Session 27.39 — Paint the new session IMMEDIATELY from the
      // optimistic row. The fast-path HTTP call below no longer
      // blocks the UI; it runs in the background and reconciles the
      // authoritative server id via the eventSync ACK handler.
      toast.success(`Table “${tableNumber}” opened.`);
      setOpenTableDialog({ open: false, tableNumber: "" });
      // Session 27.40 — Register the "opened" mutation so the 4s
      // background poll doesn't flash the new table away before the
      // sync loop has pushed it to the cloud.
      markLocalOp(optimisticSession.id, { opened: { ...optimisticSession, orders: [] } });
      setTableSessions((prev) => {
        const list = Array.isArray(prev) ? prev.slice() : [];
        const idx = list.findIndex((s) => s && s.id === optimisticSession.id);
        if (idx >= 0) list[idx] = { ...list[idx], ...optimisticSession };
        else list.push(optimisticSession);
        return list;
      });
      if (optimisticSession.id) {
        setSelectedSessionId(optimisticSession.id);
        setStatusFilter("active-tables");
        setAddItemsOpen(true);
      }

      // Fire-and-forget best-effort online fast-path — never awaited
      // from the UI thread. If it succeeds we swap the optimistic
      // row for the authoritative server row (which usually has the
      // same id thanks to `_change_id` — see server-side reducer).
      // If it fails (offline / slow Cloudflare) the eventSync loop
      // handles delivery and reconciliation later. The waiter is
      // never blocked.
      (async () => {
        try {
          const { data } = await http.post(
            "/staff/table-sessions",
            { table_number: tableNumber, _change_id: localId },
            { headers: authHeaders, timeout: 6000, bestEffort: true },
          );
          if (data?.table_session?.id) {
            const authoritative = data.table_session;
            try {
              const { putOne, deleteOne } = await import("@/lib/localDb");
              if (authoritative.id !== localId) {
                try { await deleteOne("table_sessions", localId); } catch (_) { /* noop */ }
                // Session 27.40 — Transfer the pending local op from
                // the optimistic id to the authoritative server id so
                // the overlay keeps protecting this session on the
                // next poll.
                try {
                  const map = pendingLocalOpsRef.current;
                  const carried = map.get(localId);
                  if (carried) {
                    map.delete(localId);
                    // Update the injected `opened` snapshot to use
                    // the server row so we don't inject a duplicate.
                    map.set(authoritative.id, {
                      ...carried,
                      opened: carried.opened ? { ...carried.opened, id: authoritative.id } : undefined,
                    });
                  }
                } catch (_) { /* noop */ }
              }
              await putOne("table_sessions", authoritative);
            } catch (_) { /* noop */ }
            // Reconcile React state — if the server issued a
            // different id, replace the optimistic row so subsequent
            // ops (add items, payment) hit the real session id.
            setTableSessions((prev) => {
              if (!Array.isArray(prev)) return prev;
              const withoutOptimistic = prev.filter((s) => s && s.id !== localId && s.id !== authoritative.id);
              return [...withoutOptimistic, { ...authoritative, orders: authoritative.orders || [] }];
            });
            setSelectedSessionId((cur) => (cur === localId ? authoritative.id : cur));
          }
        } catch (e) { console.warn("[staff] table session sync failed (will reconcile):", e?.message || e); }
        // Session 28b — Removed fetchDashboard(). Table create produces
        // table_session_opened WS event applied by the WS handler.
      })();
    } catch (error) {
      toast.error(error?.message || "Couldn't open table — please try again.");
    }
  };
  const [selectedSessionId, setSelectedSessionId] = useState("");
  // Session 30.6 — Active Tables clutter control. With dozens of open
  // tables the chip wall becomes unusable, so the grid is collapsed by
  // default: a "Show all tables" switch expands it and a search box finds
  // a specific table either way. The switch is remembered per device.
  // Session 31.20 — DEFAULT ON. The collapsed view hid every table that
  // wasn't selected / long-wait / cash-due, which waiters on a busy floor
  // read as "my tables keep disappearing". A device only collapses the
  // grid if someone explicitly switched it off.
  // Session 31.27 — Tables / Tabs / All filter for the Active Tables list.
  const [sessionKindFilter, setSessionKindFilter] = useState(() => {
    try { return window.localStorage.getItem("nfcs:staff:session-kind") || "all"; } catch (_) { return "all"; }
  });
  const setSessionKind = (kind) => {
    setSessionKindFilter(kind);
    try { window.localStorage.setItem("nfcs:staff:session-kind", kind); } catch (_) { /* noop */ }
  };
  const [showAllTables, setShowAllTables] = useState(() => {
    try { return window.localStorage.getItem("nfcs:staff:show-all-tables") !== "0"; } catch (_) { return true; }
  });
  const [tableSearch, setTableSearch] = useState("");
  // Session 30.30 — show only tables with digital-menu (QR) orders, so
  // staff can see which online cash orders still need to be paid.
  const [onlineTablesOnly, setOnlineTablesOnly] = useState(false);
  const toggleShowAllTables = (next) => {
    setShowAllTables(Boolean(next));
    try { window.localStorage.setItem("nfcs:staff:show-all-tables", next ? "1" : "0"); } catch (_) { /* noop */ }
  };
  const [loading, setLoading] = useState(true);
  const [busyAction, setBusyAction] = useState("");
  // Session 27.117 — Ref-based lock guarding submitAddItems.
  // The React-state `busyAction` disable on the "Place order" button
  // fights click/touchend race conditions: a fast double-tap could
  // enter submitAddItems twice before React had time to re-render
  // with the disabled state, causing two identical orders (with
  // different change_ids, so backend idempotency didn't dedupe them).
  // A ref flips synchronously, so the second re-entry exits before
  // it ever creates a new envelope.
  const submitAddItemsLockRef = useRef(false);
  const [addItemsOpen, setAddItemsOpen] = useState(false);
  // Session 41 — per-ingredient fallback policy gate result (OUT OF STOCK AT
  // SERVICE LOCATION / MANAGER APPROVAL REQUIRED). A rejected attempt, never an order.
  const [fallbackBlock, setFallbackBlock] = useState(null);
  // Items that need sides / prep prompts go through the SideSelectionSheet so
  // the waiter can configure them line-by-line. Each confirmed configuration
  // becomes its own staged line (so the same item with different sides/notes
  // stays separate). Simple items still use `menuItemQuantities` for fast
  // increment/decrement.
  const [staffSheetItem, setStaffSheetItem] = useState(null);
  const [stagedConfiguredLines, setStagedConfiguredLines] = useState([]);
  // Session 27.19 — Review-before-place-order flow. The "Add items"
  // dialog no longer commits directly; instead the primary CTA opens
  // a nested Review dialog so waiters can read the full order back
  // to the customer before it fires to the kitchen/bar.
  const [addItemsReviewOpen, setAddItemsReviewOpen] = useState(false);
  const [moveTableOpen, setMoveTableOpen] = useState(false);
  // Merge tables — combines another active session INTO the current one.
  const [mergeTableOpen, setMergeTableOpen] = useState(false);
  const [mergeTargetId, setMergeTargetId] = useState("");
  // Move to Account — convert outstanding balance into a settle-later tab.
  const [moveToAccountOpen, setMoveToAccountOpen] = useState(false);
  const [accountDraft, setAccountDraft] = useState({ customer_name: "", phone: "", notes: "", account_id: "" });
  // Session 31.2 — Tabs: open a brand-new customer tab (no table) and add
  // items to any open tab through its linked session.
  const [newTabDialog, setNewTabDialog] = useState({ open: false, customer_name: "", phone: "", saving: false });
  const [tabAddBusyId, setTabAddBusyId] = useState("");
  const [receiptOpen, setReceiptOpen] = useState(false);
  const [emailReceiptOpen, setEmailReceiptOpen] = useState(false);
  const [receiptEmail, setReceiptEmail] = useState("");
  // Voucher application on the receipt — staff types a code and the
  // backend attaches `applied_voucher` to the session. The receipt then
  // subtracts the discount amount from the payable total.
  const [voucherDialogOpen, setVoucherDialogOpen] = useState(false);
  const [voucherCodeInput, setVoucherCodeInput] = useState("");
  const [voucherBusy, setVoucherBusy] = useState(false);
  // Split-bill / per-guest receipts on the same table session. Each group
  // is `{id, name, items:[{order_id, line_index}]}`. The receipt view
  // switches between "All" and each guest tab, showing per-guest totals.
  const [splitBillOpen, setSplitBillOpen] = useState(false);
  const [splitGroups, setSplitGroups] = useState([]); // draft groups in the dialog
  const [splitAssignments, setSplitAssignments] = useState({}); // { "orderId#lineIdx": groupId }
  const [splitBusy, setSplitBusy] = useState(false);
  // Session 30 — true after a save attempt with unassigned items; used
  // to visually flag the exact rows that still need a guest.
  const [splitShowMissing, setSplitShowMissing] = useState(false);
  const [activeSplitTab, setActiveSplitTab] = useState("all"); // "all" or group id
  // Per-split payment — stepped dialog matching the main receipt payment flow.
  // Steps: "method" → "cash" | "card" | "split" → "confirm"
  const [splitPayDialog, setSplitPayDialog] = useState({
    open: false, split_id: "", split_name: "", target_total: 0,
    step: "method",       // "method" | "cash" | "card" | "split" | "confirm"
    chosenMethod: "",     // "cash" | "card" | "split"
    cashReceived: "",     // cash flow — amount handed over
    cardAmount: "",       // card flow — amount charged
    splitCash: "",        // split flow — cash portion
    splitCard: "",        // split flow — card portion
    tipDraft: "",         // manual tip input
    busy: false,
  });
  // Manual split entered by staff at close-out so the printed/displayed
  // receipt can show how the table actually paid (some places split a
  // single bill across cash + card). Empty string means "not entered".
  const [cashPaid, setCashPaid] = useState("");
  const [cardPaid, setCardPaid] = useState("");
  // ── Stepped payment workflow (Cash/Card/Split chooser + confirmation) ──
  // The receipt dialog walks staff through Method → Entry → Confirm so the
  // wrong tender can't be selected by accident during a busy shift. State
  // is reset each time the dialog opens (see effect below).
  // Steps: "idle" | "method" | "cash" | "card" | "split" | "confirm"
  const [paymentStep, setPaymentStep] = useState("idle");
  // Amount of cash physically handed over (only used to compute change in
  // the Cash flow — never sent to the backend directly).
  const [cashReceived, setCashReceived] = useState("");
  // Explicit tip entered by the cashier (R, not %). Decouples tip from
  // "overpayment" so cash change isn't accidentally booked as a tip.
  const [tipDraft, setTipDraft] = useState("");
  // Split workflow inputs (cash + card portions that must add up to total).
  const [splitCash, setSplitCash] = useState("");
  const [splitCard, setSplitCard] = useState("");
  // Card workflow input — the actual amount charged to the card. Tip is
  // auto-derived from the overpayment (cardAmount - total).
  const [cardAmount, setCardAmount] = useState("");
  // Session 31.26 — EFT workflow input (amount transferred). Multi-tender:
  // every Cash / Card / EFT entry is APPENDED to the table's ledger and the
  // receipt shows what is still outstanding until the bill is settled.
  const [eftAmount, setEftAmount] = useState("");
  // Manager-PIN gated removal of a mistaken tender from the ledger.
  const [removeTenderPrompt, setRemoveTenderPrompt] = useState({ open: false, payment: null, pin: "", busy: false });
  // Which method the cashier actually chose. Set on entering cash/card/split
  // step and read by the Confirm step to render the right summary.
  const [chosenMethod, setChosenMethod] = useState("");
  // Optional tip + (optional) waiter name for tip attribution. Empty string
  // means "not entered" — the printed/saved receipt only includes them when
  // a non-zero amount has been typed.
  const [tipAmount, setTipAmount] = useState("");
  const [tipRecipient, setTipRecipient] = useState("");
  // Roster of names from the Owner dashboard → Staff tab. Used to power
  // the "Pick" dropdown next to the Waiter input on the receipt dialog.
  const [staffRoster, setStaffRoster] = useState([]);
  // Tip details dialog (Tips button in header) — shows the signed-in
  // waiter their own pending tips with the same drill-down UX as the
  // owner's `Tips → click row` flow.
  const [staffTipDetail, setStaffTipDetail] = useState({ open: false, loading: false, data: null, error: null });
  const [staffTipReceipt, setStaffTipReceipt] = useState({ open: false, order: null });
  const [menuItemQuantities, setMenuItemQuantities] = useState({});
  // Session 70.F — the Add-items cart belongs to ONE table. It used to
  // survive closing the dialog without sending, so drinks a waiter had
  // ticked for Table 3 showed up "pre-added" when they opened Table 7
  // (and were sent to the wrong table if they didn't notice). Remember
  // which table the draft is for and wipe it the moment another table is
  // selected. Re-opening the SAME table keeps the draft (accidental close).
  const cartForSessionRef = useRef("");
  useEffect(() => {
    if (!selectedSessionId) return;
    if (cartForSessionRef.current && cartForSessionRef.current !== selectedSessionId) {
      setMenuItemQuantities({});
      setStagedConfiguredLines([]);
    }
    cartForSessionRef.current = selectedSessionId;
  }, [selectedSessionId]);
  const [catalogCategoryId, setCatalogCategoryId] = useState("");
  // Sides/Other toggle for the staff Add Items dialog — mirrors the
  // customer-menu pill grouping. "sides" shows only Sides-tagged
  // categories; "other" shows everything else (default). Search ignores
  // this filter (full catalog) so a fast type-search still wins.
  const [addItemsSidesMode, setAddItemsSidesMode] = useState("other");
  // Free-text search inside the staff "Add items" dialog — vital when a venue
  // has many categories (e.g. 21 for Spur). Staff type a few letters and the
  // results list flattens across all categories.
  const [addItemsSearchTerm, setAddItemsSearchTerm] = useState("");
  // Session 72 — the draft never outlives the dialog. Waiters still saw
  // drinks pre-ticked when re-opening "Add items" (the same-table grace
  // above kept them); the only safe rule is: the dialog always opens EMPTY
  // and leaves nothing behind when it closes. Placing the order already
  // clears everything, so this only affects a cancelled / dismissed draft.
  useEffect(() => {
    setMenuItemQuantities({});
    setStagedConfiguredLines([]);
    setStaffSheetItem(null);
    setAddItemsSearchTerm("");
    setAddItemsReviewOpen(false);
  }, [addItemsOpen]);
  // Session 27.108 — Alphabetical sort toggle for the staff "Add items"
  // dialog. Persists per-device so the setting sticks between sessions
  // without needing to travel with the user account.
  // Session 31.23 — A → Z is the DEFAULT (owner request: every item under
  // every category alphabetical unless a device explicitly turns it off).
  const [staffCatalogSortAZ, setStaffCatalogSortAZFn] = useState(() => {
    try { return localStorage.getItem("nfcs:staff:catalogSortAZ") !== "0"; } catch (_) { return true; }
  });
  const setStaffCatalogSortAZ = useCallback((val) => {
    setStaffCatalogSortAZFn(Boolean(val));
    try { localStorage.setItem("nfcs:staff:catalogSortAZ", val ? "1" : "0"); } catch (_) { /* noop */ }
  }, []);
  const [moveTableNumber, setMoveTableNumber] = useState("");
  // Move Item dialog state — staff picks delivered items to move to another table
  const [moveItemDialog, setMoveItemDialog] = useState({ open: false, selectedItems: {}, destTable: "", managerPin: "", busy: false });
  const [kitchenNotifs, setKitchenNotifs] = useState([]);
  const [barNotifs, setBarNotifs] = useState([]);
  // Track which notification IDs we've already seen so the chime only
  // fires when a NEW "ready" banner arrives (not on every 4s poll).
  // Initialised lazily on the first successful fetch so we don't ding
  // for notifications already present at login.
  const knownKitchenNotifIdsRef = useRef(null);
  const knownBarNotifIdsRef = useRef(null);
  // Same pattern for waiter-call summons — plays the "staff" chime so
  // servers know it's a customer-initiated ping (not kitchen/bar ready).
  const knownWaiterCallIdsRef = useRef(null);
  const [waiterCalls, setWaiterCalls] = useState([]);
  // Customer orders held for staff review when Menu Settings →
  // "Have staff confirm order before departments" is ON. Same
  // new-detection pattern so the chime only fires when a fresh
  // order arrives (not on every 4s poll).
  const knownPendingConfirmIdsRef = useRef(null);
  const [pendingConfirmations, setPendingConfirmations] = useState([]);
  const [confirmingOrderId, setConfirmingOrderId] = useState(null);

  // Staff Dashboard — Paid Receipt corrective actions. Both require the
  // Void Manager Password and are written to the audit log.
  //   reopenTable    → flips the linked table session back to "open" so
  //                    additional orders can be added (or accidental
  //                    closes can be undone).
  //   correctPayment → edits the recorded payment method AND amount(s)
  //                    on the active order. Audit log captures both old
  //                    and new values plus the user/role/timestamp.
  const [staffReopenTable, setStaffReopenTable] = useState({ open: false, order: null, pin: "", reason: "", saving: false });
  const [staffCorrectPayment, setStaffCorrectPayment] = useState({
    open: false,
    order: null,
    saving: false,
    values: { new_method: "cash", cash_paid: "", card_paid: "", reason: "", manager_pin: "" },
  });

  // Session 27.42 — Robust 401 handling.
  //
  // The previous version of this callback logged the user out on ANY
  // 401 from ANY background endpoint. That was too aggressive: right
  // after a fresh sign-in a dozen requests fire in parallel (dashboard
  // fetch, kitchen/bar polls, snapshot download, WS reconnect) and if
  // ONE of them raced ahead of Mongo's session write we would kick the
  // waiter straight back to the login screen with "Invalid session".
  // Two-device demos in particular hit this hard because both devices
  // shared a Cloudflare edge with jittery latency.
  //
  // Session 27.110 — Tuned further after a production incident on a
  // busy night (12+ tables active): the 4 s dashboard poll + kitchen
  // notif poll + bar notif poll cadence made "two 401s within 10 s"
  // achievable during a sustained Cloudflare wobble, tripping the
  // guard and kicking waiters mid-service. Rules now:
  //   (a) 90-second grace period after every successful sign-in.
  //   (b) Outside the grace period, require THREE consecutive 401s
  //       within a 20-second window (roughly five poll cycles).
  //   (c) Before actually clearing the session on the third strike,
  //       fire a lightweight confirmation ping to /auth/me. If that
  //       returns 200 the token is fine — the 401 storm was a
  //       transient edge glitch. Only if /auth/me ALSO 401s do we
  //       clear the session. This eliminates the last false-positive
  //       kick-outs during Cloudflare / backend hiccups.
  //
  // Refs live across renders without re-render.
  const unauthorizedGraceUntilRef = useRef(Date.now() + 90000);
  const unauthorizedStreakRef = useRef({ count: 0, at: 0 });
  const unauthorizedConfirmInFlightRef = useRef(false);
  useEffect(() => {
    // Reset the grace period every time we see a fresh token — this
    // covers re-login without a full page reload.
    if (session?.token) {
      unauthorizedGraceUntilRef.current = Date.now() + 90000;
      unauthorizedStreakRef.current = { count: 0, at: 0 };
    }
  }, [session?.token]);
  const handleUnauthorized = useCallback(() => {
    const now = Date.now();
    // Session 46 — offline tokens 401 by design until upgraded; never kick.
    if (isOfflineToken(session?.token)) return;
    // Grace-period guard — swallow silently.
    if (now < unauthorizedGraceUntilRef.current) return;
    // Consecutive-401 guard: 3 hits within a 20 s window.
    const streak = unauthorizedStreakRef.current;
    if (now - streak.at > 20000) streak.count = 0;
    streak.count += 1;
    streak.at = now;
    if (streak.count < 3) return;
    // Reset streak so we don't fire again while the confirmation
    // ping is in flight — and don't stack multiple confirmations.
    unauthorizedStreakRef.current = { count: 0, at: 0 };
    if (unauthorizedConfirmInFlightRef.current) return;
    unauthorizedConfirmInFlightRef.current = true;
    // Session 27.110 — Confirmation ping. Fire a lightweight /auth/me
    // request with the current token; only clear the session if it
    // also 401s. Any other outcome means the session is still valid.
    (async () => {
      try {
        const currentToken = session?.token;
        if (!currentToken) {
          unauthorizedConfirmInFlightRef.current = false;
          return;
        }
        await http.get("/auth/me", { headers: { Authorization: `Bearer ${currentToken}` } });
        // Success — the storm was transient. Grant a fresh grace
        // window so the poll cadence doesn't immediately re-strike.
        unauthorizedGraceUntilRef.current = Date.now() + 30000;
      } catch (err) {
        if (err?.response?.status === 401 && !embedded) {
          clearSession("staff", "server_401_confirmed");
          setSessionState(null);
          navigate("/staff", { replace: true });
        }
        // Any other error → transient / non-auth. Leave the session
        // alone; the next poll will self-heal.
      } finally {
        unauthorizedConfirmInFlightRef.current = false;
      }
    })();
  }, [navigate, session?.token, embedded]);

  const authHeaders = useMemo(() => ({ Authorization: `Bearer ${session?.token}` }), [session?.token]);
  // Session 72 — "What happened on this bill?" (server money timeline).
  const [moneyTimeline, setMoneyTimeline] = useState({ open: false, loading: false, sessionId: null, data: null, error: null });
  const loadMoneyTimeline = useCallback(async (sid) => {
    if (!sid) return;
    setMoneyTimeline({ open: true, loading: true, sessionId: sid, data: null, error: null });
    try {
      const r = await http.get(`/staff/table-sessions/${sid}/money-timeline`, { headers: authHeaders, timeout: 10000 });
      setMoneyTimeline({ open: true, loading: false, sessionId: sid, data: r.data, error: null });
    } catch (e) {
      setMoneyTimeline({ open: true, loading: false, sessionId: sid, data: null, error: e?.response?.data?.detail || "Could not load the bill history." });
    }
  }, [authHeaders]);

  // Staff Dashboard — submit Reopen Table from the paid-receipt panel.
  // Closes the receipt dialog on success so the staff member returns to
  // the (now-open) table session and can act on it.
  const submitStaffReopenTable = async () => {
    const v = staffReopenTable;
    if (!v.order) return;
    if (!v.pin || v.pin.length < 4) { toast.error("Enter the Void Manager Password."); return; }
    setStaffReopenTable((d) => ({ ...d, saving: true }));
    try {
      await http.post(
        `/owner/orders/${v.order.id}/reopen-table`,
        { manager_pin: v.pin, reason: (v.reason || "").trim() },
        { headers: authHeaders }
      );
      toast.success("Table reopened. Add corrective orders as needed.");
      setStaffReopenTable({ open: false, order: null, pin: "", reason: "", saving: false });
      setReceiptOpen(false);
      await fetchDashboard(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Unable to reopen table.");
      setStaffReopenTable((d) => ({ ...d, saving: false }));
    }
  };

  // Staff Dashboard — submit Correct Payment from the paid-receipt
  // panel. Sends method + new cash/card amounts; backend captures
  // old / new in the audit log automatically.
  const submitStaffCorrectPayment = async () => {
    const v = staffCorrectPayment;
    if (!v.order) return;
    if (!v.values.manager_pin || v.values.manager_pin.length < 4) { toast.error("Enter the Void Manager Password."); return; }
    const m = v.values.new_method;
    if (!["cash", "card", "split"].includes(m)) { toast.error("Pick a payment method."); return; }
    const cashNum = Number(v.values.cash_paid || 0);
    const cardNum = Number(v.values.card_paid || 0);
    if (m === "cash" && cashNum <= 0) { toast.error("Cash amount must be greater than zero."); return; }
    if (m === "card" && cardNum <= 0) { toast.error("Card amount must be greater than zero."); return; }
    if (m === "split" && (cashNum <= 0 || cardNum <= 0)) { toast.error("Split needs both cash AND card amounts."); return; }
    setStaffCorrectPayment((d) => ({ ...d, saving: true }));
    try {
      await http.post(
        `/owner/orders/${v.order.id}/correct-payment`,
        {
          new_method: m,
          cash_paid: m === "cash" || m === "split" ? cashNum : 0,
          card_paid: m === "card" || m === "split" ? cardNum : 0,
          reason: (v.values.reason || "").trim() || "Staff payment correction",
          manager_pin: v.values.manager_pin,
        },
        { headers: authHeaders }
      );
      toast.success("Payment corrected.");
      setStaffCorrectPayment({ open: false, order: null, saving: false, values: { new_method: "cash", cash_paid: "", card_paid: "", reason: "", manager_pin: "" } });
      setReceiptOpen(false);
      await fetchDashboard(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Unable to correct payment.");
      setStaffCorrectPayment((d) => ({ ...d, saving: false }));
    }
  };


  // Debounced version of fetchDashboard for WS events — coalesces rapid-fire
  // events into a single fetch 100ms after the last event.
  const wsFetchTimerRef = useRef(null);
  const fetchDashboardRef = useRef(null);
  // Session 27.140 — Lock+dirty pattern. Replaces the old fetchSeqRef which
  // discarded non-latest responses and caused starvation under sustained WS
  // load (pending count freezing). Now: only ONE fetch can run at a time.
  // If WS events arrive during an in-flight fetch, the dirty flag is set and
  // a follow-up fetch runs automatically when the current one completes.
  // EVERY completed fetch response is applied — no discarding.
  const staffFetchInFlightRef = useRef(false);
  const staffFetchDirtyRef = useRef(false);
  // Track recently-delivered order IDs so stale fetch responses can't re-add
  // orders the staff just acknowledged/delivered.
  const staffDeliveredIdsRef = useRef(new Map());
  const STAFF_DELIVERED_TTL = 30000; // 30s suppression
  // Session 27.140 — Event sequence tracking for gap detection.
  // Every WS message includes a monotonic `seq`. If we receive a seq
  // higher than lastSeq + 1, we missed event(s) and schedule a
  // reconciliation fetch. The fetch is the ONLY time we do an HTTP
  // round-trip — events themselves update state directly.
  const staffLastSeqRef = useRef(0);
  const reconciliationTimerRef = useRef(null);
  const scheduleReconciliation = useCallback(() => {
    // Debounce: collapse a burst of events within 400ms into one fetch
    // (the server caches the list for 1 s, so this is one cheap GET).
    if (reconciliationTimerRef.current) return;
    reconciliationTimerRef.current = setTimeout(() => {
      reconciliationTimerRef.current = null;
      if (fetchDashboardRef.current) fetchDashboardRef.current(false, false);
    }, 400);
  }, []);
  // Pass fullRefresh=false so WS-driven refreshes only fetch table-sessions.
  const debouncedFetchDashboard = useCallback(() => {
    // If a fetch is already in flight, don't schedule another — just mark dirty.
    // When the in-flight fetch completes, it will see the dirty flag and re-fetch.
    if (staffFetchInFlightRef.current) {
      staffFetchDirtyRef.current = true;
      return;
    }
    if (wsFetchTimerRef.current) clearTimeout(wsFetchTimerRef.current);
    wsFetchTimerRef.current = setTimeout(() => {
      wsFetchTimerRef.current = null;
      if (fetchDashboardRef.current) fetchDashboardRef.current(false, false);
    }, 100);
  }, []);

  const fetchDashboard = useCallback(async (showLoading = false, fullRefresh = true) => {
    if (!session?.token) return;
    // Session 27.140 — Lock guard. If a fetch is already in flight, mark dirty
    // and bail. The in-flight fetch will re-trigger when it completes.
    if (staffFetchInFlightRef.current) {
      staffFetchDirtyRef.current = true;
      return;
    }
    staffFetchInFlightRef.current = true;
    staffFetchDirtyRef.current = false;
    // Session 72 — decide "quiet device" against the moment the request was
    // SENT: anything this device did before then is already on the server.
    const fetchStartedAt = Date.now();
    if (showLoading) setLoading(true);
    try {
  

      // Map tab values to the backend status filter ("open" covers all in-progress orders)
      const backendStatusFilter = (statusFilter === "active-tables" || statusFilter === "not-completed") ? "open" : statusFilter;
      
      // Session 27.127 — On WS-triggered refreshes (fullRefresh=false)
      // skip /auth/me and /staff/menu-catalog. Only table-sessions
      // change on every order event. This cuts WS-path latency by ~60%
      // (1 API call instead of 3) and reduces server load.
      let sessionsResponse;
      let catalogResponse;
      if (fullRefresh) {
        // Session 58 — allSettled: a failing /auth/me (the ONLY source of
        // business settings before this fix) must not sink the whole
        // refresh, and the table-sessions payload now carries the
        // receipt-critical settings so we can still correct ourselves.
        const [meRes, sRes, cRes] = await Promise.allSettled([
          http.get("/auth/me", { headers: authHeaders }),
          http.get("/staff/table-sessions", { headers: authHeaders, params: { status: backendStatusFilter } }),
          http.get("/staff/menu-catalog", { headers: authHeaders }),
        ]);
        if (sRes.status === "rejected") throw sRes.reason;
        if (meRes.status === "fulfilled") {
          const meResponse = meRes.value;
          applyServerBusiness(meResponse.data?.business, "/auth/me", { full: true });
          setContext(meResponse.data);
          // Session 30.25 — Owner/Manager Overview (embedded) is always
          // silent: the ready chime belongs on the floor, not on the
          // manager's screen. Only real staff sessions follow /auth/me.
          setNotificationsMuted(embedded ? true : !!meResponse.data?.notifications_muted);
        } else {
          const err = meRes.reason;
          const st = err?.response?.status;
          if (st === 401) throw err; // let the existing auth handling run
          const sync = settingsSyncRef.current;
          sync.failures += 1;
          if (sync.failures === 3 && typeof navigator !== "undefined" && navigator.onLine !== false) {
            reportSettingsDrift({ driftKind: "settings_refresh_failed", role: "staff", source: "/auth/me", business: contextRef.current?.business || null, extra: { failures: sync.failures, http_status: st || null, error: String(err?.message || err).slice(0, 200) } });
          }
        }
        sessionsResponse = sRes.value;
        catalogResponse = cRes.status === "fulfilled" ? cRes.value : null;
        if (cRes.status === "rejected" && !catalogResponse) {
          // Keep whatever catalog we already have; log once so it is visible.
          try { crashCrumb(`menu-catalog refresh failed ${cRes.reason?.response?.status || ""}`); } catch (_) { /* noop */ }
        }
      } else {
        sessionsResponse = await http.get("/staff/table-sessions", { headers: authHeaders, params: { status: backendStatusFilter } });
      }
      // Session 58 — the compact settings blob rides on EVERY table-sessions
      // response (poll + WS-triggered), so even a device whose /auth/me is
      // failing or that was primed from a stale local snapshot converges on
      // the owner's Menu Settings within one refresh.
      if (sessionsResponse?.data?.business_settings) {
        applyServerBusiness(sessionsResponse.data.business_settings, fullRefresh ? "table-sessions" : "table-sessions(ws)");
      }
      // Session 27.140 — Every response is now applied (no stale-response
      // discard). The lock+dirty pattern ensures only one fetch runs at
      // a time, so there's no race between concurrent fetches.
      // Client-side filter for each tab
      let filteredSessions = sessionsResponse.data.table_sessions || [];
      // Session 72 — drop tables a WS close event removed moments ago (the
      // snapshot may have been built before that close committed).
      if (recentlyClosedSessionsRef.current.size > 0) {
        const nowC = Date.now();
        for (const [id, at] of recentlyClosedSessionsRef.current.entries()) {
          if (nowC - at > RECENTLY_CLOSED_TTL_MS) recentlyClosedSessionsRef.current.delete(id);
        }
        if (recentlyClosedSessionsRef.current.size > 0) {
          const closedIds = recentlyClosedSessionsRef.current;
          filteredSessions = filteredSessions.filter((s) => !(s && closedIds.has(String(s.id))));
        }
      }
      // Session 28b — Capture server event_seq for the merge function
      // but DO NOT advance staffLastSeqRef. Only WS events should advance
      // the seq counter. Advancing from HTTP caused Root Cause #3: silent
      // event drops when the server seq jumps ahead of pending WS events.
      const serverEventSeq = (typeof sessionsResponse.data.event_seq === "number") ? sessionsResponse.data.event_seq : 0;
      if (statusFilter === "not-completed") {
        filteredSessions = filteredSessions.filter(session =>
          (session.orders || []).some(order => order && order.status === "confirmed")
        );
      }

      // Session 27.25 — Overlay recently-acked orders as "completed"
      // on the fetched response so the Pending counter doesn't flash
      // back to N when a background poll returns BEFORE the backend
      // has finished committing the ack.
      //
      // The race we're squashing:
      //   1. User taps Mark Delivered.
      //   2. Optimistic setTableSessions marks order → "completed";
      //      counter drops to N-1 instantly.
      //   3. fetchDashboard(false) fires, network is still faster
      //      than the DB write; response still shows order = "confirmed".
      //   4. setTableSessions(filteredSessions) overwrites the
      //      optimistic update; counter flashes back to N.
      //   5. Next 4s poll reads a fresh state; counter drops again.
      //
      // With the overlay, step 4 never regresses — we keep applying
      // the "completed" status locally until 4s have passed since the
      // ack (by which time the DB write is definitely visible to
      // subsequent reads).
      const nowTsK = Date.now();
      const ackKitchen = recentlyAckedKitchenRef.current;
      for (const [id, at] of ackKitchen.entries()) {
        if (nowTsK - at > 30000) ackKitchen.delete(id);
      }
      const ackBar = recentlyAckedBarRef.current;
      for (const [id, at] of ackBar.entries()) {
        if (nowTsK - at > 30000) ackBar.delete(id);
      }
      const suppressIds = new Set([...ackKitchen.keys(), ...ackBar.keys()]);
      if (suppressIds.size > 0) {
        filteredSessions = filteredSessions.map((s) => {
          const orders = s.orders || [];
          if (!orders.some((o) => o && suppressIds.has(o.id))) return s;
          return {
            ...s,
            orders: orders.map((o) => {
              if (!o || !suppressIds.has(o.id)) return o;
              // Session 27.105 — Only flip to completed when BOTH
              // stations that have items on this order are truly
              // done. A blanket flip (previous behaviour) zeroed
              // out the pending-items counter for the whole order
              // even when the OTHER station still had work left,
              // or when the order had *new* items added after a
              // full station ack (e.g., waiter adds another drink
              // to the same tab after the kitchen was delivered).
              const items = Array.isArray(o.items) ? o.items : [];
              const hasKitchen = items.some((it) => it && !it.voided && String(it.department || "").toLowerCase() === "kitchen");
              const hasBar = items.some((it) => it && !it.voided && String(it.department || "").toLowerCase() === "bar");
              const kitchenDone = !hasKitchen || (!!o.kitchen_ready_at && (!!o.kitchen_acknowledged_at || ackKitchen.has(o.id)));
              const barDone = !hasBar || (!!o.bar_ready_at && (!!o.bar_acknowledged_at || ackBar.has(o.id)));
              // Additionally require every non-voided item to be
              // delivered — this catches new items added after the
              // ack (they carry no status yet) so the order stays
              // "confirmed" until they're delivered too.
              const allItemsDelivered = items.every((it) => {
                if (!it || it.voided) return true;
                const s = String(it.status || "").toLowerCase();
                return s === "delivered";
              });
              if (kitchenDone && barDone && allItemsDelivered) {
                return { ...o, status: "completed" };
              }
              return o;
            }),
          };
        });
        // Re-apply the "not-completed" filter after the overlay so
        // sessions whose only remaining confirmed order was just
        // acked drop out of the list entirely instead of showing
        // an empty row.
        if (statusFilter === "not-completed") {
          filteredSessions = filteredSessions.filter(session =>
            (session.orders || []).some(order => order && order.status === "confirmed")
          );
        }
      }

      // Session 27.140 — Staff-side delivered-order suppression. Filter out
      // orders that this staff device recently marked as delivered/completed.
      // Prevents stale server responses from resurrecting orders that the
      // user just acknowledged (the DB write may not have committed yet).
      const deliveredNow = Date.now();
      staffDeliveredIdsRef.current.forEach((ts, id) => {
        if (deliveredNow - ts > STAFF_DELIVERED_TTL) staffDeliveredIdsRef.current.delete(id);
      });
      if (staffDeliveredIdsRef.current.size > 0) {
        const suppressedIds = new Set(staffDeliveredIdsRef.current.keys());
        filteredSessions = filteredSessions.map((s) => {
          const orders = s.orders || [];
          if (!orders.some((o) => o && suppressedIds.has(o.id))) return s;
          return {
            ...s,
            orders: orders.map((o) => {
              if (!o || !suppressedIds.has(o.id)) return o;
              return { ...o, status: "completed" };
            }),
          };
        });
      }

      // Session 27.40 — Local mutation overlay. Prevent the poll from
      // flashing away the optimistic state (see `pendingLocalOpsRef`
      // for the full explanation).
      const pending = pendingLocalOpsRef.current;
      if (pending.size > 0) {
        const nowMs = Date.now();
        for (const [sid, op] of pending.entries()) {
          if (!op || op.until < nowMs) pending.delete(sid);
        }
        if (pending.size > 0) {
          // 1) Drop sessions we've already closed locally but the
          //    server still reports as open.
          filteredSessions = filteredSessions.filter((s) => {
            const op = s && pending.get(s.id);
            return !(op && op.closed);
          });
          // 2) Merge in-flight field patches (payment info, etc.)
          //    on top of whatever the server returned.
          filteredSessions = filteredSessions.map((s) => {
            const op = s && pending.get(s.id);
            if (!op || !op.patch) return s;
            const merged = { ...s, ...op.patch };
            // Session 31.26 — a multi-tender `payments` patch is a UNION
            // by tender id (another device may have added a tender in
            // the same window), and the derived totals are recomputed
            // from the union so cash/card/eft/outstanding never drift.
            if (Array.isArray(op.patch.payments)) {
              const recomputed = applyTendersToSession(s, op.patch.payments);
              merged.payments = recomputed.payments;
              merged.cash_paid = recomputed.cash_paid;
              merged.card_paid = recomputed.card_paid;
              merged.eft_paid = recomputed.eft_paid;
              merged.tip_amount = recomputed.tip_amount;
              merged.payment_method = recomputed.payment_method;
              merged.paid_amount = recomputed.paid_amount;
              merged.outstanding_amount = recomputed.outstanding_amount;
              merged.payment_recorded_at = recomputed.payment_recorded_at;
            }
            // If the patch carries an `orders` field, merge order
            // rows by id rather than replacing the array wholesale.
            if (Array.isArray(op.patch.orders) && Array.isArray(s.orders)) {
              const byId = new Map(s.orders.map((o) => [o && o.id, o]));
              for (const patched of op.patch.orders) {
                // Session 31.18 — never re-inject a `local-…` placeholder
                // whose cloud copy is already in the server snapshot.
                if (patched && isLocalOrderId(patched.id) && serverHasLocalOrder(patched, s.orders)) continue;
                if (patched && patched.id) {
                  byId.set(patched.id, { ...(byId.get(patched.id) || {}), ...patched });
                } else if (patched) {
                  byId.set(patched.id || Math.random(), patched);
                }
              }
              merged.orders = dedupeLocalOrders(Array.from(byId.values()));
            }
            return merged;
          });
          // 3) Inject sessions we opened locally but the server
          //    hasn't returned yet (offline / slow sync).
          //
          // Session 27.45 — Match on BOTH server id AND client
          // `_local_id`/`_change_id`. Before this fix, when the
          // server processed our `table_opened` event and returned
          // a session with a fresh ObjectId, the pending op was
          // still keyed on the client's `localId` — so
          // `serverIds.has(sid)` was false and we injected a
          // *duplicate* row (real ghost table!). Now the server-
          // side alias fields flow back to the client via
          // `serialize_doc`, so we can detect that the server row
          // IS the pending op and skip the injection.
          const serverIds = new Set(filteredSessions.map((s) => s && s.id));
          const serverLocalIds = new Set(
            filteredSessions
              .flatMap((s) => (s ? [s._local_id, s._change_id] : []))
              .filter(Boolean)
          );
          for (const [sid, op] of pending.entries()) {
            if (!op || !op.opened || op.closed) continue;
            if (serverIds.has(sid) || serverLocalIds.has(sid)) {
              // Server already returned this session (possibly under
              // a different id). Drop the pending op so it can't
              // ghost-inject on the NEXT poll either.
              pending.delete(sid);
              continue;
            }
            filteredSessions.push(op.opened);
          }
        }
      }

      // Session 28b — MERGE-based reconciliation. Instead of replacing
      // the entire tableSessions state with the server snapshot, we merge:
      // - Never downgrade entity statuses (monotonic state transitions)
      // - Keep local WS-mutated state when it's more advanced than server
      // - Add new sessions/orders from server that local doesn't have
      // - Remove sessions that server confirms are gone (unless local is ahead)
      const serverSeq = serverEventSeq;
      // Session 62 — if the server's seq is BEHIND what this device last saw
      // the server has restarted (counter reset): re-baseline, otherwise the
      // "local is ahead" grace below would keep stale local-only tables for
      // ever (one phone kept Table Ruan at "R88 due" after it was settled).
      if (Number.isFinite(serverSeq) && serverSeq > 0 && staffLastSeqRef.current > serverSeq) {
        staffLastSeqRef.current = serverSeq;
      }
      const localSeq = staffLastSeqRef.current;

      // Session 72 — AUTHORITATIVE vs MERGE. If nothing happened on this
      // device (optimistic action or WS delta) in the QUIET window before
      // the request went out, the snapshot is the complete truth: replace.
      // Otherwise keep the monotonic merge for this one refresh — the next
      // quiet poll (≤10 s) converges anyway.
      const lastActivityAt = Math.max(lastLocalMutationAtRef.current || 0, lastWsMutationAtRef.current || 0);
      const authoritative = fetchStartedAt - lastActivityAt > QUIET_BEFORE_AUTHORITATIVE_MS;

      // Session 73 — rows whose events are STILL QUEUED on this device
      // (slow wifi, a failing sync batch) must stay visible no matter how
      // old: a waiter's second round or a freshly opened table vanished from
      // the screen after 60 s while it was still waiting to sync.
      const queued = await queuedRowIds();
      const isQueuedOrder = (o) => Boolean(o && (queued.orderIds.has(String(o.id)) || (o._change_id && queued.changeIds.has(String(o._change_id)))));
      const isQueuedSession = (sess) => Boolean(sess && queued.sessionIds.has(String(sess.id)));

      setTableSessionsRaw((prev) => {
        const nowT = Date.now();
        const GRACE_MS = 15000;
        // Update recently-seen ref for tracking
        for (const s of filteredSessions) {
          if (s && s.id) recentlySeenSessionsRef.current.set(s.id, nowT);
        }
        // Expire ancient entries
        for (const [id, at] of recentlySeenSessionsRef.current.entries()) {
          if (nowT - at > GRACE_MS) recentlySeenSessionsRef.current.delete(id);
        }
        if (authoritative) {
          return replaceWithServerSessions(prev, filteredSessions, nowT, { isQueuedOrder, isQueuedSession });
        }
        // Use merge instead of replacement. A local-only session survives
        // only while it can still be in flight (optimistic open < 10 min,
        // or seen from the server within the grace window).
        return mergeTableSessions(prev, filteredSessions, serverSeq, localSeq, {
          isQueuedOrder,
          isInFlight: (s) => {
            if (!s) return false;
            if (isQueuedSession(s)) return true;
            const seenAt = recentlySeenSessionsRef.current.get(s.id);
            if (seenAt && nowT - seenAt <= GRACE_MS) return true;
            if (typeof s.id === "string" && !/^[0-9a-f]{24}$/i.test(s.id)) {
              const t = Date.parse(s.opened_at || s.created_at || "");
              return !Number.isFinite(t) || nowT - t < 10 * 60 * 1000;
            }
            return false;
          },
        });
      });
      // Only update menu catalog on full refreshes (4s poll or initial load).
      // WS-triggered fast-path skips the catalog fetch entirely.
      if (fullRefresh && catalogResponse) {
        setMenuCatalog(catalogResponse.data || { categories: [] });
        // Session 27.7 — for the accordion UI we default to NO category
        // expanded on first load, so the dialog opens showing the full
        // category list (matches the customer-menu accordion behaviour).
        // We still preserve a manually-picked category across the 4s
        // auto-refresh so the user isn't yanked back mid-tap.
        setCatalogCategoryId((current) => {
          if (current) {
            const stillExists = (catalogResponse.data?.categories || []).some((c) => c.id === current);
            if (stillExists) return current;
          }
          return "";
        });
      }
    } catch (error) {
      if (error.response?.status === 401) {
        handleUnauthorized();
      } else {
        // Session 27.24 — Only surface an error toast on FOREGROUND
        // fetches (initial load / manual refresh where showLoading was
        // true). Background 4s polls used to fire a red toast every
        // 4 seconds during a Cloudflare 520 hiccup, drowning the UI
        // in noise. Transient failures should be silent — the local
        // IndexedDB snapshot still has the last known good state and
        // the next poll will reconcile. Real 4xx problems (bad
        // token, permission denied) still surface via other paths
        // (401 → handleUnauthorized above).
        const status = error.response?.status;
        const isTransient = !status || status >= 500;
        if (showLoading || (!isTransient && status !== 401)) {
          toast.error(error.response?.data?.detail || "Unable to load tables.");
        }
      }
    } finally {
      setLoading(false);
      staffFetchInFlightRef.current = false;
      // Session 27.140 — If WS events arrived while we were fetching,
      // re-fetch immediately to pick up the changes. This guarantees
      // no events are silently lost under sustained load.
      if (staffFetchDirtyRef.current) {
        staffFetchDirtyRef.current = false;
        // Small delay so React can paint the just-applied state first.
        setTimeout(() => {
          if (fetchDashboardRef.current) fetchDashboardRef.current(false, false);
        }, 50);
      }
    }
  }, [authHeaders, handleUnauthorized, session?.token, statusFilter, applyServerBusiness, setContext, embedded]);
  // Keep ref in sync for debounced calls.
  fetchDashboardRef.current = fetchDashboard;

  useEffect(() => {
    if (!session?.token) {
      if (!embedded) navigate("/staff", { replace: true });
      return;
    }
    // Local-first boot: if a snapshot lives in IndexedDB, hydrate the
    // dashboard from it FIRST so the operator sees their live data
    // instantly (no skeleton). Then the network call runs in the
    // background to reconcile any changes that happened while this
    // device was offline / asleep.
    let cancelled = false;
    (async () => {
      let primedFromLocal = false;
      try {
        // Session 27.45 — Always attempt to hydrate from IDB, don't
        // gate on `hasLocalSnapshot()`. On a slow Cloudflare edge
        // the initial dashboard fetch can take 20-40s to complete
        // (axios timeout + one retry). If IDB has ANY cached
        // sessions we should paint them immediately and refresh in
        // the background instead of staring at a "Loading..."
        // skeleton for half a minute. The previous gate returned
        // false whenever the `last_snapshot_at` meta key was
        // missing — which happens on a lot of PWA installs where
        // the app came up from an SW cache but no snapshot flow
        // ever set the meta.
        const bundle = await loadDashboardBundle();
        if (bundle && !cancelled) {
            // Shape the local bundle into the same objects the
            // network fetchDashboard produces. Only the collections
            // actually used for first-paint are populated — the
            // network refresh will fill in anything missing.
            const cats = (bundle.collections?.categories || []).slice().sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
            const items = bundle.collections?.menu_items || [];
            // Group items by category id, then attach into the catalog shape.
            const itemsByCat = {};
            items.forEach((it) => {
              const cid = it.category_id;
              if (!cid) return;
              (itemsByCat[cid] = itemsByCat[cid] || []).push(it);
            });
            const localCatalog = {
              categories: cats.map((c) => ({ ...c, items: itemsByCat[c.id] || [] })),
              sides_categories: bundle.collections?.sides_categories || [],
              sides: [],
              specials: (bundle.collections?.specials || []).filter((s) => s && s.active !== false),
              business: bundle.business || null,
            };
            // Join orders onto each open table session by
            // `table_session_id` so the local-first paint has the same
            // shape as the /staff/table-sessions network response
            // (which server-side joins orders inline). Without this,
            // `selectedSession.orders` would be undefined when picking
            // a table before the network refresh returns.
            const localOrders = bundle.collections?.orders || [];
            const ordersBySession = {};
            for (const o of localOrders) {
              const sid = o && (o.table_session_id || o.session_id);
              if (!sid) continue;
              (ordersBySession[sid] = ordersBySession[sid] || []).push(o);
            }
            const localSessions = (bundle.collections?.table_sessions || [])
              .filter((s) => s && s.status === "open")
              .map((s) => {
                const attached = ordersBySession[s.id] || [];
                // Recompute lightweight totals so the header row
                // ("N orders · N items · Rxxx") isn't left as
                // undefined text when hydrating from IDB.
                const activeOrders = attached.filter((o) => o && o.status !== "voided");
                const totalItems = activeOrders.reduce(
                  (n, o) => n + (o.items || []).reduce((k, it) => k + (Number(it.quantity) || 0), 0),
                  0,
                );
                // Session 62 — voided orders are worth R0 and a local
                // snapshot's `total_value` can be stale after a void, so
                // when we have the orders we recompute from the live ones.
                const totalValue = activeOrders.reduce((n, o) => n + (Number(o.total) || 0), 0);
                return {
                  ...s,
                  orders: attached,
                  order_count: s.order_count ?? activeOrders.length,
                  total_items: s.total_items ?? totalItems,
                  total_value: attached.length ? Math.round(totalValue * 100) / 100 : (s.total_value ?? totalValue),
                };
              });
            setMenuCatalog((prev) => (prev && prev.categories && prev.categories.length ? prev : localCatalog));
            // Session 58 — the local snapshot carries the business doc
            // (VAT rate, service fee …). Use it rather than defaults when
            // nothing better is known yet; the network refresh overrides.
            if (bundle.business && !contextRef.current?.business) {
              setContext({ role: "staff", business: bundle.business, user: null, _seeded: "local-snapshot" });
            }
            // Session 72 — a cached snapshot is a stale guess, never an
            // optimistic action: use the raw setter so the very first
            // server response is allowed to replace it outright.
            setTableSessionsRaw((prev) => (prev && prev.length ? prev : localSessions.map(withRecountedOrders)));
            setLoading(false); // we already painted from local — skip skeleton
            primedFromLocal = true;
        }
      } catch (_) { /* local-first is best-effort */ }
      // If local primed the UI, do the network refresh in the background
      // (showLoading=false so no skeleton flash). Otherwise this IS the
      // first paint — show the skeleton until data arrives.
      if (!cancelled) fetchDashboard(!primedFromLocal);
    })();
    return () => { cancelled = true; };
  }, [fetchDashboard, navigate, session?.token, setContext, embedded]);

  useEffect(() => {
    if (!session?.token) return undefined;
    // Session 28 — Safety-net reconciliation poll. WS events are the
    // primary update channel. Poll only catches missed events.
    const RECONCILIATION_INTERVAL = 10000;
    const interval = window.setInterval(() => {
      if (!staffFetchInFlightRef.current) fetchDashboard(false);
    }, RECONCILIATION_INTERVAL);
    return () => window.clearInterval(interval);
  }, [fetchDashboard, session?.token]);

  // Realtime — subscribe to the business event stream so any dashboard
  // update elsewhere (orders, tables, payments, menu edits) triggers a
  // refetch here without the operator noticing a "reload". Also mirror
  // the event into IndexedDB so the local store stays in step (this is
  // the foundation for the upcoming Local Coordinator / offline engine).
  useRealtimeConnection({ baseUrl: BACKEND_URL, token: session?.token });
  // Session 70.E — see useCoalescedUpdater: one render per WS burst, not per event.
  // Session 72 — WS deltas are server-originated: stamp them separately from
  // this device's optimistic actions and recount the chip counters so every
  // device shows the same "N pending" the instant the event lands.
  const setTableSessionsFromWs = useCallback((updater) => {
    lastWsMutationAtRef.current = Date.now();
    setTableSessionsRaw((prev) => {
      const next = typeof updater === "function" ? updater(prev) : updater;
      if (!Array.isArray(next) || next === prev) return next;
      return next.map((s, i) => (prev && s === prev[i] ? s : recountSessionRef.current(s)));
    });
  }, []);
  const queueSessionsUpdate = useCoalescedUpdater(setTableSessionsFromWs);
  // Session 72 — catalog (stock) refresh, debounced: several sales in a burst
  // cost one /staff/menu-catalog round-trip.
  const catalogRefreshTimerRef = useRef(null);
  const scheduleCatalogRefresh = useCallback(() => {
    if (catalogRefreshTimerRef.current) return;
    catalogRefreshTimerRef.current = setTimeout(() => {
      catalogRefreshTimerRef.current = null;
      if (fetchDashboardRef.current) fetchDashboardRef.current(false, true);
    }, 1200);
  }, []);
  // Session 72 — open tabs (accounts) list refresh, debounced + silent.
  const loadStaffAccountsRef = useRef(null);
  const tabsRefreshTimerRef = useRef(null);
  const scheduleTabsRefresh = useCallback(() => {
    if (tabsRefreshTimerRef.current) return;
    tabsRefreshTimerRef.current = setTimeout(() => {
      tabsRefreshTimerRef.current = null;
      try { if (loadStaffAccountsRef.current) loadStaffAccountsRef.current({ silent: true }); } catch (_) { /* noop */ }
    }, 600);
  }, []);
  // Apply a stock / availability patch to one catalog item in place so the
  // "Add items" grid flips to Out of stock without waiting for the refetch.
  const patchCatalogItem = useCallback((itemId, patch) => {
    if (!itemId || !patch) return;
    const id = String(itemId);
    setMenuCatalog((prev) => {
      if (!prev || !Array.isArray(prev.categories)) return prev;
      let touched = false;
      const categories = prev.categories.map((cat) => {
        if (!cat || !Array.isArray(cat.items) || !cat.items.some((it) => it && String(it.id) === id)) return cat;
        touched = true;
        return {
          ...cat,
          items: cat.items.map((it) => {
            if (!it || String(it.id) !== id) return it;
            const next = { ...it, ...patch };
            if (patch.sellable === false && !next.service_availability) {
              next.service_availability = { status: "out_of_stock", label: "Out of stock", blocked: [], sellable: false };
            } else if (patch.sellable === true && next.service_availability && next.service_availability.status === "out_of_stock") {
              next.service_availability = null;
            }
            return next;
          }),
        };
      });
      return touched ? { ...prev, categories } : prev;
    });
  }, []);

  // Session 72 — any time this screen comes back (tab focus, screen unlock,
  // network back) pull the full truth immediately instead of waiting for
  // the next poll: this is exactly when a device is most likely stale.
  useEffect(() => {
    if (!session?.token) return undefined;
    const refresh = () => {
      if (typeof document !== "undefined" && document.visibilityState === "hidden") return;
      try { if (fetchDashboardRef.current) fetchDashboardRef.current(false, true); } catch (_) { /* noop */ }
    };
    const onVisibility = () => { if (document.visibilityState === "visible") refresh(); };
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("focus", refresh);
    window.addEventListener("online", refresh);
    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("focus", refresh);
      window.removeEventListener("online", refresh);
    };
  }, [session?.token]);

  useRealtimeEvent([
    "hello",
    "state_changed", "menu_stock_changed",
    "order_created", "order_updated", "order_held_for_confirmation",
    "order_confirmed_by_staff", "order_rejected_by_staff", "order_rejected",
    "order_status_changed",
    "payment_recorded", "payment_edited", "payment_processed", "payment_added",
    "split_payment_processed", "split_payment_edited",
    "table_session_opened", "table_session_closed", "table_session_moved",
    "bill_split", "bill_split_cleared",
    "voucher_applied", "voucher_removed",
    "waiter_called", "call_acknowledged",
    "items_moved",
    "menu_item_updated", "menu_item_deleted", "menu_item_created", "product_updated",
    "category_updated", "category_deleted", "category_created",
    "business_settings_changed",
  ], (msg) => {
    // Session 58 — the owner saved Menu Settings. Apply the compact
    // settings immediately and pull a full /auth/me so every other field
    // (schedule, gates …) catches up too — no waiting for the 10 s poll.
    if ((msg?.event_type || msg?.type) === "business_settings_changed") {
      if (typeof msg?.seq === "number" && msg.seq > staffLastSeqRef.current) staffLastSeqRef.current = msg.seq;
      try { applyServerBusiness((msg?.payload || {}).business_settings, "ws:business_settings_changed"); } catch (_) { /* noop */ }
      try { if (fetchDashboardRef.current) fetchDashboardRef.current(false, true); } catch (_) { /* noop */ }
      return;
    }
    // Session 30 — "hello" arrives on every fresh WS connection (first
    // connect AND every reconnect after a zombie socket was culled).
    // Resync the dedupe counter to the server's CURRENT seq (heals
    // backend-restart seq resets that would otherwise silently drop all
    // events), then reconcile immediately so anything missed while the
    // socket was dead is picked up in one fetch instead of waiting for
    // the safety-net poll.
    if ((msg?.event_type || msg?.type) === "hello") {
      const serverSeq = Number((msg?.payload || {}).seq);
      staffLastSeqRef.current = Number.isFinite(serverSeq) ? serverSeq : 0;
      scheduleReconciliation();
      try { fetchKitchenNotifs(); fetchBarNotifs(); } catch (_) { /* noop */ }
      return;
    }

    // Session 72 — ONE SOURCE OF TRUTH signals.
    //   • state_changed: a write finished on the server (any endpoint).
    //     Reconcile the table list; menu / stock / inventory writes also
    //     refresh the catalog so Out-of-stock flips everywhere.
    //   • menu_stock_changed / menu_item_updated: patch the catalog item in
    //     place (instant) and refetch the catalog (complete truth).
    {
      const t = msg?.event_type || msg?.type;
      const p = msg?.payload || {};
      if (t === "state_changed") {
        if (typeof msg?.seq === "number" && msg.seq > staffLastSeqRef.current) staffLastSeqRef.current = msg.seq;
        scheduleReconciliation();
        const path = String(p.path || "").toLowerCase();
        if (/menu|stock|inventory|ingredient|categor|special|side|department|prep|waste|receiv|voided-orders|void/.test(path)) scheduleCatalogRefresh();
        if (/account|tab|payment|close|merge|move/.test(path)) scheduleTabsRefresh();
        return;
      }
      if (t === "menu_stock_changed") {
        if (typeof msg?.seq === "number" && msg.seq > staffLastSeqRef.current) staffLastSeqRef.current = msg.seq;
        patchCatalogItem(p.item_id, {
          stock: p.stock,
          out_of_stock: p.out_of_stock,
          sell_when_out_of_stock: p.sell_when_out_of_stock,
          sellable: p.sellable,
        });
        scheduleCatalogRefresh();
        return;
      }
      if (t === "menu_item_updated" || t === "menu_item_created" || t === "menu_item_deleted" || t === "product_updated" ||
          t === "category_updated" || t === "category_deleted" || t === "category_created") {
        if (typeof msg?.seq === "number" && msg.seq > staffLastSeqRef.current) staffLastSeqRef.current = msg.seq;
        if (t === "menu_item_updated" && p.item && p.item.id) {
          const it = p.item;
          const stockNum = typeof it.stock === "number" ? it.stock : null;
          if (stockNum !== null) {
            const oos = stockNum <= 0;
            const sellsWhenOut = it.sell_when_out_of_stock === true || (it.sell_when_out_of_stock == null && it.hide_when_out_of_stock === false);
            patchCatalogItem(it.id, { stock: stockNum, out_of_stock: oos, sellable: !oos || sellsWhenOut, price: it.price, name: it.name });
          } else {
            patchCatalogItem(it.id, { price: it.price, name: it.name });
          }
        }
        try { applyRealtimeEvent(msg); } catch (_) { /* noop */ }
        scheduleCatalogRefresh();
        return;
      }
    }

    // 1) Keep IndexedDB in sync so any component that reads local
    //    first (or a future offline flow) sees fresh data.
    try { applyRealtimeEvent(msg); } catch (_) { /* noop */ }

    // 1.5) Session 27.140 — Event-driven state architecture. WS events
    //      are the PRIMARY state update channel. All critical mutations
    //      are applied directly to local state here — NO HTTP round-trip.
    //      HTTP fetches only happen as a reconciliation safety net when
    //      a gap in the event sequence is detected.
    try {
      const eventType = msg?.event_type || msg?.type;
      const p = msg?.payload || msg || {};
      const orderId = String(p.order_id || p.id || "");
      const seq = typeof msg?.seq === "number" ? msg.seq : 0;

      // Gap detection: if we received a seq higher than lastSeq + 1,
      // we missed event(s). Schedule a reconciliation fetch.
      // Session 28 — Dedup: skip events we've already processed.
      if (seq > 0) {
        const lastSeq = staffLastSeqRef.current;
        if (lastSeq > 0 && seq <= lastSeq) {
          return; // Already processed this seq — idempotent skip
        }
        if (lastSeq > 0 && seq > lastSeq + 1) {
          console.warn(`[staff-ws] gap detected: last=${lastSeq} received=${seq} — scheduling reconciliation`);
          scheduleReconciliation();
        }
        staffLastSeqRef.current = Math.max(staffLastSeqRef.current, seq);
      }

      // ── order_created / order_held_for_confirmation: Add the new order
      //    to the correct session. Session 31.20 — a held (gated) customer
      //    order still belongs on the table's tab; it just isn't at the
      //    kitchen yet, so also refresh the "needs confirmation" banner.
      if (eventType === "order_held_for_confirmation") {
        try { fetchPendingConfirmations(); } catch (_) { /* noop */ }
      }
      if ((eventType === "order_created" || eventType === "order_held_for_confirmation") && p.order) {
        const sessionId = String(p.table_session_id || "");
        const fullOrder = p.order;
        if (sessionId && fullOrder && fullOrder.id) {
          queueSessionsUpdate((prev) => {
            const sessions = prev || [];
            const idx = sessions.findIndex((s) => s.id === sessionId);
            if (idx < 0) {
              // Session not in local state — schedule reconciliation to pick it up
              scheduleReconciliation();
              return sessions;
            }
            const sess = sessions[idx];
            const existingOrders = sess.orders || [];
            // Don't add duplicate orders (idempotency)
            if (existingOrders.some((o) => o && o.id === fullOrder.id)) return sessions;
            // Session 31.18 — the cloud copy of an order this device placed
            // optimistically REPLACES its `local-…` placeholder (matched on
            // _local_id / _change_id) instead of being appended next to it.
            // Appending doubled every line and the total on the receipt.
            const updated = { ...sess, orders: upsertServerOrder(existingOrders, fullOrder) };
            const next = [...sessions];
            next[idx] = updated;
            return next;
          });
        } else if (!p.order) {
          // Legacy event without enriched order data — reconciliation needed
          scheduleReconciliation();
        }
      }

      // ── order_confirmed_by_staff: Same as order_created ──
      if (eventType === "order_confirmed_by_staff" && p.order) {
        const sessionId = String(p.table_session_id || "");
        const fullOrder = p.order;
        if (sessionId && fullOrder && fullOrder.id) {
          queueSessionsUpdate((prev) => {
            const sessions = prev || [];
            const idx = sessions.findIndex((s) => s.id === sessionId);
            if (idx < 0) {
              scheduleReconciliation();
              return sessions;
            }
            const sess = sessions[idx];
            const existingOrders = sess.orders || [];
            if (existingOrders.some((o) => o && o.id === fullOrder.id)) return sessions;
            const updated = { ...sess, orders: upsertServerOrder(existingOrders, fullOrder) };
            const next = [...sessions];
            next[idx] = updated;
            return next;
          });
        }
      }

      // ── Session 62 — order_rejected: the server refused a queued order
      // (out of stock, table closed…). Drop the ghost row from this device
      // too — it may have arrived here over the LAN mesh before the cloud
      // ever saw it, and "Mark served" on it 409s forever.
      if (eventType === "order_rejected") {
        const ghostId = p.local_id || p.order_id || null;
        if (ghostId) {
          let dropped = false;
          queueSessionsUpdate((prev) => (prev || []).map((s) => {
            const orders = s?.orders || [];
            if (!orders.some((o) => o && (o.id === ghostId || o._local_id === ghostId))) return s;
            dropped = true;
            return withRecomputedBill({ ...s, orders: orders.filter((o) => o && o.id !== ghostId && o._local_id !== ghostId) }, contextRef.current?.business || null);
          }));
          try { localDbDeleteOne("orders", ghostId); } catch (_) { /* noop */ }
          if (dropped) {
            toast.warning(`Order on table ${p.table_number || "?"} was not accepted: ${p.reason || "refused by the server"}`, {
              description: "It has been removed from this screen. Re-enter it if the guests still want it.",
              duration: 10000,
            });
          }
        }
        scheduleReconciliation();
      }

      // ── order_status_changed: delivery/ready/completed ──
      if (eventType === "order_status_changed" && orderId) {
        // Session 31.13 — VOID (from another device or a staff decline):
        // mirror the voided lines / order into the live session state so
        // the pending counter, long-wait alerts and table detail all
        // drop it immediately instead of waiting for the safety-net poll.
        if (p.voided === true) {
          const fullVoid = p.full_void === true || p.status === "voided" || p.status === "cancelled";
          const voidedIds = new Set((Array.isArray(p.voided_item_ids) ? p.voided_item_ids : []).map(String).filter(Boolean));
          const snapById = new Map(
            (Array.isArray(p.all_items) ? p.all_items : [])
              .filter((it) => it && it.id)
              .map((it) => [String(it.id), it])
          );
          const matchIds = new Set([orderId, p.local_id, p._change_id].filter(Boolean).map(String));
          const matchesOrder = (o) => o && (matchIds.has(String(o.id)) || (o._change_id && matchIds.has(String(o._change_id))));
          queueSessionsUpdate((prev) => (prev || []).map((sess) => {
            const orders = sess.orders || [];
            if (!orders.some(matchesOrder)) return sess;
            // Session 62 — the bill (items, fee, owed, outstanding) must
            // follow the void immediately, not wait for the next poll.
            return withRecomputedBill({
              ...sess,
              orders: orders.map((o) => {
                if (!matchesOrder(o)) return o;
                const voidedAt = p.at || new Date().toISOString();
                const items = (o.items || []).map((it) => {
                  if (!it || it.voided) return it;
                  const itId = String(it.id || it._id || "");
                  if (fullVoid || (itId && voidedIds.has(itId))) return { ...it, voided: true, voided_at: voidedAt };
                  const snap = itId ? snapById.get(itId) : null;
                  if (snap && Number.isFinite(Number(snap.quantity)) && Number(snap.quantity) > 0 && Number(snap.quantity) !== Number(it.quantity)) {
                    return { ...it, quantity: Number(snap.quantity) }; // partial-quantity void
                  }
                  return it;
                });
                const next = { ...o, items };
                if (fullVoid) { next.status = "voided"; next.voided_at = voidedAt; }
                if (typeof p.total === "number") next.total = p.total;
                if (typeof p.subtotal === "number") next.subtotal = p.subtotal;
                if (typeof p.total_items === "number") next.total_items = p.total_items;
                return next;
              }),
            }, contextRef.current?.business || null);
          }));
          scheduleReconciliation();
        }
        const isDelivery = (
          p.status === "delivered" ||
          p.status === "completed" ||
          p.acknowledged === "kitchen" ||
          p.acknowledged === "bar"
        );
        if (isDelivery) {
          const hardCompleted = p.status === "completed" || p.completed === true;
          // Session 31.6 — Only a real completion goes on the suppression
          // list. Stamping PARTIAL deliveries here made the 30 s fetch
          // overlay flip the whole order to "completed" (pending counter
          // dropped to zero, then jumped back when the overlay expired).
          if (hardCompleted) staffDeliveredIdsRef.current.set(orderId, Date.now());
          // Session 31.6 — A station ack only sets the station timestamp
          // when it covered the whole station. The counter treats
          // `kitchen_acknowledged_at` as "every kitchen item delivered".
          const fullKitchenAck = p.acknowledged === "kitchen" && (p.partial === false || p.kitchen_done === true);
          const fullBarAck = p.acknowledged === "bar" && (p.partial === false || p.bar_done === true);
          // Authoritative per-item statuses from the server snapshot.
          const snapshotById = new Map(
            (Array.isArray(p.all_items) ? p.all_items : [])
              .filter((it) => it && it.id && it.status)
              .map((it) => [String(it.id), String(it.status)])
          );
          // Use delivered_item_ids from enriched payload if available.
          // Session 29 — Filter out empty-string IDs (legacy items)
          const realDeliveredIds = new Set(
            (Array.isArray(p.delivered_item_ids) ? p.delivered_item_ids : [])
              .filter((x) => x && x !== "")
          );
          // Session 29 — CORRECTNESS SAFEGUARD: Directly update ONLY the
          // targeted order. Sessions/orders not matching orderId are
          // returned via identity reference (no new objects created).
          queueSessionsUpdate((prev) => (prev || []).map((sess) => {
            const orders = sess.orders || [];
            if (!orders.some((o) => o && o.id === orderId)) return sess; // ← identity shortcut
            return {
              ...sess,
              orders: orders.map((o) => {
                if (!o || o.id !== orderId) return o; // ← identity shortcut
                const updated = { ...o };
                if (hardCompleted) updated.status = "completed";
                if (fullKitchenAck || p.kitchen_done === true) updated.kitchen_acknowledged_at = p.at || new Date().toISOString();
                if (fullBarAck || p.bar_done === true) updated.bar_acknowledged_at = p.at || new Date().toISOString();
                // Mark individual items as delivered.
                if (updated.items && Array.isArray(updated.items)) {
                  updated.items = updated.items.map((it) => {
                    if (!it || it.voided) return it;
                    const itId = String(it.id || it._id || "");
                    // Session 31.6 — snapshot wins: it reflects the DB, so a
                    // missed earlier "ready" event can't leave the item (and
                    // the pending counter) stuck.
                    if (itId && snapshotById.has(itId)) {
                      const st = snapshotById.get(itId);
                      return st !== String(it.status || "") ? { ...it, status: st } : it;
                    }
                    if (realDeliveredIds.size > 0) {
                      if (itId && realDeliveredIds.has(itId)) {
                        return { ...it, status: "delivered" };
                      }
                      return it;
                    }
                    // Fallback: department-based matching.
                    const dept = String(it.department || "").toLowerCase();
                    if (fullKitchenAck && dept === "kitchen" && it.status !== "delivered") {
                      return { ...it, status: "delivered" };
                    }
                    if (fullBarAck && (dept === "bar" || dept === "bar / front of house") && it.status !== "delivered") {
                      return { ...it, status: "delivered" };
                    }
                    if (p.acknowledged === "kitchen" && dept === "kitchen" && it.status === "ready") {
                      return { ...it, status: "delivered" };
                    }
                    if (p.acknowledged === "bar" && (dept === "bar" || dept === "bar / front of house") && it.status === "ready") {
                      return { ...it, status: "delivered" };
                    }
                    if (p.status === "delivered" && it.status === "ready") {
                      return { ...it, status: "delivered" };
                    }
                    return it;
                  });
                }
                return updated;
              }),
            };
          }));
        }
        // Ready event: update item statuses inline.
        // Session 29 — Use all_items (complete snapshot) when available for
        // reliable item matching. Also use stable item IDs from Session 29
        // backend changes. Falls back to positional matching for legacy items.
        if (p.status === "ready" && orderId) {
          // Prefer all_items (full item snapshot with stable IDs + statuses)
          const updateSource = Array.isArray(p.all_items) && p.all_items.length > 0
            ? p.all_items
            : (Array.isArray(p.items) && p.items.length > 0 ? p.items : null);

          if (updateSource) {
            queueSessionsUpdate((prev) => (prev || []).map((sess) => {
              const orders = sess.orders || [];
              if (!orders.some((o) => o && o.id === orderId)) return sess;
              return {
                ...sess,
                orders: orders.map((o) => {
                  if (!o || o.id !== orderId) return o;
                  // Build a map of item updates using stable IDs
                  const itemUpdates = new Map();
                  for (const uit of updateSource) {
                    const uid = String(uit.id || "");
                    if (uid && uid !== "") {
                      itemUpdates.set(uid, uit);
                    }
                  }
                  // If we have stable IDs, do ID-based matching
                  if (itemUpdates.size > 0) {
                    return {
                      ...o,
                      // Session 29 — Also stamp order-level ready fields
                      ...(p.department === "kitchen" ? { kitchen_ready_at: p.ready_at || new Date().toISOString() } : {}),
                      ...(p.department === "bar" ? { bar_ready_at: p.ready_at || new Date().toISOString() } : {}),
                      items: (o.items || []).map((it) => {
                        const itId = String(it.id || "");
                        const update = itId ? itemUpdates.get(itId) : null;
                        if (update && update.status) {
                          return { ...it, status: update.status, ready_at: update.ready_at || it.ready_at };
                        }
                        return it;
                      }),
                    };
                  }
                  // Fallback: positional matching for legacy items without IDs
                  const existingItems = o.items || [];
                  const updatedItems = existingItems.map((it, idx) => {
                    if (idx < updateSource.length) {
                      const update = updateSource[idx];
                      if (update.status) {
                        return { ...it, status: update.status, ready_at: update.ready_at || it.ready_at };
                      }
                    }
                    return it;
                  });
                  return {
                    ...o,
                    ...(p.department === "kitchen" ? { kitchen_ready_at: p.ready_at || new Date().toISOString() } : {}),
                    ...(p.department === "bar" ? { bar_ready_at: p.ready_at || new Date().toISOString() } : {}),
                    items: updatedItems,
                  };
                }),
              };
            }));
          }
        }
      }

      // ── table_session_opened: Add new session to local state ──
      if (eventType === "table_session_opened") {
        const sessionId = String(p.session_id || p.table_session_id || p.id || "");
        const tableNumber = p.table_number || p.from_table || "";
        if (sessionId && tableNumber) {
          queueSessionsUpdate((prev) => {
            const sessions = prev || [];
            if (sessions.some((s) => s.id === sessionId)) return sessions;
            // Create a minimal session stub — reconciliation will fill in details.
            return [...sessions, {
              id: sessionId,
              table_number: tableNumber,
              status: "open",
              orders: [],
              created_at: new Date().toISOString(),
            }];
          });
          recentlySeenSessionsRef.current.set(sessionId, Date.now());
        }
      }

      // ── table_session_closed: Remove session from local state ──
      if (eventType === "table_session_closed") {
        const sessionId = String(p.session_id || p.table_session_id || p.id || "");
        if (sessionId) {
          recentlySeenSessionsRef.current.delete(sessionId);
          // Session 72 — a reconciliation fetch that was already in flight
          // (triggered by the status changes the close emitted first) can
          // return a snapshot built a moment BEFORE the close committed and
          // put the table back for one more poll. Remember the close for a
          // few seconds so such a snapshot cannot resurrect it.
          recentlyClosedSessionsRef.current.set(sessionId, Date.now());
          queueSessionsUpdate((prev) => (prev || []).filter((s) => s.id !== sessionId));
        }
      }

      // ── table_session_moved: Update table number ──
      if (eventType === "table_session_moved") {
        const sessionId = String(p.session_id || p.table_session_id || p.id || "");
        const newTable = p.to_table || p.table_number || "";
        if (sessionId && newTable) {
          queueSessionsUpdate((prev) => (prev || []).map((s) =>
            s.id === sessionId ? { ...s, table_number: newTable } : s
          ));
        }
      }

      // ── payment_recorded / payment_edited: Schedule reconciliation ──
      if (eventType === "payment_recorded" || eventType === "payment_edited" ||
          eventType === "split_payment_processed" || eventType === "split_payment_edited") {
        scheduleReconciliation();
      }

      // Session 72 — EVERY session-affecting event is followed by one
      // debounced, server-cached table-sessions fetch. The delta above
      // paints instantly; the fetch makes the counters, totals, fee,
      // outstanding and anything the delta did not carry identical on
      // every device within ~0.5 s — no waiting for the 10 s poll.
      if (eventType && eventType !== "waiter_called" && eventType !== "call_acknowledged") {
        scheduleReconciliation();
      }

    } catch (e) { console.warn("[staff-ws] direct mutation error:", e?.message || e); }

    // 2) Session 27.140 — NO more debounced fetch on every event.
    //    Reconciliation is handled by gap detection + safety-net poll.
    // 3) Session 27.13 — For status changes specifically, also force
    //    an immediate refetch of the kitchen/bar READY banners so the
    //    staff sees "Order ready" the instant kitchen taps the button
    //    (no 4s poll wait). The backend now broadcasts this event on
    //    every kitchen/bar mark-ready and every staff ack.
    try {
      const t = msg?.event_type || msg?.type;
      if (t === "order_status_changed") {
        fetchKitchenNotifs();
        fetchBarNotifs();
        // Session 27.14 — Populate banner directly from the WS payload
        // as a safety net. If for ANY reason the /staff/kitchen-ready
        // query doesn't return the order (stale index, ack field
        // shape drift, whatever), the "Mark delivered" banner still
        // shows because we build it from the broadcast payload itself.
        // The banner is what fires the ack → flips the order to
        // completed → clears it from the "pending" active-tables list.
        const p = msg?.payload || msg || {};

        // Session 27.90 — Multi-device banner sync fix.
        //
        // Bug: two staff devices (say the APK tablet and the Windows
        // EXE) both see the "kitchen ready" banner for order X. When
        // one taps Mark Delivered, only THAT device's banner clears
        // — the other device keeps showing the banner until it
        // spontaneously clears ~15 s later (or worse, until the
        // merge grace expires). Root cause: the WS
        // `order_status_changed` event was only used to ADD notifs
        // (when status == "ready"), never to REMOVE them when the
        // payload said another device just acknowledged the order.
        // Meanwhile the follow-up `fetchKitchenNotifs` on the second
        // device would receive an empty server row for X (correct
        // — X is acked!), but the merge helpfully preserved it as
        // "recently seen and not-yet-acked BY ME". So the second
        // device sat on a stale banner.
        //
        // Fix: if the payload carries `acknowledged: "kitchen"` OR
        // `acknowledged: "bar"` OR `completed: true`, remove the
        // matching notif from state on this device AND stamp the
        // ack-suppression ref so the very next merge sees it as
        // acknowledged and refuses to re-add it. This mirrors the
        // local behaviour after an on-device ack.
        try {
          const ackedStation = String(p.acknowledged || "").toLowerCase();
          const completed = !!p.completed || p.status === "completed";
          const orderId = String(p.order_id || p.id || "");
          // Session 31.6 — A PARTIAL ack (some items delivered, station
          // still cooking) clears the current banner but must NOT arm the
          // 30 s suppression: the kitchen's next "ready" for the remaining
          // items would be hidden on every device for half a minute.
          // Partial acks get a 3 s window only (bridges the poll race).
          const partialAck = p.partial === true && !completed;
          const stamp = partialAck ? Date.now() - 27000 : Date.now();
          if (orderId && (ackedStation === "kitchen" || completed)) {
            recentlyAckedKitchenRef.current.set(orderId, stamp);
            locallyAddedKitchenRef.current.delete(orderId);
            setKitchenNotifs((prev) => prev.filter((n) => n && n.id !== orderId));
          }
          if (orderId && (ackedStation === "bar" || completed)) {
            recentlyAckedBarRef.current.set(orderId, stamp);
            locallyAddedBarRef.current.delete(orderId);
            setBarNotifs((prev) => prev.filter((n) => n && n.id !== orderId));
          }
          // Session 31.13 — VOID: the "ready" banners lose the voided lines;
          // a whole-order void (or a decline) clears the banner outright and
          // arms the suppression so a late poll can't bring it back.
          if (orderId && p.voided === true) {
            const fullVoid = p.full_void === true || p.status === "voided" || p.status === "cancelled";
            const voidedIds = new Set((Array.isArray(p.voided_item_ids) ? p.voided_item_ids : []).map(String).filter(Boolean));
            const pruneNotifs = (prev) => prev.map((n) => {
              if (!n || n.id !== orderId) return n;
              if (fullVoid) return null;
              const items = (n.items || []).filter((it) => it && !it.voided && !voidedIds.has(String(it.id || "")));
              if (items.length === 0 && (n.items || []).length > 0) return null;
              return items.length === (n.items || []).length ? n : { ...n, items };
            }).filter(Boolean);
            if (fullVoid) {
              recentlyAckedKitchenRef.current.set(orderId, Date.now());
              recentlyAckedBarRef.current.set(orderId, Date.now());
              locallyAddedKitchenRef.current.delete(orderId);
              locallyAddedBarRef.current.delete(orderId);
            }
            setKitchenNotifs(pruneNotifs);
            setBarNotifs(pruneNotifs);
          }
        } catch (_) { /* noop */ }

        if (p.status === "ready" && (p.department === "kitchen" || p.department === "bar")) {
          const notif = {
            id: String(p.order_id || p.id || ""),
            order_number: p.order_number || null,
            table_number: p.table_number || null,
            ready_at: p.ready_at || p.at || new Date().toISOString(),
            items: Array.isArray(p.items) ? p.items : [],
            // Session 27.104 — Carry the partial flag through the notif
            // so the ack handler can skip the "completed" overlay
            // (which would otherwise remove the whole order from the
            // pending-items counter even though only some items were
            // actually delivered).
            partial: !!p.partial,
          };
          if (notif.id) {
            // Session 27.34 — Honour the 4-second ack suppression here too.
            // Without this, a delayed cloud WS "order_status_changed" event
            // arriving AFTER the staff has already tapped "Mark delivered"
            // would re-add the notif to the banner, causing the visible
            // flash-back "disappears → pops back → disappears again"
            // pattern. The suppression ref is expired every 4s in
            // fetchKitchenNotifs/fetchBarNotifs so no legitimate future
            // ready event is ever permanently masked.
            const ackedKitchen = recentlyAckedKitchenRef.current;
            const ackedBar = recentlyAckedBarRef.current;
            const nowTs = Date.now();
            const isSuppressed = (
              (p.department === "kitchen" && ackedKitchen.has(notif.id) && (nowTs - ackedKitchen.get(notif.id)) < 30000) ||
              (p.department === "bar" && ackedBar.has(notif.id) && (nowTs - ackedBar.get(notif.id)) < 30000)
            );
            if (!isSuppressed) {
              if (p.department === "kitchen") {
                locallyAddedKitchenRef.current.set(notif.id, Date.now());
                setKitchenNotifs((prev) => {
                  if (prev.some((n) => n.id === notif.id)) return prev;
                  chimeOnceForOrder("kitchen", notif.id);
                  return [notif, ...prev];
                });
              } else {
                locallyAddedBarRef.current.set(notif.id, Date.now());
                setBarNotifs((prev) => {
                  if (prev.some((n) => n.id === notif.id)) return prev;
                  chimeOnceForOrder("bar", notif.id);
                  return [notif, ...prev];
                });
              }
            }
          }
        }
      }
    } catch (_) { /* noop */ }
  });

  // Session 27.8 — LAN-mesh hot-path listener. Kitchen/Bar fire a
  // `fireAndForgetMesh` when they mark an order ready; the DeviceBootstrap
  // subscriber dispatches `nfcs:mesh-event` here. Playing the chime +
  // toast the instant the mesh delivers means staff hear the ping
  // milliseconds after "mark ready" is tapped on the kitchen tablet
  // — no cloud round-trip.
  //
  // The same event will ALSO arrive via cloud realtime a moment later;
  // we dedupe on order_id + status so we don't chime twice.
  const recentReadyRef = useRef(new Map());
  // Session 27.106 — Chime dedupe. The same "ready" event can arrive
  // via THREE independent channels (cloud WS handler, /staff/kitchen-
  // ready poll, and LAN mesh listener), each firing
  // playOrderReadyChime independently. Result was 2-4 chime plays
  // per single order. Track the last chime time per (department,
  // order_id) with an 8s dedupe window — long enough to cover
  // channel skew, short enough that a genuinely new "ready" event
  // on the same order (e.g., a partial ready after a kitchen re-fire)
  // can still play.
  const chimeDedupeRef = useRef(new Map());
  const chimeOnceForOrder = useCallback((dept, orderId) => {
    try {
      const key = `${(dept || "").toLowerCase()}|${orderId || ""}`;
      const now = Date.now();
      const last = chimeDedupeRef.current.get(key);
      if (last && (now - last) < 8000) return; // suppressed
      chimeDedupeRef.current.set(key, now);
      // Trim so the map doesn't grow unbounded.
      if (chimeDedupeRef.current.size > 200) {
        const cutoff = now - 60000;
        for (const [k, t] of chimeDedupeRef.current.entries()) {
          if (t < cutoff) chimeDedupeRef.current.delete(k);
        }
      }
      if (!notificationsMutedRef.current) playOrderReadyChime();
    } catch (_) { /* noop */ }
  }, []);
  // Session 27.11 — remember recently-acked orders so a stale poll
  // response can't ghost them back onto the banner. Values are the
  // ack timestamp; the banner filter treats entries younger than 30s
  // as "suppressed". These refs went missing during an earlier file
  // corruption event (see handoff notes), which caused every
  // "Mark delivered" click to throw ReferenceError and silently break
  // the staff ack flow — restored in Session 27.18.
  const recentlyAckedKitchenRef = useRef(new Map());
  const recentlyAckedBarRef = useRef(new Map());
  // Session 27.83 — "Locally added" notif tracking (banner-flash fix).
  //
  // Bug: When kitchen taps Mark Ready, a mesh/WS event lands on the
  // staff dashboard AHEAD of the backend DB commit. The event handler
  // immediately (a) adds the notif to `kitchenNotifs` (banner appears),
  // and (b) fires `fetchKitchenNotifs()` as a safety refresh. Because
  // the DB hasn't committed the ready flag yet, the endpoint returns
  // an empty list → `setKitchenNotifs(next)` REPLACES state with []
  // and the banner disappears. A few seconds later the next 4s poll
  // returns the order and the banner pops back. Staff saw the "flash
  // on → off → on" pattern and couldn't reliably tap Mark Delivered.
  //
  // Fix: Every time we inject a notif locally (from a WS or mesh event
  // payload), stamp its id + timestamp into these refs. `fetchKitchen
  // Notifs` / `fetchBarNotifs` then MERGE the server response with any
  // locally-added notif that is (a) less than 15s old and (b) not in
  // the ack-suppression list. After 15s the backend has definitely
  // caught up, so the merge collapses to a plain replace.
  const locallyAddedKitchenRef = useRef(new Map());
  const locallyAddedBarRef = useRef(new Map());
  // Session 27.40 — "Local mutation overlay" ref.
  //
  // Every local-first action (open table, add items, save payment, close
  // table) is applied optimistically to React state INSTANTLY, but the
  // periodic `fetchDashboard()` poll (every 4 s) fetches the server's
  // authoritative snapshot and replaces state with it. On a slow edge
  // the server hasn't yet processed the queued sync event when the
  // poll fires — so its response REVERTS the optimistic UI. Half a
  // second later the sync completes and the next poll shows the
  // expected state again → the operator sees a "flash away → flash
  // back" flicker on every action.
  //
  // This ref tracks in-flight local mutations by session id. Each entry
  // has a `until` timestamp (usually now + 20 s) and either:
  //   • `closed: true`   → filter this session out of the server list
  //   • `opened: session` → inject this session (server hasn't returned it yet)
  //   • `patch: {...}`   → merge these fields on top of the server row
  //
  // `fetchDashboard` applies the overlay AFTER filtering so the poll
  // can never regress the optimistic state. Entries auto-expire after
  // 20 s or when a server refresh confirms the expected state.
  const pendingLocalOpsRef = useRef(new Map());
  const markLocalOp = useCallback((sessionId, op) => {
    if (!sessionId) return;
    const now = Date.now();
    const map = pendingLocalOpsRef.current;
    const existing = map.get(sessionId) || {};
    // Merge patches so multiple actions on the same session compose
    // (e.g. open → add items → pay all landing before the first poll).
    const merged = {
      ...existing,
      ...op,
      until: (op && op.until) || existing.until || (now + 20000),
    };
    if (op && op.patch) {
      merged.patch = { ...(existing.patch || {}), ...op.patch };
    }
    map.set(sessionId, merged);
  }, []);
  // Session 41.4 — when the sync loop reports a PERMANENT server rejection
  // for one of this device's queued events (e.g. an order refused because
  // the table was already closed elsewhere, or a tender refused as an
  // overpayment), tell the operator and drop the optimistic ghost row so the
  // dashboard converges with the server instead of showing a phantom order.
  useEffect(() => {
    const onFlushed = (ev) => {
      // Session 73 — accepted orders may come back trimmed by the stock
      // gate (a sold-out line removed, an over-ordered line capped). Tell the
      // waiter exactly what changed so nothing is silently "missing".
      try {
        const results = Array.isArray(ev?.detail?.results) ? ev.detail.results : [];
        for (const r of results) {
          const notice = r && r.order && r.order.stock_notice;
          if (notice && (r.status === "accepted")) {
            toast.warning(`Order placed with a change: ${notice}`, {
              description: "The rest of the round went to the kitchen / bar as normal. Do not re-enter it.",
              duration: 12000,
            });
          }
        }
      } catch (_) { /* noop */ }
      const rejected = Array.isArray(ev?.detail?.rejected) ? ev.detail.rejected : [];
      if (rejected.length === 0) return;
      let touched = false;
      for (const r of rejected) {
        const type = String(r.event_type || "");
        const p = r.payload || {};
        const err = typeof r.error === "string" ? r.error : (r.error?.message || "rejected by the server");
        if (type === "order_created" || type === "order_held_for_confirmation") {
          const localId = p.order?.id || null;
          const sid = p.table_session_id || p.order?.table_session_id || null;
          if (localId) {
            setTableSessions((prev) => (prev || []).map((s) => {
              if (!s || (sid && s.id !== sid)) return s;
              const orders = (s.orders || []).filter((o) => o && o.id !== localId);
              if (orders.length === (s.orders || []).length) return s;
              return { ...s, orders };
            }));
            touched = true;
          }
          toast.error(`Order was NOT accepted by the server: ${err}`, {
            description: "The order was removed from this device. Please re-enter it if the table is still open.",
            duration: 12000,
          });
        } else if (type === "payment_recorded" || type === "payment_added") {
          toast.error(`Payment was NOT accepted by the server: ${err}`, {
            description: "Re-check the table's balance before closing it.",
            duration: 12000,
          });
          touched = true;
        } else if (type === "table_closed") {
          toast.error(`Close table was refused: ${err}`, { duration: 12000 });
          touched = true;
        } else if (type === "table_opened") {
          toast.error(`Open table was refused: ${err}`, { duration: 12000 });
          touched = true;
        }
      }
      if (touched) { try { fetchDashboard(false); } catch (_) { /* noop */ } }
    };
    window.addEventListener(EVENT_SYNC_FLUSHED, onFlushed);
    return () => window.removeEventListener(EVENT_SYNC_FLUSHED, onFlushed);
  }, [fetchDashboard]);
  useEffect(() => {
    const handler = (ev) => {
      const envelope = ev?.detail?.envelope;
      if (!envelope) return;
      if (envelope.event_type !== "order_status_changed") return;
      const p = envelope.payload || {};
      if (p.status !== "ready") return;
      // Session 30.34 — Partial sends: the kitchen can now send items
      // one at a time over LAN. Each partial carries a different item
      // set, so key the dedupe on order + items, otherwise the 2nd and
      // 3rd "single item ready" pings within 8 s were silently dropped.
      const itemSig = p.partial && Array.isArray(p.items)
        ? "|" + p.items.map((it) => `${(it.department || "").toLowerCase()}:${(it.name || "").toLowerCase()}x${it.quantity || 1}`).sort().join(",")
        : "";
      const key = String(p.order_id || envelope.change_id) + itemSig;
      const now = Date.now();
      // Dedupe window: 8s.
      const seen = recentReadyRef.current.get(key);
      if (seen && (now - seen) < 8000) return;
      recentReadyRef.current.set(key, now);
      // Housekeeping — trim old entries.
      if (recentReadyRef.current.size > 100) {
        const cutoff = now - 60000;
        for (const [k, t] of recentReadyRef.current.entries()) {
          if (t < cutoff) recentReadyRef.current.delete(k);
        }
      }
      // Chime + system notification.
      try {
        const dept = (p.department || "kitchen").toLowerCase();
        const label = dept === "bar" ? "Bar" : "Kitchen";
        const heading = p.table_number ? `Table ${p.table_number}` : `Order ${p.order_number || ""}`.trim();
        // Session 27.106 — Route the chime through the shared dedupe
        // so cloud WS + poll paths don't triple-play for the same
        // order. The system notification + vibrate still fire (they
        // are cheap and desirable per mesh event).
        chimeOnceForOrder(dept, key);
        if (!notificationsMutedRef.current) {
          try {
            if (typeof window !== "undefined" && "vibrate" in navigator) {
              navigator.vibrate([120, 60, 120]);
            }
          } catch (_) { /* noop */ }
          // Session 27.126 — Removed toast pop-ups and system Notifications
          // per user request. Chime + vibrate are sufficient cues; the
          // "Mark delivered" banner already provides the visual indicator.
        }
      } catch (_) { /* toast/chime best-effort */ }
      // Session 27.14 — Populate the "Mark delivered" banner directly
      // from the mesh payload (see the WS branch above). Guarantees
      // the actionable banner shows even if /staff/kitchen-ready
      // returns empty for any reason.
      try {
        const dept = (p.department || "kitchen").toLowerCase();
        const notif = {
          id: String(p.order_id || key || ""),
          order_number: p.order_number || null,
          table_number: p.table_number || null,
          ready_at: p.ready_at || envelope.ts || new Date().toISOString(),
          items: Array.isArray(p.items) ? p.items : [],
          // Session 27.104 — Propagate partial flag from mesh events too.
          partial: !!p.partial,
        };
        if (notif.id) {
          // Session 27.34 — Same suppression rule as the WS branch.
          // A mesh event arriving AFTER an ack was tapped would
          // otherwise re-add the notif and cause the visible flash.
          const ackedKitchen = recentlyAckedKitchenRef.current;
          const ackedBar = recentlyAckedBarRef.current;
          const nowTs = Date.now();
          const isSuppressed = (
            (dept === "kitchen" && ackedKitchen.has(notif.id) && (nowTs - ackedKitchen.get(notif.id)) < 30000) ||
            (dept === "bar" && ackedBar.has(notif.id) && (nowTs - ackedBar.get(notif.id)) < 30000)
          );
          if (!isSuppressed) {
            // Session 30.34 — Merge, don't ignore: a second partial for
            // the same order appends its items to the existing banner
            // (and a full send upgrades a partial banner to full).
            const mergeNotif = (prev) => {
              const idx = prev.findIndex((n) => n.id === notif.id);
              if (idx === -1) return [notif, ...prev];
              const cur = prev[idx];
              const seenKeys = new Set((cur.items || []).map((it) => `${(it.name || "").toLowerCase()}x${it.quantity || 1}`));
              const extra = (notif.items || []).filter((it) => !seenKeys.has(`${(it.name || "").toLowerCase()}x${it.quantity || 1}`));
              if (!extra.length && cur.partial === notif.partial) return prev;
              const merged = {
                ...cur,
                items: [...(cur.items || []), ...extra],
                partial: cur.partial && notif.partial,
                ready_at: notif.ready_at || cur.ready_at,
              };
              const next = prev.slice();
              next[idx] = merged;
              return next;
            };
            if (dept === "kitchen") {
              locallyAddedKitchenRef.current.set(notif.id, Date.now());
              setKitchenNotifs(mergeNotif);
            } else if (dept === "bar") {
              locallyAddedBarRef.current.set(notif.id, Date.now());
              setBarNotifs(mergeNotif);
            }
          }
        }
      } catch (_) { /* noop */ }
      // Session 28b — Removed fetchDashboard(). The ready status change
      // produces order_status_changed WS events applied by the WS handler.
      // Also force-refresh the banners in case the backend was
      // already ahead of the mesh event.
      try { fetchKitchenNotifs(); fetchBarNotifs(); } catch (_) { /* noop */ }
    };
    window.addEventListener("nfcs:mesh-event", handler);
    return () => window.removeEventListener("nfcs:mesh-event", handler);
    // chimeOnceForOrder / fetchKitchenNotifs / fetchBarNotifs are stable
    // useCallbacks declared further down this component (listing them here
    // would hit the temporal dead zone at render); the handler only runs
    // on later events, when they are all initialised.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchDashboard]);

  // Pull the staff roster once on mount — small dataset, no need to poll.
  useEffect(() => {
    if (!session?.token) return;
    http.get("/staff/staff-members", { headers: authHeaders })
      .then((r) => setStaffRoster(r.data?.staff || []))
      .catch(() => { /* roster is optional — silently ignore on failure */ });
  }, [authHeaders, session?.token]);

  // Local business database hydration. Downloads the full snapshot
  // once per login and stores it in IndexedDB so future launches can
  // (eventually) read from local first. This is the foundation for
  // the "Local Network First" architecture — realtime WS events keep
  // the local store fresh after the initial hydrate.
  useEffect(() => {
    if (!session?.token) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await http.get("/sync/snapshot", { headers: authHeaders });
        if (cancelled) return;
        await hydrateFromSnapshot(res.data);
      } catch (_e) { /* best-effort — falls back to network reads */ }
    })();
    return () => { cancelled = true; };
  }, [authHeaders, session?.token]);

  const fetchKitchenNotifs = useCallback(async () => {
    if (!session?.token) return;
    try {
      const response = await http.get("/staff/kitchen-ready", { headers: authHeaders });
      let next = response.data.notifications || [];
      // Session 27.25 — Short-window ghost-ack suppression restored.
      // When the staff taps "Mark delivered", we push the order id
      // into `recentlyAckedKitchenRef` and the following poll (which
      // may happen BEFORE the backend has finished committing the
      // ack) would otherwise flash the banner back for a beat.
      //
      // Previous incarnation used no expiry and permanently masked
      // notifications on production (Session 27.13 disabled it for
      // that reason). This version expires each entry after 10s —
      // long enough to bridge slow production ack-vs-poll races
      // (bumped from 4s in Session 27.36 after a report of banners
      // popping back after ~5s on a slow POS), short enough that a
      // genuinely fresh ready flag can never be permanently masked.
      const nowTs = Date.now();
      const recently = recentlyAckedKitchenRef.current;
      for (const [id, at] of recently.entries()) {
        if (nowTs - at > 30000) recently.delete(id);
      }
      if (recently.size > 0) {
        next = next.filter((n) => !recently.has(n.id));
      }
      const nextIds = new Set(next.map((n) => n.id));
      if (knownKitchenNotifIdsRef.current === null) {
        knownKitchenNotifIdsRef.current = nextIds;
      } else {
        let foundNew = false;
        for (const id of nextIds) {
          if (!knownKitchenNotifIdsRef.current.has(id)) { foundNew = true; break; }
        }
        // Session 27.75 — Use `playOrderReadyChime` (the staff-role
        // "order ready" event, honours the user's per-event preset
        // picker) instead of `playKitchenChime` (which is the tone
        // pattern INTENDED for the kitchen dashboard itself, always
        // using the general default preset). Before this fix, staff
        // heard the WS chime (correct, digital chirp / their choice)
        // followed by the poll chime (wrong, warm chord default) a
        // few seconds later — the mystery "why does it switch to
        // warm chord after playing digital chirp".
        // Session 27.106 — Route through `chimeOnceForOrder` to
        // dedupe against the WS + mesh channels that may have
        // already chimed for these same order ids seconds earlier.
        if (foundNew) {
          for (const id of nextIds) {
            if (!knownKitchenNotifIdsRef.current.has(id)) chimeOnceForOrder("kitchen", id);
          }
        }
        knownKitchenNotifIdsRef.current = nextIds;
      }
      // Session 27.83 — Merge server response with locally-added
      // notifs so a race between the WS/mesh push and the DB commit
      // can't wipe a fresh banner off the screen. See the block-comment
      // where `locallyAddedKitchenRef` is declared for the full story.
      //
      // Session 27.89 — Widen the "recently known" set to include
      // notifs that first arrived via a *previous poll* (not just
      // WS/mesh events). Previously, if a notif entered state through
      // the periodic poll and the NEXT poll transiently returned
      // empty (backend hiccup or a brief read-primary miss), the
      // merge dropped it — reproducing the "flash on → gone → back
      // 4 s later" pattern the operator kept reporting. Stamping the
      // ref for every id we've ever seen from the server plugs that
      // hole without keeping stale notifs (the 15 s grace still
      // applies, and any ack still purges via `recentlyAckedKitchen
      // Ref`).
      for (const n of next) {
        if (n && n.id && !locallyAddedKitchenRef.current.has(n.id)) {
          locallyAddedKitchenRef.current.set(n.id, Date.now());
        }
      }
      setKitchenNotifs((prev) => {
        // Session 27.100 — Bumped from 15 s → 60 s. Under heavy
        // service load (10+ tables, many concurrent orders), the
        // 4 s poll can miss a notif for multiple cycles due to
        // response gaps, replica lag on reads, or ingress
        // pressure. 15 s wasn't enough for the "notif appears then
        // disappears before an ack" cases operators reported.
        // 60 s is safe because any REAL ack still purges via the
        // WS `acknowledged` handler (Session 27.90).
        const graceMs = 60000;
        const nowT = Date.now();
        // Expire stale locally-added entries.
        for (const [id, at] of locallyAddedKitchenRef.current.entries()) {
          if (nowT - at > graceMs) locallyAddedKitchenRef.current.delete(id);
        }
        const nextById = new Map(next.map((n) => [n.id, n]));
        const preserved = [];
        for (const p of (prev || [])) {
          if (!p || !p.id) continue;
          if (nextById.has(p.id)) continue;              // server confirmed
          if (!locallyAddedKitchenRef.current.has(p.id)) continue; // not local
          if (recentlyAckedKitchenRef.current.has(p.id)) continue; // acked
          preserved.push(p);
        }
        return preserved.length ? [...preserved, ...next] : next;
      });
    } catch (error) {
      // silent (banner will simply not appear)
    }
  }, [authHeaders, session?.token, chimeOnceForOrder]);

  const fetchBarNotifs = useCallback(async () => {
    if (!session?.token) return;
    try {
      const response = await http.get("/staff/bar-ready", { headers: authHeaders });
      let next = response.data.notifications || [];
      // Session 27.37 — 30s expiring suppression for bar acks (bumped
      // from 10s to survive a slow production ack POST on Cloudflare).
      const nowTs = Date.now();
      const recently = recentlyAckedBarRef.current;
      for (const [id, at] of recently.entries()) {
        if (nowTs - at > 30000) recently.delete(id);
      }
      if (recently.size > 0) {
        next = next.filter((n) => !recently.has(n.id));
      }
      const nextIds = new Set(next.map((n) => n.id));
      if (knownBarNotifIdsRef.current === null) {
        knownBarNotifIdsRef.current = nextIds;
      } else {
        let foundNew = false;
        for (const id of nextIds) {
          if (!knownBarNotifIdsRef.current.has(id)) { foundNew = true; break; }
        }
        // Session 27.75 — Same fix as the kitchen-ready poll above.
        // Use the staff-role "order ready" event chime so the user's
        // per-event picker is honoured. Prevents the "digital chirp
        // → warm chord" double-play on bar-ready notifications.
        // Session 27.106 — Deduped against WS + mesh channels.
        if (foundNew) {
          for (const id of nextIds) {
            if (!knownBarNotifIdsRef.current.has(id)) chimeOnceForOrder("bar", id);
          }
        }
        knownBarNotifIdsRef.current = nextIds;
      }
      // Session 27.83 — Merge server response with locally-added
      // notifs (see the kitchen fetch above for the full rationale).
      // Session 27.89 — Also stamp every server-returned id so a
      // notif that first arrived via a poll (not WS) survives a
      // subsequent transient empty response.
      for (const n of next) {
        if (n && n.id && !locallyAddedBarRef.current.has(n.id)) {
          locallyAddedBarRef.current.set(n.id, Date.now());
        }
      }
      setBarNotifs((prev) => {
        // Session 27.100 — Bumped from 15 s → 60 s. Under heavy
        // service load (10+ tables, many concurrent orders), the
        // 4 s poll can miss a notif for multiple cycles due to
        // response gaps, replica lag on reads, or ingress
        // pressure. 15 s wasn't enough for the "notif appears then
        // disappears before an ack" cases operators reported.
        // 60 s is safe because any REAL ack still purges via the
        // WS `acknowledged` handler (Session 27.90).
        const graceMs = 60000;
        const nowT = Date.now();
        for (const [id, at] of locallyAddedBarRef.current.entries()) {
          if (nowT - at > graceMs) locallyAddedBarRef.current.delete(id);
        }
        const nextById = new Map(next.map((n) => [n.id, n]));
        const preserved = [];
        for (const p of (prev || [])) {
          if (!p || !p.id) continue;
          if (nextById.has(p.id)) continue;
          if (!locallyAddedBarRef.current.has(p.id)) continue;
          if (recentlyAckedBarRef.current.has(p.id)) continue;
          preserved.push(p);
        }
        return preserved.length ? [...preserved, ...next] : next;
      });
    } catch (error) {
      // silent — endpoint may 404 on an older backend build; banner won't render.
    }
  }, [authHeaders, session?.token, chimeOnceForOrder]);

  const fetchWaiterCalls = useCallback(async () => {
    if (!session?.token) return;
    try {
      const response = await http.get("/staff/waiter-calls", { headers: authHeaders });
      const next = response.data.calls || [];
      // New-summons detection — chime only when a fresh waiter call
      // appears, not on every 4s poll or when an existing call is still
      // pending. Baseline is captured lazily on first fetch.
      const nextIds = new Set(next.map((c) => c.id));
      if (knownWaiterCallIdsRef.current === null) {
        knownWaiterCallIdsRef.current = nextIds;
      } else {
        let foundNew = false;
        for (const id of nextIds) {
          if (!knownWaiterCallIdsRef.current.has(id)) { foundNew = true; break; }
        }
        if (foundNew && !notificationsMutedRef.current) playStaffChime();
        knownWaiterCallIdsRef.current = nextIds;
      }
      setWaiterCalls(next);
    } catch (error) {
      // silent — endpoint may 404 on older backend builds.
    }
  }, [authHeaders, session?.token]);

  // Customer orders held for staff review — polled alongside the other
  // banners. If the setting is OFF, the endpoint returns an empty list
  // and the banner simply never renders.
  const handledConfirmationsRef = useRef(new Map()); // Session 31.7 — id → ts of accept/decline
  const fetchPendingConfirmations = useCallback(async () => {
    if (!session?.token) return;
    try {
      const response = await http.get("/staff/pending-confirmations", { headers: authHeaders });
      // Session 31.7 — ignore orders this device accepted/declined in the
      // last 15 s so a poll racing the server write can't re-add them.
      const nowTs = Date.now();
      for (const [id, ts] of handledConfirmationsRef.current) { if (nowTs - ts > 15000) handledConfirmationsRef.current.delete(id); }
      const next = (response.data.orders || []).filter((o) => !handledConfirmationsRef.current.has(o.id));
      const nextIds = new Set(next.map((o) => o.id));
      if (knownPendingConfirmIdsRef.current === null) {
        knownPendingConfirmIdsRef.current = nextIds;
      } else {
        let foundNew = false;
        for (const id of nextIds) {
          if (!knownPendingConfirmIdsRef.current.has(id)) { foundNew = true; break; }
        }
        // New pending order → play the staff CONFIRMATION chime so
        // waiters hear the "an order is awaiting your confirmation"
        // pop-up land even if they're not looking at the tab.
        // Session 27.72 — Distinct from the waiter-call chime so the
        // ear can tell "someone tapped Call Waiter" apart from "a
        // new customer order needs approval before hitting kitchen".
        if (foundNew && !notificationsMutedRef.current) playStaffConfirmationChime();
        knownPendingConfirmIdsRef.current = nextIds;
      }
      setPendingConfirmations(next);
    } catch (error) {
      // silent — endpoint may 404 on older backend builds; banner won't render.
    }
  }, [authHeaders, session?.token]);


  useEffect(() => {
    if (!session?.token) return undefined;
    fetchKitchenNotifs();
    fetchBarNotifs();
    fetchWaiterCalls();
    fetchPendingConfirmations();
    // Both banners poll together so the staff screen picks up any station's
    // ready event within 4 seconds.
    const interval = window.setInterval(() => {
      fetchKitchenNotifs();
      fetchBarNotifs();
      fetchWaiterCalls();
      fetchPendingConfirmations();
    }, 4000);
    return () => window.clearInterval(interval);
  }, [fetchKitchenNotifs, fetchBarNotifs, fetchWaiterCalls, fetchPendingConfirmations, session?.token]);

  const confirmPendingOrder = async (orderId) => {
    setConfirmingOrderId(orderId);
    handledConfirmationsRef.current.set(orderId, Date.now());
    // Optimistic remove — the poll will restore it if the confirm fails.
    setPendingConfirmations((prev) => prev.filter((o) => o.id !== orderId));
    try {
      await http.post(`/staff/orders/${orderId}/confirm`, {}, { headers: authHeaders });
      toast.success("Order sent to kitchen & bar");
      // Refresh the dashboard so the newly-released order appears in the
      // active list right away.
      if (typeof fetchDashboard === "function") fetchDashboard(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not confirm order");
      handledConfirmationsRef.current.delete(orderId);
      fetchPendingConfirmations();
    } finally {
      setConfirmingOrderId(null);
    }
  };

  const rejectPendingOrder = async (orderId, reason = null) => {
    setConfirmingOrderId(orderId);
    handledConfirmationsRef.current.set(orderId, Date.now());
    setPendingConfirmations((prev) => prev.filter((o) => o.id !== orderId));
    try {
      await http.post(`/staff/orders/${orderId}/reject`, { reason: reason || null }, { headers: authHeaders });
      toast.success("Order declined");
      if (typeof fetchDashboard === "function") fetchDashboard(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not decline order");
      handledConfirmationsRef.current.delete(orderId);
      fetchPendingConfirmations();
    } finally {
      setConfirmingOrderId(null);
    }
  };

  // Session 30.34 — Offline / LAN-only "Mark delivered". The banner's
  // order may only exist on the LAN (a `local-…` id from a ticket that
  // never reached the cloud), or we may be online while the station
  // that marked it ready is not — in the router-only test the phone
  // (mobile data) tried to ack a LAN-only id against the cloud and got
  // "Invalid identifier". Queue the delivered event locally instead:
  // the LAN-aware localWrite mirrors it to every peer immediately and
  // syncs it to the cloud once the ticket itself has landed there.
  const queueDeliveredOffline = async (department, orderId, notif) => {
    const isPartial = !!(notif && notif.partial);
    const items = notif && Array.isArray(notif.items) ? notif.items : [];
    await localWrite({
      type: EventType.ORDER_STATUS_CHANGED,
      payload: {
        order_id: orderId,
        order_change_id: notif?.order_change_id || null,
        status: "delivered",
        delivered_departments: [department],
        delivered_at: new Date().toISOString(),
        partial: isPartial,
        items: isPartial ? items.map((it) => ({ id: it.id || null, name: it.name, quantity: Number(it.quantity || 1), department: it.department || department })) : [],
        by: "staff",
      },
      session,
    });
  };
  const shouldFallBackToOffline = (error) => {
    // Session 54 — any cloud / edge error (5xx, 503 DB-unavailable,
    // Cloudflare 520…) queues the delivery locally instead of dropping it.
    if (isTransientCloudError(error)) return true;
    const st = error.response.status;
    const detail = String(error.response.data?.detail || "");
    return st === 400 && /identifier/i.test(detail);
  };

  const acknowledgeKitchenNotif = async (orderId, notif) => {
    setKitchenNotifs((prev) => prev.filter((n) => n.id !== orderId));
    // Session 27.104 — Only add to the "recently-acked → completed"
    // overlay for FULL acks. Partial acks (kitchen sent a subset of
    // items) must not flip the order to "completed" locally,
    // otherwise the pending-items counter drops the WHOLE order
    // instead of just the delivered items.
    const isPartial = !!(notif && notif.partial);
    const deliveredItems = notif && Array.isArray(notif.items) ? notif.items : [];
    if (!isPartial) {
      // Session 27.11 — remember the ack so a racing poll can't ghost it back.
      recentlyAckedKitchenRef.current.set(orderId, Date.now());
    } else {
      // Session 31.6 — partial: 3 s window only (the poll now returns partial banners too).
      recentlyAckedKitchenRef.current.set(orderId, Date.now() - 27000);
    }
    try {
      // Session 27.17 — Retry the ack POST up to 3 times on 5xx or
      // network errors. In production, intermittent Cloudflare 520/521
      // errors were silently killing every ack POST — the staff would
      // tap "Mark delivered" 30 times and NONE of them reached the
      // backend, so orders stayed "confirmed" and the banner never
      // cleared. This retry with exponential backoff (400ms / 900ms /
      // 2000ms) masks those glitches while keeping the UX responsive.
      let viaOffline = false;
      if (String(orderId).startsWith("local-")) {
        await queueDeliveredOffline("kitchen", orderId, notif);
        viaOffline = true;
      } else {
        try {
          await postWithRetry(`/staff/kitchen-ready/${orderId}/ack`, {}, { headers: authHeaders });
        } catch (ackError) {
          if (!shouldFallBackToOffline(ackError)) throw ackError;
          await queueDeliveredOffline("kitchen", orderId, notif);
          viaOffline = true;
        }
      }
      if (viaOffline) toast.message("Delivered — saved on this device and shared over LAN; syncs to the cloud automatically.");
      // Session 27.15 — CRITICAL: after ack succeeds, force a
      // dashboard refresh so the "Pending" tab counter decrements
      // instantly. Previously this relied on the WS ack broadcast
      // arriving back, but if the WS silently dropped the counter
      // would stay stuck at N even though the order had completed.
      //
      // Session 27.16 — Even stronger defence: OPTIMISTICALLY mark
      // the order as completed in the local `tableSessions` state
      // right now, so the "Pending" counter drops the instant we
      // tap "Mark delivered" — regardless of network latency or
      // whether the backend decides to complete the order (it will,
      // for kitchen-only items; for orders with bar items too, the
      // order technically remains "confirmed" until bar acks, but
      // this UI still reflects the correct intent). The network
      // refetch below will reconcile back to reality if needed.
      setTableSessions((prev) => (prev || []).map((s) => {
        const orders = s.orders || [];
        if (!orders.some((o) => o && o.id === orderId)) return s;
        return {
          ...s,
          orders: orders.map((o) => {
            if (!o || o.id !== orderId) return o;
            // Session 27.104 — Also flip the acknowledged items to
            // "delivered" locally so `_pendingItemCountForOrder`
            // drops by exactly the number of items delivered on
            // this ack (partial or full). Matching is best-effort:
            // by item id when the notif carries one, else by name+
            // department+quantity so partial-ready deliveries still
            // decrement the counter correctly.
            const nowIso = new Date().toISOString();
            let nextItems = Array.isArray(o.items) ? [...o.items] : [];
            if (isPartial && deliveredItems.length) {
              const matchKeys = new Set(deliveredItems.map((di) =>
                `${(di.department || "").toLowerCase()}|${(di.name || "").toLowerCase()}|${di.quantity || 1}`
              ));
              const consumed = new Set();
              nextItems = nextItems.map((it) => {
                if (!it || it.voided) return it;
                if (String(it.status || "").toLowerCase() === "delivered") return it;
                const key = `${(it.department || "").toLowerCase()}|${(it.name || "").toLowerCase()}|${it.quantity || 1}`;
                if (matchKeys.has(key) && !consumed.has(it)) {
                  consumed.add(it);
                  matchKeys.delete(key);
                  return { ...it, status: "delivered", delivered_at: nowIso };
                }
                return it;
              });
            } else if (!isPartial) {
              // Full-station ack — mark every kitchen item as delivered.
              nextItems = nextItems.map((it) => {
                if (!it || it.voided) return it;
                if ((it.department || "").toLowerCase() !== "kitchen") return it;
                if (String(it.status || "").toLowerCase() === "delivered") return it;
                return { ...it, status: "delivered", delivered_at: nowIso };
              });
            }
            // Session 27.103 — Only flip to completed when the kitchen
            // has finalized the whole order (kitchen_ready_at set) AND
            // there's no unfinished bar work. Otherwise a partial ack
            // would prematurely complete the order and remove it from
            // the kitchen screen while items are still being prepped.
            const kitchenDone = !!o.kitchen_ready_at;
            const hasUnackedBar = !!o.bar_ready_at && !o.bar_acknowledged_at;
            const barDone = !o.bar_ready_at || !!o.bar_acknowledged_at;
            const nextStatus = kitchenDone && barDone && !hasUnackedBar ? "completed" : o.status;
            const nextAck = kitchenDone ? new Date().toISOString() : o.kitchen_acknowledged_at;
            return { ...o, items: nextItems, kitchen_acknowledged_at: nextAck, status: nextStatus };
          }),
        };
      }));
      // Session 28b — Removed immediate + delayed fetchDashboard() calls.
      // The kitchen ack produces a WS event (order_status_changed) that the
      // WS handler applies directly. The merge-based reconciliation prevents
      // stale HTTP snapshots from overwriting WS mutations.
      if (!viaOffline) { try { fetchKitchenNotifs(); } catch (_) { /* noop */ } }
    } catch (error) {
      // re-fetch to recover state; drop from the suppression list so
      // the banner can legitimately re-appear if the ack failed.
      recentlyAckedKitchenRef.current.delete(orderId);
      fetchKitchenNotifs();
      // Session 28b — Error path: still fetch to recover, but now merge-safe.
      fetchDashboard(false);
      // Session 27.17 — Distinguish transient server errors from real
      // 4xx errors so staff know whether to retry manually or wait.
      const status = error?.response?.status;
      const isTransient = !status || status >= 500;
      toast.error(
        error?.response?.data?.detail ||
          (isTransient
            ? "Server temporarily unavailable — please tap Mark Delivered again in a moment."
            : "Could not mark delivered — please try again.")
      );
    }
  };

  const acknowledgeBarNotif = async (orderId, notif) => {
    setBarNotifs((prev) => prev.filter((n) => n.id !== orderId));
    const isPartial = !!(notif && notif.partial);
    const deliveredItems = notif && Array.isArray(notif.items) ? notif.items : [];
    if (!isPartial) {
      recentlyAckedBarRef.current.set(orderId, Date.now());
    } else {
      recentlyAckedBarRef.current.set(orderId, Date.now() - 27000); // Session 31.6 — 3 s window
    }
    try {
      let viaOffline = false;
      if (String(orderId).startsWith("local-")) {
        await queueDeliveredOffline("bar", orderId, notif);
        viaOffline = true;
      } else {
        try {
          await postWithRetry(`/staff/bar-ready/${orderId}/ack`, {}, { headers: authHeaders });
        } catch (ackError) {
          if (!shouldFallBackToOffline(ackError)) throw ackError;
          await queueDeliveredOffline("bar", orderId, notif);
          viaOffline = true;
        }
      }
      if (viaOffline) toast.message("Delivered — saved on this device and shared over LAN; syncs to the cloud automatically.");
      // Session 27.16 — mirror the optimistic update from
      // acknowledgeKitchenNotif so the "Pending" counter drops
      // instantly even when only the bar acks the order.
      setTableSessions((prev) => (prev || []).map((s) => {
        const orders = s.orders || [];
        if (!orders.some((o) => o && o.id === orderId)) return s;
        return {
          ...s,
          orders: orders.map((o) => {
            if (!o || o.id !== orderId) return o;
            const nowIso = new Date().toISOString();
            let nextItems = Array.isArray(o.items) ? [...o.items] : [];
            if (isPartial && deliveredItems.length) {
              // Session 27.104 — Optimistic partial delivery: mark
              // only the acked items as "delivered" so the pending
              // counter drops by exactly what the bar handed over.
              const matchKeys = new Set(deliveredItems.map((di) =>
                `${(di.department || "").toLowerCase()}|${(di.name || "").toLowerCase()}|${di.quantity || 1}`
              ));
              const consumed = new Set();
              nextItems = nextItems.map((it) => {
                if (!it || it.voided) return it;
                if (String(it.status || "").toLowerCase() === "delivered") return it;
                const key = `${(it.department || "").toLowerCase()}|${(it.name || "").toLowerCase()}|${it.quantity || 1}`;
                if (matchKeys.has(key) && !consumed.has(it)) {
                  consumed.add(it);
                  matchKeys.delete(key);
                  return { ...it, status: "delivered", delivered_at: nowIso };
                }
                return it;
              });
            } else if (!isPartial) {
              nextItems = nextItems.map((it) => {
                if (!it || it.voided) return it;
                if ((it.department || "").toLowerCase() !== "bar") return it;
                if (String(it.status || "").toLowerCase() === "delivered") return it;
                return { ...it, status: "delivered", delivered_at: nowIso };
              });
            }
            // Session 27.103 — Only flip to completed when bar_ready_at
            // is actually set (not a partial ack) AND the kitchen side
            // is also done. Otherwise partial acks would prematurely
            // complete the order and wipe it from other station screens.
            const barDone = !!o.bar_ready_at;
            const kitchenDone = !o.kitchen_ready_at || !!o.kitchen_acknowledged_at;
            const hasUnackedKitchen = !!o.kitchen_ready_at && !o.kitchen_acknowledged_at;
            const nextStatus = barDone && kitchenDone && !hasUnackedKitchen ? "completed" : o.status;
            const nextAck = barDone ? new Date().toISOString() : o.bar_acknowledged_at;
            return { ...o, items: nextItems, bar_acknowledged_at: nextAck, status: nextStatus };
          }),
        };
      }));
      // Session 28b — Removed immediate + delayed fetchDashboard() calls.
      // The bar ack produces a WS event that the WS handler applies directly.
      if (!viaOffline) { try { fetchBarNotifs(); } catch (_) { /* noop */ } }
    } catch (error) {
      recentlyAckedBarRef.current.delete(orderId);
      fetchBarNotifs();
      // Session 28b — Error recovery: merge-safe fetch.
      fetchDashboard(false);
      const status = error?.response?.status;
      const isTransient = !status || status >= 500;
      toast.error(
        error?.response?.data?.detail ||
          (isTransient
            ? "Server temporarily unavailable — please tap Mark Delivered again in a moment."
            : "Could not mark delivered — please try again.")
      );
    }
  };

  const acknowledgeWaiterCall = async (callId) => {
    setWaiterCalls((prev) => prev.filter((c) => c.id !== callId));
    try {
      await http.post(`/staff/waiter-calls/${callId}/ack`, {}, { headers: authHeaders });
    } catch (error) {
      fetchWaiterCalls();
    }
  };

  useEffect(() => {
    if (!tableSessions.find((sessionItem) => sessionItem.id === selectedSessionId)) {
      setSelectedSessionId(tableSessions[0]?.id || "");
    }
  }, [selectedSessionId, tableSessions]);

  // Preserve a reference to the last valid selected session so that if a
  // transient poll gap causes the session to drop from `tableSessions`,
  // the user doesn't lose their receipt view. It re-syncs on the next
  // successful poll that includes the session.
  const lastValidSessionRef = useRef(null);
  const selectedSession = useMemo(() => {
    let found = tableSessions.find((sessionItem) => sessionItem.id === selectedSessionId) || null;
    if (found) {
      // Session 31.18 — last line of defence against a `local-…` placeholder
      // sitting next to its cloud copy (doubled receipt).
      const deduped = dedupeLocalOrders(found.orders);
      if (deduped.length !== (found.orders || []).length) found = { ...found, orders: deduped };
      lastValidSessionRef.current = found;
      return found;
    }
    // If not found but we have a cached version, keep it for continuity
    // (the grace period or next poll should bring it back).
    if (lastValidSessionRef.current && lastValidSessionRef.current.id === selectedSessionId) {
      return lastValidSessionRef.current;
    }
    lastValidSessionRef.current = null;
    return null;
  }, [selectedSessionId, tableSessions]);

  // Centralized "Add items" gate. If the current table session already
  // has payment recorded, the menu must not pop — staff has to Reopen
  // Table (Void Manager PIN) first. `sessionHasPayment` is also used as
  // a render guard on the Add Items button so the button disappears on
  // paid tables and reappears automatically after a reopen (which
  // spawns a fresh session with no payment).
  // Session 31.26 — multi-tender: only a FULLY settled bill locks the
  // menu. A partially-paid table (guest paid half by card, will settle
  // the rest later) can still order more — the extra simply raises the
  // outstanding balance.
  const sessionHasPayment = useMemo(() => {
    const s = selectedSession;
    if (!s) return false;
    const st = sessionPaymentState(s);
    return st.owed > 0 && st.isPaid;
  }, [selectedSession]);
  const tryOpenAddItems = useCallback(() => {
    if (sessionHasPayment) {
      toast.message("Payment already recorded — reopen the table from the receipt to add items.");
      setReceiptOpen(true);
      return;
    }
    setAddItemsOpen(true);
  }, [sessionHasPayment]);
  // Lightweight initial fetch so the Open Accounts button shows a
  // pending-count badge even before the dialog is opened.

  // Safety net: if state drifts and the picker is open on a paid
  // session, force it shut so the menu can never appear on a paid table.
  useEffect(() => {
    if (addItemsOpen && sessionHasPayment) setAddItemsOpen(false);
  }, [addItemsOpen, sessionHasPayment]);

  // ── Open Accounts (Staff Dashboard) ───────────────────────────────
  // Mirrors the Owner → Overview → Accounts view. Staff can see who
  // hasn't paid their bill and record a payment without escalating to
  // a manager. Backed by /owner/accounts endpoints (now opened to staff
  // scope server-side).
  const [staffAccounts, setStaffAccounts] = useState({ open: false, loading: false, accounts: [], summary: { open_count: 0, total_outstanding: 0 } });
  const [staffTabsView, setStaffTabsView] = useState("accounts"); // Session 30.12 / 31.2 — customer tabs first
  const [staffTabsSearch, setStaffTabsSearch] = useState("");
  // Session 31.6 — the "Open tables" search starts clean every time the
  // Tabs dialog opens (View / Add items close it programmatically, so an
  // onOpenChange reset alone would miss those paths).
  useEffect(() => { setStaffTabsSearch(""); }, [staffAccounts.open]);
  const [staffAccountDetail, setStaffAccountDetail] = useState({ open: false, account: null, amount: "", method: "cash", reference: "", saving: false });

  const loadStaffAccounts = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setStaffAccounts((d) => ({ ...d, loading: true }));
    try {
      const r = await http.get("/owner/accounts?status=open", { headers: authHeaders });
      setStaffAccounts((d) => ({ ...d, loading: false, accounts: r.data.accounts || [], summary: r.data.summary || { open_count: 0, total_outstanding: 0 } }));
    } catch (error) {
      if (!silent) toast.error(error.response?.data?.detail || "Could not load open tabs.");
      setStaffAccounts((d) => ({ ...d, loading: false }));
    }
  }, [authHeaders]);
  // Session 72 — tabs opened / settled / closed on ANOTHER device refresh
  // here too (the WS handler above runs before this is defined → ref).
  loadStaffAccountsRef.current = loadStaffAccounts;

  // Lightweight initial fetch so the Open Accounts button shows a
  // pending-count badge even before the dialog is opened.
  useEffect(() => { loadStaffAccounts(); }, [loadStaffAccounts]);

  // Session 31.2 — Open an empty tab (pay-later customer, no table).
  const submitNewTab = async () => {
    const name = (newTabDialog.customer_name || "").trim();
    if (!name) { toast.error("Customer name is required."); return; }
    setNewTabDialog((d) => ({ ...d, saving: true }));
    try {
      await http.post("/owner/accounts", { customer_name: name, phone: (newTabDialog.phone || "").trim() || undefined }, { headers: authHeaders });
      setNewTabDialog({ open: false, customer_name: "", phone: "", saving: false });
      toast.success(`Tab opened for ${name}.`);
      loadStaffAccounts();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not open the tab.");
      setNewTabDialog((d) => ({ ...d, saving: false }));
    }
  };

  // Session 31.2 — Add items to a tab: the server hands back the tab's
  // linked session (created on demand); we select it and open the normal
  // Add-items flow. Every order on that session posts onto the tab.
  const addItemsToTab = async (acc) => {
    setTabAddBusyId(acc.id);
    try {
      const { data } = await http.post(`/owner/accounts/${acc.id}/session`, {}, { headers: authHeaders });
      const sess = data?.table_session;
      if (!sess?.id) throw new Error("no session");
      setTableSessions((prev) => {
        const list = Array.isArray(prev) ? prev : [];
        return list.some((x) => x.id === sess.id) ? list.map((x) => (x.id === sess.id ? { ...x, ...sess } : x)) : [sess, ...list];
      });
      setSelectedSessionId(sess.id);
      setStatusFilter("active-tables");
      setStaffAccounts((d) => ({ ...d, open: false }));
      setTimeout(() => setAddItemsOpen(true), 150);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not open this tab for ordering.");
    } finally {
      setTabAddBusyId("");
    }
  };

  // ── Reservations ───────────────────────────────────────────────────
  // Stored as a flat list of upcoming/arrived/etc reservations for this
  // business. The "Open Reservation" button on the toolbar opens this
  // dialog which doubles as the list-view + create form + status actions.
  const [reservationsDialog, setReservationsDialog] = useState({
    open: false,
    loading: false,
    reservations: [],
    form: { customer_name: "", phone: "", party_size: "2", table_label: "", reserved_date: "", reserved_time: "", notes: "" },
    saving: false,
  });

  const loadReservations = useCallback(async () => {
    setReservationsDialog((d) => ({ ...d, loading: true }));
    try {
      const r = await http.get("/staff/reservations?status=upcoming", { headers: authHeaders });
      setReservationsDialog((d) => ({ ...d, loading: false, reservations: r.data.reservations || [] }));
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not load reservations.");
      setReservationsDialog((d) => ({ ...d, loading: false }));
    }
  }, [authHeaders]);

  const submitReservation = async () => {
    const v = reservationsDialog.form;
    if (!v.customer_name.trim()) { toast.error("Customer name required."); return; }
    if (!v.reserved_date || !v.reserved_time) { toast.error("Pick a date and time."); return; }
    const partySize = Math.max(1, Math.floor(Number(v.party_size) || 0));
    if (!partySize) { toast.error("Party size must be at least 1."); return; }
    // Build a local ISO datetime; backend parses & stores as UTC.
    const reservedAt = new Date(`${v.reserved_date}T${v.reserved_time}`).toISOString();
    setReservationsDialog((d) => ({ ...d, saving: true }));
    try {
      await http.post("/staff/reservations", {
        customer_name: v.customer_name.trim(),
        phone: v.phone.trim(),
        party_size: partySize,
        table_label: v.table_label.trim(),
        reserved_at: reservedAt,
        notes: v.notes.trim(),
      }, { headers: authHeaders });
      toast.success("Reservation saved.");
      setReservationsDialog((d) => ({
        ...d,
        saving: false,
        form: { customer_name: "", phone: "", party_size: "2", table_label: "", reserved_date: "", reserved_time: "", notes: "" },
      }));
      loadReservations();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not save reservation.");
      setReservationsDialog((d) => ({ ...d, saving: false }));
    }
  };

  const setReservationStatus = async (id, status) => {
    try {
      await http.patch(`/staff/reservations/${id}`, { status }, { headers: authHeaders });
      toast.success(status === "arrived" ? "Marked as arrived." : status === "cancelled" ? "Reservation cancelled." : "Updated.");
      loadReservations();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not update reservation.");
    }
  };

  const submitStaffAccountPayment = async () => {
    const v = staffAccountDetail;
    if (!v.account) return;
    const amt = Number(v.amount || 0);
    if (amt <= 0) { toast.error("Enter an amount greater than zero."); return; }
    setStaffAccountDetail((d) => ({ ...d, saving: true }));
    try {
      // Session 31.10 — API takes method cash|card|eft|other and `notes`
      // (the dialog used to send "bank" + `reference`, which 422'd / dropped).
      const methodMap = { cash: "cash", card: "card", bank: "eft", eft: "eft" };
      const remaining = Number(v.account.remaining_balance ?? 0);
      // Cash over the outstanding amount is change, not extra credit.
      const recordAmt = remaining > 0 ? Math.min(amt, remaining) : amt;
      await http.post(`/owner/accounts/${v.account.id}/payments`, {
        amount: Math.round(recordAmt * 100) / 100,
        method: methodMap[v.method] || "other",
        notes: (v.reference || "").trim() || null,
      }, { headers: authHeaders });
      toast.success(amt >= remaining - 0.009 ? "Tab settled in full." : `Payment recorded — ${formatPrice(Math.max(0, remaining - amt))} still on the tab.`);
      setStaffAccountDetail({ open: false, account: null, amount: "", method: "cash", reference: "", saving: false });
      loadStaffAccounts();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not record payment.");
      setStaffAccountDetail((d) => ({ ...d, saving: false }));
    }
  };

  // Seed the receipt dialog's cash/card inputs ONLY when the dialog opens.
  // Depending on selectedSession would cause the polling refresh to reset the
  // user's typed value mid-edit. We read the latest session via a ref-style
  // lookup on the open transition.
  useEffect(() => {
    if (!receiptOpen) return;
    const current = tableSessions.find((s) => s.id === selectedSessionId) || null;
    const existingCash = current?.cash_paid;
    const existingCard = current?.card_paid;
    setCashPaid(existingCash != null && existingCash > 0 ? String(existingCash) : "");
    setCardPaid(existingCard != null && existingCard > 0 ? String(existingCard) : "");
    const existingTip = current?.tip_amount;
    const existingRecipient = current?.tip_recipient;
    setTipAmount(existingTip != null && existingTip > 0 ? String(existingTip) : "");
    setTipRecipient(existingRecipient || "");
    // Reset stepped-payment scratch state every time the dialog reopens so
    // the cashier always starts from "idle" with empty inputs.
    setPaymentStep("idle");
    setCashReceived("");
    setTipDraft(existingTip != null && existingTip > 0 ? String(existingTip) : "");
    setSplitCash(existingCash != null && existingCash > 0 ? String(existingCash) : "");
    setSplitCard(existingCard != null && existingCard > 0 ? String(existingCard) : "");
    setCardAmount(existingCard != null && existingCard > 0 ? String(existingCard) : "");
    setEftAmount("");
    setChosenMethod("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [receiptOpen]);

  const handleLogout = async () => {
    // Session 27.115 — Clear local state FIRST so the user is never
    // trapped in a dead session. The server call is best-effort.
    beginLogoutSuppression();
    await exitFullscreenIfActive();
    const tokenSnapshot = session?.token;
    // Immediately tear down local state — user sees the login screen.
    clearSession("staff", "user_logout");
    setSessionState(null);
    navigate("/staff", { replace: true });
    // Best-effort server-side cleanup (audit log, session deletion).
    try {
      if (tokenSnapshot) {
        await http.post("/auth/logout", {}, { headers: { Authorization: `Bearer ${tokenSnapshot}` } });
      }
    } catch {
      // no-op — local session already cleared.
    }
  };

  // Auto-logout when the tablet leaves the dashboard for 30s+ (home
  // button, app switch, screen lock that isn't unlocked promptly). Brief
  // screen locks within the grace window keep the user signed in.
  useAutoLogoutOnHidden(handleLogout, { enabled: !!session?.token, graceMs: 60000 });

  // ── Tips drill-down (header button) ─────────────────────────────────
  const sessionUser = session?.user || null;
  // Session 30.37 — the tip belongs to the waiter who OPENED the table
  // (assigned_user_name; only Move Table changes it). The attribution
  // labels must say the same name the backend will actually credit.
  const tipOwnerName = String(selectedSession?.assigned_user_name || "").trim() || sessionUser?.full_name || "";
  const openStaffTips = async () => {
    if (!sessionUser?.full_name) {
      toast.error("Sign in with your PIN to see your tips.");
      return;
    }
    setStaffTipDetail({ open: true, loading: true, data: null, error: null });
    try {
      const r = await http.get("/staff/my-tips/orders", { headers: authHeaders });
      setStaffTipDetail({ open: true, loading: false, data: r.data, error: null });
    } catch (error) {
      const message = error.response?.data?.detail || error.message || "Unable to load your tips.";
      // Keep the dialog open so the waiter sees the actual reason rather
      // than a transient toast they might miss.
      setStaffTipDetail({ open: true, loading: false, data: null, error: message });
    }
  };

  // ── Shift Handover ──────────────────────────────────────────────────
  // Lists this user's open tables and lets them transfer ownership to
  // another active waiter / supervisor / manager. The signed-in user is
  // the only valid "from" — no override. Tip rule is unaffected: whoever
  // closes the table gets the tip.
  const [printerSetupOpen, setPrinterSetupOpen] = useState(false); // Session 30.7
  const [closePinPrompt, setClosePinPrompt] = useState(false); // Session 30.9 — unpaid close guard

  // Session 30.19 — Long-wait chime. Plays ONCE per order the moment it
  // crosses the 15-minute mark (checked every 15 s). Orders that were
  // already overdue when the dashboard loaded are seeded silently so a
  // refresh never re-triggers a wall of chimes. Respects the mute switch
  // and stays silent in the Owner-embedded view (the owner page has its
  // own audio preferences).
  const longWaitAlertedRef = useRef(null);
  useEffect(() => {
    if (embedded) return undefined;
    const check = () => {
      const current = longWaitOrders(tableSessions, Number(context?.business?.long_wait_minutes) || LONG_WAIT_MIN, Date.now(), context?.business?.long_wait_by_department || null);
      const ids = current.map((a) => String(a.orderId || `${a.sessionId}:${a.orderNumber || ""}`));
      if (longWaitAlertedRef.current === null) {
        // Seed silently — but only once real data has arrived, otherwise
        // the first fetch would look like a wall of fresh crossings.
        if (!Array.isArray(tableSessions) || tableSessions.length === 0) return;
        longWaitAlertedRef.current = new Set(ids);
        return;
      }
      const seen = longWaitAlertedRef.current;
      const fresh = current.filter((a, i) => !seen.has(ids[i]));
      ids.forEach((id) => seen.add(id));
      // Forget orders that are no longer overdue (delivered / voided) so
      // memory stays bounded across a long shift.
      const live = new Set(ids);
      seen.forEach((id) => { if (!live.has(id)) seen.delete(id); });
      if (fresh.length === 0) return;
      // Session 31.2 — one chime per new late order; the alert strip is the
      // single visual warning (no stacked toasts on top of it).
      if (!notificationsMutedRef.current) {
        try { playLongWaitChime(); } catch (_) { /* noop */ }
      }
    };
    check();
    const t = setInterval(check, 15000);
    return () => clearInterval(t);
  }, [tableSessions, embedded, context?.business?.long_wait_minutes, context?.business?.long_wait_by_department]);
  const [staffHandover, setStaffHandover] = useState({
    open: false, loading: false, saving: false,
    myTables: [], targets: [], selected: new Set(), toUserId: "",
  });
  const toggleNotificationsMuted = async () => {
    if (embedded) return; // Session 30.25 — Owner overview is always muted.
    try {
      const r = await http.patch("/auth/me/notifications", {}, { headers: authHeaders });
      setNotificationsMuted(!!r.data.notifications_muted);
      toast.success(r.data.notifications_muted ? "Notifications turned off" : "Notifications turned on");
    } catch (error) { toast.error("Unable to update notification setting."); }
  };

  const openStaffHandover = async () => {
    setStaffHandover((s) => ({ ...s, open: true, loading: true, selected: new Set(), toUserId: "" }));
    try {
      const slug = session?.business?.slug;
      const [tablesRes, usersRes] = await Promise.all([
        http.get("/staff/table-sessions", { headers: authHeaders }),
        slug ? axios.get(`${API}/businesses/${encodeURIComponent(slug)}/users`) : Promise.resolve({ data: { users: [] } }),
      ]);
      const allOpen = (tablesRes.data?.table_sessions || []).filter((t) => t.status === "open");
      // Show every open table — not just mine — so a waiter can hand
      // over coverage even for tables they don't currently own (e.g.
      // covering a sick colleague at end of shift). The list still
      // sorts mine first so the most common case stays one-tap.
      const mine = [...allOpen].sort((a, b) => {
        const am = a.assigned_user_id === sessionUser?.id ? 0 : 1;
        const bm = b.assigned_user_id === sessionUser?.id ? 0 : 1;
        if (am !== bm) return am - bm;
        return String(a.table_number).localeCompare(String(b.table_number), undefined, { numeric: true });
      });
      const targets = (usersRes.data?.users || [])
        .filter((u) => u.active && ["waiter", "supervisor", "shift_leader", "manager"].includes(u.role) && u.id !== sessionUser?.id);
      setStaffHandover((s) => ({ ...s, loading: false, myTables: mine, targets, toUserId: targets[0]?.id || "" }));
    } catch (error) {
      toast.error(error.response?.data?.detail || "Unable to load your tables.");
      setStaffHandover((s) => ({ ...s, open: false, loading: false }));
    }
  };
  const toggleHandoverSelected = (id) => {
    setStaffHandover((s) => {
      const next = new Set(s.selected);
      if (next.has(id)) next.delete(id); else next.add(id);
      return { ...s, selected: next };
    });
  };
  const selectAllHandover = (checked) => {
    setStaffHandover((s) => ({ ...s, selected: checked ? new Set(s.myTables.map((t) => t.id)) : new Set() }));
  };
  const submitHandover = async (mode) => {
    const ids = mode === "all"
      ? staffHandover.myTables.map((t) => t.id)
      : Array.from(staffHandover.selected);
    if (!ids.length) { toast.error("Select at least one table."); return; }
    if (!staffHandover.toUserId) { toast.error("Pick someone to hand over to."); return; }
    setStaffHandover((s) => ({ ...s, saving: true }));
    try {
      const r = await http.post("/staff/tables/transfer", {
        session_ids: ids,
        to_user_id: staffHandover.toUserId,
      }, { headers: authHeaders });
      toast.success(`Transferred ${r.data.transferred} ${r.data.transferred === 1 ? "table" : "tables"} to ${r.data.to_user.full_name}.`);
      setStaffHandover({ open: false, loading: false, saving: false, myTables: [], targets: [], selected: new Set(), toUserId: "" });
      // Trigger a refresh of the staff dashboard so the assignment chip
      // updates immediately (don't wait for the next poll).
      if (typeof fetchDashboard === "function") fetchDashboard(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Unable to transfer tables.");
      setStaffHandover((s) => ({ ...s, saving: false }));
    }
  };

  // Session 31.9 — everything on this order has gone out.
  // Session 56 — never lose a "served": if the order is still a `local-…`
  // id (placed seconds ago on a device that hasn't finished syncing), the
  // cloud says 409 "still syncing", or the cloud is erroring, queue the
  // delivery locally — the server reducer resolves local ids and waits for
  // the order to land, then completes it. Same path the ready-banner
  // "Mark delivered" uses.
  const queueServedOffline = async (orderId, order) => {
    await localWrite({
      type: EventType.ORDER_STATUS_CHANGED,
      payload: {
        order_id: orderId,
        order_change_id: order?._change_id || null,
        status: "delivered",
        delivered_departments: ["kitchen", "bar"],
        delivered_at: new Date().toISOString(),
        partial: false,
        items: [],
        by: "staff",
        served_all: true,
      },
      session,
    });
  };
  const markOrderServed = async (orderId) => {
    setBusyAction(orderId + "served");
    const order = (tableSessions || []).flatMap((s) => s.orders || []).find((o) => o && o.id === orderId) || null;
    try {
      let viaOffline = false;
      if (String(orderId).startsWith("local-")) {
        await queueServedOffline(orderId, order);
        viaOffline = true;
      } else {
        try {
          await http.post(`/staff/orders/${orderId}/mark-served`, {}, { headers: authHeaders });
        } catch (error) {
          const st = error?.response?.status;
          const stillSyncing = st === 409 && /still syncing/i.test(String(error?.response?.data?.detail || ""));
          if (!isTransientCloudError(error) && !stillSyncing) throw error;
          await queueServedOffline(orderId, order);
          viaOffline = true;
        }
      }
      staffDeliveredIdsRef.current.set(orderId, Date.now());
      setTableSessions((prev) => (prev || []).map((sess) => {
        const orders = sess.orders || [];
        if (!orders.some((o) => o && o.id === orderId)) return sess;
        return { ...sess, orders: orders.map((o) => (o && o.id === orderId
          ? { ...o, status: "completed", kitchen_acknowledged_at: o.kitchen_acknowledged_at || new Date().toISOString(), bar_acknowledged_at: o.bar_acknowledged_at || new Date().toISOString(), items: (o.items || []).map((it) => (it && !it.voided ? { ...it, status: "delivered" } : it)) }
          : o)) };
      }));
      if (viaOffline) toast.message("Served — saved on this device; it syncs to the cloud automatically.");
      else toast.success("Order marked as served");
      fetchDashboard(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not mark the order as served");
    } finally {
      setBusyAction(null);
    }
  };

  const updateOrderStatus = async (orderId, nextStatus) => {
    setBusyAction(orderId + nextStatus);
    try {
      await http.patch(`/staff/orders/${orderId}`, { status: nextStatus }, { headers: authHeaders });
      await fetchDashboard(false);
      toast.success(`Order marked ${nextStatus}.`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Unable to update order.");
    } finally {
      setBusyAction("");
    }
  };

  // ---- Void flow -------------------------------------------------------
  // Managers enter the shared `void_password` (set by the owner on the
  // Access tab) to either void the whole order or a set of individual
  // lines. Staff never see the password; the prompt gates the
  // destructive action.
  //
  // Line-mode is a multi-select checklist: when a waiter taps the "×"
  // on any line, the dialog opens with THAT line pre-ticked but every
  // other non-voided line is listed too, so they can tick as many as
  // they want and clear them all with one password entry.
  const [voidDialog, setVoidDialog] = useState({
    open: false,
    mode: "order", // "order" | "line"
    orderId: "",
    orderLabel: "",
    // For "line" mode: the pool of ticketable lines with a `checked`
    // flag on each. Voided lines are excluded.
    lines: [], // [{ index, name, quantity, line_total, checked }]
    password: "",
    reason: "",
  });
  const [voidSubmitting, setVoidSubmitting] = useState(false);

  // Given an order and (optionally) a starting line index, return the
  // list of non-voided lines with the starting one pre-checked.
  const buildVoidLinePool = (order, initialLineIndex = null) => {
    const items = order?.items || [];
    return items
      .map((it, index) => {
        if (it?.voided) return null;
        return {
          index,
          name: it?.name || "Item",
          quantity: Number(it?.quantity) || 1,
          voidQty: Number(it?.quantity) || 1, // Session 31.10 — how many of this line to void
          line_total: Number(it?.line_total) || 0,
          note: it?.note || "",
          // Auto-check the line the waiter tapped; leave others off.
          checked: initialLineIndex !== null && initialLineIndex === index,
        };
      })
      .filter(Boolean);
  };

  const openVoidOrder = (orderId, order) => {
    setVoidDialog({
      open: true,
      mode: "order",
      orderId,
      orderLabel: order?.order_number || "",
      lines: [],
      password: "",
      reason: "",
    });
  };
  const openVoidLine = (orderId, lineIndex, order) => {
    setVoidDialog({
      open: true,
      mode: "line",
      orderId,
      orderLabel: order?.order_number || "",
      lines: buildVoidLinePool(order, lineIndex),
      password: "",
      reason: "",
    });
  };
  const toggleVoidLine = (index, uniqueKey) => {
    setVoidDialog((current) => ({
      ...current,
      lines: current.lines.map((row) => {
        if (current._multiOrder) {
          return row._uniqueKey === uniqueKey ? { ...row, checked: !row.checked } : row;
        }
        return row.index === index ? { ...row, checked: !row.checked } : row;
      }),
    }));
  };
  // Session 31.10 — "1 of the 3 burgers": adjust how many units of a line to void.
  const setVoidLineQty = (index, uniqueKey, delta) => {
    setVoidDialog((current) => ({
      ...current,
      lines: current.lines.map((row) => {
        const match = current._multiOrder ? row._uniqueKey === uniqueKey : row.index === index;
        if (!match) return row;
        const next = Math.min(row.quantity, Math.max(1, (row.voidQty || row.quantity) + delta));
        return { ...row, voidQty: next, checked: true };
      }),
    }));
  };
  const _voidQuantitiesFor = (rows) => {
    const q = {};
    rows.forEach((row) => { if (row.voidQty && row.voidQty < row.quantity) q[String(row.index)] = row.voidQty; });
    return Object.keys(q).length ? q : undefined;
  };
  const setAllVoidLines = (checked) => {
    setVoidDialog((current) => ({
      ...current,
      lines: current.lines.map((row) => ({ ...row, checked })),
    }));
  };
  const closeVoidDialog = () => setVoidDialog((current) => ({ ...current, open: false }));

  // Open void dialog from Table Actions showing ALL items across all orders
  const openVoidItemsFromActions = () => {
    if (!selectedSession) return;
    const activeOrders = (selectedSession.orders || []).filter((o) => o && o.status !== "voided");
    if (activeOrders.length === 0) { toast.error("No orders to void."); return; }
    // Build a pool of all non-voided items across all orders
    const allLines = [];
    activeOrders.forEach((order) => {
      (order.items || []).forEach((item, idx) => {
        if (item.voided) return;
        const qty = Number(item.quantity) || 1;
        const price = Number(item.price) || 0;
        allLines.push({
          orderId: order.id,
          orderNumber: order.order_number || "",
          index: idx,
          _uniqueKey: `${order.id}#${idx}`,
          name: item.name || "Item",
          quantity: qty,
          price: price,
          line_total: Math.round(qty * price * 100) / 100,
          status: item.status || "pending",
          note: item.special_instructions || item.notes || "",
          checked: false,
        });
      });
    });
    if (allLines.length === 0) { toast.error("No items to void."); return; }
    setVoidDialog({
      open: true,
      mode: "line",
      orderId: activeOrders[0].id,
      orderLabel: `Table ${selectedSession.table_number}`,
      lines: allLines,
      password: "",
      reason: "",
      _multiOrder: true,
    });
  };

  const submitVoid = async () => {
    if (!voidDialog.password || voidDialog.password.length < 4) {
      toast.error("Enter the void password (min 4 characters).");
      return;
    }
    // Line mode requires at least one ticked line.
    if (voidDialog.mode === "line") {
      const picked = voidDialog.lines.filter((row) => row.checked);
      if (picked.length === 0) {
        toast.error("Tick at least one line to void.");
        return;
      }
    }
    setVoidSubmitting(true);
    // Session 40 — voids are stock-affecting: they go through the offline-
    // safe path (HTTP with a change_id; queued in IndexedDB when the
    // connection is down) so a void recorded offline is never lost and can
    // only ever be applied once. `parentChangeId` ties the void to the
    // order_created event so a sale + void queued together stay paired.
    const _voidVia = async (oid, path, body) => {
      const known = (tableSessions || []).flatMap((ts) => (ts && ts.orders) || []).find((o) => o && (String(o.id) === String(oid) || String(o._local_id || "") === String(oid)));
      const res = await performStockAction({
        http, authHeaders, path, body,
        eventType: EventType.ORDER_VOIDED,
        eventPayload: { order_id: String(oid), order_change_id: known?._change_id || null, ...body },
        session, parentChangeId: known?._change_id || null,
      });
      return res;
    };
    let _queuedAny = false;
    // Session 46 — mirror the void into local state IMMEDIATELY (server
    // already applied it, or it is queued for sync). The merge layer now
    // treats voids as sticky, so a stale poll can no longer undo this.
    const _applyVoidLocally = (oid, lineIndexes, quantities) => {
      const nowIso = new Date().toISOString();
      const idxSet = Array.isArray(lineIndexes) ? new Set(lineIndexes.map((n) => Number(n))) : null;
      setTableSessions((prev) => (prev || []).map((s) => {
        const orders = (s && s.orders) || [];
        if (!orders.some((o) => o && (String(o.id) === String(oid) || String(o._local_id || "") === String(oid)))) return s;
        const nextOrders = orders.map((o) => {
          if (!o || !(String(o.id) === String(oid) || String(o._local_id || "") === String(oid))) return o;
          const items = (o.items || []).map((it, idx) => {
            if (!it || it.voided) return it;
            if (idxSet && !idxSet.has(idx)) return it;
            const want = quantities && quantities[String(idx)] != null ? Number(quantities[String(idx)]) : null;
            const qty = Number(it.quantity) || 1;
            if (want != null && want > 0 && want < qty) {
              return { ...it, quantity: qty - want, line_total: Math.round((Number(it.price) || 0) * (qty - want) * 100) / 100 };
            }
            return { ...it, voided: true, voided_at: nowIso };
          });
          const live = items.filter((it) => it && !it.voided);
          const total = Math.round(live.reduce((n, it) => n + (Number(it.line_total ?? (Number(it.price) || 0) * (Number(it.quantity) || 1)) || 0), 0) * 100) / 100;
          const next = { ...o, items, total, subtotal: total, total_items: live.reduce((n, it) => n + (Number(it.quantity) || 0), 0) };
          if (!idxSet || live.length === 0) { next.status = "voided"; next.voided = true; next.voided_at = nowIso; }
          return next;
        });
        // Session 62 — the table bill (items, fee, owed, outstanding)
        // follows the void right away; nothing waits for the next poll.
        return withRecomputedBill({ ...s, orders: nextOrders }, contextRef.current?.business || null);
      }));
    };
    try {
      if (voidDialog.mode === "order") {
        const res = await _voidVia(voidDialog.orderId, `/staff/orders/${voidDialog.orderId}/void`, { void_password: voidDialog.password, reason: voidDialog.reason || "" });
        _applyVoidLocally(voidDialog.orderId, null, null);
        if (res.status === "queued") { _queuedAny = true; toast.info("Void saved on this device — it will sync when the connection returns."); }
        else toast.success("Order voided.");
      } else if (voidDialog._multiOrder) {
        // Multi-order void: group checked lines by orderId and submit each.
        const checked = voidDialog.lines.filter((row) => row.checked);
        const byOrder = {};
        checked.forEach((row) => {
          const oid = row.orderId;
          if (!byOrder[oid]) byOrder[oid] = [];
          byOrder[oid].push(row);
        });
        let totalVoided = 0;
        for (const [oid, rows] of Object.entries(byOrder)) {
          const indexes = rows.map((r) => r.index);
          const qtys = _voidQuantitiesFor(rows);
          const res = await _voidVia(oid, `/staff/orders/${oid}/void-lines`, { void_password: voidDialog.password, line_indexes: indexes, reason: voidDialog.reason || "", quantities: qtys });
          _applyVoidLocally(oid, indexes, qtys);
          if (res.status === "queued") { _queuedAny = true; totalVoided += indexes.length; continue; }
          totalVoided += res.data?.voided_count ?? indexes.length;
        }
        if (_queuedAny) toast.info(`${totalVoided} line void(s) saved on this device — they will sync when the connection returns.`);
        else toast.success(`${totalVoided} item${totalVoided === 1 ? "" : "s"} voided.`);
      } else {
        const pickedRows = voidDialog.lines.filter((row) => row.checked);
        const line_indexes = pickedRows.map((row) => row.index);
        const _qtys = _voidQuantitiesFor(pickedRows);
        const res = await _voidVia(voidDialog.orderId, `/staff/orders/${voidDialog.orderId}/void-lines`, {
          void_password: voidDialog.password,
          line_indexes,
          reason: voidDialog.reason || "",
          quantities: _qtys,
        });
        _applyVoidLocally(voidDialog.orderId, line_indexes, _qtys);
        if (res.status === "queued") { _queuedAny = true; toast.info("Void saved on this device — it will sync when the connection returns."); }
        else {
          const units = pickedRows.reduce((sum, r) => sum + (r.voidQty || r.quantity), 0);
          const n = res.data?.voided_count ?? line_indexes.length;
          toast.success(units !== n ? `${units} item${units === 1 ? "" : "s"} voided across ${n} line${n === 1 ? "" : "s"}.` : `${n} line${n === 1 ? "" : "s"} voided.`);
        }
      }
      closeVoidDialog();
      if (!_queuedAny) await fetchDashboard(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Unable to void.");
    } finally {
      setVoidSubmitting(false);
    }
  };

  const catalogCategories = useMemo(() => menuCatalog.categories || [], [menuCatalog.categories]);
  // Identify whether a category counts as a "Sides" bucket. Two ways:
  //  1) the synthetic side-only category the backend mints for owners who
  //     manage standalone sides; or
  //  2) any owner-defined category that opted into the "sides" group.
  const isSidesCategory = (c) => Boolean(c?.is_side_only) || (c?.group || "").toLowerCase() === "sides";
  const allNonEmptyCategories = useMemo(
    () => catalogCategories.filter((category) => (category.items || []).length > 0),
    [catalogCategories]
  );
  const visibleCatalogCategories = useMemo(
    () => allNonEmptyCategories.filter((c) => (addItemsSidesMode === "sides" ? isSidesCategory(c) : !isSidesCategory(c))),
    [allNonEmptyCategories, addItemsSidesMode]
  );
  // Session 27.7 — accordion UI: activeCatalogCategoryId is either
  // a specific category id (that one is expanded) OR empty (no category
  // expanded, show the full category list). The old fallback of
  // "auto-select the first category" was a leftover from the dropdown
  // UI and made the accordion always open on Breakfast.
  const activeCatalogCategoryId = catalogCategoryId || "";
  // Build the list of items to render in the dialog. Search wins over the
  // category filter — if the staff is searching we show matches across every
  // category, tagged with the category name on each row.
  const addItemsRenderRows = useMemo(() => {
    const term = (addItemsSearchTerm || "").trim().toLowerCase();
    const buildRow = (item, category) => ({ ...item, _categoryName: category.name });
    let rows = [];
    if (term) {
      visibleCatalogCategories.forEach((cat) => {
        (cat.items || []).forEach((it) => {
          const hay = `${it.name || ""} ${it.description || ""} ${cat.name || ""}`.toLowerCase();
          if (hay.includes(term)) rows.push(buildRow(it, cat));
        });
      });
      return rows;
    }
    if (activeCatalogCategoryId === "__all__") {
      visibleCatalogCategories.forEach((cat) => {
        (cat.items || []).forEach((it) => rows.push(buildRow(it, cat)));
      });
      return rows;
    }
    const cat = visibleCatalogCategories.find((c) => c.id === activeCatalogCategoryId);
    if (!cat) return [];
    return (cat.items || []).map((it) => buildRow(it, cat));
  }, [addItemsSearchTerm, visibleCatalogCategories, activeCatalogCategoryId]);
  // Session 27.108 — Apply the A-Z sort toggle across whatever slice
  // of items is being rendered (search results, current category, or
  // the "All" view). When OFF we preserve the owner-defined order.
  const sortedAddItemsRenderRows = useMemo(() => {
    if (!staffCatalogSortAZ) return addItemsRenderRows;
    return [...addItemsRenderRows].sort((a, b) =>
      String(a?.name || "").localeCompare(String(b?.name || ""), undefined, { sensitivity: "base" })
    );
  }, [addItemsRenderRows, staffCatalogSortAZ]);
  // Back-compat: a few places still rely on activeCatalogItems (the items in
  // the currently selected category, without search applied).
  const activeCatalogItems = visibleCatalogCategories.find((category) => category.id === activeCatalogCategoryId)?.items || [];

  const addMenuItemQuantity = useCallback((itemId, nextQuantity) => {
    setMenuItemQuantities((current) => {
      const next = { ...current };
      const safeQty = Math.max(0, Math.min(99, Number(nextQuantity) || 0));
      if (safeQty === 0) {
        delete next[itemId];
      } else {
        next[itemId] = safeQty;
      }
      return next;
    });
  }, []);

  const itemNeedsConfig = (item) => {
    if (!item) return false;
    const cfg = item.sides_config || {};
    if (cfg.selection_mode && cfg.selection_mode !== "none") return true;
    if (Array.isArray(item.prep_prompts) && item.prep_prompts.length > 0) return true;
    // Staff sees the notes textarea when the item's notes_mode is "staff"
    // or "all" — so route those items through the sheet too, even if they
    // have no sides or prep prompts.
    const mode = String(item.notes_mode || "off").toLowerCase();
    if (mode === "staff" || mode === "all") return true;
    return false;
  };

  // Tap on an item's "Add" button — routes configurable items through the
  // SideSelectionSheet so the waiter can pick sides, answer prep prompts,
  // and add a note; simple items just bump the quantity stepper.
  const handleStaffAddTap = (item) => {
    if (itemNeedsConfig(item)) {
      setStaffSheetItem(item);
      return;
    }
    const current = menuItemQuantities[item.id] || 0;
    addMenuItemQuantity(item.id, current + 1);
  };

  // SideSelectionSheet confirm callback for the staff flow — pushes a fully
  // configured line into the staged-lines list. Each confirm = one new line
  // so the same item with different sides/notes stays separate.
  const handleStaffSheetConfirm = (sideIds, prepSelections, note) => {
    const item = staffSheetItem;
    if (!item) return;
    setStagedConfiguredLines((prev) => [
      ...prev,
      {
        local_id: `staged-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
        menu_item_id: item.id,
        item_name: item.name,
        item_price: Number(item.price || 0),
        quantity: 1,
        selected_side_ids: [...(sideIds || [])].map(String),
        prep_selections: prepSelections || [],
        note: (note || "").trim(),
      },
    ]);
    setStaffSheetItem(null);
    toast.success(`${item.name} added.`);
  };

  const removeStagedLine = (localId) => {
    setStagedConfiguredLines((prev) => prev.filter((l) => l.local_id !== localId));
  };

  const stagedLineQtyForItem = (itemId) =>
    stagedConfiguredLines.filter((l) => l.menu_item_id === itemId).reduce((s, l) => s + l.quantity, 0);
  // Session 69 — stable callbacks for the memoised StaffAddItemCard (the
  // latest closures live in refs so the card props never change identity).
  const staffAddTapRef = useRef(handleStaffAddTap);
  staffAddTapRef.current = handleStaffAddTap;
  const onStaffCardIncrement = useCallback((item, quantity, needsConfig) => {
    if (item.sellable === false) { toast.error(`${item.name} is out of stock.`); return; }
    if (needsConfig) staffAddTapRef.current(item);
    else addMenuItemQuantity(item.id, quantity + 1);
  }, [addMenuItemQuantity]);
  const onStaffCardDecrement = useCallback((item, quantity) => {
    addMenuItemQuantity(item.id, quantity - 1);
  }, [addMenuItemQuantity]);

  const selectedAddItemsCount = useMemo(
    () =>
      Object.values(menuItemQuantities).reduce((sum, quantity) => sum + quantity, 0) +
      stagedConfiguredLines.reduce((sum, l) => sum + l.quantity, 0),
    [menuItemQuantities, stagedConfiguredLines]
  );

  // Session 27.19 — Build a human-readable review of the current
  // "Add items" cart so the waiter can read the full order back to
  // the customer before placing it. Each entry carries:
  //   • name, quantity, unit_price, line_total
  //   • prep_selections (e.g. "Cooking: Medium rare")
  //   • note (free-text kitchen note)
  //   • sides (names of any side selections)
  // We flatten `menuItemQuantities` (simple items keyed by menu_item_id)
  // and `stagedConfiguredLines` (items that required side/prep prompts)
  // into a single ordered list. This memo also produces `reviewTotal`
  // so the review dialog can show a subtotal footer.
  const catalogItemLookup = useMemo(() => {
    const map = new Map();
    (menuCatalog?.categories || []).forEach((cat) => {
      (cat.items || []).forEach((it) => { if (it && it.id) map.set(it.id, it); });
    });
    // Also index sides library so we can resolve side ids to names.
    (menuCatalog?.sides_library || []).forEach((s) => { if (s && s.id) map.set(s.id, s); });
    return map;
  }, [menuCatalog]);

  const addItemsReviewLines = useMemo(() => {
    const lines = [];
    // Simple items — one line per menu_item_id.
    for (const [menuItemId, quantity] of Object.entries(menuItemQuantities || {})) {
      if (!quantity) continue;
      const item = catalogItemLookup.get(menuItemId);
      const unitPrice = Number(item?.price || 0);
      lines.push({
        key: `simple-${menuItemId}`,
        name: item?.name || "Unknown item",
        quantity,
        unit_price: unitPrice,
        line_total: unitPrice * quantity,
        prep_selections: [],
        sides: [],
        note: "",
      });
    }
    // Configured lines — one entry per staged line (each has its own
    // prep/sides/note context that mustn't collapse with siblings).
    for (const line of stagedConfiguredLines || []) {
      const item = catalogItemLookup.get(line.menu_item_id);
      const basePrice = Number(item?.price || 0);
      // Session 73 — mirror the server: the item's first `free_count`
      // sides are included, the rest are charged. The review used to
      // charge EVERY side, so it promised R149 for a bill the server
      // (correctly, per the menu setup) billed at R100.
      const sidesPricing = computeSidesPricingForLine(item, line.selected_side_ids || [], menuCatalog?.sides_library || []);
      const sideEntries = sidesPricing.snapshots.map((s) => ({ name: s.name, price: s.price, charged: s.charged }));
      const unitPrice = basePrice + sidesPricing.extras_per_unit;
      lines.push({
        key: `configured-${line.local_id}`,
        name: line.item_name || item?.name || "Unknown item",
        quantity: line.quantity,
        unit_price: unitPrice,
        line_total: unitPrice * line.quantity,
        prep_selections: line.prep_selections || [],
        sides: sideEntries,
        note: line.note || "",
      });
    }
    return lines;
  }, [menuItemQuantities, stagedConfiguredLines, catalogItemLookup, menuCatalog?.sides_library]);

  const addItemsReviewTotal = useMemo(
    () => addItemsReviewLines.reduce((sum, l) => sum + l.line_total, 0),
    [addItemsReviewLines]
  );

  const submitAddItems = async () => {
    if (!selectedSession) return;
    // Session 27.117 — Synchronous re-entry guard. If a rapid double-
    // tap on the "Place order" button already sent us into this
    // function, drop the second call before it can allocate another
    // change_id (which would otherwise produce a duplicate order on
    // the same table).
    if (submitAddItemsLockRef.current) return false;
    submitAddItemsLockRef.current = true;
    const simpleItems = Object.entries(menuItemQuantities).map(([menu_item_id, quantity]) => ({
      menu_item_id,
      quantity,
    }));
    const configuredItems = stagedConfiguredLines.map((l) => ({
      menu_item_id: l.menu_item_id,
      quantity: l.quantity,
      selected_side_ids: l.selected_side_ids,
      prep_selections: l.prep_selections,
      note: l.note || "",
    }));
    const items = [...simpleItems, ...configuredItems];
    if (!items.length) {
      toast.error("Select at least one item.");
      return;
    }
    setBusyAction("add-items");
    try {
      // Session 41 — Fallback-policy PREFLIGHT (online only). Ingredients set
      // to "Do not fallback" / "Require manager" that can't be served from the
      // service location make the server refuse the order (409, structured).
      // Asking first means the waiter sees OUT OF STOCK AT SERVICE LOCATION /
      // MANAGER APPROVAL REQUIRED immediately instead of a queued order that
      // is rejected at sync time. Network trouble → skip (the queued event is
      // still gated server-side; a rejected attempt never becomes an order).
      if (typeof navigator === "undefined" || navigator.onLine !== false) {
        try {
          // Session 69 — the pre-check must never hold the waiter hostage:
          // 1.5 s hard cap, no automatic retry. A slow server means the
          // order goes straight to the local queue (still gated server-side).
          const pf = await http.post("/staff/orders/preflight", {
            items: items.map((it) => ({ menu_item_id: it.menu_item_id, quantity: Number(it.quantity) || 1 })),
            table_session_id: selectedSession.id,
          }, { headers: authHeaders, timeout: 1500, noRetry: true, bestEffort: true });
          if (pf?.data && pf.data.ok === false) {
            const names = items.map((it) => (catalogCategories || []).flatMap((c) => c.items || []).find((m) => m && m.id === it.menu_item_id)?.name).filter(Boolean);
            setFallbackBlock({ ...pf.data, itemNames: names });
            return "blocked"; // truthy: caller must NOT reopen the review dialog; the block dialog explains
          }
        } catch (pfErr) {
          if (pfErr?.response?.status && pfErr.response.status < 500 && pfErr.response.status !== 404) {
            toast.error(pfErr.response?.data?.detail?.message || pfErr.response?.data?.detail || "Couldn't check stock for this order.");
            return false;
          }
          // No response / 5xx / endpoint missing → continue on the offline path.
        }
      }
      // Session 26 — Local Event Engine.
      //
      // This is the FIRST mutation migrated to the local-first
      // architecture:
      //
      //   1. `localWrite` persists an `order_created` event to the
      //      IDB event_queue with a client-generated `change_id`.
      //   2. Its reducer immediately upserts a lightweight order
      //      row + updates the table_session in IDB, so the UI can
      //      reflect the change on the next render — no cloud
      //      round-trip.
      //   3. The eventSync loop (armed once at boot) POSTs the
      //      event to /api/events/sync in the background.
      //   4. The cloud reducer applies the same change to MongoDB
      //      (dedupe by change_id) and fans out via WebSocket, so
      //      Kitchen / Bar / Owner devices update in the same
      //      convergence tick.
      //
      // Every other write on the staff dashboard still uses the
      // legacy `http.post(...)` path — this is the proof-of-concept
      // that unblocks migrating them one-by-one.
      const optimisticOrderId = `local-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      const nowIso = new Date().toISOString();
      // Session 27.47 — Enrich the optimistic order with the FULL
      // item snapshot (name, price, extras, line_total) from the
      // cached menu catalog. Previously we stored only `menu_item_id`
      // + quantity and waited for the cloud ACK to fill in the rest —
      // which meant on a slow Cloudflare edge the dashboard showed
      // rows like "1× (no name) — R 0.00" for 5-15 s per new order.
      // Waiters reported orders "with no price" (see screenshot). We
      // fix this by looking up every menu_item_id against the cached
      // `catalogCategories` and stamping name/price/extras onto the
      // optimistic line. The server-side reducer still recomputes
      // authoritative totals on ACK, which just re-hydrates the same
      // shape — no visual flicker.
      const menuLookup = new Map();
      for (const category of (catalogCategories || [])) {
        for (const it of (category.items || [])) {
          if (it && it.id) menuLookup.set(it.id, it);
        }
      }
      const enrichItem = (rawItem) => {
        const menuItem = menuLookup.get(rawItem.menu_item_id);
        const qty = Number(rawItem.quantity) || 0;
        const price = Number(menuItem?.price) || 0;
        // Session 73 — same rule as the server: first `free_count` sides
        // are included, the rest charged (was: every side charged).
        const sidesPricing = computeSidesPricingForLine(menuItem, rawItem.selected_side_ids || [], menuCatalog?.sides_library || (Array.isArray(menuItem?.sides) ? menuItem.sides : []));
        const selectedSides = sidesPricing.snapshots.map((s) => ({ id: s.id, name: s.name || "", price: Number(s.price) || 0, charged: !!s.charged }));
        const extrasPerUnit = sidesPricing.extras_per_unit;
        const lineSubtotal = price * qty;
        const lineExtras = extrasPerUnit * qty;
        const lineTotal = lineSubtotal + lineExtras;
        return {
          menu_item_id: rawItem.menu_item_id,
          name: menuItem?.name || "",
          price,
          quantity: qty,
          selected_side_ids: rawItem.selected_side_ids || [],
          selected_sides: selectedSides,
          prep_selections: rawItem.prep_selections || [],
          note: rawItem.note || "",
          image_url: menuItem?.image_url || null,
          line_subtotal: Math.round(lineSubtotal * 100) / 100,
          extras_total: Math.round(lineExtras * 100) / 100,
          line_total: Math.round(lineTotal * 100) / 100,
          _pending: true,
        };
      };
      const enrichedItems = items.map(enrichItem);
      // Order-level totals derived from the enriched line_totals so
      // the "R X.XX" chip on the table card and the receipt preview
      // both reflect what the customer actually owes — instantly.
      const orderInclusiveTotal = enrichedItems.reduce((s, it) => s + (Number(it.line_total) || 0), 0);
      const localOrder = {
        id: optimisticOrderId,
        table_session_id: selectedSession.id,
        table_number: selectedSession.table_number,
        source: "staff",
        status: "confirmed",
        created_at: nowIso,
        updated_at: nowIso,
        items: enrichedItems,
        // Under VAT-inclusive semantics (Session 27.11) the line_total
        // sums ARE the customer-visible total; VAT is extracted from
        // it. We only stamp `total` here so the dashboard has a value
        // to render — the authoritative subtotal / vat_total will be
        // recomputed by the server reducer on ACK.
        total: Math.round(orderInclusiveTotal * 100) / 100,
        subtotal: Math.round(orderInclusiveTotal * 100) / 100,
        vat_total: 0,
        total_items: enrichedItems.reduce((n, it) => n + (Number(it.quantity) || 0), 0),
        _pending_sync: true,
        _local_only: true,
      };
      const writeResult = await localWrite({
        type: EventType.ORDER_CREATED,
        session,
        payload: {
          order: {
            ...localOrder,
            // Request-shape items so the cloud reducer sees exactly what
            // the legacy endpoint did. Session 30.26 — plus the display
            // fields (name / department / price) resolved from THIS
            // device's catalogue, so a kitchen or bar tablet receiving
            // the event over the LAN mesh (internet down) can render the
            // ticket without needing its own menu snapshot. The backend
            // ignores the extra keys (pydantic default).
            items: items.map((raw) => {
              const m = menuLookup.get(raw.menu_item_id);
              return {
                ...raw,
                name: m?.name || raw.name || "",
                department: m?.department || raw.department || "",
                price: Number(m?.price) || 0,
              };
            }),
          },
          table_session: {
            id: selectedSession.id,
            table_number: selectedSession.table_number,
            status: "open",
            updated_at: nowIso,
          },
        },
      });

      // Session 31.18 — carry the envelope's change_id on the placeholder
      // so the cloud copy (which is stamped with the same `_change_id`
      // and our `local-…` id as `_local_id`) can replace it 1:1.
      if (writeResult && writeResult.change_id) localOrder._change_id = writeResult.change_id;

      setMenuItemQuantities({});
      setStagedConfiguredLines([]);
      setAddItemsOpen(false);
      // Session 27.39 — Optimistic React state update. Attach the new
      // local order to `selectedSession.orders` immediately so the
      // "Pending" chip + item count refresh on the next render tick.
      // Previously the dashboard waited on `fetchDashboard()` HTTP,
      // which could take 5-10 s on a slow edge and made it look like
      // the order hadn't been placed at all.
      setTableSessions((prev) => (prev || []).map((s) => {
        if (!s || s.id !== selectedSession.id) return s;
        const orders = Array.isArray(s.orders) ? s.orders.slice() : [];
        orders.push(localOrder);
        const activeOrders = orders.filter((o) => o && o.status !== "voided");
        const totalItems = activeOrders.reduce(
          (n, o) => n + (o.items || []).reduce((k, it) => k + (Number(it.quantity) || 0), 0),
          0,
        );
        // Session 59 — a signed-in waiter serving a table nobody owns yet
        // (guest QR order) becomes its waiter; mirror the server-side
        // adoption instantly so the card never flashes "Unassigned".
        const actorNow = session?.user || null;
        const adopt = actorNow?.id && !s.assigned_user_id
          ? { assigned_user_id: actorNow.id, assigned_user_name: actorNow.full_name || actorNow.name || null, assigned_user_role: actorNow.role || null, assigned_at: nowIso }
          : {};
        return {
          ...s,
          ...adopt,
          orders,
          order_count: activeOrders.length,
          total_items: totalItems,
        };
      }));
      // Session 27.40 — Register order-added mutation so the 4s poll
      // doesn't briefly drop the pending order while the server is
      // still catching up on the sync event.
      try {
        markLocalOp(selectedSession.id, { patch: { orders: [localOrder] } });
      } catch (_) { /* noop */ }
      // Session 28b — Removed fetchDashboard() call. Adding items produces
      // an order_created WS event that the WS handler applies directly.
      // The merge-based reconciliation in the safety-net poll handles any
      // edge cases without risking state overwrites.
      // Session 27.29 — Confirm to the user that the order is safe.
      // The local event engine has already committed the order to
      // IndexedDB and the eventSync loop will push it to the cloud
      // as soon as the network recovers — even if we're currently
      // showing "Offline" the order will NOT be lost. Telling them
      // this explicitly builds trust that they can keep placing
      // orders during a network dip instead of running slips by
      // hand to the kitchen.
      const isOffline =
        (typeof navigator !== "undefined" && navigator.onLine === false);
      if (isOffline) {
        toast.success("Items added — will sync to kitchen when the connection returns.", {
          description: "Your order is saved locally. It will appear in the kitchen the moment we're back online.",
        });
      } else {
        toast.success("Items added to the table.");
      }
      return true;
    } catch (error) {
      toast.error(error.response?.data?.detail || error?.message || "Unable to add items.");
      return false;
    } finally {
      setBusyAction("");
      // Release the double-submit lock. Do it in `finally` so a
      // network-error retry from the operator works normally.
      submitAddItemsLockRef.current = false;
    }
  };

  const submitMoveTable = async () => {
    if (!selectedSession || !moveTableNumber) return;
    setBusyAction("move-table");
    try {
      await http.patch(`/staff/table-sessions/${selectedSession.id}/move`, { table_number: moveTableNumber }, { headers: authHeaders });
      setMoveTableOpen(false);
      setMoveTableNumber("");
      await fetchDashboard(false);
      toast.success("Table updated.");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Unable to move table.");
    } finally {
      setBusyAction("");
    }
  };

  const submitMoveItems = async () => {
    if (!selectedSession || !moveItemDialog.destTable) return;
    if (!moveItemDialog.managerPin || moveItemDialog.managerPin.length < 4) {
      toast.error("Enter the manager PIN to move items.");
      return;
    }
    const selected = moveItemDialog.selectedItems || {};
    const entries = Object.entries(selected).filter(([, v]) => v);
    if (entries.length === 0) { toast.error("Select at least one item to move."); return; }
    const items = entries.map(([key]) => {
      const [orderId, idxStr] = key.split("#");
      return { order_id: orderId, item_index: parseInt(idxStr, 10) };
    });
    setMoveItemDialog((prev) => ({ ...prev, busy: true }));
    try {
      const res = await http.post(
        `/staff/table-sessions/${selectedSession.id}/move-item`,
        { to_table_number: moveItemDialog.destTable, items, manager_pin: moveItemDialog.managerPin },
        { headers: authHeaders },
      );
      toast.success(`${res.data?.moved_count || entries.length} item(s) moved to Table ${moveItemDialog.destTable}`);
      setMoveItemDialog({ open: false, selectedItems: {}, destTable: "", managerPin: "", busy: false });
      await fetchDashboard(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Unable to move items.");
      setMoveItemDialog((prev) => ({ ...prev, busy: false }));
    }
  };

  // Merge: combine another active session INTO the current one. Source
  // closes; target keeps all orders + tips + payments.
  const submitMergeTables = async () => {
    if (!selectedSession || !mergeTargetId) return;
    setBusyAction("merge-tables");
    try {
      const { data } = await http.post(
        `/staff/table-sessions/${selectedSession.id}/merge`,
        { target_session_id: mergeTargetId },
        { headers: authHeaders },
      );
      const merged = data?.table_session;
      const sourceLabel = selectedSession.table_number;
      const targetLabel = merged?.table_number || "the other table";
      setMergeTableOpen(false);
      setMergeTargetId("");
      // Switch focus to the target so staff can keep working with the combined bill.
      if (merged?.id) setSelectedSessionId(merged.id);
      await fetchDashboard(false);
      toast.success(`Table ${sourceLabel} merged into ${targetLabel}.`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Unable to merge tables.");
    } finally {
      setBusyAction("");
    }
  };

  // Move to Account: close the table but park the outstanding balance on a
  // settle-later customer account managed from the Owner dashboard.
  const submitMoveToAccount = async () => {
    if (!selectedSession) return;
    const existingId = accountDraft.account_id || "";
    const name = existingId ? (staffAccounts.accounts.find((a) => a.id === existingId)?.customer_name || "tab") : (accountDraft.customer_name || "").trim();
    if (!name) {
      toast.error("Customer name is required.");
      return;
    }
    setBusyAction("move-to-account");
    try {
      const { data } = await http.post(
        `/staff/table-sessions/${selectedSession.id}/move-to-account`,
        {
          customer_name: name,
          account_id: existingId || undefined,
          phone: (accountDraft.phone || "").trim() || undefined,
          notes: (accountDraft.notes || "").trim() || undefined,
        },
        { headers: authHeaders },
      );
      const acc = data?.account;
      setMoveToAccountOpen(false);
      setAccountDraft({ customer_name: "", phone: "", notes: "", account_id: "" });
      await fetchDashboard(false);
      loadStaffAccounts();
      toast.success(`${existingId ? "Added to" : "Tab opened for"} ${acc?.customer_name} — R ${(acc?.remaining_balance ?? 0).toFixed(2)} outstanding.`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Unable to move balance to a tab.");
    } finally {
      setBusyAction("");
    }
  };

  // ── Voucher application on receipt ─────────────────────────────────
  const submitApplyVoucher = async () => {
    if (!selectedSession) return;
    const code = (voucherCodeInput || "").trim().toUpperCase();
    if (!code) { toast.error("Enter a voucher code."); return; }
    setVoucherBusy(true);
    try {
      await http.post("/staff/vouchers/apply", { code, session_id: selectedSession.id }, { headers: authHeaders });
      toast.success(`Voucher ${code} applied`);
      setVoucherCodeInput("");
      setVoucherDialogOpen(false);
      await fetchDashboard(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not apply voucher.");
    } finally {
      setVoucherBusy(false);
    }
  };

  const submitRemoveVoucher = async () => {
    if (!selectedSession) return;
    setVoucherBusy(true);
    try {
      await http.post("/staff/vouchers/remove", { session_id: selectedSession.id }, { headers: authHeaders });
      toast.success("Voucher removed");
      await fetchDashboard(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not remove voucher.");
    } finally {
      setVoucherBusy(false);
    }
  };

  // ── Split-bill helpers ────────────────────────────────────────────
  const openSplitBillDialog = () => {
    if (!selectedSession) return;
    const existing = selectedSession.split_receipts || [];
    if (existing.length > 0) {
      // Resume editing existing split.
      setSplitGroups(existing.map((g) => ({ id: g.id, name: g.name })));
      const map = {};
      existing.forEach((g) => {
        (g.items || []).forEach((it) => { map[`${it.order_id}#${it.line_index}`] = g.id; });
      });
      setSplitAssignments(map);
    } else {
      const g1 = `g_${Date.now()}_1`;
      const g2 = `g_${Date.now()}_2`;
      setSplitGroups([
        { id: g1, name: "Guest 1" },
        { id: g2, name: "Guest 2" },
      ]);
      setSplitAssignments({});
    }
    setSplitShowMissing(false);
    setSplitBillOpen(true);
  };

  // Session 70 — large functions: up to SPLIT_BILL_MAX_GUESTS guests on ONE
  // table (was 8 in the UI / 12 on the server).
  const SPLIT_BILL_MAX_GUESTS = 100;
  const addSplitGroup = (count = 1) => {
    const room = SPLIT_BILL_MAX_GUESTS - splitGroups.length;
    if (room <= 0) { toast.error(`Maximum of ${SPLIT_BILL_MAX_GUESTS} guests on one table.`); return; }
    const n = Math.max(1, Math.min(Number(count) || 1, room));
    const stamp = Date.now();
    setSplitGroups((prev) => {
      const used = new Set(prev.map((g) => g.name));
      const out = [...prev];
      let idx = prev.length + 1;
      for (let i = 0; i < n; i += 1) {
        while (used.has(`Guest ${idx}`)) idx += 1;
        used.add(`Guest ${idx}`);
        out.push({ id: `g_${stamp}_${idx}_${i}`, name: `Guest ${idx}` });
        idx += 1;
      }
      return out;
    });
  };
  // Set the guest count directly ("40 guests") — adds or trims empty guests.
  const setSplitGuestCount = (target) => {
    const n = Math.max(2, Math.min(Number(target) || 2, SPLIT_BILL_MAX_GUESTS));
    if (n > splitGroups.length) { addSplitGroup(n - splitGroups.length); return; }
    if (n < splitGroups.length) {
      const assignedIds = new Set(Object.values(splitAssignments));
      // remove from the end, skipping guests that already have items
      const removable = [...splitGroups].reverse().filter((g) => !assignedIds.has(g.id)).slice(0, splitGroups.length - n).map((g) => g.id);
      if (removable.length < splitGroups.length - n) toast.message("Guests with items assigned were kept.");
      setSplitGroups((prev) => prev.filter((g) => !removable.includes(g.id)));
    }
  };
  // Spread every still-unassigned item over the guests round-robin.
  const autoAssignRemainingSplit = () => {
    if (!splitGroups.length) return;
    const missing = getUnassignedSplitKeys();
    if (!missing.length) { toast.message("Every item already has a guest."); return; }
    const counts = {};
    splitGroups.forEach((g) => { counts[g.id] = 0; });
    Object.values(splitAssignments).forEach((gid) => { if (gid in counts) counts[gid] += 1; });
    setSplitAssignments((prev) => {
      const out = { ...prev };
      missing.forEach((key) => {
        const target = splitGroups.reduce((best, g) => (counts[g.id] < counts[best.id] ? g : best), splitGroups[0]);
        out[key] = target.id;
        counts[target.id] += 1;
      });
      return out;
    });
  };

  const removeSplitGroup = (groupId) => {
    if (splitGroups.length <= 2) { toast.error("Need at least two splits."); return; }
    setSplitGroups((prev) => prev.filter((g) => g.id !== groupId));
    setSplitAssignments((prev) => {
      const out = { ...prev };
      Object.keys(out).forEach((k) => { if (out[k] === groupId) delete out[k]; });
      return out;
    });
  };

  const renameSplitGroup = (groupId, name) => {
    setSplitGroups((prev) => prev.map((g) => g.id === groupId ? { ...g, name } : g));
  };

  const assignItemToGroup = (orderId, lineIndex, groupId) => {
    setSplitAssignments((prev) => ({ ...prev, [`${orderId}#${lineIndex}`]: groupId }));
  };

  // Session 30 — Every billable item must be assigned to a guest before
  // the split can be saved. Returns the list of unassigned item keys
  // (`orderId#lineIndex`) so the dialog can highlight exactly which
  // rows still need a guest.
  const getUnassignedSplitKeys = () => {
    const keys = [];
    (selectedSession?.orders || [])
      .filter((o) => o.status !== "voided")
      .forEach((o) => {
        (o.items || []).forEach((it, i) => {
          if (it.voided) return;
          const key = `${o.id}#${i}`;
          if (!splitAssignments[key]) keys.push(key);
        });
      });
    return keys;
  };
  const unassignedSplitKeys = splitBillOpen ? getUnassignedSplitKeys() : [];

  const submitSplitBill = async () => {
    if (!selectedSession) return;
    // Session 30 — HARD requirement: no unassigned items. Previously
    // unassigned items silently fell into an implicit "Unassigned" tab,
    // which caused real-world confusion at payment time. Now the save
    // is blocked and the missing rows are highlighted.
    const missing = getUnassignedSplitKeys();
    if (missing.length > 0) {
      setSplitShowMissing(true);
      toast.error(
        `${missing.length} item${missing.length === 1 ? "" : "s"} not assigned to a guest yet — assign every item before saving.`
      );
      return;
    }
    setSplitShowMissing(false);
    // Build the payload from the current assignment map.
    const groupsPayload = splitGroups.map((g) => ({
      id: g.id,
      name: (g.name || "").trim() || "Guest",
      items: Object.entries(splitAssignments)
        .filter(([, gid]) => gid === g.id)
        .map(([key]) => {
          const [order_id, line_index] = key.split("#");
          return { order_id, line_index: Number(line_index) };
        }),
    }));
    if (!groupsPayload.some((g) => g.items.length > 0)) {
      toast.error("Assign at least one item to a guest.");
      return;
    }
    setSplitBusy(true);
    try {
      await http.post(`/staff/table-sessions/${selectedSession.id}/split-bill`, { groups: groupsPayload }, { headers: authHeaders });
      toast.success("Bill split saved");
      setSplitBillOpen(false);
      await fetchDashboard(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not save bill split.");
    } finally {
      setSplitBusy(false);
    }
  };

  // Session 70.G — in-app confirm (native window.confirm() kicks Chrome
  // out of fullscreen and looks foreign on tablets).
  const [staffConfirm, setStaffConfirm] = useState(null); // { title, description, confirmLabel, onConfirm }
  const clearSplitBill = () => {
    if (!selectedSession) return;
    setStaffConfirm({
      key: "clear-split",
      title: "Clear the bill split?",
      description: "The table goes back to a single receipt. Nothing already paid is affected.",
      confirmLabel: "Clear split",
      onConfirm: () => clearSplitBillConfirmed(),
    });
  };
  const clearSplitBillConfirmed = async () => {
    setStaffConfirm(null);
    if (!selectedSession) return;
    setSplitBusy(true);
    try {
      await http.post(`/staff/table-sessions/${selectedSession.id}/split-bill/clear`, {}, { headers: authHeaders });
      toast.success("Bill split cleared");
      setActiveSplitTab("all");
      await fetchDashboard(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not clear split.");
    } finally {
      setSplitBusy(false);
    }
  };

  // Helpers: read the payment entry for a given split id (or null).
  const getSplitPayment = (splitId) => {
    if (!selectedSession) return null;
    return (selectedSession.split_payments || []).find((p) => p.split_id === splitId) || null;
  };

  // Session 31.26 — a guest may settle with SEVERAL tenders (two cards,
  // cash + EFT…). The dialog is a small ledger builder: add Cash / Card /
  // EFT rows (each with its own tip) until the guest's total is covered,
  // then record them all in one call. Editing re-seeds the rows from the
  // tenders saved last time.
  const openPaySplitDialog = (splitId, splitName, targetTotal) => {
    const existing = getSplitPayment(splitId);
    let seeded = [];
    if (existing) {
      if (Array.isArray(existing.tenders) && existing.tenders.length) {
        seeded = existing.tenders.map((t) => ({ method: t.method, amount: Number(t.amount) || 0, tip: Number(t.tip) || 0, label: t.label || null }));
      } else {
        if (Number(existing.cash_paid) > 0) seeded.push({ method: "cash", amount: Number(existing.cash_paid), tip: Number(existing.card_paid) > 0 ? 0 : (Number(existing.tip_amount) || 0), label: null });
        if (Number(existing.card_paid) > 0) seeded.push({ method: "card", amount: Number(existing.card_paid), tip: Number(existing.tip_amount) || 0, label: null });
        if (Number(existing.eft_paid) > 0) seeded.push({ method: "eft", amount: Number(existing.eft_paid), tip: 0, label: null });
      }
    }
    setSplitPayDialog({
      open: true,
      split_id: splitId,
      split_name: splitName,
      target_total: Number(targetTotal) || 0,
      step: "ledger",
      chosenMethod: "",
      amountStr: "",
      tipStr: "",
      tenders: seeded,
      is_edit: Boolean(existing),
      busy: false,
    });
  };

  /** Numbers for the tender currently being typed in the split-pay dialog. */
  const splitTenderDraft = (dlg) => {
    const r2 = (n) => Math.round((Number(n) || 0) * 100) / 100;
    const target = Math.max(0, Number(dlg.target_total) || 0);
    const paidSoFar = r2((dlg.tenders || []).reduce((sum, t) => sum + (Number(t.amount) || 0), 0));
    const due = Math.max(0, r2(target - paidSoFar));
    const typed = Math.max(0, parseFloat(dlg.amountStr) || 0);
    const tipTyped = Math.max(0, parseFloat(dlg.tipStr) || 0);
    if (dlg.chosenMethod === "cash") {
      const amount = Math.min(typed, due);
      const over = Math.max(0, typed - amount);
      const tip = Math.min(tipTyped, over);
      return { method: "cash", due, received: typed, amount: r2(amount), tip: r2(tip), change: r2(Math.max(0, over - tip)), remaining: r2(Math.max(0, due - amount)) };
    }
    // Session 31.27 — card/EFT: no separate tip field; anything charged
    // over what the guest owes is automatically the tip.
    const amount = Math.min(typed, due);
    return { method: dlg.chosenMethod, due, received: typed, amount: r2(amount), tip: r2(Math.max(0, typed - amount)), change: 0, remaining: r2(Math.max(0, due - amount)) };
  };

  const addSplitTender = () => {
    const d = splitTenderDraft(splitPayDialog);
    if (!d || (d.amount <= 0 && d.tip <= 0)) { toast.error("Enter an amount first."); return; }
    setSplitPayDialog((prev) => ({
      ...prev,
      tenders: [...(prev.tenders || []), { _k: `t${Date.now()}${Math.random().toString(36).slice(2, 7)}`, method: d.method, amount: d.amount, tip: d.tip, label: null, change: d.change }],
      step: "ledger",
      chosenMethod: "",
      amountStr: "",
      tipStr: "",
    }));
  };

  const submitPaySplit = async () => {
    if (!selectedSession || !splitPayDialog.split_id) return;
    const r2 = (n) => Math.round((Number(n) || 0) * 100) / 100;
    const tenders = (splitPayDialog.tenders || []).filter((t) => (Number(t.amount) || 0) > 0 || (Number(t.tip) || 0) > 0);
    if (!tenders.length) { toast.error("Add at least one payment."); return; }
    const paid = r2(tenders.reduce((sum, t) => sum + (Number(t.amount) || 0), 0));
    const target = r2(splitPayDialog.target_total);
    if (paid + 0.005 < target) { toast.error(`${formatPrice(target - paid)} still owed for ${splitPayDialog.split_name}.`); return; }
    const tip = r2(tenders.reduce((sum, t) => sum + (Number(t.tip) || 0), 0));
    setSplitPayDialog((prev) => ({ ...prev, busy: true }));
    try {
      const res = await http.post(
        `/staff/table-sessions/${selectedSession.id}/split-bill/pay`,
        {
          split_id: splitPayDialog.split_id,
          payments: tenders.map((t) => ({ method: t.method, amount: r2(t.amount), tip: r2(t.tip), label: t.label || undefined })),
        },
        { headers: authHeaders },
      );
      toast.success(tip > 0 ? `${splitPayDialog.split_name} paid · Tip ${formatPrice(tip)}` : `${splitPayDialog.split_name} paid`);
      setSplitPayDialog((prev) => ({ ...prev, open: false, busy: false }));
      await fetchDashboard(false);
      if (res.data?.all_splits_paid) {
        toast.success("All guests have paid — table settled");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not record split payment.");
      setSplitPayDialog((prev) => ({ ...prev, busy: false }));
    }
  };

  /**
   * Session 30.9 — Payment-aware close. Paid tables close directly; unpaid
   * tables go through the Manager PIN prompt first (`requestCloseTable`).
   * `managerPin` is forwarded in the event payload so the server-side
   * reducer can re-verify it (offline devices verify locally first).
   */
  const requestCloseTable = () => {
    if (!selectedSession) return;
    if (isSessionPaid(selectedSession)) {
      setStaffConfirm({
        key: "close-table",
        title: `Close Table ${selectedSession.table_number}?`,
        description: "Open tickets will be completed and the table will close.",
        confirmLabel: "Close table",
        onConfirm: () => { setStaffConfirm(null); closeTable(); },
      });
      return;
    }
    setClosePinPrompt(true);
  };

  const closeTable = async (managerPin = null) => {
    if (!selectedSession) return;
    // Session 27.39 — Do NOT flip `busyAction`. Under the local-first
    // contract this action is instant from the operator's viewpoint.
    // A "busy" flag would just show a phantom spinner while the local
    // write completes in <50 ms.
    try {
      // Session 27.31 — Route close-table through the Local Event
      // Engine so waiters can close a table WHILE OFFLINE without
      // losing service. The event is committed to IDB instantly (UI
      // updates immediately), then the eventSync loop pushes it to
      // /api/events/sync in the background when the network returns.
      // The server-side `_apply_table_closed_event` reducer applies
      // the same state change to Mongo, dedupes on change_id, and
      // marks any still-confirmed orders on the session as completed
      // — matching the legacy PATCH endpoint behaviour.
      await localWrite({
        type: EventType.TABLE_CLOSED,
        payload: {
          table_session_id: selectedSession.id,
          table_session: { id: selectedSession.id, table_number: selectedSession.table_number },
          ts: new Date().toISOString(),
          ...(managerPin ? { manager_pin: managerPin } : {}),
        },
        session,
      });
      // Session 27.39 — Optimistic React state update. Previously the
      // dashboard waited on `fetchDashboard()` to reflect the close, so
      // on a slow network the table appeared to "stick" for 5-15 s
      // even though IDB was already correct. Patch the state
      // immediately so the row disappears on the very next render.
      const closedId = selectedSession.id;
      // Session 27.40 — Register the close mutation FIRST so the 4s
      // poll (which may fire during our fire-and-forget dashboard
      // refresh) doesn't reinsert the row before the sync loop has
      // pushed the close to the cloud.
      markLocalOp(closedId, { closed: true });
      setTableSessions((prev) => (prev || []).filter((s) => s && s.id !== closedId));
      // Session 27.99 — Also purge from the preservation ref so the
      // next poll's merge doesn't resurrect the just-closed table.
      try { recentlySeenSessionsRef.current.delete(closedId); } catch (_) { /* noop */ }
      setSelectedSessionId((cur) => (cur === closedId ? "" : cur));
      // Session 28b — Removed fetchDashboard() call. Table close produces
      // a table_session_closed WS event applied by the WS handler.
      const isOffline = (typeof navigator !== "undefined" && navigator.onLine === false);
      if (isOffline) {
        toast.success("Table closed — will sync when the connection returns.", {
          description: "Saved locally. Reopens will be blocked; receipts remain accessible.",
        });
      } else {
        toast.success("Table closed.");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || error?.message || "Unable to close table.");
    }
  };

  // Persist the cash + card amounts entered on the View Receipt dialog. The
  // backend stamps a `payment_method` (cash | card | split | unpaid) on every
  // order so the owner dashboard can filter without recomputing.
  //
  // Session 27.37 — LOCAL-FIRST via the Local Event Engine.
  // ─────────────────────────────────────────────────────────────
  // The previous implementation did `await http.patch(...)` on the
  // critical UI path. On a bad Cloudflare edge (5-10 s latency) waiters
  // watched a "Save Payment" spinner spin for the whole request, and
  // if it timed out the payment appeared to fail — even though the
  // customer had already paid at the till. This blocked closing the
  // table too, since the payment had to save first.
  //
  // Now:
  //   1. Compute the payment locally.
  //   2. `localWrite({ type: PAYMENT_RECORDED, ... })` — reducer stamps
  //      the local table_session + orders in IDB immediately, dispatches
  //      the LOCAL_EVENT_APPLIED DOM event, and returns. The UI reflects
  //      the payment on the *next* render tick — no network wait.
  //   3. The eventSync loop POSTs the event to /api/events/sync in the
  //      background where `_apply_payment_recorded_event` performs the
  //      real Mongo write + WebSocket fan-out.
  //   4. As an extra safety net we ALSO kick off the legacy PATCH in the
  //      background (fire-and-forget) so devices on the same LAN — which
  //      may not have picked up the LAN mesh broadcast yet — get a
  //      cloud-echoed refresh faster than the ~3 s sync loop tick. If it
  //      fails silently the sync loop still guarantees delivery.
  //
  // When called from the new stepped payment workflow, callers pass explicit
  // `override` values so we don't have to wait on a state-set + React batch.
  const savePayment = async (override = null) => {
    if (!selectedSession) return;
    const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;
    // We deliberately do NOT flip `busyAction` here — the whole point of
    // the local-first refactor is that this function returns synchronously
    // from the user's perspective. Any spinner UI would just be visual
    // noise.
    try {
      const cashN = override
        ? round2(override.cash || 0)
        : parseFloat(cashPaid) || 0;
      const cardN = override
        ? round2(override.card || 0)
        : parseFloat(cardPaid) || 0;
      // Tip: explicit when the override carries it (new flow). Otherwise
      // fall back to the legacy "over-payment becomes tip" derivation so
      // any old code paths keep working.
      // Session total for tip auto-derivation. Uses backend-computed
      // order totals which already exclude voided orders AND voided
      // lines. Recomputing from raw items[] would count voided lines
      // and inflate the "correct" amount, causing legit payments to be
      // reported as overpayments (fake tips) and voided items to be
      // double-charged.
      const activeOrdersForTip = (selectedSession?.orders || []).filter((o) => o.status !== "voided");
      const grandTotal = activeOrdersForTip.reduce((s, o) => s + (Number(o.total) || 0), 0);
      const tipFromOverride = override && Object.prototype.hasOwnProperty.call(override, "tip")
        ? round2(override.tip || 0)
        : null;
      const autoTip = tipFromOverride != null
        ? tipFromOverride
        : Math.max(0, cashN + cardN - grandTotal);
      // Session 30.36 — The tip belongs to the waiter who OPENED the table
      // (`assigned_user_*` on the session — only changed by Move Table),
      // NOT whoever is signed in when the bill is settled (e.g. the owner
      // or a manager closing from their dashboard). Fall back to the
      // signed-in user only when the table has no assigned waiter. The
      // backend applies the same rule, so local + cloud stay in sync.
      const assignedName = String(selectedSession?.assigned_user_name || "").trim();
      const assignedId = selectedSession?.assigned_user_id || null;
      const recipientOverride = assignedName || (session?.user?.full_name || "").trim();
      const recipientUserId = assignedName ? assignedId : (session?.user?.id || null);
      const method = (cashN > 0 && cardN > 0) ? "split"
        : (cardN > 0) ? "card"
        : (cashN > 0) ? "cash"
        : "unpaid";
      const nowIso = new Date().toISOString();

      // 1) Local-first write. Returns as soon as IDB is updated.
      await localWrite({
        type: EventType.PAYMENT_RECORDED,
        session,
        payload: {
          table_session_id: selectedSession.id,
          cash_paid: cashN,
          card_paid: cardN,
          tip_amount: autoTip,
          tip_recipient: recipientOverride || null,
          tip_recipient_user_id: recipientOverride ? recipientUserId : null,
          payment_method: method,
          payment_recorded_at: nowIso,
        },
      });

      // Session 27.39 — Optimistic React state update. Without this,
      // the dashboard waited on the next `fetchDashboard()` before the
      // "already paid" banner and closed-out totals showed up, which
      // felt like the app hadn't saved anything at all. Now we patch
      // the session (and its orders) in-place immediately.
      const targetId = selectedSession.id;
      const paymentPatch = {
        cash_paid: cashN,
        card_paid: cardN,
        tip_amount: autoTip,
        tip_recipient: recipientOverride || null,
        payment_method: method,
        payment_recorded_at: nowIso,
      };
      setTableSessions((prev) => (prev || []).map((s) => {
        if (!s || s.id !== targetId) return s;
        const orders = (s.orders || []).map((o) => ({
          ...o,
          cash_paid: cashN,
          card_paid: cardN,
          payment_method: method,
        }));
        return { ...s, orders, ...paymentPatch };
      }));
      // Session 27.40 — Register the payment mutation so a background
      // poll landing before the sync loop finishes doesn't flash the
      // "already paid" indicator off the row.
      markLocalOp(targetId, { patch: paymentPatch });

      // 2) UI feedback — instant, since IDB has already been updated
      //    and the state above has been patched in-place.
      setCashPaid(cashN > 0 ? String(cashN) : "");
      setCardPaid(cardN > 0 ? String(cardN) : "");
      setTipAmount(autoTip > 0 ? String(autoTip) : "");
      setPaymentStep("idle");

      const isOffline =
        (typeof navigator !== "undefined" && navigator.onLine === false);
      if (isOffline) {
        toast.success("Payment saved — will sync when the connection returns.", {
          description: "Stored locally on this device. The table can be closed right away.",
        });
      } else {
        toast.success("Payment saved.");
      }

      // 3) Fire-and-forget cloud fast-path so the WS fan-out to
      //    kitchen/bar/owner happens sooner than the ~3s sync tick.
      //    Never await — a slow response must not block the UI.
      (async () => {
        // Session 41.4 — same ordering guard as addTenderPayment: the queued
        // payment_recorded event must not be overtaken by this fast path.
        if (await hasUnsyncedEventsForSession(selectedSession.id)) return;
        try {
          await http.patch(
            `/staff/table-sessions/${selectedSession.id}/payment`,
            {
              cash_paid: cashN,
              card_paid: cardN,
              tip_amount: autoTip,
              tip_recipient: recipientOverride,
            },
            { headers: authHeaders, timeout: 6000, bestEffort: true },
          );
        } catch (_) {
          // Silent — the events sync loop will retry via /api/events/sync
          // using the same change_id, so the payment is NEVER lost. On a
          // slow edge we just skip the extra hop.
        }
        try { fetchDashboard(false); } catch (_) { /* noop */ }
      })();
    } catch (error) {
      // The only way to reach here is a local IDB failure (extremely
      // rare — usually private-browsing quota errors). Surface it so
      // the operator knows the payment wasn't saved.
      toast.error(error?.message || "Unable to save payment locally.");
    }
  };

  // ─────────────────────────────────────────────────────────────
  // Session 31.26 — MULTI-TENDER: append ONE tender to the ledger.
  // A bill can be settled with any mix of Cash, several Cards and EFT,
  // each with its own tip. Every entry is written local-first
  // (PAYMENT_ADDED → IDB reducer → optimistic React patch) and then
  // fast-pathed to POST /staff/table-sessions/{id}/payments with the
  // SAME client id so the queued event and the fast path can never
  // double-record the tender. The table stays open until the ledger
  // covers the amount owed.
  // ─────────────────────────────────────────────────────────────
  const computeTenderDraft = (due) => {
    const r2 = (n) => Math.round((Number(n) || 0) * 100) / 100;
    const d = Math.max(0, Number(due) || 0);
    const tipTyped = Math.max(0, parseFloat(tipDraft) || 0);
    if (chosenMethod === "cash") {
      const received = Math.max(0, parseFloat(cashReceived) || 0);
      const amount = Math.min(received, d);
      const over = Math.max(0, received - amount);
      const tip = Math.min(tipTyped, over);
      return {
        method: "cash", label: "Cash", received, amount: r2(amount), tip: r2(tip),
        change: r2(Math.max(0, over - tip)), charged: r2(amount + tip), remaining: r2(Math.max(0, d - amount)),
      };
    }
    if (chosenMethod === "card" || chosenMethod === "eft") {
      // Session 31.27 — card/EFT have NO separate tip field: whatever is
      // charged beyond what is owed IS the tip (card/EFT never give change).
      const charged = Math.max(0, parseFloat(chosenMethod === "card" ? cardAmount : eftAmount) || 0);
      const amount = Math.min(charged, d);
      const tip = r2(Math.max(0, charged - amount));
      return {
        method: chosenMethod, label: chosenMethod === "eft" ? "EFT" : null, received: charged, amount: r2(amount), tip,
        change: 0, charged: r2(amount + tip), remaining: r2(Math.max(0, d - amount)),
      };
    }
    if (chosenMethod === "split") {
      // Session 35 — Split bill (Cash + Card) in ONE step. Cash is applied
      // to the bill first (any excess is change, or a tip if the cashier
      // types one); the card covers what is left and — as always — card
      // never gives change, so anything charged beyond the remainder is
      // recorded as a tip. Both portions are written to the ledger as two
      // separate tenders so reports / receipts stay truthful.
      const cashRecv = Math.max(0, parseFloat(splitCash) || 0);
      const cardCharged = Math.max(0, parseFloat(splitCard) || 0);
      const cashAmount = Math.min(cashRecv, d);
      const afterCash = Math.max(0, d - cashAmount);
      const cardAmount = Math.min(cardCharged, afterCash);
      const cardTip = r2(Math.max(0, cardCharged - cardAmount));
      const cashOver = Math.max(0, cashRecv - cashAmount);
      const cashTip = r2(Math.min(tipTyped, cashOver));
      const change = r2(Math.max(0, cashOver - cashTip));
      const amount = r2(cashAmount + cardAmount);
      const tip = r2(cashTip + cardTip);
      const tenders = [];
      if (cashAmount > 0.004 || cashTip > 0.004) tenders.push({ method: "cash", label: "Cash", amount: r2(cashAmount), tip: cashTip });
      if (cardAmount > 0.004 || cardTip > 0.004) tenders.push({ method: "card", label: null, amount: r2(cardAmount), tip: cardTip });
      return {
        method: "split", label: "Cash + Card", received: r2(cashRecv + cardCharged), amount, tip,
        change, charged: r2(amount + tip), remaining: r2(Math.max(0, afterCash - cardAmount)),
        tenders, cashReceived: r2(cashRecv), cardCharged: r2(cardCharged),
        cashAmount: r2(cashAmount), cardAmount: r2(cardAmount), cashTip, cardTip,
      };
    }
    return null;
  };

  const addTenderPayment = async (draft) => {
    if (!selectedSession || !draft) return;
    const r2 = (n) => Math.round((Number(n) || 0) * 100) / 100;
    const amount = r2(draft.amount);
    const tip = r2(draft.tip);
    if (amount <= 0 && tip <= 0) { toast.error("Enter an amount first."); return; }
    const mkId = () => `t-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
    // Session 35 — a split (Cash + Card) draft carries two tenders; every
    // other method is a single entry. Same client ids flow through the
    // local event AND the fast path so nothing double-records.
    const entries = (Array.isArray(draft.tenders) && draft.tenders.length)
      ? draft.tenders.map((t) => ({ id: mkId(), method: t.method, amount: r2(t.amount), tip: r2(t.tip), label: t.label || null }))
      : [{ id: mkId(), method: draft.method, amount, tip, label: draft.label || null }];
    // Tip goes to the waiter who opened the table (same rule as savePayment).
    const assignedName = String(selectedSession?.assigned_user_name || "").trim();
    const assignedId = selectedSession?.assigned_user_id || null;
    const recipientOverride = assignedName || (session?.user?.full_name || "").trim();
    const recipientUserId = assignedName ? assignedId : (session?.user?.id || null);
    const nowIso = new Date().toISOString();
    const targetId = selectedSession.id;
    try {
      await localWrite({
        type: EventType.PAYMENT_ADDED,
        session,
        payload: {
          table_session_id: targetId,
          payments: entries,
          tip_recipient: recipientOverride || null,
          tip_recipient_user_id: recipientOverride ? recipientUserId : null,
          recorded_at: nowIso,
        },
      });
      const localEntries = entries.map((e) => ({ ...e, recorded_at: nowIso, by: session?.user?.full_name || null }));
      const optimistic = applyTendersToSession(selectedSession, localEntries, { recordedAt: nowIso });
      setTableSessions((prev) => (prev || []).map((s) => {
        if (!s || s.id !== targetId) return s;
        const next = applyTendersToSession(s, localEntries, { recordedAt: nowIso });
        const orders = (s.orders || []).map((o) => ({
          ...o, cash_paid: next.cash_paid, card_paid: next.card_paid, eft_paid: next.eft_paid, payment_method: next.payment_method,
        }));
        return { ...next, orders };
      }));
      // Poll guard — the `payments` patch is unioned by tender id (see
      // fetchDashboard) so another device's tender is never hidden.
      markLocalOp(targetId, { patch: { payments: optimistic.payments } });
      setCashReceived(""); setCardAmount(""); setEftAmount(""); setSplitCash(""); setSplitCard(""); setTipDraft(""); setChosenMethod("");
      setPaymentStep("idle");
      const stillOwed = optimistic.outstanding_amount;
      const isOffline = typeof navigator !== "undefined" && navigator.onLine === false;
      if (stillOwed > 0.009) {
        toast.warning(`Partial payment recorded · ${formatPrice(stillOwed)} STILL OUTSTANDING`, {
          description: isOffline ? "Stored on this device — syncs when the connection returns. The table is NOT settled." : "The bill is NOT settled — the table stays open until the balance is paid (or moved to a tab).",
          duration: 8000,
        });
      } else {
        toast.success("Paid in full — the table can be closed.", {
          description: isOffline ? "Stored on this device — syncs when the connection returns." : undefined,
        });
      }
      (async () => {
        // Session 41.4 — never let the tender overtake the order it pays for.
        // While this table still has unsynced events (its order / open) in
        // the local queue, skip the fast path: the queued payment_added
        // event follows the order in the same sync batch, in order.
        if (await hasUnsyncedEventsForSession(targetId)) return;
        try {
          await http.post(
            `/staff/table-sessions/${targetId}/payments`,
            { payments: entries, tip_recipient: recipientOverride || null },
            { headers: authHeaders, timeout: 6000, bestEffort: true },
          );
        } catch (err) {
          // Session 36 — a hard server REJECTION (not a network blip) means
          // the tender was refused by the financial-integrity guard; tell
          // the operator and re-sync so the optimistic ledger is corrected.
          const st = err?.response?.status;
          if (st === 400 || st === 403) {
            // Session 41.4 — but if the order is STILL on its way (queued), the
            // "overpayment" is just the race; keep the local ledger — the queued
            // event lands right after the order. Only a true refusal reverts.
            if (await hasUnsyncedEventsForSession(targetId)) return;
            const d = err.response?.data?.detail;
            toast.error(typeof d === "string" ? d : (d?.message || "Payment rejected by the server."), { duration: 9000 });
            try { fetchDashboard(false); } catch (_) { /* noop */ }
            return;
          }
          /* network error: the events sync loop retries with the same tender id */
        }
        try { fetchDashboard(false); } catch (_) { /* noop */ }
      })();
    } catch (error) {
      toast.error(error?.message || "Unable to save payment locally.");
    }
  };

  const removeTenderPayment = async () => {
    const p = removeTenderPrompt.payment;
    if (!selectedSession || !p || !p.id) return;
    const pin = String(removeTenderPrompt.pin || "").trim();
    if (!pin) { toast.error("Enter the manager PIN."); return; }
    setRemoveTenderPrompt((d) => ({ ...d, busy: true }));
    try {
      const res = await http.post(
        `/staff/table-sessions/${selectedSession.id}/payments/${p.id}/remove`,
        { void_password: pin },
        { headers: authHeaders, timeout: 10000 },
      );
      const fresh = res.data?.table_session;
      if (fresh && fresh.id) {
        setTableSessions((prev) => (prev || []).map((s) => (s && s.id === fresh.id ? { ...s, ...fresh, orders: s.orders || fresh.orders } : s)));
      }
      setRemoveTenderPrompt({ open: false, payment: null, pin: "", busy: false });
      toast.success(`${p.label || p.method} payment removed.`);
      try { fetchDashboard(false); } catch (_) { /* noop */ }
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : (detail?.message || "Could not remove that payment. Check the PIN and that you are online."));
      setRemoveTenderPrompt((d) => ({ ...d, busy: false }));
    }
  };

  const openTables = (tableSessions || []).filter((tableSession) => tableSession.status === "open").length;
  // Session 27.89 — Pending / not-completed counters were reporting
  // stale numbers ("shows 2 pending when there are none"). Root cause:
  // orders can end up stuck in the "confirmed" bucket even after every
  // station has been marked delivered — for example when the backend's
  // completion check couldn't classify an item (missing / renamed
  // department) so it left the status at "confirmed" even though
  // both `kitchen_acknowledged_at` and `bar_acknowledged_at` were
  // written, OR when only one station had items and its ack landed
  // but the completion sweep didn't fire.
  //
  // The safe client-side rule mirrors the backend's completion logic
  // without needing the department index: an order is effectively
  // done if
  //   • `completed_at` is set (backend already flipped it), OR
  //   • both `kitchen_acknowledged_at` and `bar_acknowledged_at` are
  //     set (both stations delivered), OR
  //   • kitchen was acked and bar never marked ready (kitchen-only
  //     order, all work delivered), OR
  //   • bar was acked and kitchen never marked ready (bar-only order,
  //     all work delivered).
  // Anything else with status ∈ {"pending","confirmed"} still counts.
  const _orderEffectivelyDone = (o) => {
    if (!o) return false;
    if (o.status === "completed" || o.status === "voided") return true;
    if (o.completed_at) return true;
    const kAcked = !!o.kitchen_acknowledged_at;
    const bAcked = !!o.bar_acknowledged_at;
    const kReady = !!o.kitchen_ready_at;
    const bReady = !!o.bar_ready_at;
    if (kAcked && bAcked) return true;
    if (kAcked && !bReady) return true;   // kitchen-only order, delivered
    if (bAcked && !kReady) return true;   // bar-only order, delivered
    return false;
  };
  // Session 27.94 — Per-waiter pending counter.
  //
  // Operator request: "every waiter or staff dashboard should only
  // see the orders pending that they have pushed through. I don't
  // want to see how many orders a different waiter has pending."
  //
  // Order documents carry `waiter_id` (set at line ~3213 of server.py
  // on every order create) and legacy `user_id`. If the signed-in
  // user is a plain waiter/staff, filter the pending & confirmed
  // counters to just their own orders. Managers and owners keep the
  // aggregate view — they need to see everything on the shift.
  const _sessionUserRole = (session?.user?.role || "").toLowerCase();
  const _sessionUserId = session?.user?.id || null;
  const _shouldFilterByOwnOrders = (
    !!_sessionUserId
    && _sessionUserRole !== "owner"
    && _sessionUserRole !== "manager"
    && _sessionUserRole !== "supervisor"
    && _sessionUserRole !== "shift_leader"
  );
  const _orderIsMine = (o) => {
    if (!_shouldFilterByOwnOrders) return true;
    const ownerId = String(o?.waiter_id || o?.user_id || o?.created_by_user_id || "");
    return ownerId === String(_sessionUserId);
  };
  // Session 27.97 — Item-level pending workload.
  //
  // Previous version used an order-level "effectively done" heuristic:
  // if the bar station was acknowledged and the kitchen never marked
  // ready, treat the whole order as done. That's wrong for mixed
  // orders (a burger + a coke) where the bar finishes first — the
  // burger is still cooking but the counter would incorrectly drop
  // to zero.
  //
  // The fix is to count PER ITEM based on the item's own
  // department + the corresponding station ack, and NOT gate on any
  // order-level "done" shortcut. If we have a proper `item.status`
  // field (partial-ready flow — Session 27.98+), we honour that too.
  const _pendingItemCountForOrder = (o) => {
    if (!o) return 0;
    if (o.status === "completed" || o.status === "voided") return 0;
    if (o.completed_at) return 0;
    const items = Array.isArray(o.items) ? o.items : [];
    let count = 0;
    for (const it of items) {
      if (!it || it.voided) continue;
      // Item-level status (partial-ready) takes precedence when
      // present. Any item that's already been delivered no longer
      // counts as pending workload.
      const itemStatus = String(it.status || "").toLowerCase();
      if (itemStatus === "delivered" || itemStatus === "voided") continue;
      // Session 31.9 — lines no station prepares (server tags station: null)
      // are handed over by the waiter and never "pending".
      if (Object.prototype.hasOwnProperty.call(it, "station") && it.station === null) continue;
      // Station-level fallback: if the item's department has been
      // acknowledged by staff (kitchen_acknowledged_at /
      // bar_acknowledged_at set), the item was delivered as part of
      // a full-station ack — skip it. We only skip when the ack
      // matches the item's own department.
      const dept = String(it.department || "").toLowerCase();
      if (dept === "kitchen" && o.kitchen_acknowledged_at) continue;
      if (dept === "bar" && o.bar_acknowledged_at) continue;
      const qty = Number(it.quantity) > 0 ? Number(it.quantity) : 1;
      count += qty;
    }
    return count;
  };
  const needsConfirmation = (tableSessions || [])
    .filter((s) => s.status === "open")
    .reduce((sum, s) => sum + ((s.orders || [])
      .filter(o => o && o.status === "pending")
      .reduce((s2, o) => s2 + _pendingItemCountForOrder(o), 0)), 0);
  const notCompletedCount = (tableSessions || [])
    .filter((s) => s.status === "open")
    .reduce((sum, s) => sum + ((s.orders || [])
      .filter(o => o && o.status === "confirmed" && _orderIsMine(o))
      .reduce((s2, o) => s2 + _pendingItemCountForOrder(o), 0)), 0);

  if (!session?.token) return null;

  const openSessionsForPicker = (tableSessions || []).filter((s) => s && s.status === "open");
  const longWaitMin = Number(context?.business?.long_wait_minutes) || LONG_WAIT_MIN; // Session 30.20 owner-configurable
  const longWaitDepts = context?.business?.long_wait_by_department || null; // Session 31.2
  const longWaitIds = longWaitSessionIds(tableSessions, longWaitMin, Date.now(), longWaitDepts); // Session 30.18
  const Wrapper = embedded ? "div" : "main";

  return (
    <Wrapper className={embedded ? "" : "min-h-screen bg-background px-4 py-6 sm:px-6 sm:py-8"} data-testid={embedded ? "owner-table-management" : undefined}>
      <div className={embedded ? "flex w-full flex-col gap-4" : "mx-auto flex w-full max-w-7xl flex-col gap-6"}>
        {embedded ? null : (
        <header className="rounded-[28px] border border-border bg-card p-4 shadow-[0_1px_0_rgba(0,0,0,0.04)] sm:p-6">
          {/* Utility bar — light/dark on the left, mesh in the centre,
              fullscreen on the right. Always on its own row so the
              tap targets never collide with each other on phones. */}
          <div className="flex items-center justify-between gap-2 border-b border-border/60 pb-3">
            <ThemeToggleButton />
            <MeshStatusPill />
            <div className="flex items-center gap-1">
              {/* Session 27.34 — Manual refresh button. On phones a
                  pull-to-refresh gesture works, but the laptop/POS
                  had no way to trigger a re-fetch. Refreshing also
                  gives the LAN mesh signalling socket a nudge to
                  re-establish peer connections if they never
                  auto-paired on first load. */}
              {/* Session 30.8 — Refresh is DATA-ONLY now. It never tears
                  down the LAN mesh (an established handshake must survive
                  a refresh). When an installed device has LOST its LAN
                  peers the button is hidden entirely; the mesh pill offers
                  a controlled "Reconnect LAN" instead. */}
              {connectivity.refreshAllowed ? (
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-9 w-9 rounded-full"
                title="Refresh dashboard data"
                onClick={() => {
                  try { fetchDashboard(true); } catch (_) { /* noop */ }
                  try { fetchKitchenNotifs(); } catch (_) { /* noop */ }
                  try { fetchBarNotifs(); } catch (_) { /* noop */ }
                  toast.success("Refreshed");
                }}
                data-testid="staff-refresh-button"
              >
                <RefreshCw className="h-4 w-4" />
              </Button>
              ) : null}
              <ChimeVolumeControl role="staff" variant="ghost" />
              <FullscreenToggleButton lockDown />
            </div>
          </div>
          {/* Identity + actions row. Wraps under on narrow screens so
              the Tabs / Actions buttons stay full-width and finger-
              friendly on phones. */}
          <div className="mt-4 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <Badge className="bg-[hsl(var(--primary))] text-primary-foreground">Staff dashboard</Badge>
                <PollingIndicator />
                {notificationsMuted && (
                  <Badge
                    variant="outline"
                    className="gap-1 rounded-full border-amber-500/60 bg-amber-50 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300"
                    data-testid="staff-notifications-muted-badge"
                    title="Notifications are turned off — tap Actions → Turn on notifications to re-enable"
                  >
                    <BellOff className="h-3 w-3" />
                    Notifications off
                  </Badge>
                )}
                {sessionUser ? (
                  <Badge variant="outline" className="rounded-full capitalize" data-testid="staff-current-user">
                    {sessionUser.full_name} · {sessionUser.role}
                  </Badge>
                ) : null}
              </div>
              <h1 className="mt-3 truncate font-['Space_Grotesk'] text-2xl font-semibold tracking-tight sm:text-3xl">{context?.business?.name || "Loading…"}</h1>
            </div>
            <div className="flex flex-wrap items-center gap-2 sm:justify-end">
              <Button variant="outline" className="flex-1 rounded-xl sm:flex-none" onClick={() => { setStaffAccounts((d) => ({ ...d, open: true })); loadStaffAccounts(); }} data-testid="staff-open-accounts-button">
                <Wallet className="h-4 w-4" />
                Tabs
                {staffAccounts.summary?.open_count ? (
                  <span className="ml-2 flex items-center">
                    <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold text-amber-700 dark:bg-amber-900/40 dark:text-amber-200" data-testid="staff-tabs-count">
                      {staffAccounts.summary.open_count}
                    </span>
                  </span>
                ) : null}
              </Button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" className="flex-1 rounded-xl sm:flex-none" data-testid="staff-actions-button">
                    Actions
                    <ChevronDown className="ml-1 h-4 w-4 opacity-70" />
                  </Button>
                </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-52 rounded-xl" data-testid="staff-actions-menu">
                <DropdownMenuItem onSelect={() => { setTimeout(openStaffHandover, 0); }} data-testid="staff-handover-button">
                  <Users className="mr-2 h-4 w-4" />
                  Shift Handover
                </DropdownMenuItem>
                <DropdownMenuItem onSelect={() => { setTimeout(openStaffTips, 0); }} data-testid="staff-tips-button">
                  <Coins className="mr-2 h-4 w-4" />
                  Tips
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onSelect={() => { setTimeout(() => setPrinterSetupOpen(true), 0); }} data-testid="staff-printer-setup-button">
                  <Printer className="mr-2 h-4 w-4" />
                  Printer setup
                </DropdownMenuItem>
                <DropdownMenuItem onSelect={() => { setTimeout(toggleNotificationsMuted, 0); }} data-testid="staff-mute-notifications-button">
                  {notificationsMuted ? <Bell className="mr-2 h-4 w-4" /> : <BellOff className="mr-2 h-4 w-4" />}
                  {notificationsMuted ? "Turn on notifications" : "Turn off notifications"}
                </DropdownMenuItem>
                {/* Session 27.91 — Removed the "Test sounds" sub-block
                    (Login chime / New order / Order confirmation /
                    Order marked ready / Coordinator handover). Each
                    of those chimes is already testable from the
                    dedicated sound-preset picker where the operator
                    hears the exact preset they'll receive in the
                    field. Duplicating them here just cluttered the
                    Actions menu and hid the actually useful
                    diagnostic ("Check for ready orders"). */}
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onSelect={() => {
                    // Session 27.15 — diagnostic: fetch both notif endpoints
                    // and dump the raw response into a persistent toast so
                    // operators can see EXACTLY what the backend returns
                    // without opening DevTools. Also force-refreshes the
                    // banner state so any ready-orders that were missed
                    // show up.
                    setTimeout(async () => {
                      try {
                        const [k, b] = await Promise.all([
                          http.get("/staff/kitchen-ready", { headers: authHeaders }),
                          http.get("/staff/bar-ready", { headers: authHeaders }),
                        ]);
                        const kn = k.data.notifications || [];
                        const bn = b.data.notifications || [];
                        setKitchenNotifs(kn);
                        setBarNotifs(bn);
                        toast.success(
                          `Kitchen: ${kn.length} · Bar: ${bn.length}`,
                          {
                            description: kn.length + bn.length === 0
                              ? "Backend returned zero ready orders. Kitchen mark-ready may be failing OR the query isn't matching your data."
                              : `Loaded ${kn.length + bn.length} pending notification${kn.length + bn.length === 1 ? "" : "s"} into the banner.`,
                            duration: 8000,
                          },
                        );
                      } catch (e) {
                        toast.error("Diagnostic failed", { description: String(e?.response?.data?.detail || e?.message || e) });
                      }
                    }, 0);
                  }}
                  data-testid="staff-diagnostic-notifs-button"
                >
                  <Search className="mr-2 h-4 w-4" />
                  Check for ready orders
                </DropdownMenuItem>
                <DropdownMenuItem onSelect={() => { setTimeout(handleLogout, 0); }} data-testid="staff-logout-button" className="text-destructive focus:text-destructive focus:bg-destructive/10">
                  <LogOut className="mr-2 h-4 w-4" />
                  Logout
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            </div>
          </div>
        </header>
        )}

        {/* Session 31.7 — Customer orders held for approval ("Have staff
            confirm order before departments" is ON). Accept releases them
            to kitchen/bar, Decline voids them. */}
        <PendingApprovalsCard
          orders={pendingConfirmations}
          busyId={confirmingOrderId}
          onAccept={confirmPendingOrder}
          onDecline={rejectPendingOrder}
          testIdPrefix={embedded ? "owner-approvals" : "staff-approvals"}
        />

        {/* Session 30.18 — Long-wait alerts: which tables/orders have been
            waiting over 15 min and exactly what is on them. */}
        <LongWaitAlerts
          sessions={tableSessions}
          thresholdMin={longWaitMin}
          deptThresholds={longWaitDepts}
          onSelect={(id) => { setSelectedSessionId(id); setStatusFilter("active-tables"); }}
          onMarkServed={embedded ? null : (orderId) => markOrderServed(orderId)}
          busyOrderId={busyAction && String(busyAction).endsWith("served") ? String(busyAction).slice(0, -6) : null}
          testIdPrefix={embedded ? "owner-long-wait" : "staff-long-wait"}
        />

        {/* Session 30.10 — Owner/Manager embedded mode: compact
            "Active Tables ▾" selector instead of the chip wall. */}
        {embedded ? (
          <div className="flex flex-wrap items-center gap-2" data-testid="owner-active-tables-bar">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className="h-11 rounded-xl px-4 font-semibold" data-testid="owner-active-tables-trigger">
                  <ClipboardList className="h-4 w-4" />
                  Active Tables
                  <span className="ml-1 inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-[hsl(var(--primary))] px-1.5 text-[11px] font-bold text-primary-foreground" data-testid="owner-active-tables-count">{openSessionsForPicker.length}</span>
                  <ChevronDown className="h-4 w-4 opacity-70" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="max-h-[60vh] w-80 overflow-y-auto rounded-xl p-1" data-testid="owner-active-tables-menu">
                {openSessionsForPicker.length === 0 ? (
                  <div className="px-3 py-4 text-center text-sm text-muted-foreground" data-testid="owner-active-tables-empty">No active tables right now.</div>
                ) : openSessionsForPicker.map((s) => {
                  const pend = (s.pending_orders || 0) + (s.confirmed_orders || 0);
                  const paid = isSessionPaid(s);
                  const isSel = selectedSessionId === s.id;
                  return (
                    <DropdownMenuItem
                      key={s.id}
                      onSelect={() => { setSelectedSessionId(s.id); setStatusFilter("active-tables"); }}
                      className={`flex items-start gap-3 rounded-lg px-3 py-2 ${isSel ? "bg-secondary" : ""}`}
                      data-testid={`owner-active-table-option-${s.table_number}`}
                    >
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-semibold">{/^\d+$/.test(String(s.table_number)) ? `Table ${s.table_number}` : s.table_number}</p>
                        <p className="truncate text-[11px] text-muted-foreground">
                          {s.assigned_user_name || "Unassigned"} · {s.total_items || 0} item{(s.total_items || 0) === 1 ? "" : "s"}
                        </p>
                      </div>
                      <div className="shrink-0 text-right">
                        <p className="text-sm font-bold tabular-nums">{formatPrice(s.total_value || 0)}</p>
                        <p className={`text-[10px] font-semibold ${longWaitIds.has(s.id) ? "text-rose-600 dark:text-rose-400 animate-pulse" : paid ? "text-emerald-600 dark:text-emerald-400" : pend > 0 ? "text-amber-600 dark:text-amber-400" : "text-muted-foreground"}`}>
                          {longWaitIds.has(s.id) ? "Waiting 15+ min" : paid ? "Paid" : pend > 0 ? `${pend} pending` : "Unpaid"}
                        </p>
                      </div>
                    </DropdownMenuItem>
                  );
                })}
              </DropdownMenuContent>
            </DropdownMenu>
            {selectedSession ? (
              <span className="text-sm text-muted-foreground" data-testid="owner-active-tables-selected">
                Viewing <span className="font-semibold text-foreground">{/^\d+$/.test(String(selectedSession.table_number)) ? `Table ${selectedSession.table_number}` : selectedSession.table_number}</span>
              </span>
            ) : openSessionsForPicker.length > 0 ? (
              <span className="text-sm text-muted-foreground">Pick a table to manage it.</span>
            ) : null}
          </div>
        ) : null}

        {/* Active Tables Section */}
        {!embedded && openTables > 0 && (() => {
          const allOpenSessions = (tableSessions || []).filter((s) => s.status === "open");
          const tabCount = allOpenSessions.filter((s) => s.channel === "tab").length;
          const tableCount = allOpenSessions.length - tabCount;
          const openSessions = sessionKindFilter === "tabs"
            ? allOpenSessions.filter((s) => s.channel === "tab")
            : sessionKindFilter === "tables"
              ? allOpenSessions.filter((s) => s.channel !== "tab")
              : allOpenSessions;
          const q = tableSearch.trim().toLowerCase();
          const matches = (s) => {
            if (!q) return true;
            const hay = `${s.table_number || ""} table ${s.table_number || ""} ${s.assigned_user_name || ""} ${s.customer_name || ""} ${s.customer_phone || ""}`.toLowerCase();
            return hay.includes(q);
          };
          const onlineSessions = openSessions.filter((s) => Number(s.online_orders || 0) > 0);
          const cashDueSessions = openSessions.filter((s) => Number(s.online_cash_due || 0) > 0);
          const cashDueTotal = Math.round(cashDueSessions.reduce((n, s) => n + Number(s.online_cash_due || 0), 0) * 100) / 100;
          let shown;
          if (onlineTablesOnly) shown = onlineSessions.filter(matches);
          else if (q) shown = openSessions.filter(matches);
          else if (showAllTables) shown = openSessions;
          else {
            // Collapsed view: the waiter's OWN tables always stay visible, plus
            // the selected / long-wait / cash-due ones. (Session 31.20)
            const myId = context?.user?.id ? String(context.user.id) : "";
            shown = openSessions.filter((s) => s.id === selectedSessionId || longWaitIds.has(s.id) || Number(s.online_cash_due || 0) > 0 || (myId && String(s.assigned_user_id || "") === myId));
          }
          const hiddenCount = openSessions.length - shown.length;
          return (
          <Card className="rounded-[24px] border-border bg-card" data-testid="staff-active-tables-card">
            <CardHeader className="pb-3">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <CardTitle className="text-lg">🟢 Active Tables <span className="ml-1 text-sm font-normal text-muted-foreground" data-testid="staff-active-tables-count">({openSessions.length})</span></CardTitle>
                  <CardDescription>Click a table to view details</CardDescription>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <Button
                    type="button"
                    size="sm"
                    variant={onlineTablesOnly ? "default" : "outline"}
                    className="h-9 rounded-xl"
                    onClick={() => setOnlineTablesOnly((v) => !v)}
                    aria-pressed={onlineTablesOnly}
                    data-testid="staff-online-tables-toggle"
                  >
                    <QrCode className="h-4 w-4" /> Online orders ({onlineSessions.length})
                  </Button>
                  <div className="inline-flex h-9 items-center rounded-xl border border-border bg-background p-0.5 text-xs font-semibold" role="group" aria-label="Show tables, tabs or all" data-testid="staff-session-kind-filter">
                    {[["tables", `Tables (${tableCount})`], ["tabs", `Tabs (${tabCount})`], ["all", "All"]].map(([k, lbl]) => (
                      <button
                        key={k}
                        type="button"
                        onClick={() => setSessionKind(k)}
                        aria-pressed={sessionKindFilter === k}
                        className={`h-8 rounded-[10px] px-3 transition-colors ${sessionKindFilter === k ? (k === "tabs" ? "bg-rose-600 text-white" : "bg-[hsl(var(--primary))] text-primary-foreground") : "text-muted-foreground hover:bg-secondary"}`}
                        data-testid={`staff-session-kind-${k}`}
                      >
                        {lbl}
                      </button>
                    ))}
                  </div>
                  <label className="flex cursor-pointer select-none items-center gap-2 rounded-xl border border-border bg-background px-3 py-2 text-sm font-medium" data-testid="staff-show-all-tables-toggle">
                    <Switch checked={showAllTables} onCheckedChange={toggleShowAllTables} aria-label="Show all tables" data-testid="staff-show-all-tables-switch" />
                    Show all tables
                  </label>
                </div>
              </div>
              {cashDueSessions.length > 0 ? (
                <p className="mt-2 inline-flex items-center gap-1.5 rounded-full border border-amber-500/40 bg-amber-500/10 px-3 py-1 text-xs font-medium text-amber-800 dark:text-amber-200" data-testid="staff-cash-due-summary">
                  <Banknote className="h-3.5 w-3.5" /> {cashDueSessions.length} online order{cashDueSessions.length === 1 ? "" : "s"} waiting for cash · {formatPrice(cashDueTotal)} to collect
                </p>
              ) : null}
              <div className="relative mt-2">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <FastInput
                  value={tableSearch}
                  onChange={(e) => setTableSearch(e.target.value)}
                  placeholder="Search tables (e.g. 5, Booth 2, waiter name)…"
                  className="h-11 rounded-xl pl-9 pr-9"
                  data-testid="staff-table-search-input"
                />
                {tableSearch ? (
                  <button
                    type="button"
                    className="absolute right-2.5 top-1/2 -translate-y-1/2 rounded-full p-1 text-muted-foreground hover:bg-secondary hover:text-foreground"
                    onClick={() => setTableSearch("")}
                    aria-label="Clear table search"
                    data-testid="staff-table-search-clear"
                  >
                    <XIcon className="h-4 w-4" />
                  </button>
                ) : null}
              </div>
            </CardHeader>
            <CardContent>
              {shown.length === 0 ? (
                <p className="text-sm text-muted-foreground" data-testid="staff-tables-empty-hint">
                  {onlineTablesOnly
                    ? "No tables with digital-menu orders right now. Tables where a guest ordered from the QR menu (and any cash still owed) will show here."
                    : q
                    ? `No open table matches "${tableSearch.trim()}".`
                    : openSessions.length === 0 && sessionKindFilter === "tabs"
                    ? "No open tabs right now. Open one from Tabs, or switch the filter to Tables / All."
                    : openSessions.length === 0 && sessionKindFilter === "tables"
                    ? "No open tables right now — only tabs are active. Switch the filter to Tabs / All."
                    : `${openSessions.length} active table${openSessions.length === 1 ? "" : "s"} hidden — turn on "Show all tables" or search for one above.`}
                </p>
              ) : null}
              <div className="flex flex-wrap gap-2">
                {shown.map(session => {
                    // Session 31.26 — multi-tender: a table is "paid" only when the
                    // ledger covers the bill; anything in between is "partially paid".
                    const payState = sessionPaymentState(session);
                    const isPaid = payState.isPaid && payState.owed > 0;
                    const isPartial = payState.partiallyPaid;
                    // Session 31.27 — customer tabs get their own (crimson) tone.
                    const isTab = session.channel === "tab";
                    const cashDue = Number(session.online_cash_due || 0);
                    const isOnline = Number(session.online_orders || 0) > 0;
                    return (
                    <button
                      key={session.id}
                      onClick={() => {
                        setSelectedSessionId(session.id);
                        setStatusFilter("active-tables");
                      }}
                      className={`rounded-xl border-2 px-4 py-2 font-medium transition-all ${
                        longWaitIds.has(session.id)
                          ? "border-rose-500 bg-rose-500/10 text-rose-900 dark:text-rose-200 animate-pulse ring-2 ring-rose-400/60 ring-offset-1"
                          : selectedSessionId === session.id
                          ? "border-primary bg-primary text-primary-foreground shadow-sm"
                          : isTab
                            ? "border-rose-600 bg-rose-600/10 text-rose-900 dark:text-rose-200 hover:bg-rose-600/20"
                          : isPaid
                            ? "border-emerald-500 bg-emerald-500/10 text-emerald-800 dark:text-emerald-300 hover:bg-emerald-500/20"
                            : isPartial
                              ? "border-amber-500 border-dashed bg-amber-500/10 text-amber-900 dark:text-amber-200 hover:bg-amber-500/20"
                            : cashDue > 0
                              ? "border-amber-500 bg-amber-500/10 text-amber-900 dark:text-amber-200 hover:bg-amber-500/20"
                              : isOnline
                                // Session 31.19 — guests who ordered from the QR menu: blue so
                                // staff can spot them at a glance (purple stays "split bill").
                                ? "border-sky-500 bg-sky-500/10 text-sky-900 dark:text-sky-200 hover:bg-sky-500/20"
                                : "border-border bg-background hover:border-primary hover:bg-secondary"
                      }`}
                      data-testid={`staff-table-chip-${session.table_number}`}
                      data-online={isOnline ? "true" : "false"}
                    >
                      <span className="flex items-center gap-1">Table {session.table_number}{isOnline ? <QrCode className="h-3.5 w-3.5 opacity-80" aria-label="Online order" data-testid={`staff-table-online-icon-${session.table_number}`} /> : null}</span>
                      {isOnline && !isPaid && !(cashDue > 0) ? (
                        <span className={`mt-0.5 block text-[10px] font-semibold ${selectedSessionId === session.id ? "text-primary-foreground/80" : "text-sky-700 dark:text-sky-300"}`} data-testid={`staff-table-online-badge-${session.table_number}`}>
                          Online order
                        </span>
                      ) : null}
                      {!isPaid && cashDue > 0 ? (
                        <span className={`mt-0.5 block text-[10px] font-semibold ${selectedSessionId === session.id ? "text-primary-foreground/80" : "text-amber-700 dark:text-amber-300"}`} data-testid={`staff-table-cash-due-badge-${session.table_number}`}>
                          Cash due {formatPrice(cashDue)} · online order
                          {session.customer_name || session.customer_phone ? (
                            <span className="block font-normal" data-testid={`staff-table-guest-${session.table_number}`}>
                              {[session.customer_name, session.customer_phone].filter(Boolean).join(" · ")}
                            </span>
                          ) : null}
                        </span>
                      ) : isPaid ? (
                        <span className={`mt-0.5 block text-[10px] font-semibold ${selectedSessionId === session.id ? "text-primary-foreground/80" : "text-emerald-700 dark:text-emerald-300"}`} data-testid={`staff-table-paid-badge-${session.table_number}`}>
                          ✓ Paid — ready to close
                        </span>
                      ) : isPartial ? (
                        <span className={`mt-0.5 block text-[10px] font-semibold ${selectedSessionId === session.id ? "text-primary-foreground/80" : "text-amber-700 dark:text-amber-300"}`} data-testid={`staff-table-partial-badge-${session.table_number}`}>
                          Partially paid · {formatPrice(payState.outstanding)} due
                        </span>
                      ) : session.assigned_user_name ? (
                        <span className={`mt-0.5 block text-[10px] font-normal ${selectedSessionId === session.id ? "text-primary-foreground/70" : "text-muted-foreground"}`} data-testid={`table-chip-assigned-${session.id}`}>
                          Assigned: {session.assigned_user_name.split(" ")[0]}
                        </span>
                      ) : null}
                    </button>
                    );
                  })}
              </div>
              {shown.length > 0 ? (
                <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-muted-foreground" data-testid="staff-table-legend">
                  <span className="flex items-center gap-1"><span className="h-2.5 w-2.5 rounded-full border-2 border-sky-500 bg-sky-500/30" /> Online order</span>
                  <span className="flex items-center gap-1"><span className="h-2.5 w-2.5 rounded-full border-2 border-amber-500 bg-amber-500/30" /> Cash due</span>
                  <span className="flex items-center gap-1"><span className="h-2.5 w-2.5 rounded-full border-2 border-dashed border-amber-500 bg-amber-500/30" /> Partially paid</span>
                  <span className="flex items-center gap-1"><span className="h-2.5 w-2.5 rounded-full border-2 border-rose-600 bg-rose-600/30" /> Tab</span>
                  <span className="flex items-center gap-1"><span className="h-2.5 w-2.5 rounded-full border-2 border-emerald-500 bg-emerald-500/30" /> Paid</span>
                  <span className="flex items-center gap-1"><span className="h-2.5 w-2.5 rounded-full border-2 border-rose-500 bg-rose-500/30" /> Long wait</span>
                </div>
              ) : null}
              {!q && !showAllTables && hiddenCount > 0 && shown.length > 0 ? (
                <p className="mt-3 text-xs text-muted-foreground" data-testid="staff-tables-hidden-hint">
                  +{hiddenCount} more open table{hiddenCount === 1 ? "" : "s"} — turn on "Show all tables" or search.
                </p>
              ) : null}
            </CardContent>
          </Card>
          );
        })()}

        {!embedded && !notificationsMuted && waiterCalls.length > 0 ? (
          <div className="space-y-2 animate-in fade-in slide-in-from-top-2" data-testid="waiter-calls-section">
            {waiterCalls.map((call) => (
              <Card
                key={call.id}
                className="overflow-hidden rounded-[24px] border-2 border-amber-500 bg-amber-50/70 shadow-md dark:bg-amber-950/30"
                data-testid={`waiter-call-${call.id}`}
              >
                <CardContent className="flex flex-col gap-3 p-5 sm:flex-row sm:items-start sm:justify-between">
                  <div className="flex min-w-0 items-start gap-3">
                    <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-amber-500 text-white shadow">
                      <Bell className="h-5 w-5" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="font-['Space_Grotesk'] text-xl font-semibold text-amber-700 dark:text-amber-300">
                        Please attend to Table {call.table_number || "—"}
                      </p>
                      <p className="mt-0.5 text-xs uppercase tracking-wide text-muted-foreground">
                        Waiter requested <KitchenTimeAgo from={call.created_at} />
                      </p>
                      {call.note ? (
                        <p className="mt-2 text-sm italic text-foreground/80">"{call.note}"</p>
                      ) : null}
                    </div>
                  </div>
                  <Button
                    className="rounded-xl bg-amber-600 text-white hover:bg-amber-700"
                    onClick={() => acknowledgeWaiterCall(call.id)}
                    data-testid={`waiter-call-ack-${call.id}`}
                  >
                    <Check className="h-4 w-4" />
                    On the way
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : null}

        {!embedded && !notificationsMuted && kitchenNotifs.length > 0 ? (
          <div className="space-y-2 animate-in fade-in slide-in-from-top-2" data-testid="kitchen-notifications-section">
            {kitchenNotifs.map((notif) => (
              <Card
                key={notif.id}
                className="overflow-hidden rounded-[24px] border-2 border-emerald-500 bg-emerald-50/60 shadow-md dark:bg-emerald-950/30"
                data-testid={`kitchen-notif-${notif.id}`}
              >
                <CardContent className="flex flex-col gap-3 p-5 sm:flex-row sm:items-start sm:justify-between">
                  <div className="flex min-w-0 items-start gap-3">
                    <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-emerald-500 text-white shadow">
                      <Bell className="h-5 w-5" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="font-['Space_Grotesk'] text-xl font-semibold text-emerald-700 dark:text-emerald-300">
                        Kitchen ready — Table {notif.table_number || "—"}
                      </p>
                      <p className="mt-0.5 text-xs uppercase tracking-wide text-muted-foreground">
                        {notif.order_number} · ready <KitchenTimeAgo from={notif.ready_at} />
                      </p>
                      <ul className="mt-3 space-y-1 text-sm" data-testid={`kitchen-notif-items-${notif.id}`}>
                        {(notif.items || []).length > 0 ? (
                          (notif.items || []).map((item, idx) => (
                            <li key={`${notif.id}-${idx}`} className="flex items-center gap-2">
                              <Check className="h-4 w-4 text-emerald-600" />
                              <span className="font-medium">{item.quantity} × {item.name}</span>
                            </li>
                          ))
                        ) : (
                          // Session 27.15 — Defensive fallback. If the WS
                          // event beat the REST refresh and we don't have
                          // the item list yet, still show an actionable
                          // line so staff know food is waiting (not just
                          // an order number in isolation).
                          <li className="flex items-center gap-2 italic text-emerald-800/80 dark:text-emerald-200/80">
                            <Check className="h-4 w-4 text-emerald-600" />
                            <span>Order is ready — tap "Mark delivered" once served.</span>
                          </li>
                        )}
                      </ul>
                    </div>
                  </div>
                  <Button
                    className="rounded-xl bg-emerald-600 text-white hover:bg-emerald-700"
                    onClick={() => acknowledgeKitchenNotif(notif.id, notif)}
                    data-testid={`kitchen-notif-ack-${notif.id}`}
                  >
                    <Check className="h-4 w-4" />
                    Mark delivered
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : null}

        {/* Bar-ready notifications — same shape as kitchen but indigo-themed
            so staff can tell at a glance which station just buzzed them. */}
        {!embedded && !notificationsMuted && barNotifs.length > 0 ? (
          <div className="space-y-2 animate-in fade-in slide-in-from-top-2" data-testid="bar-notifications-section">
            {barNotifs.map((notif) => (
              <Card
                key={notif.id}
                className="overflow-hidden rounded-[24px] border-2 border-indigo-500 bg-indigo-50/60 shadow-md dark:bg-indigo-950/30"
                data-testid={`bar-notif-${notif.id}`}
              >
                <CardContent className="flex flex-col gap-3 p-5 sm:flex-row sm:items-start sm:justify-between">
                  <div className="flex min-w-0 items-start gap-3">
                    <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-indigo-500 text-white shadow">
                      <Wine className="h-5 w-5" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="font-['Space_Grotesk'] text-xl font-semibold text-indigo-700 dark:text-indigo-300">
                        Bar ready — Table {notif.table_number || "—"}
                      </p>
                      <p className="mt-0.5 text-xs uppercase tracking-wide text-muted-foreground">
                        {notif.order_number} · ready <KitchenTimeAgo from={notif.ready_at} />
                      </p>
                      <ul className="mt-3 space-y-1 text-sm" data-testid={`bar-notif-items-${notif.id}`}>
                        {(notif.items || []).length > 0 ? (
                          (notif.items || []).map((item, idx) => (
                            <li key={`${notif.id}-${idx}`} className="flex items-center gap-2">
                              <Check className="h-4 w-4 text-indigo-600" />
                              <span className="font-medium">{item.quantity} × {item.name}</span>
                            </li>
                          ))
                        ) : (
                          <li className="flex items-center gap-2 italic text-indigo-800/80 dark:text-indigo-200/80">
                            <Check className="h-4 w-4 text-indigo-600" />
                            <span>Drinks are ready — tap "Mark delivered" once served.</span>
                          </li>
                        )}
                      </ul>
                    </div>
                  </div>
                  <Button
                    className="rounded-xl bg-indigo-600 text-white hover:bg-indigo-700"
                    onClick={() => acknowledgeBarNotif(notif.id, notif)}
                    data-testid={`bar-notif-ack-${notif.id}`}
                  >
                    <Check className="h-4 w-4" />
                    Mark delivered
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : null}

        <Tabs value={statusFilter} onValueChange={setStatusFilter}>
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <TabsList className="h-auto w-full justify-start gap-2 overflow-x-auto rounded-2xl bg-secondary p-2 sm:w-auto" data-testid="staff-table-status-tabs">
              {[
                { label: "Active", value: "active-tables", count: openTables },
                { label: "Pending Items", value: "not-completed", count: notCompletedCount },
              ].map((tab) => (
                <TabsTrigger
                  key={tab.value}
                  value={tab.value}
                  className="relative whitespace-nowrap rounded-xl px-3 py-2 text-sm"
                  data-testid={`staff-table-status-tab-${tab.value}`}
                >
                  {tab.label}
                  {tab.count > 0 ? (
                    <span
                      className="absolute -left-1.5 -top-1.5 flex h-5 min-w-[1.25rem] items-center justify-center rounded-full bg-[hsl(var(--primary))] px-1.5 text-[10px] font-semibold leading-none text-[hsl(var(--primary-foreground))] shadow-sm"
                      data-testid={`staff-table-status-count-${tab.value}`}
                    >
                      {tab.count}
                    </span>
                  ) : null}
                </TabsTrigger>
              ))}
            </TabsList>
            {embedded ? null : (
            <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row">
              <Button
                onClick={() => setOpenTableDialog({ open: true, tableNumber: "" })}
                className="h-10 w-full rounded-xl bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))] hover:bg-[hsl(var(--accent))]/90 sm:w-auto"
                data-testid="staff-open-table-button"
              >
                <Plus className="h-4 w-4" />
                Open table
              </Button>
              <Button
                variant="outline"
                onClick={() => { setReservationsDialog((d) => ({ ...d, open: true })); loadReservations(); }}
                className="h-10 w-full rounded-xl sm:w-auto"
                data-testid="staff-open-reservations-button"
              >
                <Calendar className="h-4 w-4" />
                Open Reservation
              </Button>
            </div>
            )}
          </div>

          {/* Open Tab - Direct table details, no sidebar */}
          <TabsContent value="open">
            {loading ? (
              <DashboardLoadingState />
            ) : selectedSession ? (
              <Card className="rounded-[24px] border-border bg-card">
                <CardContent className="p-0">
                              <div className="space-y-5 p-5">
                                  <div className="space-y-5 p-5">
                    <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                          <div>
                            <p className="text-sm text-muted-foreground">Active table</p>
                            <div className="mt-1 flex items-center gap-3">
                              <h2 className="font-['Space_Grotesk'] text-4xl font-semibold tracking-tight">Table {selectedSession.table_number}</h2>
                              {sessionHasPayment ? (
                                <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/15 px-3 py-1 text-xs font-bold text-emerald-700 dark:text-emerald-300" data-testid="staff-selected-table-paid-pill">
                                  ✓ PAID
                                </span>
                              ) : null}
                            </div>
                            <p className="mt-2 text-sm text-muted-foreground">{selectedSession.order_count} orders · {selectedSession.total_items} items · {formatPrice(selectedSession.total_value)}</p>
                            <p className="mt-1 text-xs text-muted-foreground" data-testid={`active-table-assigned-${selectedSession.id}`}>
                              Assigned waiter: <span className="font-medium text-foreground">{selectedSession.assigned_user_name || "Unassigned"}</span>
                            </p>
                            {sessionHasPayment ? (
                              <p className="mt-1 text-xs font-semibold text-emerald-700 dark:text-emerald-400" data-testid="staff-selected-table-paid-hint">
                                Payment recorded — safe to close via Table actions.
                              </p>
                            ) : null}
                          </div>
                          <div className="flex flex-wrap gap-3">
                            {!sessionHasPayment ? (
                            <Button className="rounded-xl bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))] hover:bg-[hsl(var(--accent))]/90" onClick={tryOpenAddItems} data-testid="staff-add-items-button">
                              <Plus className="h-4 w-4" />
                              Add items
                            </Button>
                            ) : null}
                            <Button variant="outline" className="rounded-xl border-teal-500/60 bg-teal-500/10 text-teal-800 hover:bg-teal-500/20 hover:text-teal-900 dark:text-teal-200 dark:hover:text-teal-100" onClick={() => setReceiptOpen(true)} data-testid="staff-view-receipt-button">
                              <Receipt className="h-4 w-4" />
                              View Receipt
                            </Button>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="outline" className="rounded-xl border-amber-500/60 bg-amber-500/10 text-amber-900 hover:bg-amber-500/20 hover:text-amber-950 dark:text-amber-200 dark:hover:text-amber-100" data-testid="staff-table-actions-button">
                                  Table actions
                                  <ChevronDown className="ml-1 h-4 w-4 opacity-70" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end" className="w-56 rounded-xl" data-testid="staff-table-actions-menu">
                                <DropdownMenuItem onSelect={() => setTimeout(openSplitBillDialog, 0)} data-testid="staff-table-actions-split">
                                  <Users className="mr-2 h-4 w-4" />
                                  {(selectedSession.split_receipts || []).length > 0 ? "Edit split" : "Split bill"}
                                </DropdownMenuItem>
                                <DropdownMenuItem onSelect={() => { setMoveTableOpen(true); setMoveTableNumber(selectedSession.table_number); }} data-testid="staff-table-actions-move">
                                  <ArrowRight className="mr-2 h-4 w-4" />
                                  Move table
                                </DropdownMenuItem>
                                <DropdownMenuItem onSelect={() => setTimeout(() => setMoveItemDialog({ open: true, selectedItems: {}, destTable: "", managerPin: "", busy: false }), 0)} data-testid="staff-table-actions-move-item">
                                  <ArrowLeftRight className="mr-2 h-4 w-4" />
                                  Move item
                                </DropdownMenuItem>
                                <DropdownMenuItem onSelect={() => setTimeout(() => openVoidItemsFromActions(), 0)} data-testid="staff-table-actions-void-items">
                                  <Ban className="mr-2 h-4 w-4" />
                                  Void items
                                </DropdownMenuItem>
                                <DropdownMenuItem onSelect={() => { setMergeTableOpen(true); setMergeTargetId(""); }} data-testid="staff-table-actions-merge">
                                  <Users className="mr-2 h-4 w-4" />
                                  Merge tables
                                </DropdownMenuItem>
                                {selectedSession.channel !== "tab" ? (
                                <DropdownMenuItem onSelect={() => setTimeout(() => { loadStaffAccounts(); setMoveToAccountOpen(true); }, 0)} data-testid="staff-table-actions-account">
                                  <Wallet className="mr-2 h-4 w-4" />
                                  Move to Tab
                                </DropdownMenuItem>
                                ) : null}
                                <DropdownMenuItem
                                  onSelect={() => { setTimeout(() => requestCloseTable(), 0); }}
                                  className="text-destructive focus:text-destructive"
                                  data-testid="staff-table-actions-close"
                                >
                                  <X className="mr-2 h-4 w-4" />
                                  Close table
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>
                        </div>

                        {/* Compact status pills — "Pending" covers
                            everything that's placed but not yet delivered
                            (status = pending OR confirmed). */}
                        <div className="flex flex-wrap items-center gap-1.5">
                          <span className="inline-flex items-center gap-1.5 rounded-full border border-border bg-secondary/60 px-2.5 py-1 text-xs font-medium">
                            <span className="h-1.5 w-1.5 rounded-full bg-amber-500" />
                            Pending
                            <span className="ml-0.5 tabular-nums font-semibold">{(selectedSession.pending_orders || 0) + (selectedSession.confirmed_orders || 0)}</span>
                          </span>
                          <span className="inline-flex items-center gap-1.5 rounded-full border border-border bg-secondary/60 px-2.5 py-1 text-xs font-medium">
                            <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                            Completed
                            <span className="ml-0.5 tabular-nums font-semibold">{selectedSession.completed_orders}</span>
                          </span>
                        </div>

                        {/* Session 30.10 — pending orders with live "pending for" timers */}
                        <PendingOrdersPanel orders={selectedSession.orders} thresholdMin={longWaitMin} testIdPrefix={`staff-pending-${selectedSession.id}`} />

                        <div className="space-y-4">
                          {(selectedSession.orders || []).map((order) => (
                            <Card key={order.id} className="rounded-[22px] border-border bg-background">
                              <CardContent className="space-y-4 p-4">
                                <div className="flex items-start justify-between gap-4">
                                  <div>
                                    <div className="flex flex-wrap items-center gap-2">
                                      <p className="font-semibold">{order.order_number}</p>
                                      <Badge variant="outline" className="capitalize">{order.source === "customer" ? "Online order" : order.source}</Badge>
                                      {order.payment_status === "cash_at_counter" ? (
                                        <Badge className="rounded-full border border-amber-500/40 bg-amber-500/15 text-[10px] font-semibold text-amber-800 hover:bg-amber-500/15 dark:text-amber-200" data-testid={`staff-order-cash-badge-${order.id}`}>Cash due</Badge>
                                      ) : order.payment_status === "paid" ? (
                                        <Badge className="rounded-full border border-emerald-500/40 bg-emerald-500/15 text-[10px] font-semibold text-emerald-800 hover:bg-emerald-500/15 dark:text-emerald-200">Paid online</Badge>
                                      ) : null}
                                    </div>
                                    <p className="mt-1 text-sm text-muted-foreground">{getOrderTimeLabel(order.created_at)}</p>
                                    {order.customer_name || order.customer_phone ? (
                                      <p className="mt-1 flex flex-wrap items-center gap-x-2 text-sm font-medium" data-testid={`staff-order-guest-${order.id}`}>
                                        {order.customer_name ? <span>{order.customer_name}</span> : null}
                                        {order.customer_phone ? (
                                          <a href={`tel:${String(order.customer_phone).replace(/\s+/g, "")}`} className="inline-flex items-center gap-1 text-sky-700 underline-offset-2 hover:underline dark:text-sky-300">
                                            <Phone className="h-3.5 w-3.5" /> {order.customer_phone}
                                          </a>
                                        ) : null}
                                      </p>
                                    ) : null}
                                  </div>
                                  <div className="flex items-center gap-2">
                                    {order._pending_sync || order._local_only ? (
                                      <Badge variant="outline" className="rounded-lg border-amber-500/40 bg-amber-500/10 text-[10px] text-amber-700 dark:text-amber-300" title="Saved on this device — still being sent to the server. Do not re-enter it.">
                                        Syncing…
                                      </Badge>
                                    ) : null}
                                    <StatusBadge status={order.status} />
                                  </div>
                                </div>
                                <div className="space-y-2 rounded-2xl border border-border bg-secondary/60 p-4">
                                  {(order.items || []).map((item, lineIdx) => {
                                    const voided = Boolean(item.voided);
                                    const canVoidLine = !voided && order.status !== "voided" && order.status !== "completed" && item.status !== "delivered" && item.status !== "ready";
                                    return (
                                      <div key={`${order.id}-${lineIdx}`} className={`flex items-start justify-between gap-3 text-sm ${voided ? "text-muted-foreground line-through" : ""}`} data-testid={`staff-order-line-${order.id}-${lineIdx}`}>
                                        <div className="min-w-0">
                                          <p className="font-medium">{item.quantity} × {item.name}</p>
                                          {Array.isArray(item.selected_sides) && item.selected_sides.length ? (
                                            <p className="mt-0.5 text-xs text-muted-foreground">+ {item.selected_sides.map((s) => s.name).join(", ")}</p>
                                          ) : null}
                                          {Array.isArray(item.prep_selections) && item.prep_selections.length ? (
                                            <p className="mt-0.5 text-xs text-muted-foreground">
                                              {item.prep_selections.map((p) => `${p.label}: ${p.choice}`).join(" · ")}
                                            </p>
                                          ) : null}
                                          {voided ? (
                                            <p className="mt-0.5 text-[10px] font-semibold uppercase tracking-wide text-destructive no-underline">Voided</p>
                                          ) : null}
                                        </div>
                                        <div className="flex shrink-0 items-center gap-2">
                                          <p className="whitespace-nowrap font-semibold">{formatPrice(item.line_total)}</p>
                                          {canVoidLine ? (
                                            <Button
                                              type="button"
                                              variant="ghost"
                                              size="icon"
                                              className="h-7 w-7 rounded-full text-destructive hover:bg-destructive/15 hover:text-destructive"
                                              onClick={() => openVoidLine(order.id, lineIdx, order)}
                                              aria-label="Void this line"
                                              data-testid={`staff-void-line-${order.id}-${lineIdx}`}
                                            >
                                              <X className="h-4 w-4" />
                                            </Button>
                                          ) : null}
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                                <div className="flex flex-wrap items-center justify-between gap-3">
                                  <div className="text-sm text-muted-foreground">Total {formatPrice(order.total)}</div>
                                  <div className="flex flex-wrap gap-3">
                                    {order.status === "confirmed" ? (
                                      <Button className="rounded-xl bg-emerald-600 text-white hover:bg-emerald-700 dark:bg-emerald-500 dark:hover:bg-emerald-600" disabled={busyAction === order.id + "completed"} onClick={() => updateOrderStatus(order.id, "completed")} data-testid="order-complete-button">
                                        <Check className="h-4 w-4" />
                                        Complete
                                      </Button>
                                    ) : null}
                                  </div>
                                </div>
                              </CardContent>
                            </Card>
                          ))}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
            ) : (
              <EmptyState icon={ClipboardList} title="No open tables" description="New customer orders will open table sessions automatically." />
            )}
          </TabsContent>


          {/* Active Tables Tab - Direct table details view */}
          <TabsContent value="active-tables" className="space-y-5">
            {loading ? (
              <DashboardLoadingState />
            ) : (
              <>
                {/* Selected Table Details - Show directly without grid */}
                {selectedSession && (
                  <Card className="rounded-[24px] border-border bg-card">
                    <CardContent className="p-0">
                                  <div className="space-y-5 p-5">
                    <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                          <div>
                            <p className="text-sm text-muted-foreground">Active table</p>
                            <div className="mt-1 flex items-center gap-3">
                              <h2 className="font-['Space_Grotesk'] text-4xl font-semibold tracking-tight">Table {selectedSession.table_number}</h2>
                              {sessionHasPayment ? (
                                <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/15 px-3 py-1 text-xs font-bold text-emerald-700 dark:text-emerald-300" data-testid="staff-selected-table-paid-pill">
                                  ✓ PAID
                                </span>
                              ) : null}
                            </div>
                            <p className="mt-2 text-sm text-muted-foreground">{selectedSession.order_count} orders · {selectedSession.total_items} items · {formatPrice(selectedSession.total_value)}</p>
                            <p className="mt-1 text-xs text-muted-foreground" data-testid={`active-table-assigned-${selectedSession.id}`}>
                              Assigned waiter: <span className="font-medium text-foreground">{selectedSession.assigned_user_name || "Unassigned"}</span>
                            </p>
                            {sessionHasPayment ? (
                              <p className="mt-1 text-xs font-semibold text-emerald-700 dark:text-emerald-400" data-testid="staff-selected-table-paid-hint">
                                Payment recorded — safe to close via Table actions.
                              </p>
                            ) : null}
                          </div>
                          <div className="flex flex-wrap gap-3">
                            {!sessionHasPayment ? (
                            <Button className="rounded-xl bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))] hover:bg-[hsl(var(--accent))]/90" onClick={tryOpenAddItems} data-testid="staff-active-add-items-button">
                              <Plus className="h-4 w-4" />
                              Add items
                            </Button>
                            ) : null}
                            <Button variant="outline" className="rounded-xl border-teal-500/60 bg-teal-500/10 text-teal-800 hover:bg-teal-500/20 hover:text-teal-900 dark:text-teal-200 dark:hover:text-teal-100" onClick={() => setReceiptOpen(true)} data-testid="staff-active-view-receipt-button">
                              <Receipt className="h-4 w-4" />
                              View Receipt
                            </Button>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="outline" className="rounded-xl border-amber-500/60 bg-amber-500/10 text-amber-900 hover:bg-amber-500/20 hover:text-amber-950 dark:text-amber-200 dark:hover:text-amber-100" data-testid="staff-active-table-actions-button">
                                  Table actions
                                  <ChevronDown className="ml-1 h-4 w-4 opacity-70" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end" className="w-56 rounded-xl">
                                <DropdownMenuItem onSelect={() => setTimeout(openSplitBillDialog, 0)}>
                                  <Users className="mr-2 h-4 w-4" />
                                  {(selectedSession.split_receipts || []).length > 0 ? "Edit split" : "Split bill"}
                                </DropdownMenuItem>
                                <DropdownMenuItem onSelect={() => { setMoveTableOpen(true); setMoveTableNumber(selectedSession.table_number); }}>
                                  <ArrowRight className="mr-2 h-4 w-4" />
                                  Move table
                                </DropdownMenuItem>
                                <DropdownMenuItem onSelect={() => setTimeout(() => setMoveItemDialog({ open: true, selectedItems: {}, destTable: "", managerPin: "", busy: false }), 0)}>
                                  <ArrowLeftRight className="mr-2 h-4 w-4" />
                                  Move item
                                </DropdownMenuItem>
                                <DropdownMenuItem onSelect={() => setTimeout(() => openVoidItemsFromActions(), 0)}>
                                  <Ban className="mr-2 h-4 w-4" />
                                  Void items
                                </DropdownMenuItem>
                                <DropdownMenuItem onSelect={() => { setMergeTableOpen(true); setMergeTargetId(""); }}>
                                  <Users className="mr-2 h-4 w-4" />
                                  Merge tables
                                </DropdownMenuItem>
                                {selectedSession.channel !== "tab" ? (
                                <DropdownMenuItem onSelect={() => setTimeout(() => { loadStaffAccounts(); setMoveToAccountOpen(true); }, 0)}>
                                  <Wallet className="mr-2 h-4 w-4" />
                                  Move to Tab
                                </DropdownMenuItem>
                                ) : null}
                                <DropdownMenuItem
                                  onSelect={() => { setTimeout(() => requestCloseTable(), 0); }}
                                  className="text-destructive focus:text-destructive"
                                >
                                  <X className="mr-2 h-4 w-4" />
                                  Close table
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>
                        </div>

                        {/* Compact status pills — "Pending" covers
                            everything that's placed but not yet delivered
                            (status = pending OR confirmed). */}
                        <div className="flex flex-wrap items-center gap-1.5">
                          <span className="inline-flex items-center gap-1.5 rounded-full border border-border bg-secondary/60 px-2.5 py-1 text-xs font-medium">
                            <span className="h-1.5 w-1.5 rounded-full bg-amber-500" />
                            Pending
                            <span className="ml-0.5 tabular-nums font-semibold">{(selectedSession.pending_orders || 0) + (selectedSession.confirmed_orders || 0)}</span>
                          </span>
                          <span className="inline-flex items-center gap-1.5 rounded-full border border-border bg-secondary/60 px-2.5 py-1 text-xs font-medium">
                            <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                            Completed
                            <span className="ml-0.5 tabular-nums font-semibold">{selectedSession.completed_orders}</span>
                          </span>
                        </div>

                        {/* Session 30.10 — pending orders with live "pending for" timers */}
                        <PendingOrdersPanel orders={selectedSession.orders} thresholdMin={longWaitMin} testIdPrefix={`staff-pending-${selectedSession.id}`} />

                        <div className="space-y-3">
                          {(selectedSession.orders || []).map((order) => (
                            <Card key={order.id} className="overflow-hidden rounded-2xl border-border bg-background">
                              <CardContent className="p-4">
                                <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                                  <div className="flex flex-wrap items-center gap-2">
                                    <span className="text-sm font-semibold">{order.order_number}</span>
                                    <span className="text-xs text-muted-foreground">{order.source === "customer" ? "Online order" : "Staff"}</span>
                                    {order.payment_status === "cash_at_counter" ? (
                                      <Badge className="rounded-full border border-amber-500/40 bg-amber-500/15 text-[10px] font-semibold text-amber-800 hover:bg-amber-500/15 dark:text-amber-200" data-testid={`staff-order-cash-badge-${order.id}`}>Cash due</Badge>
                                    ) : order.payment_status === "paid" ? (
                                      <Badge className="rounded-full border border-emerald-500/40 bg-emerald-500/15 text-[10px] font-semibold text-emerald-800 hover:bg-emerald-500/15 dark:text-emerald-200">Paid online</Badge>
                                    ) : null}
                                  </div>
                                  <div className="flex items-center gap-2">
                                    {order._pending_sync || order._local_only ? (
                                      <Badge variant="outline" className="rounded-lg border-amber-500/40 bg-amber-500/10 text-[10px] text-amber-700 dark:text-amber-300" title="Saved on this device — still being sent to the server. Do not re-enter it." data-testid={`order-syncing-badge-${order.id}`}>
                                        Syncing…
                                      </Badge>
                                    ) : null}
                                    <StatusBadge status={order.status} />
                                    <span className="text-xs text-muted-foreground">{getOrderTimeLabel(order.created_at)}</span>
                                  </div>
                                </div>
                                {order.stock_notice ? (
                                  <p className="mb-2 rounded-lg border border-amber-500/40 bg-amber-500/10 px-2 py-1 text-xs text-amber-800 dark:text-amber-200" data-testid={`order-stock-notice-${order.id}`}>
                                    Changed at placement: {order.stock_notice}
                                  </p>
                                ) : null}
                                {order.customer_name || order.customer_phone ? (
                                  <p className="-mt-1 mb-3 flex flex-wrap items-center gap-x-2 text-sm font-medium" data-testid={`staff-order-guest-${order.id}`}>
                                    {order.customer_name ? <span>{order.customer_name}</span> : null}
                                    {order.customer_phone ? (
                                      <a href={`tel:${String(order.customer_phone).replace(/\s+/g, "")}`} className="inline-flex items-center gap-1 text-sky-700 underline-offset-2 hover:underline dark:text-sky-300">
                                        <Phone className="h-3.5 w-3.5" /> {order.customer_phone}
                                      </a>
                                    ) : null}
                                  </p>
                                ) : null}
                                <div className="space-y-2">
                                  {(order.items || []).map((item, itemIndex) => {
                                    // Session 73 — the server's `line_total` includes charged sides;
                                    // price × qty showed a R7.50 burger on a R67.50 line.
                                    const itemTotal = Number(item.line_total ?? ((item.price || 0) * (item.quantity || 0))) || 0;
                                    const voided = Boolean(item.voided);
                                    const canVoidLine = !voided && order.status !== "voided" && order.status !== "completed" && item.status !== "delivered" && item.status !== "ready";
                                    return (
                                      <div
                                        key={item.id || `${order.id}-item-${itemIndex}`}
                                        className={`flex items-start justify-between gap-2 text-sm ${voided ? "text-muted-foreground line-through" : ""}`}
                                      >
                                        <span className="min-w-0 flex-1">
                                          {item.quantity}× {item.name}
                                          {voided ? <span className="ml-2 text-[10px] font-semibold uppercase tracking-wide text-destructive no-underline">Voided</span> : null}
                                          {Array.isArray(item.selected_sides) && item.selected_sides.length ? (
                                            <span className="block text-xs text-muted-foreground">
                                              {item.selected_sides.map((sd) => (sd.charged ? `${sd.name} (+${formatPrice(Number(sd.price) || 0)})` : `${sd.name} (included)`)).join(", ")}
                                            </span>
                                          ) : null}
                                        </span>
                                        <div className="flex shrink-0 items-center gap-1.5">
                                          <span className="font-medium">{formatPrice(itemTotal)}</span>
                                          {canVoidLine ? (
                                            <Button
                                              type="button"
                                              variant="ghost"
                                              size="icon"
                                              className="h-6 w-6 rounded-full text-destructive hover:bg-destructive/15 hover:text-destructive"
                                              onClick={() => openVoidLine(order.id, itemIndex, order)}
                                              aria-label="Void this line"
                                              data-testid={`staff-void-line-alt-${order.id}-${itemIndex}`}
                                            >
                                              <X className="h-3.5 w-3.5" />
                                            </Button>
                                          ) : null}
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                                <div className="mt-3 flex items-center justify-between border-t border-border pt-3">
                                  <span className="text-sm font-semibold">Total</span>
                                  <span className="text-sm font-semibold">{formatPrice(order.total)}</span>
                                </div>
                                {/* Session 31.9 — "Mark all served": lets staff clear an order
                                    that went out without (or beyond) the kitchen/bar screens,
                                    so long-wait alerts and pending counts stop nagging. */}
                                {["pending", "confirmed"].includes(String(order.status || "").toLowerCase())
                                  && !order.awaiting_staff_confirmation
                                  && (order.items || []).some((it) => it && !it.voided && String(it.status || "").toLowerCase() !== "delivered") ? (
                                  <div className="mt-3 flex justify-end">
                                    <Button
                                      type="button"
                                      variant="outline"
                                      className="h-9 rounded-xl border-emerald-500/50 text-emerald-700 hover:bg-emerald-500/10 hover:text-emerald-800 dark:text-emerald-300"
                                      disabled={busyAction === order.id + "served"}
                                      onClick={() => markOrderServed(order.id)}
                                      data-testid={`staff-order-mark-served-${order.id}`}
                                    >
                                      <Check className="h-4 w-4" />
                                      {busyAction === order.id + "served" ? "Marking…" : "Mark all served"}
                                    </Button>
                                  </div>
                                ) : null}
                              </CardContent>
                            </Card>
                          ))}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}
              </>
            )}
          </TabsContent>
        </Tabs>
      </div>

      <Dialog open={openTableDialog.open} onOpenChange={(open) => setOpenTableDialog((d) => ({ ...d, open }))}>
        <DialogContent className="rounded-[24px] border-border bg-background sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Open table</DialogTitle>
            <DialogDescription>Start a fresh session so you can place orders on behalf of a customer who'd rather not use the menu themselves.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="open-table-number">Table number or name</Label>
            <FastInput
              id="open-table-number"
              autoFocus
              value={openTableDialog.tableNumber}
              onChange={(e) => setOpenTableDialog((d) => ({ ...d, tableNumber: e.target.value }))}
              onKeyDown={(e) => { if (e.key === "Enter") submitOpenTable(); }}
              placeholder="e.g., 5  •  Bar – John  •  Patio 2"
              className="h-11 rounded-xl"
              data-testid="staff-open-table-input"
            />
            <p className="text-xs text-muted-foreground">Use a number for normal tables, or a name (like "Bar – John") for bar seats and unnumbered spots. If a session is already open with that label, you'll be taken to it.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-xl" onClick={() => setOpenTableDialog({ open: false, tableNumber: "" })}>Cancel</Button>
            <Button className="rounded-xl bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))] hover:bg-[hsl(var(--accent))]/90" onClick={submitOpenTable} data-testid="staff-open-table-confirm">
              Open table
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      {/* Session 31.19 — Full-screen "Add items" menu. Categories are a
          scrollable chip row at the top (like the Retail dashboard), items
          are a tap-friendly grid, and the running order sits in a pinned
          bar at the bottom. Test ids are unchanged from the accordion. */}
      <Dialog open={addItemsOpen} onOpenChange={(o) => {
        if (!o) setCatalogCategoryId("");
        setAddItemsOpen(o);
      }}>
        <DialogContent
          className="left-0 top-0 flex h-[100dvh] max-h-none w-screen max-w-none translate-x-0 translate-y-0 flex-col gap-0 overflow-hidden rounded-none border-0 bg-background p-0 sm:max-w-none sm:rounded-none data-[state=open]:slide-in-from-left-0 data-[state=open]:slide-in-from-top-0 data-[state=closed]:slide-out-to-left-0 data-[state=closed]:slide-out-to-top-0 [&>button]:hidden"
          data-testid="staff-add-items-dialog"
        >
          {(() => {
            const inSearch = !!addItemsSearchTerm;
            const showingAll = !inSearch && (!activeCatalogCategoryId || activeCatalogCategoryId === "__all__");
            const activeCat = visibleCatalogCategories.find((c) => c.id === activeCatalogCategoryId);
            // What the grid shows: search → flat matches; a category → its
            // items; nothing picked → every category as its own section.
            const sections = inSearch
              ? [{ id: "__search__", name: `Results for "${addItemsSearchTerm}"`, items: sortedAddItemsRenderRows }]
              : showingAll
                ? visibleCatalogCategories.map((cat) => ({
                    id: cat.id,
                    name: cat.name,
                    items: staffCatalogSortAZ
                      ? [...(cat.items || [])].sort((a, b) => String(a?.name || "").localeCompare(String(b?.name || ""), undefined, { sensitivity: "base" }))
                      : (cat.items || []),
                  }))
                : [{ id: activeCat?.id || "cat", name: activeCat?.name || "Items", items: sortedAddItemsRenderRows }];
            const totalShown = sections.reduce((n, sct) => n + sct.items.length, 0);
            const renderItem = (item) => (
              <StaffAddItemCard
                key={item.id}
                item={item}
                quantity={menuItemQuantities[item.id] || 0}
                stagedCount={stagedLineQtyForItem(item.id)}
                needsConfig={itemNeedsConfig(item)}
                inSearch={inSearch}
                onIncrement={onStaffCardIncrement}
                onDecrement={onStaffCardDecrement}
              />
            );
            return (
              <>
                {/* ── Top bar: title · search · sort · close ── */}
                <div className="shrink-0 border-b border-border bg-card/95 px-3 pt-3 pb-2 backdrop-blur sm:px-5" data-testid="staff-add-items-topbar">
                  <div className="flex items-center gap-2 sm:gap-3">
                    <div className="min-w-0 flex-1">
                      <DialogTitle className="truncate font-['Space_Grotesk'] text-xl leading-tight sm:text-2xl">Table {selectedSession?.table_number} · Add items</DialogTitle>
                      <DialogDescription className="hidden text-xs sm:block">Tap an item to add it. Tap again for more. Review before it fires to the kitchen.</DialogDescription>
                    </div>
                    <div className="relative hidden w-72 md:block">
                      <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                      <FastInput
                        value={addItemsSearchTerm}
                        onChange={(e) => { setAddItemsSearchTerm(e.target.value); if (e.target.value) setCatalogCategoryId(""); }}
                        placeholder="Search items…"
                        className="h-11 rounded-xl bg-background pl-10"
                        data-testid="staff-add-items-search-input"
                      />
                    </div>
                    <label className="hidden items-center gap-2 rounded-xl border border-border bg-secondary/40 px-3 py-2 text-xs lg:flex" data-testid="staff-catalog-sort-az-row">
                      <span className="font-medium">A → Z</span>
                      <Switch checked={staffCatalogSortAZ} onCheckedChange={setStaffCatalogSortAZ} data-testid="staff-catalog-sort-az-switch" aria-label="Sort items alphabetically" />
                    </label>
                    <Button type="button" variant="outline" className="h-11 w-11 shrink-0 rounded-xl p-0" onClick={() => setAddItemsOpen(false)} aria-label="Close" data-testid="staff-add-items-close">
                      <X className="h-5 w-5" />
                    </Button>
                  </div>
                  <div className="relative mt-2 md:hidden">
                    <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                    <FastInput
                      value={addItemsSearchTerm}
                      onChange={(e) => { setAddItemsSearchTerm(e.target.value); if (e.target.value) setCatalogCategoryId(""); }}
                      placeholder="Search items…"
                      className="h-11 rounded-xl bg-background pl-10"
                      data-testid="staff-add-items-search-input-mobile"
                    />
                  </div>
                  {/* ── Category chip row (scrolls sideways) ── */}
                  <div className="-mx-3 mt-2 flex gap-2 overflow-x-auto px-3 pb-1 sm:-mx-5 sm:px-5 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden" data-testid="staff-add-items-category-row" style={{ WebkitOverflowScrolling: "touch" }}>
                    <button
                      type="button"
                      onClick={() => { setAddItemsSidesMode("other"); setCatalogCategoryId(""); setAddItemsSearchTerm(""); }}
                      className={`inline-flex h-10 shrink-0 items-center gap-1.5 rounded-full border px-4 text-sm font-semibold transition-colors ${addItemsSidesMode === "other" && showingAll ? "border-[hsl(var(--primary))] bg-[hsl(var(--primary))] text-primary-foreground" : "border-border bg-card text-muted-foreground hover:text-foreground"}`}
                      data-testid="staff-add-items-mode-other"
                    >
                      All
                    </button>
                    {addItemsSidesMode === "other" ? visibleCatalogCategories.map((cat) => {
                      const active = !inSearch && activeCatalogCategoryId === cat.id;
                      return (
                        <button
                          key={cat.id}
                          type="button"
                          onClick={() => { setAddItemsSearchTerm(""); setCatalogCategoryId(active ? "" : cat.id); }}
                          className={`inline-flex h-10 shrink-0 items-center gap-1.5 rounded-full border px-4 text-sm font-semibold transition-colors ${active ? "border-[hsl(var(--accent))] bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))]" : "border-border bg-card text-foreground hover:bg-secondary"}`}
                          aria-pressed={active}
                          data-testid={`staff-add-items-category-row-${cat.id}`}
                        >
                          {cat.name}
                          <span className={`rounded-full px-1.5 text-[10px] ${active ? "bg-black/10" : "bg-secondary text-muted-foreground"}`}>{(cat.items || []).length}</span>
                        </button>
                      );
                    }) : null}
                    <button
                      type="button"
                      onClick={() => { setAddItemsSidesMode(addItemsSidesMode === "sides" ? "other" : "sides"); setCatalogCategoryId(""); setAddItemsSearchTerm(""); }}
                      className={`inline-flex h-10 shrink-0 items-center gap-1.5 rounded-full border px-4 text-sm font-semibold transition-colors ${addItemsSidesMode === "sides" ? "border-[hsl(var(--primary))] bg-[hsl(var(--primary))] text-primary-foreground" : "border-dashed border-border bg-card text-muted-foreground hover:text-foreground"}`}
                      data-testid="staff-add-items-mode-sides"
                    >
                      Sides & Extras
                    </button>
                    {addItemsSidesMode === "sides" ? visibleCatalogCategories.map((cat) => {
                      const active = !inSearch && activeCatalogCategoryId === cat.id;
                      return (
                        <button
                          key={cat.id}
                          type="button"
                          onClick={() => { setAddItemsSearchTerm(""); setCatalogCategoryId(active ? "" : cat.id); }}
                          className={`inline-flex h-10 shrink-0 items-center gap-1.5 rounded-full border px-4 text-sm font-semibold transition-colors ${active ? "border-[hsl(var(--accent))] bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))]" : "border-border bg-card text-foreground hover:bg-secondary"}`}
                          aria-pressed={active}
                          data-testid={`staff-add-items-category-row-${cat.id}`}
                        >
                          {cat.name}
                          <span className={`rounded-full px-1.5 text-[10px] ${active ? "bg-black/10" : "bg-secondary text-muted-foreground"}`}>{(cat.items || []).length}</span>
                        </button>
                      );
                    }) : null}
                  </div>
                </div>

                {/* ── Item grid ── */}
                <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-3 py-3 sm:px-5" style={{ WebkitOverflowScrolling: "touch" }} data-testid="staff-add-items-grid">
                  <p className="mb-2 text-xs text-muted-foreground" data-testid="staff-add-items-result-count">
                    {inSearch
                      ? `${totalShown} match${totalShown === 1 ? "" : "es"} for "${addItemsSearchTerm}"`
                      : showingAll
                        ? `${totalShown} item${totalShown === 1 ? "" : "s"} across ${visibleCatalogCategories.length} categor${visibleCatalogCategories.length === 1 ? "y" : "ies"}`
                        : `${totalShown} item${totalShown === 1 ? "" : "s"} in ${activeCat?.name || "this category"}`}
                  </p>
                  {totalShown === 0 ? (
                    <p className="rounded-2xl border border-dashed border-border bg-secondary/40 p-8 text-center text-sm text-muted-foreground">
                      {inSearch ? `No items match "${addItemsSearchTerm}".` : "No items here yet."}
                    </p>
                  ) : sections.map((sct) => (
                    sct.items.length === 0 ? null : (
                      <section key={sct.id} className="mb-5" data-testid={`staff-add-items-section-${sct.id}`}>
                        {showingAll || inSearch ? (
                          <h3 className="mb-2 flex items-center gap-3 font-['Space_Grotesk'] text-lg font-semibold" data-testid={`staff-add-items-section-title-${sct.id}`}>
                            <span>{sct.name} <span className="text-sm font-normal text-muted-foreground">· {sct.items.length}</span></span>
                            <span aria-hidden="true" className="h-px flex-1 bg-border" />
                          </h3>
                        ) : null}
                        <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6">
                          {sct.items.map(renderItem)}
                        </div>
                      </section>
                    )
                  ))}
                </div>

                {/* ── Pinned order bar ── */}
                <div className="shrink-0 border-t border-border bg-card/95 px-3 py-3 backdrop-blur sm:px-5" data-testid="staff-add-items-cart-bar">
                  {stagedConfiguredLines.length > 0 ? (
                    <div className="mb-2 flex gap-2 overflow-x-auto pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden" data-testid="staff-staged-lines">
                      {stagedConfiguredLines.map((line) => (
                        <span key={line.local_id} className="inline-flex shrink-0 items-center gap-1.5 rounded-full border border-border bg-background px-3 py-1.5 text-xs">
                          <span className="font-medium">{line.quantity > 1 ? `${line.quantity}× ` : ""}{line.item_name}</span>
                          {line.prep_selections?.length ? <span className="text-muted-foreground">· {line.prep_selections.map((pp) => pp.choice).join(", ")}</span> : null}
                          <button type="button" onClick={() => removeStagedLine(line.local_id)} className="ml-1 text-muted-foreground hover:text-foreground" aria-label="Remove line"><X className="h-3.5 w-3.5" /></button>
                        </span>
                      ))}
                    </div>
                  ) : null}
                  <div className="flex items-center gap-3">
                    <div className="min-w-0 flex-1">
                      <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Current order</p>
                      <p className="truncate text-base font-semibold tabular-nums" data-testid="staff-add-items-cart-summary">
                        {selectedAddItemsCount ? `${selectedAddItemsCount} item${selectedAddItemsCount === 1 ? "" : "s"} · ${formatPrice(addItemsReviewTotal)}` : "Nothing added yet"}
                      </p>
                    </div>
                    <Button type="button" variant="outline" className="hidden h-12 rounded-xl px-4 sm:inline-flex" onClick={() => setAddItemsOpen(false)} data-testid="staff-add-items-cancel">
                      Cancel
                    </Button>
                    <Button
                      type="button"
                      disabled={!selectedAddItemsCount || busyAction === "add-items"}
                      className="h-12 rounded-xl bg-[hsl(var(--accent))] px-5 text-base text-[hsl(var(--accent-foreground))] hover:bg-[hsl(var(--accent))]/90"
                      onClick={() => setAddItemsReviewOpen(true)}
                      data-testid="staff-add-items-review-button"
                    >
                      <ClipboardCheck className="h-5 w-5" />
                      Review order{selectedAddItemsCount ? ` · ${selectedAddItemsCount}` : ""}
                    </Button>
                  </div>
                </div>
              </>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* SideSelectionSheet reused for the staff flow — lets the waiter pick
          sides, answer prep prompts, and add a note for items that need it. */}
      {/* Session 70.G — in-app confirm (replaces window.confirm, which exits fullscreen) */}
      <AlertDialog open={Boolean(staffConfirm)} onOpenChange={(o) => { if (!o) setStaffConfirm(null); }}>
        <AlertDialogContent className="rounded-[24px] sm:max-w-md" data-testid="staff-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle data-testid="staff-confirm-title">{staffConfirm?.title}</AlertDialogTitle>
            {staffConfirm?.description ? <AlertDialogDescription>{staffConfirm.description}</AlertDialogDescription> : null}
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-xl" data-testid="staff-confirm-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction className="rounded-xl" onClick={() => { const fn = staffConfirm?.onConfirm; setStaffConfirm(null); if (fn) fn(); }} data-testid="staff-confirm-accept">
              {staffConfirm?.confirmLabel || "Confirm"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <SideSelectionSheet
        item={staffSheetItem}
        sidesLibrary={menuCatalog?.sides_library || []}
        onClose={() => setStaffSheetItem(null)}
        onConfirm={handleStaffSheetConfirm}
        audience="staff"
      />

      {/* Session 27.19 — Review Order dialog.
          Opens on top of the Add Items dialog when the waiter taps
          "Review order". Lists every line with qty, prep prompts,
          sides, notes and subtotal so it can be read back to the
          customer. "Place order" here is the ONLY path that fires
          submitAddItems() — the previous button was a foot-gun that
          committed with no readback opportunity. */}
      <Dialog open={addItemsReviewOpen} onOpenChange={(v) => { if (!v) setAddItemsReviewOpen(false); }}>
        <DialogContent className="rounded-[28px] border-border bg-background sm:max-w-lg" data-testid="staff-review-order-dialog">
          <DialogHeader>
            <DialogTitle>
              Review order · Table {selectedSession?.table_number || "—"}
            </DialogTitle>
            <DialogDescription>
              Confirm the items with the customer, then tap "Place order" to send them to the kitchen and bar.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 max-h-[55vh] overflow-y-auto pr-1">
            {addItemsReviewLines.length === 0 ? (
              <p className="rounded-2xl border border-dashed border-border bg-secondary/40 p-4 text-center text-sm text-muted-foreground">
                No items selected yet.
              </p>
            ) : (
              addItemsReviewLines.map((line) => (
                <div
                  key={line.key}
                  className="rounded-2xl border border-border bg-secondary/30 p-3"
                  data-testid={`staff-review-line-${line.key}`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <p className="font-['Space_Grotesk'] text-base font-semibold">
                        {line.quantity} × {line.name}
                      </p>
                      {line.prep_selections.length > 0 ? (
                        <p className="mt-0.5 text-xs text-muted-foreground">
                          {line.prep_selections.map((p) => `${p.label}: ${p.choice}`).join(" · ")}
                        </p>
                      ) : null}
                      {line.sides.length > 0 ? (
                        <p className="mt-0.5 text-xs text-muted-foreground">
                          Sides: {line.sides.map((s) => s.charged ? `${s.name} (+${formatPrice(s.price)})` : `${s.name} (included)`).join(", ")}
                        </p>
                      ) : null}
                      {line.note ? (
                        <p className="mt-0.5 text-xs italic text-muted-foreground">
                          Note: "{line.note}"
                        </p>
                      ) : null}
                    </div>
                    <div className="text-right shrink-0">
                      <p className="text-xs uppercase tracking-wide text-muted-foreground">Line</p>
                      <p className="font-['Space_Grotesk'] text-base font-semibold tabular-nums" data-testid={`staff-review-line-total-${line.key}`}>
                        {formatPrice(line.line_total)}
                      </p>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
          {addItemsReviewLines.length > 0 ? (
            <div className="flex items-center justify-between rounded-2xl border border-border bg-secondary/50 px-4 py-3">
              <span className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">Order total</span>
              <span className="font-['Space_Grotesk'] text-xl font-semibold tabular-nums" data-testid="staff-review-order-total">
                {formatPrice(addItemsReviewTotal)}
              </span>
            </div>
          ) : null}
          <DialogFooter className="gap-2">
            <Button
              type="button"
              variant="outline"
              className="rounded-xl"
              onClick={() => setAddItemsReviewOpen(false)}
              data-testid="staff-review-back-button"
            >
              <ArrowLeft className="h-4 w-4" />
              Back
            </Button>
            <Button
              type="button"
              disabled={!selectedAddItemsCount || busyAction === "add-items"}
              className="rounded-xl bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))] hover:bg-[hsl(var(--accent))]/90"
              onClick={async () => {
                // Session 27.23 — Snapshot the item count BEFORE calling
                // submitAddItems, then close the review dialog
                // immediately. Previously we kept the review open while
                // submit ran, and the moment submitAddItems cleared
                // menuItemQuantities + stagedConfiguredLines, the memo
                // recomputed to an empty array — the user saw a
                // confusing "No items selected yet" + "Placing…" state
                // for a couple of seconds until the dialog finally
                // closed. Closing first solves that entirely: the
                // dialog vanishes on click, a spinner-style toast
                // handles the "sending" affordance, and errors are
                // surfaced via the same toast channel.
                setAddItemsReviewOpen(false);
                const ok = await submitAddItems();
                if (!ok) {
                  // If submit failed, re-open the review dialog so the
                  // waiter can retry with the same items still selected
                  // (menuItemQuantities + stagedConfiguredLines were
                  // NOT cleared on failure). The failure toast from
                  // submitAddItems is already visible.
                  setAddItemsReviewOpen(true);
                }
              }}
              data-testid="staff-review-place-order-button"
            >
              <Check className="h-4 w-4" />
              {busyAction === "add-items" ? "Placing…" : `Place order · ${selectedAddItemsCount} item${selectedAddItemsCount === 1 ? "" : "s"}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={moveTableOpen} onOpenChange={setMoveTableOpen}>
        <DialogContent className="rounded-[28px] border-border bg-background sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Move table</DialogTitle>
            <DialogDescription>Enter the new table number for this active session.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="move-table-number">New table</Label>
            <FastInput id="move-table-number" value={moveTableNumber} onChange={(event) => setMoveTableNumber(event.target.value)} className="h-11 rounded-xl" data-testid="staff-move-table-input" />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" className="rounded-xl" onClick={() => setMoveTableOpen(false)}>
              Cancel
            </Button>
            <Button type="button" disabled={!moveTableNumber || busyAction === "move-table"} className="rounded-xl bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))] hover:bg-[hsl(var(--accent))]/90" onClick={submitMoveTable} data-testid="staff-move-table-confirm-button">
              {busyAction === "move-table" ? "Saving..." : "Move table"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Move Item Dialog — move delivered items to another table */}
      <Dialog open={moveItemDialog.open} onOpenChange={(open) => { if (!open) setMoveItemDialog((p) => ({ ...p, open: false })); }}>
        <DialogContent className="flex max-h-[92vh] flex-col gap-3 overflow-hidden rounded-[28px] border-border bg-background p-4 sm:max-w-lg sm:p-6" data-testid="move-item-dialog">
          {/* Session 69 — build the body only while open (a closed dialog used to re-render its whole list on every tap) */}
          {moveItemDialog.open ? (<>
          <DialogHeader className="shrink-0">
            <DialogTitle className="flex items-center gap-2">
              <ArrowLeftRight className="h-5 w-5" />
              Move item — Table {selectedSession?.table_number}
            </DialogTitle>
            <DialogDescription>Select delivered items to move to another table.</DialogDescription>
          </DialogHeader>
          <div className="flex-1 space-y-4 overflow-y-auto">
            {/* Item list */}
            <div className="space-y-2">
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Select items</p>
              {(() => {
                const activeOrders = (selectedSession?.orders || []).filter((o) => o && o.status !== "voided");
                if (activeOrders.length === 0) return <p className="text-sm text-muted-foreground">No orders on this table.</p>;
                const allItems = [];
                activeOrders.forEach((order) => {
                  (order.items || []).forEach((item, idx) => {
                    if (item.voided) return;
                    const key = `${order.id}#${idx}`;
                    const isDelivered = item.status === "delivered" || item.status === "ready";
                    allItems.push({ key, item, order, idx, isDelivered });
                  });
                });
                if (allItems.length === 0) return <p className="text-sm text-muted-foreground">No items on this table.</p>;
                return (
                  <div className="space-y-1.5 rounded-xl border border-border p-2">
                    {allItems.map(({ key, item, isDelivered }) => (
                      <label key={key} className={`flex items-center gap-3 rounded-lg px-3 py-2 text-sm transition-colors ${isDelivered ? "cursor-pointer hover:bg-secondary" : "cursor-not-allowed opacity-50"}`} data-testid={`move-item-row-${key}`}>
                        <input type="checkbox" disabled={!isDelivered} checked={!!moveItemDialog.selectedItems[key]} onChange={(e) => setMoveItemDialog((p) => ({ ...p, selectedItems: { ...p.selectedItems, [key]: e.target.checked } }))} className="h-4 w-4 rounded border-border accent-[hsl(var(--accent))]" />
                        <span className="flex-1 font-medium">{item.name}{item.quantity > 1 ? ` x${item.quantity}` : ""}</span>
                        <span className="text-xs text-muted-foreground capitalize">{item.status || "pending"}</span>
                        {!isDelivered && <span className="text-[10px] text-muted-foreground">(not delivered)</span>}
                      </label>
                    ))}
                  </div>
                );
              })()}
            </div>
            {/* Destination table */}
            <div className="space-y-2">
              <Label htmlFor="move-item-dest" className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Destination table</Label>
              <FastInput id="move-item-dest" value={moveItemDialog.destTable} onChange={(e) => setMoveItemDialog((p) => ({ ...p, destTable: e.target.value }))} placeholder="e.g. 5" className="h-11 rounded-xl" data-testid="move-item-dest-input" />
              {/* Quick-pick active tables */}
              {(() => {
                const otherTables = (tableSessions || []).filter((s) => s.status === "open" && s.id !== selectedSession?.id).map((s) => s.table_number).filter(Boolean);
                if (otherTables.length === 0) return null;
                return (
                  <div className="flex flex-wrap gap-1.5 pt-1">
                    {otherTables.slice(0, 12).map((tn) => (
                      <button key={tn} type="button" onClick={() => setMoveItemDialog((p) => ({ ...p, destTable: tn }))} className={`h-8 rounded-lg border px-3 text-xs font-semibold transition-colors ${moveItemDialog.destTable === tn ? "border-[hsl(var(--accent))] bg-[hsl(var(--accent))]/10 text-[hsl(var(--accent-foreground))]" : "border-border bg-secondary text-foreground hover:bg-secondary/70"}`} data-testid={`move-item-table-${tn}`}>
                        {tn}
                      </button>
                    ))}
                  </div>
                );
              })()}
            </div>
            {/* Manager PIN */}
            <div className="space-y-2">
              <Label htmlFor="move-item-pin" className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Manager PIN</Label>
              <FastInput id="move-item-pin" type="password" inputMode="numeric" value={moveItemDialog.managerPin} onChange={(e) => setMoveItemDialog((p) => ({ ...p, managerPin: e.target.value }))} placeholder="Enter manager PIN" className="h-11 rounded-xl" data-testid="move-item-pin-input" />
            </div>
          </div>
          <DialogFooter className="shrink-0">
            <Button variant="outline" className="rounded-xl" onClick={() => setMoveItemDialog((p) => ({ ...p, open: false }))} data-testid="move-item-cancel">Cancel</Button>
            <Button disabled={moveItemDialog.busy || !moveItemDialog.destTable || !moveItemDialog.managerPin || moveItemDialog.managerPin.length < 4 || !Object.values(moveItemDialog.selectedItems).some(Boolean)} className="rounded-xl bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))] hover:bg-[hsl(var(--accent))]/90" onClick={submitMoveItems} data-testid="move-item-confirm">{moveItemDialog.busy ? "Moving…" : "Move items"}</Button>
          </DialogFooter>
          </>) : null}
        </DialogContent>
      </Dialog>

      {/* Merge Tables — combine another active session INTO the current one. */}
      <Dialog open={mergeTableOpen} onOpenChange={setMergeTableOpen}>
        <DialogContent className="rounded-[28px] border-border bg-background sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Merge tables</DialogTitle>
            <DialogDescription>Select another active table to merge into the current table. All orders, payments and tips will transfer.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1">
              <Label className="text-xs">Current table</Label>
              <div className="flex h-11 items-center rounded-xl border border-border bg-secondary px-3 text-sm font-medium" data-testid="staff-merge-current-table">
                Table {selectedSession?.table_number || ""}
              </div>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Target table</Label>
              {(() => {
                const others = (tableSessions || []).filter(
                  (t) => t.status === "open" && t.id !== selectedSession?.id,
                );
                if (others.length === 0) {
                  return <p className="rounded-xl bg-amber-50 px-3 py-2 text-xs text-amber-800 dark:bg-amber-950/30 dark:text-amber-200">No other active tables to merge into.</p>;
                }
                return (
                  <Select value={mergeTargetId} onValueChange={setMergeTargetId}>
                    <SelectTrigger className="h-11 rounded-xl" data-testid="staff-merge-target-select">
                      <SelectValue placeholder="Pick a table…" />
                    </SelectTrigger>
                    <SelectContent>
                      {others.map((t) => (
                        <SelectItem key={t.id} value={t.id}>Table {t.table_number} ({t.order_count} order{t.order_count === 1 ? "" : "s"})</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                );
              })()}
              <p className="text-xs text-muted-foreground">The current table will close after merging. Orders move to the target.</p>
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" className="rounded-xl" onClick={() => setMergeTableOpen(false)}>
              Cancel
            </Button>
            <Button
              type="button"
              disabled={!mergeTargetId || busyAction === "merge-tables"}
              className="rounded-xl bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))] hover:bg-[hsl(var(--accent))]/90"
              onClick={submitMergeTables}
              data-testid="staff-merge-tables-confirm-button"
            >
              {busyAction === "merge-tables" ? "Merging…" : "Merge tables"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Move to Tab — park outstanding balance on a pay-later customer tab. */}
      <Dialog open={moveToAccountOpen} onOpenChange={setMoveToAccountOpen}>
        <DialogContent className="rounded-[28px] border-border bg-background sm:max-w-md" data-testid="staff-move-to-tab-dialog">
          {/* Session 69 — build the body only while open (a closed dialog used to re-render its whole list on every tap) */}
          {moveToAccountOpen ? (<>
          <DialogHeader>
            <DialogTitle>Move to tab</DialogTitle>
            <DialogDescription>Close the table and put the outstanding balance on a customer tab. Add more items to the tab any time; settle it from Tabs when they pay.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {selectedSession ? (() => {
              const st = sessionPaymentState(selectedSession);
              return (
                <div className="flex items-center justify-between rounded-xl border border-amber-400/60 bg-amber-500/10 px-3 py-2 text-sm" data-testid="staff-move-to-tab-outstanding">
                  <div>
                    <p className="font-semibold">Balance moving to the tab</p>
                    <p className="text-[11px] text-muted-foreground">
                      Bill {formatPrice(st.owed)}{st.paidAmount > 0 ? ` · already paid ${formatPrice(st.paidAmount)}` : ""}
                    </p>
                  </div>
                  <span className="text-base font-semibold tabular-nums text-amber-700 dark:text-amber-300" data-testid="staff-move-to-tab-outstanding-value">{formatPrice(st.outstanding)}</span>
                </div>
              );
            })() : null}
            {staffAccounts.accounts.length ? (
              <div className="space-y-1">
                <Label className="text-xs">Add to an existing tab</Label>
                <div className="flex max-h-40 flex-col gap-1 overflow-y-auto rounded-xl border border-border p-1" data-testid="staff-move-to-tab-existing">
                  <button type="button" onClick={() => setAccountDraft((d) => ({ ...d, account_id: "" }))} className={`flex items-center justify-between rounded-lg px-3 py-2 text-left text-sm ${!accountDraft.account_id ? "bg-[hsl(var(--primary))] text-primary-foreground" : "hover:bg-secondary"}`} data-testid="staff-move-to-tab-new">
                    <span>New tab</span>
                  </button>
                  {staffAccounts.accounts.map((a) => (
                    <button key={a.id} type="button" onClick={() => setAccountDraft((d) => ({ ...d, account_id: a.id }))} className={`flex items-center justify-between rounded-lg px-3 py-2 text-left text-sm ${accountDraft.account_id === a.id ? "bg-[hsl(var(--primary))] text-primary-foreground" : "hover:bg-secondary"}`} data-testid={`staff-move-to-tab-existing-${a.id}`}>
                      <span className="truncate">{a.customer_name}</span>
                      <span className="shrink-0 tabular-nums text-xs opacity-80">{formatPrice(a.remaining_balance ?? 0)} owed</span>
                    </button>
                  ))}
                </div>
              </div>
            ) : null}
            {accountDraft.account_id ? null : (<>
            <div className="space-y-1">
              <Label htmlFor="acc-name" className="text-xs">Customer name <span className="text-destructive">*</span></Label>
              <FastInput
                id="acc-name"
                value={accountDraft.customer_name}
                onChange={(e) => setAccountDraft((d) => ({ ...d, customer_name: e.target.value }))}
                className="h-11 rounded-xl"
                placeholder="e.g. John Smith"
                data-testid="staff-account-name-input"
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="acc-phone" className="text-xs">Phone number (optional)</Label>
              <FastInput
                id="acc-phone"
                value={accountDraft.phone}
                onChange={(e) => setAccountDraft((d) => ({ ...d, phone: e.target.value }))}
                className="h-11 rounded-xl"
                placeholder="e.g. 082 123 4567"
                data-testid="staff-account-phone-input"
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="acc-notes" className="text-xs">Notes (optional)</Label>
              <Textarea
                id="acc-notes"
                value={accountDraft.notes}
                onChange={(e) => setAccountDraft((d) => ({ ...d, notes: e.target.value }))}
                className="min-h-[64px] rounded-xl"
                placeholder="e.g. regular customer, bar tab"
                data-testid="staff-account-notes-input"
              />
            </div>
            </>)}
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" className="rounded-xl" onClick={() => setMoveToAccountOpen(false)}>
              Cancel
            </Button>
            <Button
              type="button"
              disabled={(!accountDraft.account_id && !accountDraft.customer_name.trim()) || busyAction === "move-to-account"}
              className="rounded-xl bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))] hover:bg-[hsl(var(--accent))]/90"
              onClick={submitMoveToAccount}
              data-testid="staff-account-confirm-button"
            >
              {busyAction === "move-to-account" ? "Saving…" : accountDraft.account_id ? "Add to tab" : "Open tab"}
            </Button>
          </DialogFooter>
          </>) : null}
        </DialogContent>
      </Dialog>

      {/* Receipt Dialog */}
      <Dialog open={receiptOpen} onOpenChange={setReceiptOpen}>
        <DialogContent className="flex max-h-[90vh] flex-col rounded-[28px] border-border bg-background sm:max-w-lg">
          {/* Session 69 — build the body only while open (a closed dialog used to re-render its whole list on every tap) */}
          {receiptOpen ? (<>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Receipt className="h-5 w-5" />
              Table Receipt
            </DialogTitle>
            <DialogDescription>Complete order summary for Table {selectedSession?.table_number}</DialogDescription>
          </DialogHeader>
          
          {selectedSession && (() => {
            // Filter out fully-voided orders AND rely on backend order
            // totals which are already recomputed after every void
            // (see _recompute_order_totals). Do NOT sum raw items[] —
            // that would count voided lines and inflate the total,
            // causing legit payments to be reported as overpayments
            // and voided items to be double-charged.
            const activeOrders = (selectedSession.orders || []).filter((o) => o && o.status !== "voided" && o.voided !== true);
            // ── Split-bill tab filtering ──────────────────────────────
            const splitReceipts = selectedSession.split_receipts || null;
            const groupIdByKey = {};
            if (splitReceipts) {
              splitReceipts.forEach((g) => {
                (g.items || []).forEach((it) => { groupIdByKey[`${it.order_id}#${it.line_index}`] = g.id; });
              });
            }
            const isItemVisible = (orderId, lineIdx) => {
              if (!splitReceipts || activeSplitTab === "all") return true;
              const gid = groupIdByKey[`${orderId}#${lineIdx}`];
              if (activeSplitTab === "unassigned") return !gid;
              return gid === activeSplitTab;
            };
            // Recompute totals over visible (filtered) items using each
            // line's `line_total` (VAT-inclusive). VAT is derived from
            // the business rate — precise enough for split receipts.
            let grossTotal = 0;
            activeOrders.forEach((o) => {
              (o.items || []).forEach((it, i) => {
                if (it.voided) return;
                if (!isItemVisible(o.id, i)) return;
                grossTotal += Number(it.line_total ?? ((it.price || 0) * (it.quantity || 0))) || 0;
              });
            });
            const vatRateForCalc = Number(context?.business?.vat_rate ?? 15);
            // Voucher discount (flat R amount) — applied before payment.
            // Only counted on the "All" tab so per-guest splits aren't
            // double-discounted.
            const appliedVoucher = selectedSession.applied_voucher || null;
            let voucherDiscount = appliedVoucher && (activeSplitTab === "all" || !splitReceipts)
              ? Math.min(grossTotal, Number(appliedVoucher.discount_amount) || 0)
              : 0;
            let totalBeforeFee = Math.max(0, grossTotal - voucherDiscount);
            // Session 34 — automatic service fee on the guest total (after
            // the voucher). Per-guest split tabs get their proportional
            // share. Settled bills use the pct locked on the session.
            let serviceFeePct = sessionServiceFeePct(selectedSession, context?.business);
            let serviceFee = serviceFeeOn(totalBeforeFee, serviceFeePct);
            let total = Math.round((totalBeforeFee + serviceFee) * 100) / 100;
            // ?? not || so a 0% VAT business displays "VAT (0%)".
            const displayVatRate = context?.business?.vat_rate ?? 15;
            const cashNum = parseFloat(cashPaid) || 0;
            const cardNum = parseFloat(cardPaid) || 0;
            // ── Session 31.26 — Multi-tender ledger numbers ──
            // Everything below is measured against what is STILL OWED
            // (total − tenders already on the ledger), not the bill total,
            // so a second card / an EFT top-up computes correctly.
            const ledgerPayments = Array.isArray(selectedSession.payments) ? selectedSession.payments : [];
            const ledgerT = ledgerTotals(ledgerPayments);
            const savedCash = Number(selectedSession.cash_paid || 0);
            const savedCard = Number(selectedSession.card_paid || 0);
            const savedEft = Number(selectedSession.eft_paid || 0);
            // Ledger totals win; fall back to the mirrored fields for
            // sessions that pre-date the ledger.
            let paidSoFar = ledgerPayments.length ? ledgerT.paid : Math.round((savedCash + savedCard + savedEft) * 100) / 100;
            const isGuestView = Boolean(splitReceipts && activeSplitTab !== "all" && activeSplitTab !== "unassigned");
            const _EPS = 0.005;
            // Session 62 — the fee follows the money: once the tenders cover
            // the ITEMS the bill is settled; whatever was collected above the
            // items is the fee (R0 if none). The table never chases the fee.
            let feeNotCollected = (!isGuestView && paidSoFar > 0.005 && totalBeforeFee > 0 && paidSoFar >= totalBeforeFee - 0.005 && paidSoFar < total - 0.005)
              ? Math.round((total - paidSoFar) * 100) / 100 : 0;
            let outstandingDue = isGuestView ? total : (feeNotCollected > 0 ? 0 : Math.max(0, Math.round((total - paidSoFar) * 100) / 100));
            let isFullySettled = total > 0 && outstandingDue <= _EPS;
            // ── Session 72 — ONE SOURCE OF TRUTH FOR MONEY ──────────────
            // Whenever the FULL bill is shown (not a per-guest split tab),
            // the server's figures replace the phone's recomputation. The
            // server decides whether a tender settles a table (it is what
            // "Close table" checks); the phone used to re-add line items with
            // its own voucher / discount / fee rules and could disagree —
            // "Partially paid · R49.50 owed" on a bill the server had
            // already accepted as paid. Those fields ride on every session
            // payload; optimistic sessions that do not have them yet keep
            // the local estimate until the next reconcile (~0.5 s).
            const srvOwed = Number(selectedSession.amount_owed);
            const serverMoney = !isGuestView && activeSplitTab !== "unassigned"
              && Number.isFinite(srvOwed) && typeof selectedSession.fully_paid === "boolean"
              && Number.isFinite(Number(selectedSession.total_value));
            if (serverMoney) {
              grossTotal = Math.round(Number(selectedSession.total_value) * 100) / 100;
              voucherDiscount = Math.min(grossTotal, Number(appliedVoucher?.discount_amount) || 0);
              totalBeforeFee = Number.isFinite(Number(selectedSession.amount_before_service_fee))
                ? Number(selectedSession.amount_before_service_fee)
                : Math.max(0, grossTotal - voucherDiscount);
              serviceFeePct = Number.isFinite(Number(selectedSession.service_fee_pct)) ? Number(selectedSession.service_fee_pct) : serviceFeePct;
              serviceFee = Number.isFinite(Number(selectedSession.service_fee_amount)) ? Number(selectedSession.service_fee_amount) : serviceFee;
              total = srvOwed;
              paidSoFar = Number.isFinite(Number(selectedSession.paid_amount)) ? Number(selectedSession.paid_amount) : paidSoFar;
              outstandingDue = Number.isFinite(Number(selectedSession.outstanding_amount)) ? Number(selectedSession.outstanding_amount) : Math.max(0, Math.round((total - paidSoFar) * 100) / 100);
              feeNotCollected = Number(selectedSession.service_fee_not_collected) > _EPS && outstandingDue <= _EPS ? Number(selectedSession.service_fee_not_collected) : 0;
              isFullySettled = total > 0 && outstandingDue <= _EPS;
            }
            const subtotal = vatRateForCalc > 0 ? grossTotal / (1 + vatRateForCalc / 100) : grossTotal;
            const vatAmount = grossTotal - subtotal;
            const isPartiallyPaid = paidSoFar > _EPS && !isFullySettled;
            // ── Stepped payment workflow numbers ──
            const cashReceivedNum = parseFloat(cashReceived) || 0;
            const tipDraftNum = parseFloat(tipDraft) || 0;
            const cashTipAuto = Math.max(0, cashReceivedNum - outstandingDue);
            const cashShort = Math.max(0, outstandingDue - cashReceivedNum);
            const cashFullySettled = cashReceivedNum + _EPS >= outstandingDue && outstandingDue > 0;
            const cashChange = 0; // legacy — change is now treated as tip
            // Split flow scratch numbers (legacy — Split is now "add tenders one at a time")
            const splitCashNum = parseFloat(splitCash) || 0;
            const splitCardNum = parseFloat(splitCard) || 0;
            const splitPaid = splitCashNum + splitCardNum;
            const splitRemaining = Math.max(0, outstandingDue - splitPaid);
            const splitTip = Math.max(0, splitPaid - outstandingDue);
            const splitFullySettled = splitPaid + _EPS >= outstandingDue && outstandingDue > 0 && splitCashNum > 0 && splitCardNum > 0;
            // Card / EFT flow scratch numbers — "amount charged" includes the
            // typed tip; anything beyond what is owed is also a tip.
            const cardAmountNum = parseFloat(cardAmount) || 0;
            const cardTipAuto = Math.max(0, cardAmountNum - outstandingDue);
            const cardShort = Math.max(0, outstandingDue - cardAmountNum);
            const cardFullySettled = cardAmountNum + _EPS >= outstandingDue && outstandingDue > 0;
            const eftAmountNum = parseFloat(eftAmount) || 0;
            const eftTipAuto = Math.max(0, eftAmountNum - outstandingDue);
            const eftShort = Math.max(0, outstandingDue - eftAmountNum);
            const tenderDraft = computeTenderDraft(outstandingDue);
            // What's already been recorded for this session.
            const savedMethod = selectedSession.payment_method;
            const recordedMethod = savedMethod || (savedCash > 0 && savedCard > 0 ? "split" : savedCard > 0 ? "card" : savedCash > 0 ? "cash" : savedEft > 0 ? "eft" : null);
            const isAlreadyPaid = isFullySettled || Boolean(selectedSession.moved_to_account_id);
            // Print mode mirrors saved values (used when staff hits Print).
            const totalPaid = paidSoFar;
            const tipNum = ledgerPayments.length ? ledgerT.tip : Number(selectedSession.tip_amount || 0);
            const hasTip = tipNum > 0;
            return (
            <div id="receipt-print-area" className="-mx-1 flex-1 space-y-4 overflow-y-auto px-1 py-1">
              {/* Business Header */}
              <div className="border-b border-border pb-4 text-center">
                <h2 className="text-xl font-semibold">{context?.business?.name}</h2>
                <p className="text-sm text-muted-foreground mt-1">Table {selectedSession.table_number}</p>
                <p className="text-xs text-muted-foreground">{new Date().toLocaleString()}</p>
                {splitReceipts && activeSplitTab !== "all" ? (
                  <p className="text-xs font-semibold text-emerald-700 dark:text-emerald-400 mt-1" data-testid="receipt-split-guest-label">
                    {activeSplitTab === "unassigned"
                      ? "Unassigned items"
                      : (splitReceipts.find((g) => g.id === activeSplitTab)?.name || "Guest")}
                  </p>
                ) : null}
                {(() => {
                  // Per-guest paid banner: only appears when a specific
                  // split tab (not "all"/"unassigned") is open AND a
                  // payment has already been recorded for that split.
                  if (!splitReceipts || activeSplitTab === "all" || activeSplitTab === "unassigned") return null;
                  const paid = (selectedSession.split_payments || []).find((p) => p.split_id === activeSplitTab);
                  if (!paid) return null;
                  return (
                    <div className="mt-2 inline-flex items-center gap-2 rounded-full bg-emerald-500/10 px-3 py-1 text-xs font-semibold text-emerald-700 dark:text-emerald-400 print:hidden" data-testid="receipt-split-paid-badge">
                      Paid · {formatPrice((Number(paid.cash_paid) || 0) + (Number(paid.card_paid) || 0))}
                      {paid.tip_amount ? ` · tip ${formatPrice(paid.tip_amount)}` : ""}
                      <span className="text-[10px] font-normal text-muted-foreground">by {paid.paid_by}</span>
                    </div>
                  );
                })()}
              </div>

              {/* Split-bill tab bar */}
              {splitReceipts ? (
                <div className="print:hidden flex flex-wrap items-center gap-2 border-b border-border pb-3" data-testid="receipt-split-tabs">
                  <button
                    type="button"
                    onClick={() => setActiveSplitTab("all")}
                    className={`h-9 rounded-xl px-3 text-xs font-semibold transition-colors ${activeSplitTab === "all" ? "bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))]" : "bg-secondary text-foreground hover:bg-secondary/70"}`}
                    data-testid="receipt-split-tab-all"
                  >
                    All items
                  </button>
                  {splitReceipts.map((g) => {
                    const isPaid = (selectedSession.split_payments || []).some((p) => p.split_id === g.id);
                    return (
                      <button
                        key={g.id}
                        type="button"
                        onClick={() => setActiveSplitTab(g.id)}
                        className={`h-9 rounded-xl px-3 text-xs font-semibold transition-colors ${activeSplitTab === g.id ? "bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))]" : "bg-secondary text-foreground hover:bg-secondary/70"}`}
                        data-testid={`receipt-split-tab-${g.id}`}
                      >
                        {g.name}
                        {isPaid ? (
                          <span className="ml-1.5 inline-flex items-center rounded-full bg-emerald-500/20 px-1.5 text-[9px] font-bold text-emerald-700 dark:text-emerald-300">✓ Paid</span>
                        ) : null}
                      </button>
                    );
                  })}
                  {(() => {
                    const anyUnassigned = activeOrders.some((o) => (o.items || []).some((it, i) => !it.voided && !groupIdByKey[`${o.id}#${i}`]));
                    if (!anyUnassigned) return null;
                    return (
                      <button
                        type="button"
                        onClick={() => setActiveSplitTab("unassigned")}
                        className={`h-9 rounded-xl px-3 text-xs font-semibold transition-colors ${activeSplitTab === "unassigned" ? "bg-amber-500 text-white" : "bg-amber-500/10 text-amber-700 dark:text-amber-400 hover:bg-amber-500/20"}`}
                        data-testid="receipt-split-tab-unassigned"
                      >
                        Unassigned
                      </button>
                    );
                  })()}
                  <button
                    type="button"
                    onClick={clearSplitBill}
                    disabled={splitBusy}
                    className="ml-auto text-xs underline text-muted-foreground hover:text-destructive disabled:opacity-50"
                    data-testid="receipt-split-clear"
                  >
                    Clear split
                  </button>
                </div>
              ) : null}

              {/* Orders & Items */}
              <div className="space-y-3 print:max-h-none print:overflow-visible">
                {activeOrders.map((order, idx) => {
                  const activeItems = (order.items || [])
                    .map((it, origIdx) => ({ it, origIdx }))
                    .filter(({ it, origIdx }) => !it.voided && isItemVisible(order.id, origIdx));
                  const voidedItems = (order.items || []).filter((it) => it.voided);
                  if (activeItems.length === 0 && (activeSplitTab !== "all" || voidedItems.length === 0)) return null;
                  return (
                    <div key={order.id || idx} className="space-y-2">
                      <div className="flex items-center justify-between text-sm">
                        <span className="font-medium">Order {order.order_number || idx + 1}</span>
                        <span className="print:hidden"><StatusBadge status={order.status} /></span>
                      </div>
                      {activeItems.map(({ it: item, origIdx: itemIdx }) => {
                        const itemTotal = Number(item.line_total ?? ((item.price || 0) * (item.quantity || 0)));
                        const gid = groupIdByKey[`${order.id}#${itemIdx}`];
                        const gName = gid ? (splitReceipts?.find((g) => g.id === gid)?.name) : null;
                        return (
                          <div key={itemIdx} className="flex justify-between text-sm pl-3">
                            <span>
                              {item.quantity}× {item.name}
                              {Array.isArray(item.selected_sides) && item.selected_sides.length ? (
                                <span className="block text-xs text-muted-foreground" data-testid={`receipt-item-sides-${order.id}-${itemIdx}`}>
                                  {item.selected_sides.map((s) => (s.charged ? `${s.name} (+${formatPrice(Number(s.price) || 0)})` : `${s.name} (included)`)).join(", ")}
                                </span>
                              ) : null}
                              {splitReceipts && activeSplitTab === "all" && gName ? (
                                <span className="ml-2 rounded-md bg-emerald-500/10 px-1.5 py-0.5 text-[10px] font-semibold text-emerald-700 dark:text-emerald-400 print:hidden" data-testid={`receipt-item-group-${gid}`}>
                                  {gName}
                                </span>
                              ) : null}
                            </span>
                            <span className="font-medium">{formatPrice(itemTotal)}</span>
                          </div>
                        );
                      })}
                      {voidedItems.length > 0 && activeSplitTab === "all" ? (
                        <div className="pl-3 print:hidden" data-testid={`receipt-voided-lines-${order.id || idx}`}>
                          {voidedItems.map((item, itemIdx) => (
                            <div key={`v-${itemIdx}`} className="flex justify-between text-xs text-muted-foreground line-through">
                              <span>{item.quantity}× {item.name} <span className="no-underline text-[10px] uppercase tracking-wide text-destructive/80 ml-1">Voided</span></span>
                              <span>{formatPrice(Number(item.line_total ?? ((item.price || 0) * (item.quantity || 0))))}</span>
                            </div>
                          ))}
                        </div>
                      ) : null}
                    </div>
                  );
                })}
              </div>

              {/* Totals */}
              <div className="border-t border-border pt-4 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Subtotal</span>
                  <span className="font-medium">{formatPrice(subtotal)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">VAT ({displayVatRate}%)</span>
                  <span className="font-medium">{formatPrice(vatAmount)}</span>
                </div>
                {appliedVoucher ? (
                  <div className="flex items-center justify-between text-sm" data-testid="receipt-voucher-line">
                    <span className="flex items-center gap-2 text-emerald-700 dark:text-emerald-400">
                      <Ticket className="h-3.5 w-3.5" />
                      {appliedVoucher.kind === "reconciliation"
                        ? <span data-testid="receipt-reconciliation-label">Reconciliation adjustment</span>
                        : <>Voucher <span className="font-mono">{appliedVoucher.code}</span></>}
                      {appliedVoucher.kind === "reconciliation" ? null : (
                      <button
                        type="button"
                        onClick={submitRemoveVoucher}
                        disabled={voucherBusy || paymentStep !== "idle"}
                        className="text-xs underline text-muted-foreground hover:text-destructive disabled:opacity-50 print:hidden"
                        data-testid="receipt-voucher-remove"
                      >
                        remove
                      </button>
                      )}
                    </span>
                    <span className="font-medium text-emerald-700 dark:text-emerald-400">−{formatPrice(voucherDiscount)}</span>
                  </div>
                ) : null}
                {serviceFee > 0 ? (
                  <div className="flex items-center justify-between text-sm" data-testid="receipt-service-fee-line">
                    <span className="flex items-center gap-2 text-muted-foreground">
                      <HandCoins className="h-3.5 w-3.5" />
                      Service fee ({serviceFeePct}%)
                    </span>
                    <span className="font-medium" data-testid="receipt-service-fee-value">{formatPrice(serviceFee)}</span>
                  </div>
                ) : null}
                <div className="flex justify-between text-lg font-semibold border-t border-border pt-2">
                  <span>Total</span>
                  <span data-testid="receipt-total-value">{formatPrice(total)}</span>
                </div>
                {serviceFee > 0 ? (
                  <p className="text-[11px] text-muted-foreground" data-testid="receipt-service-fee-note">Includes a {serviceFeePct}% service fee on {formatPrice(totalBeforeFee)}. Extra tips are welcome and optional.</p>
                ) : null}
              </div>

              {/* ──────────────────────────────────────────────────────────
                  Stepped Payment Workflow
                  Forces the cashier to pick Cash/Card/Split BEFORE entering
                  amounts so the wrong tender can't be booked by accident.
                  Hidden on print — print only renders the saved summary.
                  ────────────────────────────────────────────────────────── */}
              <div className="print:hidden">
                {/* IDLE: receipt review + Start Payment CTA */}
                {paymentStep === "idle" && (
                  <div className="space-y-3">
                    {/* Per-guest payment CTA — shown when the receipt is
                        filtered to a specific split tab. Each guest pays
                        their own subtotal; once all guests have paid the
                        backend automatically settles the whole session. */}
                    {splitReceipts && activeSplitTab !== "all" && activeSplitTab !== "unassigned" ? (() => {
                      const paid = (selectedSession.split_payments || []).find((p) => p.split_id === activeSplitTab);
                      const groupName = splitReceipts.find((g) => g.id === activeSplitTab)?.name || "Guest";
                      if (paid) {
                        return (
                          <div className="rounded-2xl border border-emerald-500/40 bg-emerald-500/10 p-4" data-testid="split-paid-panel">
                            <div className="flex items-start justify-between gap-3">
                              <div className="flex items-center gap-2">
                                <CheckCircle2 className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
                                <div>
                                  <p className="text-sm font-semibold">{groupName} paid</p>
                                  <p className="text-xs text-muted-foreground capitalize" data-testid="split-paid-methods">
                                    {Array.isArray(paid.tenders) && paid.tenders.length
                                      ? paid.tenders.map((t) => `${t.label || t.method}${Number(t.tip) > 0 ? ` (tip ${formatPrice(t.tip)})` : ""} ${formatPrice(t.amount)}`).join(" · ")
                                      : `${paid.payment_method}${paid.tip_amount ? ` · Tip ${formatPrice(paid.tip_amount)}` : ""}`}
                                  </p>
                                </div>
                              </div>
                              <p className="text-sm font-semibold">{formatPrice((Number(paid.cash_paid) || 0) + (Number(paid.card_paid) || 0) + (Number(paid.eft_paid) || 0))}</p>
                            </div>
                            <Button
                              type="button"
                              variant="outline"
                              onClick={() => openPaySplitDialog(activeSplitTab, groupName, total)}
                              className="mt-3 h-10 w-full rounded-xl"
                              data-testid="split-edit-payment-button"
                            >
                              Edit payment
                            </Button>
                          </div>
                        );
                      }
                      return (
                        <Button
                          type="button"
                          onClick={() => openPaySplitDialog(activeSplitTab, groupName, total)}
                          className="h-14 w-full rounded-2xl bg-emerald-600 text-base font-semibold text-white shadow-sm hover:bg-emerald-600/90"
                          data-testid="split-pay-guest-button"
                        >
                          <Receipt className="h-5 w-5" />
                          Pay {groupName} · {formatPrice(total)}
                        </Button>
                      );
                    })() : null}
                    {/* ── Session 31.26 — Multi-tender PAYMENTS LEDGER ──
                        Every Cash / Card / EFT tender recorded on this table,
                        each with its own tip, plus what is still owed. The
                        table can only close once the ledger covers the bill. */}
                    {!isGuestView && (ledgerPayments.length > 0 || paidSoFar > _EPS) ? (
                      <div
                        className={`rounded-2xl border p-4 ${isFullySettled ? "border-emerald-500/40 bg-emerald-500/10" : "border-amber-400/60 bg-amber-500/10"}`}
                        data-testid="receipt-payments-ledger"
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex items-center gap-2">
                            {isFullySettled ? (
                              <CheckCircle2 className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
                            ) : (
                              <AlertTriangle className="h-5 w-5 text-amber-600 dark:text-amber-400" />
                            )}
                            <div>
                              <p className="text-sm font-semibold" data-testid="receipt-payments-status">
                                {isFullySettled ? "Paid in full" : "Partially paid"}
                              </p>
                              <p className="text-xs text-muted-foreground">
                                {ledgerPayments.length || 1} payment{(ledgerPayments.length || 1) === 1 ? "" : "s"}
                                {hasTip ? ` · Tips ${formatPrice(tipNum)}` : ""}
                                {selectedSession.tip_recipient && hasTip ? ` · ${selectedSession.tip_recipient}` : ""}
                                {feeNotCollected > 0 ? <span data-testid="receipt-fee-not-collected"> · Service fee {formatPrice(feeNotCollected)} not collected</span> : null}
                              </p>
                            </div>
                          </div>
                          <p className="text-sm font-semibold" data-testid="receipt-paid-so-far">{formatPrice(paidSoFar)}</p>
                        </div>
                        {ledgerPayments.length > 0 ? (
                          <ul className="mt-3 divide-y divide-border/60 rounded-xl border border-border/60 bg-background/70" data-testid="receipt-payments-list">
                            {ledgerPayments.map((p, idx) => {
                              const m = String(p.method || "").toLowerCase();
                              const Icon = m === "cash" ? Banknote : m === "eft" ? Landmark : CreditCard;
                              const tone = m === "cash" ? "text-emerald-700 dark:text-emerald-300" : m === "eft" ? "text-teal-700 dark:text-teal-300" : "text-sky-700 dark:text-sky-300";
                              return (
                                <li key={p.id || idx} className="flex items-center gap-3 px-3 py-2 text-sm" data-testid={`receipt-payment-row-${idx}`}>
                                  <Icon className={`h-4 w-4 shrink-0 ${tone}`} />
                                  <div className="min-w-0 flex-1">
                                    <p className="truncate font-medium">{p.label || p.split_name || m.toUpperCase()}</p>
                                    <p className="truncate text-[11px] text-muted-foreground">
                                      {Number(p.tip) > 0 ? `Tip ${formatPrice(p.tip)}` : "No tip"}
                                      {p.by ? ` · taken by ${p.by}` : ""}
                                    </p>
                                  </div>
                                  <span className="tabular-nums font-semibold" data-testid={`receipt-payment-amount-${idx}`}>{formatPrice(p.amount)}</span>
                                  {!p.split_id ? (
                                    <button
                                      type="button"
                                      onClick={() => setRemoveTenderPrompt({ open: true, payment: p, pin: "", busy: false })}
                                      className="rounded-md p-1 text-muted-foreground hover:bg-destructive/10 hover:text-destructive focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-destructive"
                                      aria-label={`Remove ${p.label || m} payment`}
                                      title="Remove this payment (manager PIN)"
                                      data-testid={`receipt-payment-remove-${idx}`}
                                    >
                                      <X className="h-3.5 w-3.5" />
                                    </button>
                                  ) : <span className="w-[22px]" />}
                                </li>
                              );
                            })}
                          </ul>
                        ) : null}
                        <div className="mt-3 flex items-baseline justify-between border-t border-border/60 pt-2 text-sm">
                          <span className="text-muted-foreground">{isFullySettled ? "Outstanding" : "Still owed"}</span>
                          <span className={`text-base font-semibold tabular-nums ${isFullySettled ? "text-emerald-700 dark:text-emerald-300" : "text-amber-700 dark:text-amber-300"}`} data-testid="receipt-outstanding-value">
                            {formatPrice(outstandingDue)}
                          </span>
                        </div>
                        {(() => {
                          // Session 35 — make it explicit WHO the gratuity (service fee
                          // + extra tips) is credited to: the waiter who opened the table.
                          const who = String(selectedSession.assigned_user_name || selectedSession.tip_recipient || "").trim();
                          const gratTotal = Math.round((serviceFee + tipNum) * 100) / 100;
                          if (!who || gratTotal <= 0) return null;
                          return (
                            <div className="mt-2 flex items-center justify-between gap-3 rounded-xl bg-background/70 px-3 py-2 text-xs" data-testid="receipt-gratuity-recipient">
                              <span className="flex items-center gap-1.5 text-muted-foreground">
                                <HandCoins className="h-3.5 w-3.5" />
                                {serviceFee > 0 && hasTip ? "Service fee + tip for" : serviceFee > 0 ? "Service fee for" : "Tip for"} <span className="font-semibold text-foreground">{who}</span>
                              </span>
                              <span className="tabular-nums font-semibold" data-testid="receipt-gratuity-recipient-amount">
                                {formatPrice(gratTotal)}
                                {serviceFee > 0 && hasTip ? <span className="ml-1 font-normal text-muted-foreground">({formatPrice(serviceFee)} + {formatPrice(tipNum)})</span> : null}
                              </span>
                            </div>
                          );
                        })()}
                        {isPartiallyPaid ? (() => {
                          // Session 72 — say WHY a paid bill shows a balance:
                          // orders that landed after the settling payment.
                          const paidAt = Date.parse(selectedSession.payment_recorded_at || "");
                          const late = Number.isFinite(paidAt)
                            ? activeOrders.filter((o) => Date.parse(o.created_at || "") > paidAt)
                            : [];
                          if (!late.length) return null;
                          const lateTotal = Math.round(late.reduce((n, o) => n + (Number(o.total) || 0), 0) * 100) / 100;
                          const names = late.flatMap((o) => (o.items || []).filter((it) => it && !it.voided).map((it) => `${it.quantity}× ${it.name}`)).slice(0, 4).join(", ");
                          return (
                            <p className="mt-2 rounded-xl bg-background/70 px-3 py-2 text-xs text-muted-foreground" data-testid="receipt-late-items-note">
                              <span className="font-semibold text-foreground">{formatPrice(lateTotal)} of items were added after the bill was paid</span>
                              {" "}at {new Date(paidAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}{names ? ` (${names})` : ""} — the balance owed is for those items only.
                            </p>
                          );
                        })() : null}
                        {isPartiallyPaid ? (
                          <p className="mt-2 text-xs text-muted-foreground" data-testid="receipt-partial-hint">
                            You can add the next payment now, come back later, or move the balance to a tab from the Actions menu — the table stays open until it is settled.
                          </p>
                        ) : null}
                        {/* Session 72 — the server's chronological account of this
                            bill: orders, tenders (with what was owed after each),
                            removed / reduced payments, voids, vouchers, reopen. */}
                        {!isLocalOrderId(selectedSession.id) ? (
                          <div className="mt-2">
                            <button
                              type="button"
                              onClick={() => (moneyTimeline.open && moneyTimeline.sessionId === selectedSession.id
                                ? setMoneyTimeline((d) => ({ ...d, open: false }))
                                : loadMoneyTimeline(selectedSession.id))}
                              className="inline-flex h-8 items-center gap-1.5 rounded-lg px-2 text-xs font-semibold text-foreground/80 transition-colors hover:bg-background/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--accent))]"
                              data-testid="receipt-money-timeline-toggle"
                            >
                              <History className="h-3.5 w-3.5" />
                              {moneyTimeline.open && moneyTimeline.sessionId === selectedSession.id ? "Hide bill history" : "What happened on this bill?"}
                            </button>
                            {moneyTimeline.open && moneyTimeline.sessionId === selectedSession.id ? (
                              <div className="mt-1 rounded-xl border border-border/60 bg-background/80 p-2" data-testid="receipt-money-timeline">
                                {moneyTimeline.loading ? (
                                  <p className="px-1 py-2 text-xs text-muted-foreground">Loading…</p>
                                ) : moneyTimeline.error ? (
                                  <p className="px-1 py-2 text-xs text-destructive">{String(moneyTimeline.error)}</p>
                                ) : (
                                  <ol className="divide-y divide-border/50">
                                    {(moneyTimeline.data?.events || []).map((ev, i) => {
                                      const t = ev.at ? new Date(ev.at) : null;
                                      const amt = typeof ev.amount === "number" ? ev.amount : null;
                                      const tone = ev.kind === "payment" ? "text-emerald-700 dark:text-emerald-300"
                                        : ev.kind === "order_added" && ev.late ? "text-amber-700 dark:text-amber-300"
                                        : /removed|reduced|voided|rejected|refund/.test(String(ev.kind)) ? "text-rose-700 dark:text-rose-300"
                                        : "text-foreground";
                                      return (
                                        <li key={`${ev.kind}-${i}`} className="flex items-start gap-2 px-1 py-1.5 text-[11px]" data-testid={`receipt-money-event-${i}`}>
                                          <span className="w-12 shrink-0 tabular-nums text-muted-foreground">{t ? t.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "—"}</span>
                                          <span className={`min-w-0 flex-1 ${tone}`}>{ev.text}</span>
                                          {amt !== null ? <span className={`shrink-0 tabular-nums font-semibold ${tone}`}>{amt < 0 ? "−" : ""}{formatPrice(Math.abs(amt))}</span> : null}
                                        </li>
                                      );
                                    })}
                                  </ol>
                                )}
                              </div>
                            ) : null}
                          </div>
                        ) : null}
                      </div>
                    ) : null}
                    {/* Hide the generic "Process payment" button when
                        we're viewing a specific guest tab — that tab has
                        its own per-guest pay CTA above. */}
                    {/* Session 31.27 — tab sessions are paid from this receipt like
                        any table; the backend mirrors each tender onto the tab. */}
                    {!isGuestView ? (
                      <Button
                        type="button"
                        onClick={() => { if (!isAlreadyPaid) setPaymentStep("method"); }}
                        disabled={isAlreadyPaid || total <= 0}
                        className={`h-14 w-full rounded-2xl text-base font-semibold shadow-sm disabled:opacity-60 ${isPartiallyPaid ? "bg-amber-600 text-white hover:bg-amber-600/90" : "bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))] hover:bg-[hsl(var(--accent))]/90"}`}
                        data-testid="receipt-process-payment-button"
                      >
                        <Receipt className="h-5 w-5" />
                        {isAlreadyPaid ? "Paid in full" : isPartiallyPaid ? `Add payment · ${formatPrice(outstandingDue)} owed` : "Process payment"}
                      </Button>
                    ) : null}
                    {isAlreadyPaid && selectedSession && !isGuestView && ledgerPayments.length === 0 ? (
                      // Legacy sessions without a ledger keep the old
                      // manager-PIN "Correct Payment" path.
                      <div className="flex justify-center">
                        <Button
                          type="button"
                          variant="outline"
                          className="h-12 w-full max-w-sm rounded-2xl border-sky-500/40 text-sky-700 hover:bg-sky-500/10 dark:text-sky-300"
                          onClick={() => {
                            const firstOrder = (selectedSession.orders || []).find((o) => o && o.id);
                            if (!firstOrder) { toast.error("No order on this receipt to correct."); return; }
                            const m = (recordedMethod === "card") ? "cash" : "card";
                            setStaffCorrectPayment({ open: true, order: firstOrder, saving: false, values: { new_method: m, cash_paid: m === "cash" || m === "split" ? String(cashNum || total.toFixed(2)) : "0", card_paid: m === "card" || m === "split" ? String(cardNum || total.toFixed(2)) : "0", reason: "", manager_pin: "" } });
                          }}
                          data-testid="staff-receipt-correct-payment-btn"
                        >
                          Correct Payment
                        </Button>
                      </div>
                    ) : null}
                  </div>
                )}

                {/* STEP 1: Choose payment method (Session 31.26 — one tender at a
                    time: Cash, Card or EFT. Several cards / cash + card / EFT top-ups
                    are simply added one after another until the bill is settled.) */}
                {paymentStep === "method" && (
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Step 1 of 3 · How is this payment made?</p>
                      <p className="text-sm font-semibold" data-testid="payment-method-outstanding">{isPartiallyPaid ? "Still owed " : ""}{formatPrice(outstandingDue)}</p>
                    </div>
                    <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                      <button
                        type="button"
                        onClick={() => { setPaymentStep("cash"); setChosenMethod("cash"); setCashReceived(""); setTipDraft(""); }}
                        className="group flex h-24 flex-col items-center justify-center gap-1 rounded-2xl border-2 border-emerald-500/40 bg-emerald-500/10 px-3 text-emerald-700 transition-colors hover:border-emerald-500 hover:bg-emerald-500/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 focus-visible:ring-offset-2 dark:text-emerald-300"
                        data-testid="payment-method-cash"
                      >
                        <Banknote className="h-7 w-7" strokeWidth={2.25} />
                        <span className="text-sm font-semibold">Cash</span>
                      </button>
                      <button
                        type="button"
                        onClick={() => { setPaymentStep("card"); setChosenMethod("card"); setCardAmount(outstandingDue > 0 ? String(outstandingDue.toFixed(2)) : ""); setTipDraft(""); }}
                        className="group flex h-24 flex-col items-center justify-center gap-1 rounded-2xl border-2 border-sky-500/40 bg-sky-500/10 px-3 text-sky-700 transition-colors hover:border-sky-500 hover:bg-sky-500/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-500 focus-visible:ring-offset-2 dark:text-sky-300"
                        data-testid="payment-method-card"
                      >
                        <CreditCard className="h-7 w-7" strokeWidth={2.25} />
                        <span className="text-sm font-semibold">Card</span>
                        {ledgerPayments.some((p) => p.method === "card") ? (
                          <span className="text-[10px] font-medium opacity-80">Card {ledgerPayments.filter((p) => p.method === "card").length + 1}</span>
                        ) : null}
                      </button>
                      <button
                        type="button"
                        onClick={() => { setPaymentStep("eft"); setChosenMethod("eft"); setEftAmount(outstandingDue > 0 ? String(outstandingDue.toFixed(2)) : ""); setTipDraft(""); }}
                        className="group flex h-24 flex-col items-center justify-center gap-1 rounded-2xl border-2 border-teal-500/40 bg-teal-500/10 px-3 text-teal-700 transition-colors hover:border-teal-500 hover:bg-teal-500/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-500 focus-visible:ring-offset-2 dark:text-teal-300"
                        data-testid="payment-method-eft"
                      >
                        <Landmark className="h-7 w-7" strokeWidth={2.25} />
                        <span className="text-sm font-semibold">EFT</span>
                      </button>
                      <button
                        type="button"
                        onClick={() => { setPaymentStep("split"); setChosenMethod("split"); setSplitCash(""); setSplitCard(""); setTipDraft(""); }}
                        className="group flex h-24 flex-col items-center justify-center gap-1 rounded-2xl border-2 border-violet-500/40 bg-violet-500/10 px-3 text-violet-700 transition-colors hover:border-violet-500 hover:bg-violet-500/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-500 focus-visible:ring-offset-2 dark:text-violet-300"
                        data-testid="payment-method-split"
                      >
                        <SplitSquareHorizontal className="h-7 w-7" strokeWidth={2.25} />
                        <span className="text-sm font-semibold">Split</span>
                        <span className="text-[10px] font-medium opacity-80">Cash + Card</span>
                      </button>
                    </div>
                    <p className="text-xs text-muted-foreground" data-testid="payment-method-hint">
                      Use <span className="font-medium text-foreground">Split</span> when one bill is paid part cash, part card. Paying with several cards or an EFT top-up? Record each one in turn — the table stays open until the full amount is covered.
                    </p>
                  </div>
                )}

                {/* STEP 2a: Cash entry */}
                {paymentStep === "cash" && (
                  <div className="space-y-3 rounded-2xl border-2 border-emerald-500/40 bg-emerald-500/10 p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 text-emerald-700 dark:text-emerald-300">
                        <Banknote className="h-5 w-5" strokeWidth={2.25} />
                        <p className="text-sm font-semibold">Cash Payment</p>
                      </div>
                      <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Step 2 of 3</p>
                    </div>
                    <div className="space-y-3">
                      <div className="flex items-baseline justify-between">
                        <span className="text-sm text-muted-foreground">{isPartiallyPaid ? "Still owed" : "Order Total"}</span>
                        <span className="text-lg font-semibold" data-testid="cash-step-due">{formatPrice(outstandingDue)}</span>
                      </div>
                      <div>
                        <Label htmlFor="cash-received" className="text-xs font-medium text-emerald-700 dark:text-emerald-300">Cash received</Label>
                        <FastInput
                          id="cash-received"
                          type="number"
                          inputMode="decimal"
                          step="0.01"
                          min="0"
                          autoFocus
                          placeholder="0.00"
                          value={cashReceived}
                          onChange={(e) => setCashReceived(e.target.value)}
                          className="h-12 rounded-xl border-emerald-500/40 bg-background text-base font-semibold focus-visible:ring-emerald-500"
                          data-testid="cash-received-input"
                        />
                      </div>
                      <div className="rounded-xl bg-background/60 p-3 space-y-2">
                        <div className="flex items-baseline justify-between">
                          <p className="text-[11px] uppercase tracking-wide text-muted-foreground">
                            {cashShort > 0.005 ? "Still owed after this payment" : "Change"}
                          </p>
                          <p className={`text-lg font-semibold ${cashShort > 0.005 ? "text-amber-700 dark:text-amber-300" : "text-emerald-700 dark:text-emerald-300"}`} data-testid="cash-tip-or-remaining">
                            {formatPrice(cashShort > 0.005 ? cashShort : cashTipAuto)}
                          </p>
                        </div>
                        {cashTipAuto > 0.005 ? (
                          <>
                            <div className="flex items-center justify-between gap-3 pt-1">
                              <Label htmlFor="cash-tip-input" className="text-[11px] uppercase tracking-wide text-muted-foreground">Tip (kept from change)</Label>
                              <FastInput
                                id="cash-tip-input"
                                type="number"
                                inputMode="decimal"
                                step="0.01"
                                min="0"
                                max={cashTipAuto.toFixed(2)}
                                placeholder="0.00"
                                value={tipDraft}
                                onChange={(e) => setTipDraft(e.target.value)}
                                className="h-9 w-28 rounded-lg border-border bg-background text-right text-sm font-semibold focus-visible:ring-emerald-500"
                                data-testid="cash-tip-input"
                              />
                            </div>
                            <div className="flex items-baseline justify-between border-t border-border/60 pt-2">
                              <p className="text-[11px] uppercase tracking-wide text-muted-foreground">Return to customer</p>
                              <p className="text-base font-semibold text-emerald-700 dark:text-emerald-300" data-testid="cash-change-return">
                                {formatPrice(Math.max(0, cashTipAuto - tipDraftNum))}
                              </p>
                            </div>
                            {tipDraftNum > cashTipAuto + 0.005 ? (
                              <p className="text-xs text-destructive" data-testid="cash-tip-overflow-warning">Tip can't be more than the change amount ({formatPrice(cashTipAuto)}).</p>
                            ) : null}
                          </>
                        ) : null}
                      </div>
                      {tipDraftNum > 0 && tipOwnerName ? (
                        <p className="text-xs text-muted-foreground" data-testid="cash-tip-attribution">Tip will be attributed to <span className="font-medium text-foreground">{tipOwnerName}</span>.</p>
                      ) : null}
                    </div>
                  </div>
                )}

                {/* STEP 2b: Card entry (Session 31.26 — measured against what is still owed;
                    "amount charged" includes any tip typed below; card never gives change) */}
                {paymentStep === "card" && (
                  <div className="space-y-3 rounded-2xl border-2 border-sky-500/40 bg-sky-500/10 p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 text-sky-700 dark:text-sky-300">
                        <CreditCard className="h-5 w-5" strokeWidth={2.25} />
                        <p className="text-sm font-semibold">Card Payment {ledgerPayments.some((p) => p.method === "card") ? `· Card ${ledgerPayments.filter((p) => p.method === "card").length + 1}` : ""}</p>
                      </div>
                      <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Step 2 of 3</p>
                    </div>
                    <div className="space-y-3">
                      <div className="flex items-baseline justify-between">
                        <span className="text-sm text-muted-foreground">{isPartiallyPaid ? "Still owed" : "Order Total"}</span>
                        <span className="text-lg font-semibold" data-testid="card-step-due">{formatPrice(outstandingDue)}</span>
                      </div>
                      <div>
                        <Label htmlFor="card-amount-input" className="text-xs font-medium text-sky-700 dark:text-sky-300">Amount charged to card</Label>
                        <FastInput
                          id="card-amount-input"
                          type="number"
                          inputMode="decimal"
                          step="0.01"
                          min="0"
                          autoFocus
                          placeholder="0.00"
                          value={cardAmount}
                          onChange={(e) => setCardAmount(e.target.value)}
                          className="h-12 rounded-xl border-sky-500/40 bg-background text-base font-semibold focus-visible:ring-sky-500"
                          data-testid="card-amount-input"
                        />
                        <div className="mt-1.5 flex flex-wrap gap-1.5">
                          <button type="button" onClick={() => setCardAmount(outstandingDue.toFixed(2))} className="rounded-lg border border-border bg-background px-3 py-1 text-xs font-medium hover:bg-secondary" data-testid="card-amount-exact">Full balance {formatPrice(outstandingDue)}</button>
                          <button type="button" onClick={() => setCardAmount((outstandingDue / 2).toFixed(2))} className="rounded-lg border border-border bg-background px-3 py-1 text-xs font-medium hover:bg-secondary" data-testid="card-amount-half">Half</button>
                        </div>
                      </div>
                      <div className="rounded-xl bg-background/60 p-3 space-y-2">
                        <div className="flex items-baseline justify-between">
                          <p className="text-[11px] uppercase tracking-wide text-muted-foreground">Toward the bill</p>
                          <p className="text-base font-semibold" data-testid="card-toward-bill">{formatPrice(tenderDraft?.amount || 0)}</p>
                        </div>
                        <div className="flex items-baseline justify-between">
                          <p className="text-[11px] uppercase tracking-wide text-muted-foreground">
                            {cardShort > 0.005 ? "Still owed after this payment" : "Tip (charged over the bill)"}
                          </p>
                          <p className={`text-lg font-semibold ${cardShort > 0.005 ? "text-amber-700 dark:text-amber-300" : "text-sky-700 dark:text-sky-300"}`} data-testid="card-tip-or-remaining">
                            {formatPrice(cardShort > 0.005 ? cardShort : (tenderDraft?.tip || 0))}
                          </p>
                        </div>
                        {cardTipAuto > 0.005 ? (
                          <p className="text-xs text-muted-foreground" data-testid="card-overpay-note">Card never gives change — the extra {formatPrice(cardTipAuto)} is automatically recorded as a tip.</p>
                        ) : null}
                      </div>
                      {(tenderDraft?.tip || 0) > 0 && tipOwnerName ? (
                        <p className="text-xs text-muted-foreground" data-testid="card-tip-attribution">Tip will be attributed to <span className="font-medium text-foreground">{tipOwnerName}</span>.</p>
                      ) : null}
                    </div>
                  </div>
                )}

                {/* STEP 2c: EFT entry (Session 31.26 — measured against what is still owed;
                    "amount charged" includes any tip typed below; eft never gives change) */}
                {paymentStep === "eft" && (
                  <div className="space-y-3 rounded-2xl border-2 border-teal-500/40 bg-teal-500/10 p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 text-teal-700 dark:text-teal-300">
                        <Landmark className="h-5 w-5" strokeWidth={2.25} />
                        <p className="text-sm font-semibold">EFT Payment</p>
                      </div>
                      <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Step 2 of 3</p>
                    </div>
                    <div className="space-y-3">
                      <div className="flex items-baseline justify-between">
                        <span className="text-sm text-muted-foreground">{isPartiallyPaid ? "Still owed" : "Order Total"}</span>
                        <span className="text-lg font-semibold" data-testid="eft-step-due">{formatPrice(outstandingDue)}</span>
                      </div>
                      <div>
                        <Label htmlFor="eft-amount-input" className="text-xs font-medium text-teal-700 dark:text-teal-300">Amount transferred (EFT)</Label>
                        <FastInput
                          id="eft-amount-input"
                          type="number"
                          inputMode="decimal"
                          step="0.01"
                          min="0"
                          autoFocus
                          placeholder="0.00"
                          value={eftAmount}
                          onChange={(e) => setEftAmount(e.target.value)}
                          className="h-12 rounded-xl border-teal-500/40 bg-background text-base font-semibold focus-visible:ring-teal-500"
                          data-testid="eft-amount-input"
                        />
                        <div className="mt-1.5 flex flex-wrap gap-1.5">
                          <button type="button" onClick={() => setEftAmount(outstandingDue.toFixed(2))} className="rounded-lg border border-border bg-background px-3 py-1 text-xs font-medium hover:bg-secondary" data-testid="eft-amount-exact">Full balance {formatPrice(outstandingDue)}</button>
                          <button type="button" onClick={() => setEftAmount((outstandingDue / 2).toFixed(2))} className="rounded-lg border border-border bg-background px-3 py-1 text-xs font-medium hover:bg-secondary" data-testid="eft-amount-half">Half</button>
                        </div>
                      </div>
                      <div className="rounded-xl bg-background/60 p-3 space-y-2">
                        <div className="flex items-baseline justify-between">
                          <p className="text-[11px] uppercase tracking-wide text-muted-foreground">Toward the bill</p>
                          <p className="text-base font-semibold" data-testid="eft-toward-bill">{formatPrice(tenderDraft?.amount || 0)}</p>
                        </div>
                        <div className="flex items-baseline justify-between">
                          <p className="text-[11px] uppercase tracking-wide text-muted-foreground">
                            {eftShort > 0.005 ? "Still owed after this payment" : "Tip (charged over the bill)"}
                          </p>
                          <p className={`text-lg font-semibold ${eftShort > 0.005 ? "text-amber-700 dark:text-amber-300" : "text-teal-700 dark:text-teal-300"}`} data-testid="eft-tip-or-remaining">
                            {formatPrice(eftShort > 0.005 ? eftShort : (tenderDraft?.tip || 0))}
                          </p>
                        </div>
                        {eftTipAuto > 0.005 ? (
                          <p className="text-xs text-muted-foreground" data-testid="eft-overpay-note">EFT never gives change — the extra {formatPrice(eftTipAuto)} is automatically recorded as a tip.</p>
                        ) : null}
                      </div>
                      {(tenderDraft?.tip || 0) > 0 && tipOwnerName ? (
                        <p className="text-xs text-muted-foreground" data-testid="eft-tip-attribution">Tip will be attributed to <span className="font-medium text-foreground">{tipOwnerName}</span>.</p>
                      ) : null}
                    </div>
                  </div>
                )}

                {/* STEP 2d: Split (Cash + Card) entry — Session 35. One bill paid
                    part cash, part card, recorded in a single step. Cash is applied
                    first; the card covers the rest (card never gives change, so
                    anything over is a tip). */}
                {paymentStep === "split" && (
                  <div className="space-y-3 rounded-2xl border-2 border-violet-500/40 bg-violet-500/10 p-4" data-testid="split-payment-step">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 text-violet-700 dark:text-violet-300">
                        <SplitSquareHorizontal className="h-5 w-5" strokeWidth={2.25} />
                        <p className="text-sm font-semibold">Split Payment · Cash + Card</p>
                      </div>
                      <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Step 2 of 3</p>
                    </div>
                    <div className="space-y-3">
                      <div className="flex items-baseline justify-between">
                        <span className="text-sm text-muted-foreground">{isPartiallyPaid ? "Still owed" : "Order Total"}</span>
                        <span className="text-lg font-semibold" data-testid="split-step-due">{formatPrice(outstandingDue)}</span>
                      </div>
                      <div className="grid gap-3 sm:grid-cols-2">
                        <div>
                          <Label htmlFor="split-cash-input" className="flex items-center gap-1.5 text-xs font-medium text-emerald-700 dark:text-emerald-300"><Banknote className="h-3.5 w-3.5" /> Cash received</Label>
                          <FastInput
                            id="split-cash-input"
                            type="number"
                            inputMode="decimal"
                            step="0.01"
                            min="0"
                            autoFocus
                            placeholder="0.00"
                            value={splitCash}
                            onChange={(e) => setSplitCash(e.target.value)}
                            className="h-12 rounded-xl border-emerald-500/40 bg-background text-base font-semibold focus-visible:ring-emerald-500"
                            data-testid="split-cash-input"
                          />
                        </div>
                        <div>
                          <Label htmlFor="split-card-input" className="flex items-center gap-1.5 text-xs font-medium text-sky-700 dark:text-sky-300"><CreditCard className="h-3.5 w-3.5" /> Amount charged to card</Label>
                          <FastInput
                            id="split-card-input"
                            type="number"
                            inputMode="decimal"
                            step="0.01"
                            min="0"
                            placeholder="0.00"
                            value={splitCard}
                            onChange={(e) => setSplitCard(e.target.value)}
                            className="h-12 rounded-xl border-sky-500/40 bg-background text-base font-semibold focus-visible:ring-sky-500"
                            data-testid="split-card-input"
                          />
                          <div className="mt-1.5 flex flex-wrap gap-1.5">
                            <button
                              type="button"
                              onClick={() => setSplitCard(Math.max(0, outstandingDue - Math.min(outstandingDue, parseFloat(splitCash) || 0)).toFixed(2))}
                              className="rounded-lg border border-border bg-background px-3 py-1 text-xs font-medium hover:bg-secondary"
                              data-testid="split-card-rest"
                            >
                              Rest on card {formatPrice(Math.max(0, outstandingDue - Math.min(outstandingDue, parseFloat(splitCash) || 0)))}
                            </button>
                            <button type="button" onClick={() => { setSplitCash((outstandingDue / 2).toFixed(2)); setSplitCard((outstandingDue / 2).toFixed(2)); }} className="rounded-lg border border-border bg-background px-3 py-1 text-xs font-medium hover:bg-secondary" data-testid="split-half-half">Half / half</button>
                          </div>
                        </div>
                      </div>
                      <div className="rounded-xl bg-background/60 p-3 space-y-2">
                        <div className="flex items-baseline justify-between">
                          <p className="text-[11px] uppercase tracking-wide text-muted-foreground">Cash toward the bill</p>
                          <p className="text-sm font-semibold text-emerald-700 dark:text-emerald-300" data-testid="split-cash-toward">{formatPrice(tenderDraft?.cashAmount || 0)}</p>
                        </div>
                        <div className="flex items-baseline justify-between">
                          <p className="text-[11px] uppercase tracking-wide text-muted-foreground">Card toward the bill</p>
                          <p className="text-sm font-semibold text-sky-700 dark:text-sky-300" data-testid="split-card-toward">{formatPrice(tenderDraft?.cardAmount || 0)}</p>
                        </div>
                        <div className="flex items-baseline justify-between border-t border-border/60 pt-2">
                          <p className="text-[11px] uppercase tracking-wide text-muted-foreground">
                            {(tenderDraft?.remaining || 0) > 0.005 ? "Still owed after this payment" : "Bill settled"}
                          </p>
                          <p className={`text-lg font-semibold ${(tenderDraft?.remaining || 0) > 0.005 ? "text-amber-700 dark:text-amber-300" : "text-emerald-700 dark:text-emerald-300"}`} data-testid="split-remaining">
                            {formatPrice(tenderDraft?.remaining || 0)}
                          </p>
                        </div>
                        {(tenderDraft?.cardTip || 0) > 0.005 ? (
                          <p className="text-xs text-muted-foreground" data-testid="split-card-tip-note">Card never gives change — the extra {formatPrice(tenderDraft.cardTip)} on the card is recorded as a tip.</p>
                        ) : null}
                        {(() => {
                          const cashOverSplit = Math.max(0, (parseFloat(splitCash) || 0) - (tenderDraft?.cashAmount || 0));
                          if (cashOverSplit <= 0.005) return null;
                          return (
                            <>
                              <div className="flex items-center justify-between gap-3 border-t border-border/60 pt-2">
                                <Label htmlFor="split-tip-input" className="text-[11px] uppercase tracking-wide text-muted-foreground">Tip kept from cash change (max {formatPrice(cashOverSplit)})</Label>
                                <FastInput
                                  id="split-tip-input"
                                  type="number"
                                  inputMode="decimal"
                                  step="0.01"
                                  min="0"
                                  max={cashOverSplit.toFixed(2)}
                                  placeholder="0.00"
                                  value={tipDraft}
                                  onChange={(e) => setTipDraft(e.target.value)}
                                  className="h-9 w-28 rounded-lg border-border bg-background text-right text-sm font-semibold focus-visible:ring-emerald-500"
                                  data-testid="split-tip-input"
                                />
                              </div>
                              <div className="flex items-baseline justify-between">
                                <p className="text-[11px] uppercase tracking-wide text-muted-foreground">Cash change to customer</p>
                                <p className="text-base font-semibold text-emerald-700 dark:text-emerald-300" data-testid="split-change">{formatPrice(tenderDraft?.change || 0)}</p>
                              </div>
                              {tipDraftNum > cashOverSplit + 0.005 ? (
                                <p className="text-xs text-destructive" data-testid="split-tip-overflow-warning">Tip can't be more than the cash change ({formatPrice(cashOverSplit)}).</p>
                              ) : null}
                            </>
                          );
                        })()}
                      </div>
                      {(tenderDraft?.tip || 0) > 0 && tipOwnerName ? (
                        <p className="text-xs text-muted-foreground" data-testid="split-tip-attribution">Tip will be attributed to <span className="font-medium text-foreground">{tipOwnerName}</span>.</p>
                      ) : null}
                    </div>
                  </div>
                )}

                {/* STEP 3: Confirm (Session 31.26 — one tender at a time) */}
                {paymentStep === "confirm" && tenderDraft && (
                  <div className="space-y-3 rounded-2xl border border-border bg-secondary/40 p-4">
                    <div className="flex items-center justify-between">
                      <p className="text-sm font-semibold">Payment Summary</p>
                      <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Step 3 of 3</p>
                    </div>
                    <div className="space-y-2 text-sm">
                      <div className="flex items-center justify-between">
                        <span className="text-muted-foreground">Method</span>
                        <Badge className={`uppercase ${tenderDraft.method === "cash" ? "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-emerald-500/30" : tenderDraft.method === "card" ? "bg-sky-500/15 text-sky-700 dark:text-sky-300 border-sky-500/30" : tenderDraft.method === "split" ? "bg-violet-500/15 text-violet-700 dark:text-violet-300 border-violet-500/30" : "bg-teal-500/15 text-teal-700 dark:text-teal-300 border-teal-500/30"}`} data-testid="confirm-method-badge">
                          {tenderDraft.method === "card" ? `Card ${ledgerPayments.filter((p) => p.method === "card").length + 1}` : tenderDraft.method === "split" ? "Cash + Card" : tenderDraft.method}
                        </Badge>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">{isPartiallyPaid ? "Still owed before this payment" : "Order Total"}</span>
                        <span className="font-medium">{formatPrice(outstandingDue)}</span>
                      </div>
                      {tenderDraft.method === "split" ? (
                        <>
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Cash received</span>
                            <span className="font-medium" data-testid="confirm-split-cash">{formatPrice(tenderDraft.cashReceived)}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Charged to card</span>
                            <span className="font-medium" data-testid="confirm-split-card">{formatPrice(tenderDraft.cardCharged)}</span>
                          </div>
                        </>
                      ) : (
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">{tenderDraft.method === "cash" ? "Cash received" : "Amount charged"}</span>
                          <span className="font-medium">{formatPrice(tenderDraft.received)}</span>
                        </div>
                      )}
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Toward the bill</span>
                        <span className="font-medium" data-testid="confirm-toward-bill">{formatPrice(tenderDraft.amount)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Tip{tipOwnerName ? ` (${tipOwnerName})` : ""}</span>
                        <span className="font-medium" data-testid="confirm-tip">{formatPrice(tenderDraft.tip)}</span>
                      </div>
                      {tenderDraft.method === "cash" || tenderDraft.method === "split" ? (
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Change back to customer</span>
                          <span className="font-medium" data-testid="confirm-change">{formatPrice(tenderDraft.change)}</span>
                        </div>
                      ) : null}
                      <div className={`flex justify-between border-t border-border pt-2 text-base font-semibold ${tenderDraft.remaining > 0.005 ? "text-amber-700 dark:text-amber-300" : "text-emerald-700 dark:text-emerald-300"}`}>
                        <span>{tenderDraft.remaining > 0.005 ? "Still owed after this payment" : "Bill settled"}</span>
                        <span data-testid="confirm-remaining">{tenderDraft.remaining > 0.005 ? formatPrice(tenderDraft.remaining) : formatPrice(0)}</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Print-only payment summary (renders when user hits Print).
                  Hidden in the live UI; visible on the printed sheet only. */}
              <div className="hidden print:block space-y-1 text-sm">
                {ledgerPayments.length > 0 ? ledgerPayments.map((p, idx) => (
                  <div key={p.id || idx} className="flex justify-between"><span>{p.label || p.split_name || String(p.method || "").toUpperCase()}{Number(p.tip) > 0 ? ` (tip ${formatPrice(p.tip)})` : ""}</span><span>{formatPrice(p.amount)}</span></div>
                )) : (
                  <>
                    {savedCash > 0 && (
                      <div className="flex justify-between"><span>Paid in cash</span><span>{formatPrice(savedCash)}</span></div>
                    )}
                    {savedCard > 0 && (
                      <div className="flex justify-between"><span>Paid by card</span><span>{formatPrice(savedCard)}</span></div>
                    )}
                    {savedEft > 0 && (
                      <div className="flex justify-between"><span>Paid by EFT</span><span>{formatPrice(savedEft)}</span></div>
                    )}
                  </>
                )}
                {hasTip && (
                  <div className="flex justify-between" data-testid="receipt-extra-tip-line"><span>{serviceFee > 0 ? "Extra tip" : "Tip"}{(selectedSession.tip_recipient || tipRecipient) ? ` for ${selectedSession.tip_recipient || tipRecipient}` : ""}</span><span>{formatPrice(tipNum)}</span></div>
                )}
                {serviceFee > 0 && hasTip ? (
                  <div className="flex justify-between text-muted-foreground" data-testid="receipt-gratuity-total-line"><span>Service fee + tip</span><span>{formatPrice(Math.round((serviceFee + tipNum) * 100) / 100)}</span></div>
                ) : null}
                {(() => {
                  // Session 35 — name the waiter the gratuity goes to on the printed sheet.
                  const who = String(selectedSession.assigned_user_name || selectedSession.tip_recipient || tipRecipient || "").trim();
                  const gratTotal = Math.round((serviceFee + tipNum) * 100) / 100;
                  if (!who || gratTotal <= 0) return null;
                  const lbl = serviceFee > 0 && hasTip ? "Service fee + tip for" : serviceFee > 0 ? "Service fee for" : "Tip for";
                  return (
                    <div className="flex justify-between border-t border-dotted border-border pt-1 font-semibold" data-testid="receipt-gratuity-recipient-line"><span>{lbl} {who}</span><span>{formatPrice(gratTotal)}</span></div>
                  );
                })()}
                {outstandingDue > 0.005 && paidSoFar > 0 ? (
                  <div className="flex justify-between font-semibold"><span>Still owed</span><span>{formatPrice(outstandingDue)}</span></div>
                ) : null}
              </div>

              {/* Footer Info */}
              <div className="text-center text-xs text-muted-foreground pt-2 border-t border-border">
                <p>{selectedSession.order_count} orders · {selectedSession.total_items} items</p>
                <p className="mt-1 hidden print:block">Thank you!</p>
              </div>
            </div>
            );
          })()}

          <DialogFooter className="print:hidden gap-2 sm:gap-2">
            {(() => {
              // Recompute totals + flow flags so the footer can render the
              // right buttons without depending on the body's IIFE scope.
              if (!selectedSession) return null;
              // Same post-void math as the receipt body — sum backend
              // order totals, skip fully-voided orders. Recomputing from
              // raw items[] silently double-charges voided lines.
              const activeOrdersFooter = (selectedSession.orders || []).filter((o) => o.status !== "voided");
              const splitReceiptsFooter = selectedSession.split_receipts || null;
              const groupIdByKeyFooter = {};
              if (splitReceiptsFooter) {
                splitReceiptsFooter.forEach((g) => {
                  (g.items || []).forEach((it) => { groupIdByKeyFooter[`${it.order_id}#${it.line_index}`] = g.id; });
                });
              }
              const footerVisible = (orderId, lineIdx) => {
                if (!splitReceiptsFooter || activeSplitTab === "all") return true;
                const gid = groupIdByKeyFooter[`${orderId}#${lineIdx}`];
                if (activeSplitTab === "unassigned") return !gid;
                return gid === activeSplitTab;
              };
              let grossFooter = 0;
              activeOrdersFooter.forEach((o) => {
                (o.items || []).forEach((it, i) => {
                  if (it.voided) return;
                  if (!footerVisible(o.id, i)) return;
                  grossFooter += Number(it.line_total ?? ((it.price || 0) * (it.quantity || 0))) || 0;
                });
              });
              const appliedVoucherFooter = selectedSession.applied_voucher || null;
              const voucherDiscountFooter = appliedVoucherFooter && (activeSplitTab === "all" || !splitReceiptsFooter)
                ? Math.min(grossFooter, Number(appliedVoucherFooter.discount_amount) || 0)
                : 0;
              const totBeforeFeeFooter = Math.max(0, grossFooter - voucherDiscountFooter);
              // Session 34 — service fee is part of what is owed.
              const feePctFooter = sessionServiceFeePct(selectedSession, context?.business);
              const feeFooter = serviceFeeOn(totBeforeFeeFooter, feePctFooter);
              const tot = Math.round((totBeforeFeeFooter + feeFooter) * 100) / 100;
              const recvN = parseFloat(cashReceived) || 0;
              const cardN = parseFloat(cardAmount) || 0;
              const eftN = parseFloat(eftAmount) || 0;
              const sCashN = parseFloat(splitCash) || 0;
              const sCardN = parseFloat(splitCard) || 0;
              // Session 31.26 — multi-tender: what is still owed on the bill.
              const ledgerFooter = Array.isArray(selectedSession.payments) ? selectedSession.payments : [];
              const paidFooter = ledgerFooter.length
                ? ledgerTotals(ledgerFooter).paid
                : Math.round((Number(selectedSession.cash_paid || 0) + Number(selectedSession.card_paid || 0) + Number(selectedSession.eft_paid || 0)) * 100) / 100;
              const owedFooter = Math.max(0, Math.round((tot - paidFooter) * 100) / 100);
              const draftFooter = computeTenderDraft(owedFooter);
              // Session 27.41 — Float-tolerant equality. Previously
              // these were strict `>= tot`, which meant a payment of
              // exactly R296.70 against a total that summed to
              // R296.7000000001 (floating-point noise from summing
              // multiple line_totals) failed the check. Waiters
              // reported the Continue button was "stuck" until they
              // typed in a slightly larger amount — effectively
              // forcing them to enter a tip. A 0.005 tolerance
              // corresponds to half a cent, well below any real
              // amount the operator would enter.
              const EPS = 0.005;
              // Partial tenders are allowed (that is the whole point of
              // multi-tender) — the gate is simply "an amount was typed".
              const cashOk = recvN > EPS && owedFooter > 0;
              const cardOk = cardN > EPS && owedFooter > 0 && !!draftFooter && (draftFooter.amount > EPS || draftFooter.tip > EPS);
              const eftOk = eftN > EPS && owedFooter > 0 && !!draftFooter && (draftFooter.amount > EPS || draftFooter.tip > EPS);
              const splitOk = sCashN > EPS && sCardN > EPS && owedFooter > 0 && !!draftFooter && (draftFooter.amount > EPS || draftFooter.tip > EPS);
              if (paymentStep === "idle") {
                return (
                  <>
                    <Button variant="outline" onClick={() => {
                      // Session 27.4 — print via self-contained iframe
                      // renderer. Bypasses the surrounding app layout
                      // (which was pushing the receipt onto a second
                      // page on Edge/Windows with the SNBC printer).
                      //
                      // NOTE: This click handler lives inside the
                      // DialogFooter callback, whose scope contains
                      // only the `*Footer` variants of the receipt
                      // computations (activeOrdersFooter, footerVisible,
                      // groupIdByKeyFooter, tot, appliedVoucherFooter,
                      // voucherDiscountFooter). All other totals must
                      // be re-derived from `selectedSession` and
                      // `context` inside this handler — never reach
                      // out to the receipt-body variables (they are
                      // out of scope and referencing them throws a
                      // ReferenceError → uncaught runtime error).
                      const businessRec  = context?.business || {};
                      const vatRatePct   = businessRec.vat_rate ?? 15;
                      const grossHere    = grossFooter;
                      const totalHere    = tot;
                      const subtotalHere = vatRatePct > 0 ? grossHere / (1 + vatRatePct / 100) : grossHere;
                      const vatAmountHere = grossHere - subtotalHere;
                      const savedCashHere = Number(selectedSession.cash_paid || 0);
                      const savedCardHere = Number(selectedSession.card_paid || 0);
                      const tipHere       = Number(selectedSession.tip_amount || 0);
                      // Session 35 — who the gratuity (service fee + tip) goes to:
                      // the waiter who opened the table, else whoever the
                      // backend recorded as tip recipient.
                      const gratuityRecipientHere = String(selectedSession.assigned_user_name || selectedSession.tip_recipient || "").trim();
                      const lines = [];
                      (activeOrdersFooter || []).forEach((order, idx) => {
                        const orderLabel = `Order ${order.order_number || idx + 1}`;
                        lines.push({ qty: "", name: `— ${orderLabel} —`, price: "", modifiers: [] });
                        (order.items || []).forEach((item, itemIdx) => {
                          if (item.voided) return;
                          if (!footerVisible(order.id, itemIdx)) return;
                          const itemTotal = Number(item.line_total ?? ((item.price || 0) * (item.quantity || 0)));
                          const mods = [];
                          if (Array.isArray(item.modifiers)) {
                            item.modifiers.forEach((m) => {
                              if (m && m.name) mods.push(m.name);
                            });
                          }
                          if (Array.isArray(item.selected_sides)) {
                            item.selected_sides.forEach((sd) => {
                              if (sd && sd.name) mods.push(sd.charged ? `${sd.name} (+${formatPrice(Number(sd.price) || 0)})` : `${sd.name} (included)`);
                            });
                          }
                          if (item.notes) mods.push(item.notes);
                          lines.push({
                            qty: item.quantity || 1,
                            name: item.name || "",
                            price: itemTotal,
                            modifiers: mods,
                          });
                        });
                      });
                      const payments = [];
                      if (ledgerFooter.length) {
                        ledgerFooter.forEach((p) => payments.push({ label: `${p.label || p.split_name || String(p.method || "").toUpperCase()}${Number(p.tip) > 0 ? ` (tip ${formatPrice(p.tip)})` : ""}`, amount: Number(p.amount) || 0 }));
                      } else {
                        if (savedCashHere > 0) payments.push({ label: "Cash",  amount: savedCashHere });
                        if (savedCardHere > 0) payments.push({ label: "Card",  amount: savedCardHere });
                        const savedEftHere = Number(selectedSession.eft_paid || 0);
                        if (savedEftHere > 0) payments.push({ label: "EFT",  amount: savedEftHere });
                      }
                      if (owedFooter > 0.005 && paidFooter > 0) payments.push({ label: "STILL OWED", amount: owedFooter });
                      const totalPaidNow = paidFooter;
                      const change = Math.max(0, totalPaidNow - totalHere - tipHere);
                      const orderNos = [...new Set((activeOrdersFooter || []).map((o) => o.order_number).filter(Boolean))];
                      // Session 30.7 — printSlip picks the silent native
                      // path (Bluetooth / Windows spooler) when available.
                      printSlip({
                        business: businessRec,
                        tableLabel: `Table ${selectedSession.table_number}`,
                        orderLabel: orderNos.length ? `Order ${orderNos.join(", ")}` : "",
                        servedBy: selectedSession.assigned_user_name || "",
                        header: (splitReceiptsFooter && activeSplitTab !== "all")
                          ? (activeSplitTab === "unassigned"
                              ? "Unassigned items"
                              : (splitReceiptsFooter.find((g) => g.id === activeSplitTab)?.name || "Guest"))
                          : "",
                        dateStr: new Date().toLocaleString(),
                        lines,
                        subtotal: subtotalHere,
                        vatRate: vatRatePct,
                        vatAmount: vatAmountHere,
                        voucherDiscount: voucherDiscountFooter,
                        voucherLabel: appliedVoucherFooter ? appliedVoucherFooter.code : "",
                        serviceFeePct: feePctFooter,
                        serviceFeeAmount: feeFooter,
                        total: totalHere,
                        payments,
                        tip: tipHere,
                        gratuityRecipient: gratuityRecipientHere,
                        changeDue: change,
                        footer: "Thank you!",
                        formatPrice,
                      }).then((r) => {
                        if (!r?.ok) {
                          toast.error("Could not print the slip", {
                            description: r?.error || "Check Printer setup in the Actions menu.",
                            action: { label: "Printer setup", onClick: () => setPrinterSetupOpen(true) },
                          });
                        } else if (r.method === "bluetooth" || r.method === "windows-raw") {
                          toast.success("Slip sent to the printer");
                        }
                      });
                    }} className="rounded-xl" data-testid="receipt-dialog-print-button">
                      <Printer className="h-4 w-4" />
                      Print
                    </Button>
                    <Button variant="outline" onClick={() => { setReceiptOpen(false); setEmailReceiptOpen(true); }} className="rounded-xl" data-testid="receipt-dialog-email-button">
                      <Mail className="h-4 w-4" />
                      Email
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => { setVoucherCodeInput(""); setVoucherDialogOpen(true); }}
                      disabled={Boolean(appliedVoucherFooter)}
                      className="rounded-xl"
                      data-testid="receipt-dialog-voucher-button"
                    >
                      <Ticket className="h-4 w-4" />
                      Voucher
                    </Button>
                  </>
                );
              }
              if (paymentStep === "method") {
                return (
                  <Button variant="outline" onClick={() => setPaymentStep("idle")} className="rounded-xl" data-testid="payment-back-to-receipt">
                    <ArrowLeft className="h-4 w-4" />
                    Back to Receipt
                  </Button>
                );
              }
              if (paymentStep === "cash" || paymentStep === "card" || paymentStep === "eft" || paymentStep === "split") {
                const okNow = paymentStep === "cash" ? cashOk : paymentStep === "card" ? cardOk : paymentStep === "split" ? splitOk : eftOk;
                const btnTone = paymentStep === "cash" ? "bg-emerald-600 hover:bg-emerald-600/90" : paymentStep === "card" ? "bg-sky-600 hover:bg-sky-600/90" : paymentStep === "split" ? "bg-violet-600 hover:bg-violet-600/90" : "bg-teal-600 hover:bg-teal-600/90";
                return (
                  <>
                    <Button variant="outline" onClick={() => setPaymentStep("method")} className="rounded-xl" data-testid="payment-back-button">
                      <ArrowLeft className="h-4 w-4" />
                      Back
                    </Button>
                    <Button
                      onClick={() => setPaymentStep("confirm")}
                      disabled={!okNow}
                      className={`rounded-xl text-white ${btnTone} disabled:opacity-50`}
                      data-testid="payment-continue-button"
                    >
                      Continue
                      <ArrowRight className="h-4 w-4" />
                    </Button>
                  </>
                );
              }
              if (paymentStep === "confirm") {
                const final = draftFooter || { method: chosenMethod, amount: 0, tip: 0, change: 0, remaining: owedFooter, received: 0 };
                const themeBtn = chosenMethod === "cash"
                  ? "bg-emerald-600 hover:bg-emerald-600/90"
                  : chosenMethod === "card"
                    ? "bg-sky-600 hover:bg-sky-600/90"
                    : chosenMethod === "split"
                      ? "bg-violet-600 hover:bg-violet-600/90"
                      : "bg-teal-600 hover:bg-teal-600/90";
                const methodLabel = chosenMethod === "cash" ? "cash" : chosenMethod === "card" ? "card" : chosenMethod === "split" ? "cash + card" : "EFT";
                const settlesBill = final.remaining <= EPS;
                return (
                  <>
                    <Button variant="outline" onClick={() => setPaymentStep(chosenMethod || "method")} className="rounded-xl" data-testid="payment-confirm-back">
                      <ArrowLeft className="h-4 w-4" />
                      Back
                    </Button>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button
                          disabled={busyAction === "save-payment" || (final.amount <= EPS && final.tip <= EPS)}
                          className={`rounded-xl text-white ${themeBtn} disabled:opacity-50`}
                          data-testid="payment-confirm-button"
                        >
                          <CheckCircle2 className="h-4 w-4" />
                          {busyAction === "save-payment" ? "Saving..." : settlesBill ? "Confirm & settle bill" : `Record PARTIAL · ${formatPrice(final.remaining)} still owed`}
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent className="rounded-[24px] sm:max-w-md" data-testid="payment-method-confirm-dialog">
                        <AlertDialogHeader>
                          <AlertDialogTitle className="font-['Space_Grotesk'] text-2xl" data-testid="payment-method-confirm-title">
                            {settlesBill ? `Confirm ${methodLabel} payment` : `Partial ${methodLabel} payment`}
                          </AlertDialogTitle>
                          <AlertDialogDescription>
                            {settlesBill
                              ? "This settles the bill and the table can be closed."
                              : `This does NOT settle the bill. ${formatPrice(final.remaining)} will still be outstanding — the table stays open and cannot be closed as paid until the balance is covered (or moved to a tab). Closing it unpaid needs the manager PIN.`}
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        {!settlesBill ? (
                          <div className="flex items-center gap-2 rounded-xl border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-sm font-semibold text-amber-800 dark:text-amber-200" data-testid="payment-partial-warning">
                            <AlertTriangle className="h-4 w-4 shrink-0" />
                            {formatPrice(final.remaining)} still outstanding after this payment
                          </div>
                        ) : null}
                        <div className="space-y-2 rounded-2xl border border-border bg-secondary/40 p-3 text-sm">
                          <div className="flex items-center justify-between">
                            <span className="text-muted-foreground">Method</span>
                            <Badge className={`uppercase ${chosenMethod === "cash" ? "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-emerald-500/30" : chosenMethod === "card" ? "bg-sky-500/15 text-sky-700 dark:text-sky-300 border-sky-500/30" : chosenMethod === "split" ? "bg-violet-500/15 text-violet-700 dark:text-violet-300 border-violet-500/30" : "bg-teal-500/15 text-teal-700 dark:text-teal-300 border-teal-500/30"}`}>{methodLabel}</Badge>
                          </div>
                          <div className="flex justify-between"><span className="text-muted-foreground">Still owed before</span><span className="font-medium">{formatPrice(owedFooter)}</span></div>
                          {chosenMethod === "split" ? (
                            <>
                              <div className="flex justify-between"><span className="text-muted-foreground">Cash toward the bill</span><span className="font-medium" data-testid="receipt-confirm-split-cash">{formatPrice(final.cashAmount || 0)}</span></div>
                              <div className="flex justify-between"><span className="text-muted-foreground">Card toward the bill</span><span className="font-medium" data-testid="receipt-confirm-split-card">{formatPrice(final.cardAmount || 0)}</span></div>
                            </>
                          ) : null}
                          <div className="flex justify-between"><span className="text-muted-foreground">Toward the bill</span><span className="font-medium">{formatPrice(final.amount)}</span></div>
                          <div className="flex justify-between"><span className="text-muted-foreground">Tip{tipOwnerName ? ` (${tipOwnerName})` : ""}</span><span className="font-medium">{formatPrice(final.tip)}</span></div>
                          {chosenMethod === "cash" || chosenMethod === "split" ? (
                            <div className="flex justify-between"><span className="text-muted-foreground">Change back to customer</span><span className="font-medium" data-testid="receipt-confirm-change">{formatPrice(final.change || 0)}</span></div>
                          ) : null}
                          <div className={`flex justify-between border-t border-border pt-2 text-base font-semibold ${settlesBill ? "text-emerald-700 dark:text-emerald-300" : "text-amber-700 dark:text-amber-300"}`}>
                            <span>{settlesBill ? "Bill settled" : "Still owed after"}</span>
                            <span data-testid="receipt-confirm-remaining">{formatPrice(final.remaining || 0)}</span>
                          </div>
                        </div>
                        <AlertDialogFooter className="flex flex-col gap-2 sm:flex-row">
                          <AlertDialogCancel className="h-11 rounded-xl" data-testid="payment-method-confirm-cancel">Cancel</AlertDialogCancel>
                          <AlertDialogAction
                            className={`h-11 rounded-xl text-white ${themeBtn}`}
                            onClick={() => addTenderPayment(final)}
                            data-testid="payment-method-confirm-save"
                          >
                            {settlesBill ? `Save ${methodLabel} payment` : `Record partial ${methodLabel} payment`}
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </>
                );
              }
              return null;
            })()}
          </DialogFooter>
          </>) : null}
        </DialogContent>
      </Dialog>

      {/* Session 31.26 — Remove a mistaken tender from the ledger (manager PIN) */}
      <Dialog open={removeTenderPrompt.open} onOpenChange={(open) => { if (!open && !removeTenderPrompt.busy) setRemoveTenderPrompt({ open: false, payment: null, pin: "", busy: false }); }}>
        <DialogContent className="rounded-[24px] sm:max-w-sm" data-testid="remove-tender-dialog">
          <DialogHeader>
            <DialogTitle>Remove payment</DialogTitle>
            <DialogDescription>
              {removeTenderPrompt.payment ? (
                <>Take <span className="font-semibold text-foreground">{removeTenderPrompt.payment.label || removeTenderPrompt.payment.method}</span> · <span className="font-semibold text-foreground">{formatPrice(removeTenderPrompt.payment.amount)}</span>{Number(removeTenderPrompt.payment.tip) > 0 ? ` (tip ${formatPrice(removeTenderPrompt.payment.tip)})` : ""} off this table's ledger. The outstanding balance goes back up. Manager PIN required.</>
              ) : null}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="remove-tender-pin" className="text-xs font-medium">Manager PIN</Label>
            <FastInput
              id="remove-tender-pin"
              type="password"
              inputMode="numeric"
              autoComplete="off"
              value={removeTenderPrompt.pin}
              onChange={(e) => setRemoveTenderPrompt((d) => ({ ...d, pin: e.target.value }))}
              onKeyDown={(e) => { if (e.key === "Enter") removeTenderPayment(); }}
              className="h-11 rounded-xl"
              data-testid="remove-tender-pin-input"
            />
          </div>
          <DialogFooter className="gap-2 sm:gap-2">
            <Button variant="outline" className="rounded-xl" disabled={removeTenderPrompt.busy} onClick={() => setRemoveTenderPrompt({ open: false, payment: null, pin: "", busy: false })} data-testid="remove-tender-cancel">Cancel</Button>
            <Button variant="destructive" className="rounded-xl" disabled={removeTenderPrompt.busy || !String(removeTenderPrompt.pin || "").trim()} onClick={removeTenderPayment} data-testid="remove-tender-confirm">
              {removeTenderPrompt.busy ? "Removing…" : "Remove payment"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Split Pay Dialog — Session 31.26: per-guest multi-tender ledger */}
      <Dialog open={splitPayDialog.open} onOpenChange={(open) => { if (!open) setSplitPayDialog((p) => ({ ...p, open: false })); }}>
        <DialogContent className="rounded-[28px] border-border bg-background sm:max-w-md" data-testid="split-pay-dialog">
          {/* Session 69 — build the body only while open (a closed dialog used to re-render its whole list on every tap) */}
          {splitPayDialog.open ? (<>
          {(() => {
            const dlg = splitPayDialog;
            const r2 = (n) => Math.round((Number(n) || 0) * 100) / 100;
            const target = Math.max(0, Number(dlg.target_total) || 0);
            const rows = dlg.tenders || [];
            const paidRows = r2(rows.reduce((sum, t) => sum + (Number(t.amount) || 0), 0));
            const tipRows = r2(rows.reduce((sum, t) => sum + (Number(t.tip) || 0), 0));
            const dueRows = Math.max(0, r2(target - paidRows));
            const covered = target > 0 && dueRows <= 0.005;
            const draft = dlg.step !== "ledger" ? splitTenderDraft(dlg) : null;
            const cardCount = rows.filter((t) => t.method === "card").length;
            const tone = (m) => m === "cash" ? "emerald" : m === "eft" ? "teal" : "sky";
            const Icon = (m) => m === "cash" ? Banknote : m === "eft" ? Landmark : CreditCard;
            const label = (t, idx) => t.label || (t.method === "card" ? `Card ${rows.slice(0, idx + 1).filter((x) => x.method === "card").length}` : t.method === "eft" ? "EFT" : "Cash");
            return (
              <>
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2">
                    <Receipt className="h-5 w-5" />
                    Pay {dlg.split_name}
                  </DialogTitle>
                  <DialogDescription>
                    Amount due: <span className="font-semibold text-foreground" data-testid="split-pay-target-total">{formatPrice(target)}</span>
                    {dlg.is_edit ? " · editing a recorded payment" : ""}
                  </DialogDescription>
                </DialogHeader>

                {dlg.step === "ledger" ? (
                  <div className="space-y-3">
                    {rows.length ? (
                      <ul className="divide-y divide-border/60 rounded-2xl border border-border bg-secondary/30" data-testid="split-pay-tender-list">
                        {rows.map((t, idx) => {
                          const I = Icon(t.method);
                          return (
                            <li key={t._k || `${t.method}-${t.amount}-${idx}`} className="flex items-center gap-3 px-3 py-2 text-sm" data-testid={`split-pay-tender-row-${idx}`}>
                              <I className={`h-4 w-4 shrink-0 text-${tone(t.method)}-700 dark:text-${tone(t.method)}-300`} />
                              <div className="min-w-0 flex-1">
                                <p className="font-medium">{label(t, idx)}</p>
                                <p className="text-[11px] text-muted-foreground">{Number(t.tip) > 0 ? `Tip ${formatPrice(t.tip)}` : "No tip"}{Number(t.change) > 0 ? ` · change ${formatPrice(t.change)}` : ""}</p>
                              </div>
                              <span className="tabular-nums font-semibold">{formatPrice(t.amount)}</span>
                              <button type="button" onClick={() => setSplitPayDialog((p) => ({ ...p, tenders: (p.tenders || []).filter((_, k) => k !== idx) }))} className="rounded-md p-1 text-muted-foreground hover:bg-destructive/10 hover:text-destructive" aria-label="Remove this payment" data-testid={`split-pay-tender-remove-${idx}`}>
                                <X className="h-3.5 w-3.5" />
                              </button>
                            </li>
                          );
                        })}
                      </ul>
                    ) : (
                      <p className="rounded-2xl border border-dashed border-border px-3 py-4 text-center text-sm text-muted-foreground" data-testid="split-pay-empty">
                        How is {dlg.split_name} paying? Add each payment below — cash, one or more cards, or EFT.
                      </p>
                    )}
                    <div className={`flex items-baseline justify-between rounded-xl px-3 py-2 text-sm ${covered ? "bg-emerald-500/10 text-emerald-800 dark:text-emerald-200" : "bg-amber-500/10 text-amber-900 dark:text-amber-200"}`}>
                      <span className="font-medium">{covered ? "Covered" : rows.length ? "Still owed" : "Amount due"}</span>
                      <span className="text-base font-semibold tabular-nums" data-testid="split-pay-remaining">{formatPrice(covered ? 0 : dueRows)}</span>
                    </div>
                    {tipRows > 0 ? <p className="text-xs text-muted-foreground" data-testid="split-pay-tip-total">Tips {formatPrice(tipRows)}{tipOwnerName ? ` · attributed to ${tipOwnerName}` : ""}</p> : null}
                    {!covered ? (
                      <div className="grid grid-cols-3 gap-2">
                        {[["cash", "Cash", Banknote], ["card", cardCount ? `Card ${cardCount + 1}` : "Card", CreditCard], ["eft", "EFT", Landmark]].map(([m, lbl, I]) => (
                          <button
                            key={m}
                            type="button"
                            onClick={() => setSplitPayDialog((p) => ({ ...p, step: m, chosenMethod: m, amountStr: m === "cash" ? "" : String(dueRows.toFixed(2)), tipStr: "" }))}
                            className={`flex h-16 flex-col items-center justify-center gap-1 rounded-2xl border-2 border-${tone(m)}-500/40 bg-${tone(m)}-500/10 text-${tone(m)}-700 transition-colors hover:border-${tone(m)}-500 hover:bg-${tone(m)}-500/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-${tone(m)}-500 dark:text-${tone(m)}-300`}
                            data-testid={`split-pay-method-${m}`}
                          >
                            <I className="h-5 w-5" strokeWidth={2.25} />
                            <span className="text-xs font-semibold">+ {lbl}</span>
                          </button>
                        ))}
                      </div>
                    ) : null}
                  </div>
                ) : (
                  <div className={`space-y-3 rounded-2xl border-2 border-${tone(dlg.chosenMethod)}-500/40 bg-${tone(dlg.chosenMethod)}-500/10 p-4`}>
                    <div className="flex items-center justify-between">
                      <div className={`flex items-center gap-2 text-${tone(dlg.chosenMethod)}-700 dark:text-${tone(dlg.chosenMethod)}-300`}>
                        {(() => { const I = Icon(dlg.chosenMethod); return <I className="h-5 w-5" strokeWidth={2.25} />; })()}
                        <p className="text-sm font-semibold">{dlg.chosenMethod === "cash" ? "Cash" : dlg.chosenMethod === "eft" ? "EFT" : `Card ${cardCount + 1}`} · {dlg.split_name}</p>
                      </div>
                      <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Still owed {formatPrice(dueRows)}</p>
                    </div>
                    <div>
                      <Label htmlFor="split-pay-amount" className="text-xs font-medium">{dlg.chosenMethod === "cash" ? "Cash received" : dlg.chosenMethod === "eft" ? "Amount transferred (EFT)" : "Amount charged to card"}</Label>
                      <FastInput id="split-pay-amount" type="number" inputMode="decimal" step="0.01" min="0" autoFocus placeholder="0.00" value={dlg.amountStr} onChange={(e) => setSplitPayDialog((p) => ({ ...p, amountStr: e.target.value }))} className="h-12 rounded-xl bg-background text-base font-semibold" data-testid="split-pay-amount-input" />
                      <div className="mt-1.5 flex flex-wrap gap-1.5">
                        <button type="button" onClick={() => setSplitPayDialog((p) => ({ ...p, amountStr: dueRows.toFixed(2) }))} className="rounded-lg border border-border bg-background px-3 py-1 text-xs font-medium hover:bg-secondary" data-testid="split-pay-amount-exact">Full {formatPrice(dueRows)}</button>
                        <button type="button" onClick={() => setSplitPayDialog((p) => ({ ...p, amountStr: (dueRows / 2).toFixed(2) }))} className="rounded-lg border border-border bg-background px-3 py-1 text-xs font-medium hover:bg-secondary" data-testid="split-pay-amount-half">Half</button>
                      </div>
                    </div>
                    {dlg.chosenMethod === "cash" ? (
                    <div className="flex items-center justify-between gap-3">
                      <Label htmlFor="split-pay-tip" className="text-xs font-medium">Tip (kept from change)</Label>
                      <FastInput id="split-pay-tip" type="number" inputMode="decimal" step="0.01" min="0" placeholder="0.00" value={dlg.tipStr} onChange={(e) => setSplitPayDialog((p) => ({ ...p, tipStr: e.target.value }))} className="h-9 w-28 rounded-lg bg-background text-right text-sm font-semibold" data-testid="split-pay-tip-input" />
                    </div>
                    ) : null}
                    {draft ? (
                      <div className="space-y-1.5 rounded-xl bg-background/60 p-3 text-sm">
                        <div className="flex justify-between"><span className="text-muted-foreground">Toward the bill</span><span className="font-semibold" data-testid="split-pay-toward-bill">{formatPrice(draft.amount)}</span></div>
                        <div className="flex justify-between"><span className="text-muted-foreground">{dlg.chosenMethod === "cash" ? "Tip" : "Tip (charged over the bill)"}</span><span className="font-semibold" data-testid="split-pay-draft-tip">{formatPrice(draft.tip)}</span></div>
                        {dlg.chosenMethod === "cash" ? <div className="flex justify-between"><span className="text-muted-foreground">Change</span><span className="font-semibold" data-testid="split-pay-draft-change">{formatPrice(draft.change)}</span></div> : null}
                        <div className={`flex justify-between border-t border-border/60 pt-1.5 font-semibold ${draft.remaining > 0.005 ? "text-amber-700 dark:text-amber-300" : "text-emerald-700 dark:text-emerald-300"}`}>
                          <span>{draft.remaining > 0.005 ? "Still owed after this" : "Guest covered"}</span><span data-testid="split-pay-draft-remaining">{formatPrice(draft.remaining)}</span>
                        </div>
                      </div>
                    ) : null}
                  </div>
                )}

                <DialogFooter className="flex-row gap-2">
                  {dlg.step === "ledger" ? (
                    <>
                      <Button variant="outline" className="rounded-xl" onClick={() => setSplitPayDialog((p) => ({ ...p, open: false }))} data-testid="split-pay-cancel">Cancel</Button>
                      <Button className="rounded-xl bg-emerald-600 text-white hover:bg-emerald-600/90 disabled:opacity-50" disabled={dlg.busy || !covered || !rows.length} onClick={submitPaySplit} data-testid="split-pay-submit">
                        {dlg.busy ? "Recording…" : rows.length > 1 ? `Record ${rows.length} payments` : "Record payment"}
                      </Button>
                    </>
                  ) : (
                    <>
                      <Button variant="outline" className="rounded-xl" onClick={() => setSplitPayDialog((p) => ({ ...p, step: "ledger", chosenMethod: "", amountStr: "", tipStr: "" }))} data-testid="split-pay-back">Back</Button>
                      <Button className={`rounded-xl text-white bg-${tone(dlg.chosenMethod)}-600 hover:bg-${tone(dlg.chosenMethod)}-600/90 disabled:opacity-50`} disabled={!draft || (draft.amount <= 0.005 && draft.tip <= 0.005)} onClick={addSplitTender} data-testid="split-pay-next">Add payment</Button>
                    </>
                  )}
                </DialogFooter>
              </>
            );
          })()}
          </>) : null}
        </DialogContent>
      </Dialog>

      {/* Split Bill Dialog */}
      <Dialog open={splitBillOpen} onOpenChange={setSplitBillOpen}>
        <DialogContent className="flex max-h-[92vh] flex-col gap-3 overflow-hidden rounded-[28px] border-border bg-background p-4 sm:max-w-3xl sm:p-6" data-testid="split-bill-dialog">
          {/* Session 69 — build the body only while open (a closed dialog used to re-render its whole list on every tap) */}
          {splitBillOpen ? (<>
          <DialogHeader className="shrink-0">
            <DialogTitle className="flex items-center gap-2">
              <Users className="h-5 w-5" />
              Split bill — Table {selectedSession?.table_number}
            </DialogTitle>
            <DialogDescription>
              Assign each ordered item to a guest. Each guest gets their own receipt with subtotal, VAT and total. Every item must be assigned before the split can be saved.
            </DialogDescription>
          </DialogHeader>

          {/* Session 30 — Unassigned-items banner. Live count of items
              that still need a guest; turns destructive after a blocked
              save attempt so the operator knows exactly why. */}
          {unassignedSplitKeys.length > 0 ? (
            <div
              className={`flex shrink-0 items-center gap-2 rounded-2xl border px-3 py-2 text-sm font-medium ${splitShowMissing ? "border-destructive/50 bg-destructive/10 text-destructive" : "border-amber-400/50 bg-amber-500/10 text-amber-700 dark:text-amber-300"}`}
              data-testid="split-unassigned-banner"
            >
              <AlertTriangle className="h-4 w-4 shrink-0" />
              {unassignedSplitKeys.length} item{unassignedSplitKeys.length === 1 ? "" : "s"} not assigned to a guest yet
            </div>
          ) : null}

          {/* Scrollable body — everything between the header and footer
              lives here so mobile users can reach the totals and save
              button even with many items to assign. */}
          <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain pr-1" style={{ WebkitOverflowScrolling: "touch" }}>
          {/* Session 70 — guest count stepper + auto-assign for big tables */}
          <div className="flex flex-wrap items-center justify-between gap-2 pb-2">
            <div className="flex items-center gap-2" data-testid="split-guest-count-control">
              <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Guests</span>
              <Button type="button" variant="outline" size="icon" className="h-8 w-8 rounded-lg" onClick={() => setSplitGuestCount(splitGroups.length - 1)} disabled={splitGroups.length <= 2} aria-label="Fewer guests" data-testid="split-guest-count-minus"><Minus className="h-3.5 w-3.5" /></Button>
              <Input
                type="number"
                inputMode="numeric"
                min={2}
                max={SPLIT_BILL_MAX_GUESTS}
                value={splitGroups.length}
                onChange={(e) => setSplitGuestCount(e.target.value)}
                className="h-8 w-16 rounded-lg text-center text-sm font-semibold"
                data-testid="split-guest-count-input"
              />
              <Button type="button" variant="outline" size="icon" className="h-8 w-8 rounded-lg" onClick={() => addSplitGroup(1)} disabled={splitGroups.length >= SPLIT_BILL_MAX_GUESTS} aria-label="More guests" data-testid="split-guest-count-plus"><Plus className="h-3.5 w-3.5" /></Button>
              <span className="text-[11px] text-muted-foreground">max {SPLIT_BILL_MAX_GUESTS}</span>
            </div>
            <Button type="button" variant="outline" size="sm" className="rounded-xl" onClick={autoAssignRemainingSplit} disabled={unassignedSplitKeys.length === 0} data-testid="split-auto-assign-button">
              <Shuffle className="h-3.5 w-3.5" />
              Spread {unassignedSplitKeys.length > 0 ? `${unassignedSplitKeys.length} unassigned` : "items"} evenly
            </Button>
          </div>
          {/* Guest chips */}
          <div className="flex max-h-40 flex-wrap items-center gap-2 overflow-y-auto border-b border-border pb-3" data-testid="split-guest-chips">
            {splitGroups.map((g, gi) => (
              <div key={g.id} className="flex items-center gap-1 rounded-2xl border border-border bg-secondary/40 px-2 py-1" data-testid={`split-group-chip-${gi}`}>
                <Input
                  value={g.name}
                  onChange={(e) => renameSplitGroup(g.id, e.target.value)}
                  className={`h-8 rounded-lg border-0 bg-transparent text-sm font-semibold focus-visible:ring-1 ${splitGroups.length > 12 ? "w-24" : "w-28"}`}
                  maxLength={20}
                  data-testid={`split-group-name-${gi}`}
                />
                <button
                  type="button"
                  onClick={() => removeSplitGroup(g.id)}
                  className="rounded-full p-1 text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
                  aria-label={`Remove ${g.name}`}
                  data-testid={`split-group-remove-${gi}`}
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
            ))}
            <Button
              variant="outline"
              size="sm"
              onClick={() => addSplitGroup(1)}
              className="rounded-xl"
              disabled={splitGroups.length >= SPLIT_BILL_MAX_GUESTS}
              data-testid="split-add-group-button"
            >
              <Plus className="h-3.5 w-3.5" />
              Add guest
            </Button>
          </div>

          {/* Item list with per-item group selector */}
          <div className="max-h-[45vh] space-y-4 overflow-y-auto py-2">
            {(selectedSession?.orders || [])
              .filter((o) => o.status !== "voided")
              .map((order, orderIdx) => {
                const items = (order.items || [])
                  .map((it, origIdx) => ({ it, origIdx }))
                  .filter(({ it }) => !it.voided);
                if (items.length === 0) return null;
                return (
                  <div key={order.id || orderIdx} className="space-y-2 rounded-2xl border border-border bg-card p-3">
                    <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                      Order {order.order_number || orderIdx + 1}
                    </p>
                    {items.map(({ it: item, origIdx }) => {
                      const key = `${order.id}#${origIdx}`;
                      const assignedGid = splitAssignments[key];
                      const isMissing = !assignedGid;
                      const itemTotal = Number(item.line_total ?? ((item.price || 0) * (item.quantity || 0)));
                      return (
                        <div
                          key={origIdx}
                          className={`flex flex-wrap items-center justify-between gap-2 rounded-xl px-3 py-2 ${isMissing
                            ? (splitShowMissing
                              ? "bg-destructive/10 ring-1 ring-destructive/50"
                              : "bg-amber-500/10 ring-1 ring-amber-400/50")
                            : "bg-secondary/40"}`}
                          data-testid={`split-item-row-${order.id}-${origIdx}`}
                          data-unassigned={isMissing ? "true" : "false"}
                        >
                          <div className="min-w-0 flex-1">
                            <p className="text-sm font-medium truncate">
                              {item.quantity}× {item.name}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              {formatPrice(itemTotal)}
                              {isMissing ? (
                                <span className={`ml-2 font-semibold ${splitShowMissing ? "text-destructive" : "text-amber-700 dark:text-amber-300"}`}>
                                  · needs a guest
                                </span>
                              ) : null}
                            </p>
                          </div>
                          <div className="flex flex-wrap items-center gap-1">
                            {splitGroups.length > 6 ? (
                              /* Session 70 — 40 guests would be 40 buttons per line; use a picker instead */
                              <Select value={assignedGid || ""} onValueChange={(gid) => assignItemToGroup(order.id, origIdx, gid)}>
                                <SelectTrigger className={`h-8 w-40 rounded-lg text-xs ${assignedGid ? "border-[hsl(var(--accent))]/60 font-semibold" : ""}`} data-testid={`split-item-select-${order.id}-${origIdx}`}>
                                  <SelectValue placeholder="Pick a guest…" />
                                </SelectTrigger>
                                <SelectContent className="max-h-72">
                                  {splitGroups.map((g) => (
                                    <SelectItem key={g.id} value={g.id} data-testid={`split-item-assign-${order.id}-${origIdx}-${g.id}`}>{g.name}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            ) : splitGroups.map((g) => (
                              <button
                                key={g.id}
                                type="button"
                                onClick={() => assignItemToGroup(order.id, origIdx, g.id)}
                                className={`h-8 rounded-lg px-2.5 text-xs font-semibold transition-colors ${assignedGid === g.id ? "bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))]" : "bg-background text-foreground hover:bg-secondary"}`}
                                data-testid={`split-item-assign-${order.id}-${origIdx}-${g.id}`}
                              >
                                {g.name}
                              </button>
                            ))}
                            {assignedGid ? (
                              <button
                                type="button"
                                onClick={() => setSplitAssignments((prev) => { const out = { ...prev }; delete out[key]; return out; })}
                                className="h-8 rounded-lg px-2 text-xs text-muted-foreground hover:text-destructive"
                                data-testid={`split-item-clear-${order.id}-${origIdx}`}
                              >
                                Clear
                              </button>
                            ) : null}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                );
              })}
          </div>

          {/* Live per-guest totals preview */}
          <div className={`grid gap-2 border-t border-border pt-3 ${splitGroups.length > 12 ? "grid-cols-2 sm:grid-cols-4 md:grid-cols-6" : "sm:grid-cols-2 md:grid-cols-3"}`} data-testid="split-preview-totals">
            {splitGroups.map((g) => {
              const total = (selectedSession?.orders || [])
                .filter((o) => o.status !== "voided")
                .reduce((sum, o) => {
                  return sum + (o.items || []).reduce((s, it, i) => {
                    if (it.voided) return s;
                    if (splitAssignments[`${o.id}#${i}`] !== g.id) return s;
                    return s + (Number(it.line_total ?? ((it.price || 0) * (it.quantity || 0))) || 0);
                  }, 0);
                }, 0);
              return (
                <div key={g.id} className="rounded-xl border border-border bg-card p-3">
                  <p className="text-xs font-semibold text-muted-foreground">{g.name}</p>
                  <p className="text-lg font-bold text-[hsl(var(--accent))]" data-testid={`split-preview-total-${g.id}`}>{formatPrice(total)}</p>
                </div>
              );
            })}
          </div>

          </div>

          <DialogFooter>
            <Button variant="outline" className="rounded-xl" onClick={() => setSplitBillOpen(false)} data-testid="split-cancel-button">
              Cancel
            </Button>
            <Button
              className="rounded-xl bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))] hover:bg-[hsl(var(--accent))]/90"
              disabled={splitBusy}
              onClick={submitSplitBill}
              data-testid="split-save-button"
            >
              {splitBusy
                ? "Saving…"
                : unassignedSplitKeys.length > 0
                  ? `Save split (${unassignedSplitKeys.length} unassigned)`
                  : "Save split"}
            </Button>
          </DialogFooter>
          </>) : null}
        </DialogContent>
      </Dialog>

      {/* Voucher Apply Dialog */}
      <Dialog open={voucherDialogOpen} onOpenChange={setVoucherDialogOpen}>
        <DialogContent className="rounded-[28px] border-border bg-background sm:max-w-md" data-testid="voucher-apply-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Ticket className="h-5 w-5" />
              Apply voucher
            </DialogTitle>
            <DialogDescription>Enter a voucher code to discount this receipt before payment.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Label htmlFor="voucher-apply-code">Voucher code</Label>
            <Input
              id="voucher-apply-code"
              value={voucherCodeInput}
              onChange={(e) => setVoucherCodeInput(e.target.value.toUpperCase())}
              onKeyDown={(e) => { if (e.key === "Enter") submitApplyVoucher(); }}
              className="h-12 rounded-xl font-mono tracking-wider text-lg"
              placeholder="e.g. WELCOME50"
              autoFocus
              data-testid="voucher-apply-code-input"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-xl" onClick={() => setVoucherDialogOpen(false)} data-testid="voucher-apply-cancel">
              Cancel
            </Button>
            <Button
              className="rounded-xl bg-emerald-600 text-white hover:bg-emerald-600/90"
              disabled={voucherBusy || !voucherCodeInput.trim()}
              onClick={submitApplyVoucher}
              data-testid="voucher-apply-submit"
            >
              {voucherBusy ? "Applying…" : "Apply"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Email Receipt Dialog */}
      <Dialog open={emailReceiptOpen} onOpenChange={setEmailReceiptOpen}>
        <DialogContent className="rounded-[28px] border-border bg-background sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="h-5 w-5" />
              Email Receipt to Customer
            </DialogTitle>
            <DialogDescription>Send a digital receipt for Table {selectedSession?.table_number} to the customer's email</DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="customer-email">Customer Email</Label>
              <FastInput
                id="customer-email"
                type="email"
                value={receiptEmail}
                onChange={(e) => setReceiptEmail(e.target.value)}
                className="h-11 rounded-xl"
                placeholder="customer@example.com"
              />
            </div>
            <div className="rounded-xl bg-secondary/50 p-4 space-y-1 text-sm">
              <p className="font-medium">Receipt will include:</p>
              <ul className="list-disc list-inside text-muted-foreground space-y-0.5">
                <li>{selectedSession?.order_count || 0} orders</li>
                <li>{selectedSession?.total_items || 0} items</li>
                <li>Subtotal, VAT breakdown, and total</li>
              </ul>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => { setEmailReceiptOpen(false); setReceiptEmail(""); }} className="rounded-xl">
              Cancel
            </Button>
            <Button 
              className="rounded-xl bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))] hover:bg-[hsl(var(--accent))]/90"
              onClick={async () => {
                if (!receiptEmail || !selectedSession?.id) {
                  toast.error("Please enter a valid email address");
                  return;
                }
                try {
                  await http.post(
                    `/staff/email-receipt?session_id=${encodeURIComponent(selectedSession.id)}`,
                    { email: receiptEmail.trim() },
                    { headers: authHeaders }
                  );
                  toast.success(`Receipt sent to ${receiptEmail}`);
                  setEmailReceiptOpen(false);
                  setReceiptEmail("");
                } catch (error) {
                  toast.error(error.response?.data?.detail || "Could not send receipt. Check email settings.");
                }
              }}
              disabled={!receiptEmail}
              data-testid="staff-send-receipt-button"
            >
              <Mail className="h-4 w-4" />
              Send Receipt
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ---- Void password prompt --------------------------------------
          Shared by both whole-order void and line-void flows.
          Line mode shows a checklist of unvoided lines — the waiter can
          tick as many as they need and clear them with one password.
          Password is never persisted. */}
      <Dialog open={voidDialog.open} onOpenChange={(open) => setVoidDialog((d) => ({ ...d, open }))}>
        <DialogContent className="rounded-[24px] sm:max-w-md" data-testid="void-dialog">
          {/* Session 69 — build the body only while open (a closed dialog used to re-render its whole list on every tap) */}
          {voidDialog.open ? (<>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Ban className="h-5 w-5 text-destructive" />
              {voidDialog.mode === "order" ? "Void entire order" : "Void items"}
            </DialogTitle>
            <DialogDescription>
              {voidDialog.mode === "order"
                ? "All items on this order will be marked voided and the table total will drop to zero for this ticket."
                : "Tick every line you want to void. Enter the manager's void password once and clear them all at once."}
              {voidDialog.orderLabel ? <span className="ml-1 text-xs opacity-75">Order {voidDialog.orderLabel}.</span> : null}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {voidDialog.mode === "line" && voidDialog.lines.length > 0 ? (
              <div className="space-y-2 rounded-2xl border border-border bg-secondary/40 p-3" data-testid="void-lines-checklist">
                <div className="flex items-center justify-between">
                  <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    Lines to void ({voidDialog.lines.filter((l) => l.checked).length} of {voidDialog.lines.length})
                  </p>
                  <div className="flex gap-2 text-xs">
                    <button
                      type="button"
                      className="rounded-md px-2 py-0.5 text-xs font-medium text-[hsl(var(--accent))] hover:bg-[hsl(var(--accent))]/10"
                      onClick={() => setAllVoidLines(true)}
                      data-testid="void-lines-select-all"
                    >
                      Select all
                    </button>
                    <button
                      type="button"
                      className="rounded-md px-2 py-0.5 text-xs font-medium text-muted-foreground hover:bg-muted"
                      onClick={() => setAllVoidLines(false)}
                      data-testid="void-lines-clear-all"
                    >
                      Clear
                    </button>
                  </div>
                </div>
                <div className="max-h-[240px] space-y-1 overflow-y-auto pr-1">
                  {voidDialog.lines.map((row) => (
                    <label
                      key={row._uniqueKey || row.index}
                      className={`flex cursor-pointer items-start gap-3 rounded-xl border px-3 py-2 text-sm transition ${row.checked ? "border-destructive/60 bg-destructive/5" : "border-border bg-card hover:border-border/80"}`}
                      data-testid={`void-line-row-${row._uniqueKey || row.index}`}
                    >
                      <Checkbox
                        checked={row.checked}
                        onCheckedChange={() => toggleVoidLine(row.index, row._uniqueKey)}
                        className="mt-0.5"
                        data-testid={`void-line-check-${row._uniqueKey || row.index}`}
                      />
                      <div className="min-w-0 flex-1">
                        <div className="flex items-start justify-between gap-2">
                          <span className="min-w-0 truncate font-medium">
                            <span className="text-muted-foreground">{row.quantity}× </span>{row.name}
                          </span>
                          <span className="shrink-0 font-semibold">{formatPrice(row.line_total)}</span>
                        </div>
                        {row.note ? <p className="mt-0.5 text-xs italic text-muted-foreground">{row.note}</p> : null}
                        {row.quantity > 1 ? (
                          // Session 31.10 — void only some of this line ("1 of 3").
                          <div className="mt-1.5 flex items-center gap-2 text-xs" onClick={(e) => e.preventDefault()}>
                            <span className="text-muted-foreground">Void</span>
                            <div className="inline-flex items-center rounded-lg border border-border bg-background">
                              <button type="button" className="h-7 w-7 rounded-l-lg text-base leading-none hover:bg-muted disabled:opacity-40" disabled={(row.voidQty || row.quantity) <= 1} onClick={(e) => { e.preventDefault(); e.stopPropagation(); setVoidLineQty(row.index, row._uniqueKey, -1); }} aria-label="Void one fewer" data-testid={`void-line-qty-minus-${row._uniqueKey || row.index}`}>−</button>
                              <span className="min-w-[1.5rem] text-center font-semibold tabular-nums" data-testid={`void-line-qty-${row._uniqueKey || row.index}`}>{row.voidQty || row.quantity}</span>
                              <button type="button" className="h-7 w-7 rounded-r-lg text-base leading-none hover:bg-muted disabled:opacity-40" disabled={(row.voidQty || row.quantity) >= row.quantity} onClick={(e) => { e.preventDefault(); e.stopPropagation(); setVoidLineQty(row.index, row._uniqueKey, 1); }} aria-label="Void one more" data-testid={`void-line-qty-plus-${row._uniqueKey || row.index}`}>+</button>
                            </div>
                            <span className="text-muted-foreground">of {row.quantity}{(row.voidQty || row.quantity) < row.quantity ? ` · ${row.quantity - (row.voidQty || row.quantity)} stay${row.quantity - (row.voidQty || row.quantity) === 1 ? "s" : ""} on the order` : ""}</span>
                          </div>
                        ) : null}
                      </div>
                    </label>
                  ))}
                </div>
              </div>
            ) : null}
            <div className="space-y-2">
              <Label htmlFor="void-password">Void password</Label>
              <FastInput
                id="void-password"
                type="password"
                autoFocus
                className="h-11 rounded-xl"
                value={voidDialog.password}
                onChange={(e) => setVoidDialog((d) => ({ ...d, password: e.target.value }))}
                placeholder="Manager password"
                data-testid="void-password-field"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="void-reason">Reason <span className="text-muted-foreground">(optional)</span></Label>
              <FastInput
                id="void-reason"
                className="h-11 rounded-xl"
                maxLength={240}
                value={voidDialog.reason}
                onChange={(e) => setVoidDialog((d) => ({ ...d, reason: e.target.value }))}
                placeholder="e.g. customer left, wrong item"
                data-testid="void-reason-field"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-xl" onClick={closeVoidDialog} disabled={voidSubmitting}>Cancel</Button>
            <Button
              className="rounded-xl bg-destructive text-white hover:bg-destructive/90"
              onClick={submitVoid}
              disabled={voidSubmitting || (voidDialog.mode === "line" && voidDialog.lines.filter((l) => l.checked).length === 0)}
              data-testid="void-confirm-button"
            >
              <Ban className="h-4 w-4" />
              {voidSubmitting
                ? "Voiding…"
                : voidDialog.mode === "order"
                  ? "Void order"
                  : `Void ${voidDialog.lines.filter((l) => l.checked).length || ""} line${voidDialog.lines.filter((l) => l.checked).length === 1 ? "" : "s"}`.trim()}
            </Button>
          </DialogFooter>
          </>) : null}
        </DialogContent>
      </Dialog>

      {/* Shift Handover — lists this user's open tables with checkboxes
          and lets them transfer ownership to another active waiter /
          supervisor / manager. Mobile-first: large touch targets, one
          column on phones, stacked footer buttons. */}
      <Dialog open={staffHandover.open} onOpenChange={(open) => setStaffHandover((s) => ({ ...s, open }))}>
        <DialogContent className="flex max-h-[92vh] flex-col rounded-[24px] sm:max-w-md" data-testid="staff-handover-dialog">
          <DialogHeader>
            <DialogTitle className="font-['Space_Grotesk'] text-2xl">Shift Handover</DialogTitle>
            <DialogDescription>
              {sessionUser ? `Hand your open tables over to another waiter, supervisor or manager.` : ""}
            </DialogDescription>
          </DialogHeader>
          <div className="-mx-1 flex-1 space-y-4 overflow-y-auto px-1">
            {staffHandover.loading ? (
              <Skeleton className="h-32 w-full rounded-2xl" />
            ) : staffHandover.myTables.length ? (
              <>
                <div className="rounded-2xl border border-border bg-secondary/40 p-3" data-testid="handover-from">
                  <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">From</p>
                  <p className="mt-1 text-sm font-medium">{sessionUser?.full_name} <span className="text-xs text-muted-foreground capitalize">· {sessionUser?.role}</span></p>
                </div>
                <div className="space-y-1">
                  <div className="flex items-center justify-between">
                    <Label className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Active open tables ({staffHandover.myTables.length})</Label>
                    <button
                      type="button"
                      className="text-xs font-medium text-[hsl(var(--primary))] underline-offset-2 hover:underline"
                      onClick={() => selectAllHandover(staffHandover.selected.size !== staffHandover.myTables.length)}
                      data-testid="handover-toggle-all"
                    >
                      {staffHandover.selected.size === staffHandover.myTables.length ? "Clear" : "Select all"}
                    </button>
                  </div>
                  <ul className="space-y-1.5" data-testid="handover-my-tables">
                    {staffHandover.myTables.map((t) => {
                      const checked = staffHandover.selected.has(t.id);
                      return (
                        <li key={t.id}>
                          <button
                            type="button"
                            onClick={() => toggleHandoverSelected(t.id)}
                            className={`flex w-full items-center gap-3 rounded-xl border px-3 py-3 text-left transition-colors ${checked ? "border-[hsl(var(--accent))] bg-[hsl(var(--accent))]/10" : "border-border bg-background hover:bg-secondary/60"}`}
                            data-testid={`handover-table-${t.id}`}
                          >
                            <span className={`flex h-5 w-5 shrink-0 items-center justify-center rounded border ${checked ? "border-[hsl(var(--accent))] bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))]" : "border-border"}`}>
                              {checked ? <Check className="h-3.5 w-3.5" /> : null}
                            </span>
                            <div className="min-w-0 flex-1">
                              <p className="font-medium">Table {t.table_number}</p>
                              <p className="text-xs text-muted-foreground">
                                {t.order_count} order{t.order_count === 1 ? "" : "s"} · {formatPrice(t.total_value || 0)}
                                <span className="ml-1">· {t.assigned_user_id === sessionUser?.id ? "You" : (t.assigned_user_name || "Unassigned")}</span>
                              </p>
                            </div>
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                </div>
                <div className="space-y-2 rounded-2xl border border-border bg-secondary/40 p-3">
                  <Label htmlFor="handover-to" className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Transfer to</Label>
                  {staffHandover.targets.length ? (
                    <Select value={staffHandover.toUserId} onValueChange={(v) => setStaffHandover((s) => ({ ...s, toUserId: v }))}>
                      <SelectTrigger id="handover-to" className="h-11 w-full rounded-xl border-border bg-background" data-testid="handover-to-select">
                        <SelectValue placeholder="Pick a user" />
                      </SelectTrigger>
                      <SelectContent>
                        {staffHandover.targets.map((u) => (
                          <SelectItem key={u.id} value={u.id} className="capitalize">{u.full_name} · {u.role}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <p className="rounded-xl border border-dashed border-border p-3 text-xs text-muted-foreground">No other active waiters / supervisors / managers to hand over to.</p>
                  )}
                </div>
              </>
            ) : (
              <EmptyState icon={Users} title="No open tables" description="There are no open tables to hand over right now." />
            )}
          </div>
          <DialogFooter className="flex flex-col gap-2 sm:flex-row">
            <Button variant="outline" className="h-11 rounded-xl" onClick={() => setStaffHandover((s) => ({ ...s, open: false }))} data-testid="handover-cancel">Cancel</Button>
            {staffHandover.myTables.length && staffHandover.targets.length ? (
              <>
                <Button
                  variant="outline"
                  className="h-11 rounded-xl"
                  disabled={staffHandover.saving || !staffHandover.selected.size}
                  onClick={() => submitHandover("selected")}
                  data-testid="handover-transfer-selected"
                >
                  {staffHandover.saving ? "Transferring…" : `Transfer selected (${staffHandover.selected.size})`}
                </Button>
                <Button
                  className="h-11 rounded-xl bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))] hover:bg-[hsl(var(--accent))]/90"
                  disabled={staffHandover.saving}
                  onClick={() => submitHandover("all")}
                  data-testid="handover-transfer-all"
                >
                  {staffHandover.saving ? "Transferring…" : `Transfer all (${staffHandover.myTables.length})`}
                </Button>
              </>
            ) : null}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Tips drill-down — same UX as the owner's `Tips → click row` flow,
          but scoped to the signed-in waiter via /staff/my-tips/orders. */}
      <Dialog open={staffTipDetail.open} onOpenChange={(open) => setStaffTipDetail((d) => ({ ...d, open }))}>
        <DialogContent className="flex max-h-[90vh] flex-col rounded-[24px] sm:max-w-2xl" data-testid="staff-tip-detail-dialog">
          <DialogHeader>
            <DialogTitle className="font-['Space_Grotesk'] text-2xl">
              Tip details · {sessionUser?.full_name || "Me"}
            </DialogTitle>
            <DialogDescription>
              {staffTipDetail.data
                ? `${staffTipDetail.data.summary.count} ${staffTipDetail.data.summary.count === 1 ? "order" : "orders"} with tips · click any order to open the full receipt.`
                : "Loading your tip orders…"}
            </DialogDescription>
          </DialogHeader>
          <div className="-mx-1 flex-1 space-y-4 overflow-y-auto px-1">
            {staffTipDetail.loading ? (
              <Skeleton className="h-32 w-full rounded-2xl" />
            ) : staffTipDetail.error ? (
              <div className="rounded-2xl border border-destructive/30 bg-destructive/10 p-4 text-sm" data-testid="staff-tip-error">
                <p className="font-medium text-destructive">Couldn't load your tips</p>
                <p className="mt-1 text-xs text-muted-foreground">{staffTipDetail.error}</p>
              </div>
            ) : staffTipDetail.data ? (
              <>
                <div className="grid grid-cols-3 gap-2">
                  <div className="rounded-2xl border border-emerald-500/30 bg-emerald-500/10 p-3" data-testid="staff-tip-cash">
                    <p className="text-[10px] font-semibold uppercase tracking-wide text-emerald-700 dark:text-emerald-300">Cash tips</p>
                    <p className="mt-1 text-xl font-semibold text-emerald-700 dark:text-emerald-300">{formatPrice(staffTipDetail.data.summary.cash_tips)}</p>
                  </div>
                  <div className="rounded-2xl border border-sky-500/30 bg-sky-500/10 p-3" data-testid="staff-tip-card">
                    <p className="text-[10px] font-semibold uppercase tracking-wide text-sky-700 dark:text-sky-300">Card tips</p>
                    <p className="mt-1 text-xl font-semibold text-sky-700 dark:text-sky-300">{formatPrice(staffTipDetail.data.summary.card_tips)}</p>
                  </div>
                  <div className="rounded-2xl border border-amber-500/30 bg-amber-500/10 p-3" data-testid="staff-tip-total">
                    <p className="text-[10px] font-semibold uppercase tracking-wide text-amber-700 dark:text-amber-300">Total tips</p>
                    <p className="mt-1 text-xl font-semibold text-amber-700 dark:text-amber-300">{formatPrice(staffTipDetail.data.summary.total_tips)}</p>
                  </div>
                </div>
                {staffTipDetail.data.summary.split_tips > 0 ? (
                  <p className="text-xs text-muted-foreground">Split-method tips: {formatPrice(staffTipDetail.data.summary.split_tips)} (included in totals).</p>
                ) : null}
                {Number(staffTipDetail.data.summary.service_fee_total) > 0 ? (
                  <div className="flex flex-wrap items-center justify-between gap-2 rounded-2xl border border-border bg-secondary/40 px-3 py-2 text-xs" data-testid="staff-tip-gratuity-breakdown">
                    <span className="inline-flex items-center gap-1.5 text-muted-foreground"><HandCoins className="h-3.5 w-3.5" /> Service fee <span className="font-semibold text-foreground">{formatPrice(staffTipDetail.data.summary.service_fee_total)}</span></span>
                    <span className="text-muted-foreground">+ Extra tips <span className="font-semibold text-foreground">{formatPrice(staffTipDetail.data.summary.extra_tip_total ?? 0)}</span></span>
                    <span className="text-muted-foreground">= Total <span className="font-semibold text-foreground">{formatPrice(staffTipDetail.data.summary.total_tips)}</span></span>
                  </div>
                ) : null}
                {(() => {
                  const grouped = groupTipRowsBySplitTable(staffTipDetail.data.orders);
                  return grouped.tables.map((t) => (
                    <div key={t.key} className="space-y-1" data-testid={`staff-tip-split-table-${t.key}`}>
                      <SplitGuestBreakdown
                        breakdown={t.breakdown}
                        title={`Table ${t.breakdown.table_number || "—"} · split bill · your tips ${formatPrice(t.myTip)}`}
                        myTipBySplit={t.myTipBySplit}
                        testId={`staff-tip-split-${t.key}`}
                      />
                    </div>
                  ));
                })()}
                {groupTipRowsBySplitTable(staffTipDetail.data.orders).plain.length ? (
                  <div className="rounded-2xl border border-border bg-card">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Order</TableHead>
                          <TableHead>Table</TableHead>
                          <TableHead>Date / Time</TableHead>
                          <TableHead className="text-right">Total</TableHead>
                          <TableHead className="text-right">Tip</TableHead>
                          <TableHead>Method</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {groupTipRowsBySplitTable(staffTipDetail.data.orders).plain.map((o) => {
                          const m = (o.payment_method || "").toLowerCase();
                          const tone = m === "cash"
                            ? "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-emerald-500/30"
                            : m === "card"
                              ? "bg-sky-500/15 text-sky-700 dark:text-sky-300 border-sky-500/30"
                              : m === "split"
                                ? "bg-violet-500/15 text-violet-700 dark:text-violet-300 border-violet-500/30"
                                : "bg-secondary text-muted-foreground border-border";
                          return (
                            <TableRow
                              key={o.id}
                              className="cursor-pointer transition-colors hover:bg-secondary/50"
                              onClick={() => setStaffTipReceipt({ open: true, order: o })}
                              data-testid={`staff-tip-order-row-${o.id}`}
                            >
                              <TableCell className="font-medium">
                                {o.order_number}
                                {o.split_label ? (
                                  <span className={`ml-2 rounded-md border px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide ${o.credit_kind === "service_fee" ? "border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-300" : "border-violet-500/40 bg-violet-500/10 text-violet-700 dark:text-violet-300"}`}>
                                    {o.split_label}
                                  </span>
                                ) : null}
                              </TableCell>
                              <TableCell>{o.table_number || "—"}</TableCell>
                              <TableCell className="text-xs text-muted-foreground">{o.created_at ? new Date(o.created_at).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }) : "—"}</TableCell>
                              <TableCell className="text-right">{formatPrice(o.total ?? (Number(o.subtotal || 0) + Number(o.vat_total || 0)))}</TableCell>
                              <TableCell className="text-right font-semibold">
                                {formatPrice(o.tip_amount || 0)}
                                {!o.credit_kind && Number(o.service_fee_amount) > 0 ? (
                                  <p className="text-[10px] font-normal text-muted-foreground">fee {formatPrice(o.service_fee_amount)}{Number(o.extra_tip_amount) > 0 ? ` + tip ${formatPrice(o.extra_tip_amount)}` : ""}</p>
                                ) : null}
                              </TableCell>
                              <TableCell><Badge className={`capitalize ${tone}`}>{m || "—"}</Badge></TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </div>
                ) : !groupTipRowsBySplitTable(staffTipDetail.data.orders).tables.length ? (
                  <EmptyState icon={Receipt} title="No pending tips" description="You don't have any pending tips right now. Closed tables with tips routed to you will show up here." />
                ) : null}
              </>
            ) : null}
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-xl" onClick={() => setStaffTipDetail({ open: false, loading: false, data: null, error: null })}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Lightweight receipt view for a staff tip row — read-only,
          no refund / void actions because waiters can't issue those. */}
      <Dialog open={staffTipReceipt.open} onOpenChange={(open) => setStaffTipReceipt((d) => ({ ...d, open }))}>
        <DialogContent className="flex max-h-[90vh] flex-col rounded-[24px] sm:max-w-md" data-testid="staff-tip-receipt-dialog">
          <DialogHeader>
            <DialogTitle className="font-['Space_Grotesk'] text-xl">
              Receipt · {staffTipReceipt.order?.order_number || ""}
            </DialogTitle>
            <DialogDescription>
              {staffTipReceipt.order ? `Table ${staffTipReceipt.order.table_number || "—"} · ${staffTipReceipt.order.created_at ? new Date(staffTipReceipt.order.created_at).toLocaleString() : ""}` : ""}
            </DialogDescription>
          </DialogHeader>
          <div className="-mx-1 flex-1 space-y-2 overflow-y-auto px-1 text-sm">
            {staffTipReceipt.order ? (
              <>
                <ul className="divide-y divide-border rounded-2xl border border-border">
                  {(staffTipReceipt.order.items || []).map((it, idx) => (
                    <li key={it.id || `receipt-item-${idx}`} className="flex items-start justify-between gap-2 px-3 py-2">
                      <div className="min-w-0">
                        <p className="truncate font-medium">{it.quantity || 1} × {it.name}</p>
                        {it.note ? <p className="text-xs text-muted-foreground">{it.note}</p> : null}
                      </div>
                      <span className="font-semibold">{formatPrice(Number(it.line_total ?? (Number(it.unit_price || 0) * Number(it.quantity || 1))))}</span>
                    </li>
                  ))}
                </ul>
                <div className="space-y-1 rounded-2xl border border-border bg-secondary/40 p-3">
                  <div className="flex justify-between"><span className="text-muted-foreground">Subtotal</span><span className="font-medium">{formatPrice(Number(staffTipReceipt.order.subtotal || 0))}</span></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">VAT</span><span className="font-medium">{formatPrice(Number(staffTipReceipt.order.vat_total || 0))}</span></div>
                  {Number(staffTipReceipt.order.service_fee_amount) > 0 && staffTipReceipt.order.credit_kind !== "service_fee" ? (
                    <>
                      <div className="flex justify-between"><span className="text-muted-foreground">Service fee{staffTipReceipt.order.service_fee_pct ? ` (${staffTipReceipt.order.service_fee_pct}%)` : ""}</span><span className="font-medium">{formatPrice(Number(staffTipReceipt.order.service_fee_amount || 0))}</span></div>
                      <div className="flex justify-between"><span className="text-muted-foreground">Extra tip</span><span className="font-medium">{formatPrice(Number(staffTipReceipt.order.extra_tip_amount ?? Math.max(0, Number(staffTipReceipt.order.tip_amount || 0) - Number(staffTipReceipt.order.service_fee_amount || 0))))}</span></div>
                    </>
                  ) : null}
                  <div className="flex justify-between"><span className="text-muted-foreground">{staffTipReceipt.order.credit_kind === "service_fee" ? "Service fee" : Number(staffTipReceipt.order.service_fee_amount) > 0 ? "Service fee + tip" : "Tip"}</span><span className="font-medium">{formatPrice(Number(staffTipReceipt.order.tip_amount || 0))}</span></div>
                  <div className="flex justify-between border-t border-border pt-1 text-base"><span>Total</span><span className="font-semibold">{formatPrice(Number(staffTipReceipt.order.total ?? (Number(staffTipReceipt.order.subtotal || 0) + Number(staffTipReceipt.order.vat_total || 0))))}</span></div>
                </div>
                <p className="text-xs text-muted-foreground">Payment method: <span className="capitalize font-medium">{staffTipReceipt.order.payment_method || "—"}</span></p>
              </>
            ) : null}
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-xl" onClick={() => setStaffTipReceipt({ open: false, order: null })}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Staff — Reopen Table dialog (Void Manager Password gated).
          Available from the Paid Receipt screen. Reopens the linked
          table session so additional / corrective orders can be added. */}
      <Dialog open={staffReopenTable.open} onOpenChange={(open) => setStaffReopenTable((d) => ({ ...d, open }))}>
        <DialogContent className="rounded-[24px] sm:max-w-md" data-testid="staff-reopen-table-dialog">
          <DialogHeader>
            <DialogTitle className="font-['Space_Grotesk'] text-2xl">Reopen Table</DialogTitle>
            <DialogDescription>
              {staffReopenTable.order ? (
                <>Order {staffReopenTable.order.order_number} · Void Manager Password required. Existing paid receipt stays in history; the table goes back to "open" so you can add corrective orders.</>
              ) : null}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label htmlFor="staff-rt-reason">Reason <span className="text-xs font-normal text-muted-foreground">(optional)</span></Label>
              <FastInput id="staff-rt-reason" type="text" maxLength={240} placeholder="e.g. Customer added a coffee after closing" value={staffReopenTable.reason} onChange={(e) => setStaffReopenTable((d) => ({ ...d, reason: e.target.value }))} className="h-11 rounded-xl" data-testid="staff-reopen-table-reason" />
            </div>
            <div>
              <Label htmlFor="staff-rt-pin">Void Manager Password</Label>
              <FastInput id="staff-rt-pin" type="password" inputMode="text" maxLength={72} value={staffReopenTable.pin} onChange={(e) => setStaffReopenTable((d) => ({ ...d, pin: e.target.value }))} className="h-11 rounded-xl tracking-widest" data-testid="staff-reopen-table-pin" />
            </div>
          </div>
          <DialogFooter className="flex flex-col gap-2 sm:flex-row">
            <Button variant="outline" className="h-11 rounded-xl" onClick={() => setStaffReopenTable({ open: false, order: null, pin: "", reason: "", saving: false })}>Cancel</Button>
            <Button className="h-11 rounded-xl bg-amber-600 text-white hover:bg-amber-600/90" disabled={staffReopenTable.saving || !staffReopenTable.pin} onClick={submitStaffReopenTable} data-testid="staff-reopen-table-submit">
              <Undo2 className="h-4 w-4" />
              {staffReopenTable.saving ? "Reopening…" : "Reopen Table"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Staff — Correct Payment dialog. Lets a manager change BOTH the
          recorded method AND the cash/card amounts on a paid receipt.
          Void Manager Password required; audit captures old + new
          method + amounts. */}
      <Dialog open={staffCorrectPayment.open} onOpenChange={(open) => setStaffCorrectPayment((d) => ({ ...d, open }))}>
        <DialogContent className="rounded-[24px] sm:max-w-md" data-testid="staff-correct-payment-dialog">
          <DialogHeader>
            <DialogTitle className="font-['Space_Grotesk'] text-2xl">Correct Payment</DialogTitle>
            <DialogDescription>
              {staffCorrectPayment.order ? (
                <>Order {staffCorrectPayment.order.order_number} · Void Manager Password required. Edit method and / or amount.</>
              ) : null}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Payment method</Label>
              <div className="grid grid-cols-3 gap-2 pt-1">
                {[
                  { id: "cash", on: "border-emerald-500 bg-emerald-500/10 text-emerald-700 hover:bg-emerald-500/20 hover:text-emerald-700 dark:text-emerald-300 dark:hover:text-emerald-300" },
                  { id: "card", on: "border-sky-500 bg-sky-500/10 text-sky-700 hover:bg-sky-500/20 hover:text-sky-700 dark:text-sky-300 dark:hover:text-sky-300" },
                  { id: "split", on: "border-violet-500 bg-violet-500/10 text-violet-700 hover:bg-violet-500/20 hover:text-violet-700 dark:text-violet-300 dark:hover:text-violet-300" },
                ].map(({ id, on }) => {
                  const selected = staffCorrectPayment.values.new_method === id;
                  return (
                    <Button
                      key={id}
                      type="button"
                      variant="outline"
                      className={`h-11 rounded-xl capitalize ${selected ? on : ""}`}
                      onClick={() => setStaffCorrectPayment((d) => ({ ...d, values: { ...d.values, new_method: id } }))}
                      data-testid={`staff-correct-payment-method-${id}`}
                    >
                      {id}
                    </Button>
                  );
                })}
              </div>
            </div>
            {(staffCorrectPayment.values.new_method === "cash" || staffCorrectPayment.values.new_method === "split") ? (
              <div>
                <Label htmlFor="staff-cp-cash" className="text-emerald-700 dark:text-emerald-300">Cash amount (R)</Label>
                <FastInput id="staff-cp-cash" type="number" inputMode="decimal" step="0.01" min="0" value={staffCorrectPayment.values.cash_paid} onChange={(e) => setStaffCorrectPayment((d) => ({ ...d, values: { ...d.values, cash_paid: e.target.value } }))} className="h-11 rounded-xl" data-testid="staff-correct-payment-cash" />
              </div>
            ) : null}
            {(staffCorrectPayment.values.new_method === "card" || staffCorrectPayment.values.new_method === "split") ? (
              <div>
                <Label htmlFor="staff-cp-card" className="text-sky-700 dark:text-sky-300">Card amount (R)</Label>
                <FastInput id="staff-cp-card" type="number" inputMode="decimal" step="0.01" min="0" value={staffCorrectPayment.values.card_paid} onChange={(e) => setStaffCorrectPayment((d) => ({ ...d, values: { ...d.values, card_paid: e.target.value } }))} className="h-11 rounded-xl" data-testid="staff-correct-payment-card" />
              </div>
            ) : null}
            <div>
              <Label htmlFor="staff-cp-reason">Reason <span className="text-xs font-normal text-muted-foreground">(optional)</span></Label>
              <FastInput id="staff-cp-reason" type="text" maxLength={240} placeholder="e.g. Cash entered as card by mistake" value={staffCorrectPayment.values.reason} onChange={(e) => setStaffCorrectPayment((d) => ({ ...d, values: { ...d.values, reason: e.target.value } }))} className="h-11 rounded-xl" data-testid="staff-correct-payment-reason" />
            </div>
            <div>
              <Label htmlFor="staff-cp-pin">Void Manager Password</Label>
              <FastInput id="staff-cp-pin" type="password" inputMode="text" maxLength={72} value={staffCorrectPayment.values.manager_pin} onChange={(e) => setStaffCorrectPayment((d) => ({ ...d, values: { ...d.values, manager_pin: e.target.value } }))} className="h-11 rounded-xl tracking-widest" data-testid="staff-correct-payment-pin" />
            </div>
          </div>
          <DialogFooter className="flex flex-col gap-2 sm:flex-row">
            <Button variant="outline" className="h-11 rounded-xl" onClick={() => setStaffCorrectPayment({ open: false, order: null, saving: false, values: { new_method: "cash", cash_paid: "", card_paid: "", reason: "", manager_pin: "" } })}>Cancel</Button>
            <Button className="h-11 rounded-xl bg-sky-600 text-white hover:bg-sky-600/90" disabled={staffCorrectPayment.saving || !staffCorrectPayment.values.manager_pin} onClick={submitStaffCorrectPayment} data-testid="staff-correct-payment-submit">
              {staffCorrectPayment.saving ? "Saving…" : "Apply correction"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Staff — Open Accounts dialog. Mirrors Owner Overview →
          Accounts. Staff can view who hasn't paid AND record a payment
          inline (Cash / Card / Bank, optional reference). */}

      {/* ── Reservations dialog — list + create form + status actions ── */}
      <Dialog open={reservationsDialog.open} onOpenChange={(open) => setReservationsDialog((d) => ({ ...d, open }))}>
        <DialogContent className="flex max-h-[92dvh] flex-col rounded-[24px] border-border bg-background p-0 sm:max-w-2xl" data-testid="staff-reservations-dialog">
          {/* Session 69 — build the body only while open (a closed dialog used to re-render its whole list on every tap) */}
          {reservationsDialog.open ? (<>
          <DialogHeader className="px-6 pt-6">
            <DialogTitle className="flex items-center gap-2 font-['Space_Grotesk'] text-2xl">
              <Calendar className="h-5 w-5" />
              Reservations
            </DialogTitle>
            <DialogDescription>
              {reservationsDialog.reservations.length
                ? `${reservationsDialog.reservations.length} upcoming booking${reservationsDialog.reservations.length === 1 ? "" : "s"}.`
                : "No upcoming reservations. Add one below."}
            </DialogDescription>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto px-6 pb-2 pt-2 space-y-5">
            {/* ── Create form ─────────────────────────────────────── */}
            <div className="rounded-2xl border border-border bg-secondary/40 p-4 space-y-3" data-testid="staff-reservation-create-form">
              <p className="text-sm font-medium">New reservation</p>
              <div className="grid gap-3 sm:grid-cols-2">
                <div className="space-y-1">
                  <Label htmlFor="rsv-name">Customer name</Label>
                  <FastInput id="rsv-name" value={reservationsDialog.form.customer_name}
                    onChange={(e) => setReservationsDialog((d) => ({ ...d, form: { ...d.form, customer_name: e.target.value } }))}
                    placeholder="e.g. Tracy"
                    className="h-11 rounded-xl" data-testid="staff-reservation-name" />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="rsv-phone">Phone</Label>
                  <FastInput id="rsv-phone" value={reservationsDialog.form.phone}
                    onChange={(e) => setReservationsDialog((d) => ({ ...d, form: { ...d.form, phone: e.target.value } }))}
                    inputMode="tel" placeholder="e.g. 072 123 4567"
                    className="h-11 rounded-xl" data-testid="staff-reservation-phone" />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="rsv-party">Party size</Label>
                  <FastInput id="rsv-party" type="number" min="1" value={reservationsDialog.form.party_size}
                    onChange={(e) => setReservationsDialog((d) => ({ ...d, form: { ...d.form, party_size: e.target.value } }))}
                    className="h-11 rounded-xl" data-testid="staff-reservation-party-size" />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="rsv-table">Table</Label>
                  <FastInput id="rsv-table" value={reservationsDialog.form.table_label}
                    onChange={(e) => setReservationsDialog((d) => ({ ...d, form: { ...d.form, table_label: e.target.value } }))}
                    placeholder="e.g. Table 5 / Patio 2"
                    className="h-11 rounded-xl" data-testid="staff-reservation-table" />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="rsv-date">Date</Label>
                  <FastInput id="rsv-date" type="date" value={reservationsDialog.form.reserved_date}
                    onChange={(e) => setReservationsDialog((d) => ({ ...d, form: { ...d.form, reserved_date: e.target.value } }))}
                    className="h-11 rounded-xl" data-testid="staff-reservation-date" />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="rsv-time">Time</Label>
                  <FastInput id="rsv-time" type="time" value={reservationsDialog.form.reserved_time}
                    onChange={(e) => setReservationsDialog((d) => ({ ...d, form: { ...d.form, reserved_time: e.target.value } }))}
                    className="h-11 rounded-xl" data-testid="staff-reservation-time" />
                </div>
                <div className="space-y-1 sm:col-span-2">
                  <Label htmlFor="rsv-notes">Notes <span className="text-muted-foreground">(optional)</span></Label>
                  <FastInput id="rsv-notes" value={reservationsDialog.form.notes}
                    onChange={(e) => setReservationsDialog((d) => ({ ...d, form: { ...d.form, notes: e.target.value } }))}
                    placeholder="e.g. Birthday, vegan party, high chair needed…"
                    className="h-11 rounded-xl" data-testid="staff-reservation-notes" />
                </div>
              </div>
              <Button onClick={submitReservation} disabled={reservationsDialog.saving}
                className="rounded-xl bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] hover:bg-[hsl(var(--primary))]/90"
                data-testid="staff-reservation-save">
                <Plus className="h-4 w-4" />
                {reservationsDialog.saving ? "Saving…" : "Save reservation"}
              </Button>
            </div>

            {/* ── Upcoming list ────────────────────────────────────── */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <p className="text-sm font-medium">Upcoming</p>
                <Button variant="ghost" size="sm" className="rounded-xl" onClick={loadReservations}
                  data-testid="staff-reservation-refresh">
                  <RefreshCw className="h-4 w-4" />
                  Refresh
                </Button>
              </div>
              {reservationsDialog.loading ? (
                <div className="rounded-2xl border border-dashed border-border p-6 text-center text-sm text-muted-foreground">Loading…</div>
              ) : reservationsDialog.reservations.length === 0 ? (
                <div className="rounded-2xl border border-dashed border-border p-6 text-center text-sm text-muted-foreground">No upcoming reservations.</div>
              ) : (
                <div className="space-y-2">
                  {reservationsDialog.reservations.map((r) => {
                    const dt = r.reserved_at ? new Date(r.reserved_at) : null;
                    const when = dt ? dt.toLocaleString(undefined, { weekday: "short", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }) : "—";
                    return (
                      <div key={r.id} className="rounded-2xl border border-border bg-card p-3 space-y-2" data-testid={`staff-reservation-row-${r.id}`}>
                        <div className="flex flex-wrap items-start justify-between gap-2">
                          <div>
                            <p className="font-medium">{r.customer_name} <span className="ml-2 text-xs text-muted-foreground">party of {r.party_size}</span></p>
                            <p className="text-xs text-muted-foreground">{when} {r.table_label ? `· ${r.table_label}` : ""}</p>
                            {r.phone ? <p className="text-xs text-muted-foreground">📞 {r.phone}</p> : null}
                            {r.notes ? <p className="text-xs text-muted-foreground italic">"{r.notes}"</p> : null}
                          </div>
                          <Badge variant="secondary" className="rounded-full">{r.status}</Badge>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          <Button size="sm" className="rounded-xl bg-emerald-600 text-white hover:bg-emerald-700" onClick={() => setReservationStatus(r.id, "arrived")}
                            data-testid={`staff-reservation-arrived-${r.id}`}>
                            Mark arrived
                          </Button>
                          <Button size="sm" variant="outline" className="rounded-xl" onClick={() => setReservationStatus(r.id, "cancelled")}
                            data-testid={`staff-reservation-cancel-${r.id}`}>
                            Cancel
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
          <DialogFooter className="border-t border-border bg-background px-6 py-4">
            <Button variant="outline" className="rounded-xl" onClick={() => setReservationsDialog((d) => ({ ...d, open: false }))} data-testid="staff-reservation-close">Close</Button>
          </DialogFooter>
          </>) : null}
        </DialogContent>
      </Dialog>

      <Dialog open={staffAccounts.open} onOpenChange={(open) => { setStaffTabsSearch(""); setStaffAccounts((d) => ({ ...d, open })); }}>
        <DialogContent className="rounded-[24px] sm:max-w-2xl" data-testid="staff-open-accounts-dialog">
          {/* Session 69 — build the body only while open (a closed dialog used to re-render its whole list on every tap) */}
          {staffAccounts.open ? (<>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-['Space_Grotesk'] text-2xl">
              <Wallet className="h-5 w-5" />
              Tabs
            </DialogTitle>
            <DialogDescription>
              Customer tabs you can keep adding items to and settle when they pay, plus the tables that are open right now.
            </DialogDescription>
          </DialogHeader>
          {/* Session 30.12 — Staff can add items to an EXISTING tab from here,
              mirroring the Retail dashboard's "Add to tab" flow. */}
          <Tabs value={staffTabsView} onValueChange={setStaffTabsView}>
            <TabsList className="h-auto w-full justify-start gap-2 rounded-2xl bg-secondary p-1.5" data-testid="staff-tabs-dialog-tabs">
              <TabsTrigger value="accounts" className="rounded-xl px-3 py-1.5 text-sm" data-testid="staff-tabs-dialog-tab-accounts">
                Tabs
                {staffAccounts.summary?.open_count ? <span className="ml-1.5 rounded-full bg-amber-100 px-1.5 text-[10px] font-semibold text-amber-700 dark:bg-amber-900/40 dark:text-amber-200">{staffAccounts.summary.open_count}</span> : null}
              </TabsTrigger>
              <TabsTrigger value="tabs" className="rounded-xl px-3 py-1.5 text-sm" data-testid="staff-tabs-dialog-tab-open">
                Open tables
                <span className="ml-1.5 rounded-full bg-[hsl(var(--primary))]/15 px-1.5 text-[10px] font-semibold text-[hsl(var(--primary))]">{(tableSessions || []).filter((x) => x.status === "open").length}</span>
              </TabsTrigger>
            </TabsList>
            <TabsContent value="tabs" className="mt-3">
              <div className="relative mb-2">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <FastInput value={staffTabsSearch} onChange={(e) => setStaffTabsSearch(e.target.value)} placeholder="Search tabs & tables…" className="h-10 rounded-xl pl-9" data-testid="staff-tabs-dialog-search" />
              </div>
              {(() => {
                const q = staffTabsSearch.trim().toLowerCase();
                const rows = (tableSessions || []).filter((x) => x.status === "open").filter((x) => {
                  if (!q) return true;
                  const hay = `${x.table_number || ""} table ${x.table_number || ""} ${x.assigned_user_name || ""}`.toLowerCase();
                  return hay.includes(q);
                });
                if (rows.length === 0) {
                  return <p className="py-6 text-center text-sm text-muted-foreground" data-testid="staff-tabs-dialog-empty">{q ? `No table matches "${staffTabsSearch.trim()}".` : "No open tables right now."}</p>;
                }
                return (
                  <div className="max-h-[55vh] space-y-2 overflow-y-auto" data-testid="staff-tabs-dialog-list">
                    {rows.map((sess) => {
                      const paid = isSessionPaid(sess);
                      const pend = (sess.pending_orders || 0) + (sess.confirmed_orders || 0);
                      const label = /^\d+$/.test(String(sess.table_number)) ? `Table ${sess.table_number}` : sess.table_number;
                      return (
                        <div key={sess.id} className="flex flex-wrap items-center justify-between gap-2 rounded-2xl border border-border bg-card p-3" data-testid={`staff-tab-row-${sess.id}`}>
                          <div className="min-w-0 flex-1">
                            <p className="truncate font-semibold">{label}</p>
                            <p className="text-xs text-muted-foreground">
                              {sess.total_items || 0} item{(sess.total_items || 0) === 1 ? "" : "s"} · {formatPrice(sess.total_value || 0)}
                              {sess.assigned_user_name ? ` · ${sess.assigned_user_name}` : ""}
                              {paid ? " · Paid" : pend > 0 ? ` · ${pend} pending` : ""}
                            </p>
                          </div>
                          <div className="flex items-center gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              className="rounded-xl"
                              onClick={() => { setSelectedSessionId(sess.id); setStatusFilter("active-tables"); setStaffAccounts((d) => ({ ...d, open: false })); }}
                              data-testid={`staff-tab-view-${sess.id}`}
                            >
                              View
                            </Button>
                            <Button
                              size="sm"
                              className="rounded-xl bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))] hover:bg-[hsl(var(--accent))]/90"
                              disabled={paid}
                              title={paid ? "Payment already recorded — reopen the table to add items" : "Add more items to this table"}
                              onClick={() => {
                                setSelectedSessionId(sess.id);
                                setStatusFilter("active-tables");
                                setStaffAccounts((d) => ({ ...d, open: false }));
                                setTimeout(() => setAddItemsOpen(true), 120);
                              }}
                              data-testid={`staff-tab-add-items-${sess.id}`}
                            >
                              <Plus className="h-3.5 w-3.5" />
                              Add items
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                );
              })()}
            </TabsContent>
            <TabsContent value="accounts" className="mt-3">
          <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
            <p className="text-xs text-muted-foreground">
              {staffAccounts.summary?.open_count
                ? `${staffAccounts.summary.open_count} tab${staffAccounts.summary.open_count === 1 ? "" : "s"} open · ${formatPrice(staffAccounts.summary.total_outstanding || 0)} outstanding`
                : "No customer tabs are open right now."}
            </p>
            <Button size="sm" className="rounded-xl" onClick={() => setNewTabDialog({ open: true, customer_name: "", phone: "", saving: false })} data-testid="staff-open-tab-button">
              <Plus className="h-3.5 w-3.5" />
              Open tab
            </Button>
          </div>
          {staffAccounts.loading ? (
            <p className="py-6 text-center text-sm text-muted-foreground">Loading…</p>
          ) : staffAccounts.accounts.length ? (
            <div className="space-y-2 max-h-[60vh] overflow-y-auto">
              {staffAccounts.accounts.map((acc) => (
                <div key={acc.id} className="flex items-center justify-between gap-2 rounded-2xl border border-border bg-card p-3" data-testid={`staff-account-row-${acc.id}`}>
                  <div className="min-w-0">
                    <p className="truncate font-semibold">{acc.customer_name || "Unnamed customer"}</p>
                    <p className="text-xs text-muted-foreground">Opened {acc.created_at ? new Date(acc.created_at).toLocaleString() : "—"} · {(acc.orders || []).length} order{(acc.orders || []).length === 1 ? "" : "s"} · {(acc.payments || []).length} payment{(acc.payments || []).length === 1 ? "" : "s"}</p>
                  </div>
                  <div className="flex flex-wrap items-center justify-end gap-2">
                    <div className="text-right">
                      <p className="text-[10px] uppercase tracking-wide text-muted-foreground">Outstanding</p>
                      <p className="font-semibold tabular-nums">{formatPrice(acc.remaining_balance ?? 0)}</p>
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      className="rounded-xl"
                      disabled={tabAddBusyId === acc.id}
                      onClick={() => addItemsToTab(acc)}
                      data-testid={`staff-tab-add-items-account-${acc.id}`}
                    >
                      <Plus className="h-3.5 w-3.5" />
                      {tabAddBusyId === acc.id ? "Opening…" : "Add items"}
                    </Button>
                    <Button
                      size="sm"
                      className="rounded-xl bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))] hover:bg-[hsl(var(--accent))]/90"
                      onClick={() => setStaffAccountDetail({ open: true, account: acc, amount: String(acc.remaining_balance ?? 0), method: "cash", reference: "", saving: false })}
                      data-testid={`staff-account-pay-${acc.id}`}
                    >
                      Record payment
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="py-6 text-center text-sm text-muted-foreground" data-testid="staff-tabs-empty">No open tabs. Tap “Open tab” to start one for a pay-later customer.</p>
          )}
            </TabsContent>
          </Tabs>
          <DialogFooter>
            <Button variant="outline" className="rounded-xl" onClick={() => { loadStaffAccounts(); try { fetchDashboard(true); } catch (_) { /* noop */ } }} disabled={staffAccounts.loading}>
              <RefreshCw className={`h-4 w-4 ${staffAccounts.loading ? "animate-spin" : ""}`} />
              Refresh
            </Button>
            <Button variant="outline" className="rounded-xl" onClick={() => setStaffAccounts((d) => ({ ...d, open: false }))}>Close</Button>
          </DialogFooter>
          </>) : null}
        </DialogContent>
      </Dialog>

      {/* Session 31.2 — Open a new customer tab (no table). */}
      <Dialog open={newTabDialog.open} onOpenChange={(open) => setNewTabDialog((d) => ({ ...d, open }))}>
        <DialogContent className="rounded-[24px] sm:max-w-sm" data-testid="staff-new-tab-dialog">
          <DialogHeader>
            <DialogTitle className="font-['Space_Grotesk'] text-2xl">Open a tab</DialogTitle>
            <DialogDescription>For customers who order now and settle later. Add items to the tab whenever they visit.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1">
              <Label htmlFor="new-tab-name" className="text-xs">Customer name <span className="text-destructive">*</span></Label>
              <FastInput id="new-tab-name" value={newTabDialog.customer_name} onChange={(e) => setNewTabDialog((d) => ({ ...d, customer_name: e.target.value }))} className="h-11 rounded-xl" placeholder="e.g. Willem" data-testid="staff-new-tab-name" />
            </div>
            <div className="space-y-1">
              <Label htmlFor="new-tab-phone" className="text-xs">Phone (optional)</Label>
              <FastInput id="new-tab-phone" value={newTabDialog.phone} onChange={(e) => setNewTabDialog((d) => ({ ...d, phone: e.target.value }))} className="h-11 rounded-xl" placeholder="e.g. 082 123 4567" data-testid="staff-new-tab-phone" />
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" className="rounded-xl" onClick={() => setNewTabDialog((d) => ({ ...d, open: false }))}>Cancel</Button>
            <Button type="button" className="rounded-xl bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))] hover:bg-[hsl(var(--accent))]/90" disabled={newTabDialog.saving || !newTabDialog.customer_name.trim()} onClick={submitNewTab} data-testid="staff-new-tab-save">{newTabDialog.saving ? "Opening…" : "Open tab"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Staff — record payment on a specific open tab. */}
      <Dialog open={staffAccountDetail.open} onOpenChange={(open) => setStaffAccountDetail((d) => ({ ...d, open }))}>
        <DialogContent className="rounded-[24px] sm:max-w-md" data-testid="staff-account-payment-dialog">
          {/* Session 69 — build the body only while open (a closed dialog used to re-render its whole list on every tap) */}
          {staffAccountDetail.open ? (<>
          <DialogHeader>
            <DialogTitle className="font-['Space_Grotesk'] text-2xl">Record payment</DialogTitle>
            <DialogDescription>
              {staffAccountDetail.account ? `${staffAccountDetail.account.customer_name || "Tab"} · outstanding ${formatPrice(staffAccountDetail.account.remaining_balance ?? 0)}` : ""}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Method</Label>
              <div className="grid grid-cols-3 gap-2 pt-1">
                {[
                  { id: "cash", on: "border-emerald-500 bg-emerald-500/10 text-emerald-700 hover:bg-emerald-500/20 hover:text-emerald-700 dark:text-emerald-300 dark:hover:text-emerald-300" },
                  { id: "card", on: "border-sky-500 bg-sky-500/10 text-sky-700 hover:bg-sky-500/20 hover:text-sky-700 dark:text-sky-300 dark:hover:text-sky-300" },
                  { id: "bank", on: "border-violet-500 bg-violet-500/10 text-violet-700 hover:bg-violet-500/20 hover:text-violet-700 dark:text-violet-300 dark:hover:text-violet-300" },
                ].map(({ id, on }) => {
                  const selected = staffAccountDetail.method === id;
                  return (
                    <Button key={id} type="button" variant="outline" className={`h-11 rounded-xl capitalize ${selected ? on : ""}`} onClick={() => setStaffAccountDetail((d) => ({ ...d, method: id }))} data-testid={`staff-account-payment-method-${id}`}>{id}</Button>
                  );
                })}
              </div>
            </div>
            <div>
              <div className="flex items-center justify-between">
                <Label htmlFor="sap-amt">Amount (R)</Label>
                <button
                  type="button"
                  className="text-xs font-medium text-[hsl(var(--primary))] underline-offset-2 hover:underline"
                  onClick={() => setStaffAccountDetail((d) => ({ ...d, amount: String(Number(d.account?.remaining_balance ?? 0).toFixed(2)) }))}
                  data-testid="staff-account-payment-exact"
                >
                  Exact — {formatPrice(staffAccountDetail.account?.remaining_balance ?? 0)}
                </button>
              </div>
              <FastInput id="sap-amt" type="number" inputMode="decimal" min="0" step="0.01" value={staffAccountDetail.amount} onChange={(e) => setStaffAccountDetail((d) => ({ ...d, amount: e.target.value }))} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); submitStaffAccountPayment(); } }} className="h-11 rounded-xl" data-testid="staff-account-payment-amount" />
              {(() => {
                const due = Number(staffAccountDetail.account?.remaining_balance ?? 0);
                const amt = Number(staffAccountDetail.amount || 0);
                if (!(amt > 0)) return null;
                const left = Math.max(0, due - amt);
                const change = Math.max(0, amt - due);
                return (
                  <p className="mt-1 text-xs text-muted-foreground" data-testid="staff-account-payment-hint">
                    {change > 0.009
                      ? `Settles the tab in full — give ${formatPrice(change)} change.`
                      : left <= 0.009
                        ? "This settles the tab in full."
                        : `Part payment — ${formatPrice(left)} will remain on the tab.`}
                  </p>
                );
              })()}
            </div>
            <div>
              <Label htmlFor="sap-ref">Reference <span className="text-xs font-normal text-muted-foreground">(optional)</span></Label>
              <FastInput id="sap-ref" type="text" maxLength={120} placeholder="EFT ref / card last 4 / etc." value={staffAccountDetail.reference} onChange={(e) => setStaffAccountDetail((d) => ({ ...d, reference: e.target.value }))} className="h-11 rounded-xl" data-testid="staff-account-payment-reference" />
            </div>
          </div>
          <DialogFooter className="gap-2 sm:gap-2">
            <Button variant="outline" className="rounded-xl" onClick={() => setStaffAccountDetail({ open: false, account: null, amount: "", method: "cash", reference: "", saving: false })} data-testid="staff-account-payment-cancel">Cancel</Button>
            {/* Session 31.10 — the submit button had gone missing: staff could type an amount but never record it. */}
            <Button
              className="rounded-xl bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))] hover:bg-[hsl(var(--accent))]/90"
              disabled={staffAccountDetail.saving || !(Number(staffAccountDetail.amount || 0) > 0)}
              onClick={submitStaffAccountPayment}
              data-testid="staff-account-payment-submit"
            >
              {staffAccountDetail.saving ? "Recording…" : (() => {
                const due = Number(staffAccountDetail.account?.remaining_balance ?? 0);
                const amt = Number(staffAccountDetail.amount || 0);
                return amt > 0 && amt >= due - 0.009 ? `Settle tab · ${formatPrice(Math.min(amt, due))}` : `Record payment · ${formatPrice(amt)}`;
              })()}
            </Button>
          </DialogFooter>
          </>) : null}
        </DialogContent>
      </Dialog>
      {/* Session 30.7 — per-device slip printer setup */}
      <PrinterSettingsDialog open={printerSetupOpen} onOpenChange={setPrinterSetupOpen} businessName={context?.business?.name} formatPrice={formatPrice} />
      {/* Session 30.9 — unpaid table close requires the Manager PIN */}
      <ManagerPinDialog
        open={closePinPrompt}
        onOpenChange={setClosePinPrompt}
        title="Close unpaid table?"
        description={selectedSession ? (() => {
          const st = sessionPaymentState(selectedSession);
          const paidNote = st.paidAmount > 0.009 ? ` (${formatPrice(st.paidAmount)} already paid — partially paid)` : " and no payment processed";
          return `Table ${selectedSession.table_number} has ${formatPrice(st.outstanding)} outstanding${paidNote}. Enter the manager PIN to close it anyway — the balance will NOT be collected and open tickets will be completed.`;
        })() : ""}
        confirmLabel="Close table"
        onVerify={(pin) => verifyManagerPin(pin, { token: session?.token })}
        onSuccess={(pin) => closeTable(pin)}
        testIdPrefix="staff-close-pin"
      />
      {/* Session 41 — out-of-stock / manager-approval gate for the order just attempted. */}
      <FallbackBlockDialog
        open={!!fallbackBlock}
        onOpenChange={(o) => { if (!o) setFallbackBlock(null); }}
        block={fallbackBlock}
        http={http}
        authHeaders={authHeaders}
        tableSession={selectedSession}
        staffName={sessionUser?.full_name || sessionUser?.name || null}
      />
    </Wrapper>
  );
}
